import { Logger as t, LogLevel as e } from "@firebase/logger";

import { getUA as n, isMobileCordova as s, isReactNative as i, isElectron as r, isIE as o, isUWP as c, isBrowserExtension as a } from "@firebase/util";

import { XhrIo as u, EventType as h, ErrorCode as l, createWebChannelTransport as _, getStatEventTarget as f, WebChannel as d, Event as w, Stat as E } from "@firebase/webchannel-wrapper";

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class T {
    /**
     * Constructs a DatabaseInfo using the provided host, databaseId and
     * persistenceKey.
     *
     * @param databaseId - The database to use.
     * @param persistenceKey - A unique identifier for this Firestore's local
     * storage (used in conjunction with the databaseId).
     * @param host - The Firestore backend host to connect to.
     * @param ssl - Whether to use SSL when connecting.
     * @param forceLongPolling - Whether to use the forceLongPolling option
     * when using WebChannel as the network transport.
     * @param autoDetectLongPolling - Whether to use the detectBufferingProxy
     * option when using WebChannel as the network transport.
     */
    constructor(t, e, n, s, i, r) {
        this.t = t, this.persistenceKey = e, this.host = n, this.ssl = s, this.forceLongPolling = i, 
        this.i = r;
    }
}

/** The default database name for a project. */
/** Represents the database ID a Firestore client is associated with. */
class I {
    constructor(t, e) {
        this.projectId = t, this.database = e || "(default)";
    }
    get o() {
        return "(default)" === this.database;
    }
    isEqual(t) {
        return t instanceof I && t.projectId === this.projectId && t.database === this.database;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Converts a Base64 encoded string to a binary string. */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const m = new t("@firebase/firestore");

// Helper methods are needed because variables can't be exported as read/write
function A() {
    return m.logLevel;
}

/**
 * Sets the verbosity of Cloud Firestore logs (debug, error, or silent).
 *
 * @param logLevel - The verbosity you set for activity and error logging. Can
 *   be any of the following values:
 *
 *   <ul>
 *     <li>`debug` for the most verbose logging level, primarily for
 *     debugging.</li>
 *     <li>`error` to log errors only.</li>
 *     <li><code>`silent` to turn off logging.</li>
 *   </ul>
 */ function R(t, ...n) {
    if (m.logLevel <= e.DEBUG) {
        const e = n.map(g);
        m.debug(`Firestore (8.2.7): ${t}`, ...e);
    }
}

function P(t, ...n) {
    if (m.logLevel <= e.ERROR) {
        const e = n.map(g);
        m.error(`Firestore (8.2.7): ${t}`, ...e);
    }
}

function y(t, ...n) {
    if (m.logLevel <= e.WARN) {
        const e = n.map(g);
        m.warn(`Firestore (8.2.7): ${t}`, ...e);
    }
}

/**
 * Converts an additional log parameter to a string representation.
 */ function g(t) {
    if ("string" == typeof t) return t;
    try {
        return e = t, JSON.stringify(e);
    } catch (e) {
        // Converting to JSON failed, just log the object directly
        return t;
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /** Formats an object as a JSON string, suitable for logging. */
    var e;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Unconditionally fails, throwing an Error with the given message.
 * Messages are stripped in production builds.
 *
 * Returns `never` and can be used in expressions:
 * @example
 * let futureVar = fail('not implemented yet');
 */ function V(t = "Unexpected state") {
    // Log the failure in addition to throw an exception, just in case the
    // exception is swallowed.
    const e = "FIRESTORE (8.2.7) INTERNAL ASSERTION FAILED: " + t;
    // NOTE: We don't use FirestoreError here because these are internal failures
    // that cannot be handled by the user. (Also it would create a circular
    // dependency between the error and assert modules which doesn't work.)
    throw P(e), new Error(e);
}

/**
 * Fails if the given assertion condition is false, throwing an Error with the
 * given message if it did.
 *
 * Messages are stripped in production builds.
 */ function p(t, e) {
    t || V();
}

/**
 * Casts `obj` to `T`. In non-production builds, verifies that `obj` is an
 * instance of `T` before casting.
 */ function b(t, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
e) {
    return t;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Generates `nBytes` of random bytes.
 *
 * If `nBytes < 0` , an error will be thrown.
 */ function v(t) {
    // Polyfills for IE and WebWorker by using `self` and `msCrypto` when `crypto` is not available.
    const e = 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    "undefined" != typeof self && (self.crypto || self.msCrypto), n = new Uint8Array(t);
    if (e && "function" == typeof e.getRandomValues) e.getRandomValues(n); else 
    // Falls back to Math.random
    for (let e = 0; e < t; e++) n[e] = Math.floor(256 * Math.random());
    return n;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class S {
    static u() {
        // Alphanumeric characters
        const t = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", e = Math.floor(256 / t.length) * t.length;
        // The largest byte value that is a multiple of `char.length`.
                let n = "";
        for (;n.length < 20; ) {
            const s = v(40);
            for (let i = 0; i < s.length; ++i) 
            // Only accept values that are [0, maxMultiple), this ensures they can
            // be evenly mapped to indices of `chars` via a modulo operation.
            n.length < 20 && s[i] < e && (n += t.charAt(s[i] % t.length));
        }
        return n;
    }
}

function D(t, e) {
    return t < e ? -1 : t > e ? 1 : 0;
}

/** Helper to compare arrays using isEqual(). */ function C(t, e, n) {
    return t.length === e.length && t.every(((t, s) => n(t, e[s])));
}

/**
 * Returns the immediate lexicographically-following string. This is useful to
 * construct an inclusive range for indexeddb iterators.
 */ function x(t) {
    // Return the input string, with an additional NUL byte appended.
    return t + "\0";
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Immutable class that represents a "proto" byte string.
 *
 * Proto byte strings can either be Base64-encoded strings or Uint8Arrays when
 * sent on the wire. This class abstracts away this differentiation by holding
 * the proto byte string in a common class that must be converted into a string
 * before being sent as a proto.
 */ class N {
    constructor(t) {
        this.h = t;
    }
    static fromBase64String(t) {
        const e = atob(t);
        return new N(e);
    }
    static fromUint8Array(t) {
        const e = 
        /**
 * Helper function to convert an Uint8array to a binary string.
 */
        function(t) {
            let e = "";
            for (let n = 0; n < t.length; ++n) e += String.fromCharCode(t[n]);
            return e;
        }
        /**
 * Helper function to convert a binary string to an Uint8Array.
 */ (t);
        return new N(e);
    }
    toBase64() {
        return t = this.h, btoa(t);
        /** Converts a binary string to a Base64 encoded string. */
        var t;
        /** True if and only if the Base64 conversion functions are available. */    }
    toUint8Array() {
        return function(t) {
            const e = new Uint8Array(t.length);
            for (let n = 0; n < t.length; n++) e[n] = t.charCodeAt(n);
            return e;
        }
        /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (this.h);
    }
    l() {
        return 2 * this.h.length;
    }
    _(t) {
        return D(this.h, t.h);
    }
    isEqual(t) {
        return this.h === t.h;
    }
}

N.T = new N("");

const $ = {
    // Causes are copied from:
    // https://github.com/grpc/grpc/blob/bceec94ea4fc5f0085d81235d8e1c06798dc341a/include/grpc%2B%2B/impl/codegen/status_code_enum.h
    /** Not an error; returned on success. */
    OK: "ok",
    /** The operation was cancelled (typically by the caller). */
    CANCELLED: "cancelled",
    /** Unknown error or an error from a different error domain. */
    UNKNOWN: "unknown",
    /**
     * Client specified an invalid argument. Note that this differs from
     * FAILED_PRECONDITION. INVALID_ARGUMENT indicates arguments that are
     * problematic regardless of the state of the system (e.g., a malformed file
     * name).
     */
    INVALID_ARGUMENT: "invalid-argument",
    /**
     * Deadline expired before operation could complete. For operations that
     * change the state of the system, this error may be returned even if the
     * operation has completed successfully. For example, a successful response
     * from a server could have been delayed long enough for the deadline to
     * expire.
     */
    DEADLINE_EXCEEDED: "deadline-exceeded",
    /** Some requested entity (e.g., file or directory) was not found. */
    NOT_FOUND: "not-found",
    /**
     * Some entity that we attempted to create (e.g., file or directory) already
     * exists.
     */
    ALREADY_EXISTS: "already-exists",
    /**
     * The caller does not have permission to execute the specified operation.
     * PERMISSION_DENIED must not be used for rejections caused by exhausting
     * some resource (use RESOURCE_EXHAUSTED instead for those errors).
     * PERMISSION_DENIED must not be used if the caller can not be identified
     * (use UNAUTHENTICATED instead for those errors).
     */
    PERMISSION_DENIED: "permission-denied",
    /**
     * The request does not have valid authentication credentials for the
     * operation.
     */
    UNAUTHENTICATED: "unauthenticated",
    /**
     * Some resource has been exhausted, perhaps a per-user quota, or perhaps the
     * entire file system is out of space.
     */
    RESOURCE_EXHAUSTED: "resource-exhausted",
    /**
     * Operation was rejected because the system is not in a state required for
     * the operation's execution. For example, directory to be deleted may be
     * non-empty, an rmdir operation is applied to a non-directory, etc.
     *
     * A litmus test that may help a service implementor in deciding
     * between FAILED_PRECONDITION, ABORTED, and UNAVAILABLE:
     *  (a) Use UNAVAILABLE if the client can retry just the failing call.
     *  (b) Use ABORTED if the client should retry at a higher-level
     *      (e.g., restarting a read-modify-write sequence).
     *  (c) Use FAILED_PRECONDITION if the client should not retry until
     *      the system state has been explicitly fixed. E.g., if an "rmdir"
     *      fails because the directory is non-empty, FAILED_PRECONDITION
     *      should be returned since the client should not retry unless
     *      they have first fixed up the directory by deleting files from it.
     *  (d) Use FAILED_PRECONDITION if the client performs conditional
     *      REST Get/Update/Delete on a resource and the resource on the
     *      server does not match the condition. E.g., conflicting
     *      read-modify-write on the same resource.
     */
    FAILED_PRECONDITION: "failed-precondition",
    /**
     * The operation was aborted, typically due to a concurrency issue like
     * sequencer check failures, transaction aborts, etc.
     *
     * See litmus test above for deciding between FAILED_PRECONDITION, ABORTED,
     * and UNAVAILABLE.
     */
    ABORTED: "aborted",
    /**
     * Operation was attempted past the valid range. E.g., seeking or reading
     * past end of file.
     *
     * Unlike INVALID_ARGUMENT, this error indicates a problem that may be fixed
     * if the system state changes. For example, a 32-bit file system will
     * generate INVALID_ARGUMENT if asked to read at an offset that is not in the
     * range [0,2^32-1], but it will generate OUT_OF_RANGE if asked to read from
     * an offset past the current file size.
     *
     * There is a fair bit of overlap between FAILED_PRECONDITION and
     * OUT_OF_RANGE. We recommend using OUT_OF_RANGE (the more specific error)
     * when it applies so that callers who are iterating through a space can
     * easily look for an OUT_OF_RANGE error to detect when they are done.
     */
    OUT_OF_RANGE: "out-of-range",
    /** Operation is not implemented or not supported/enabled in this service. */
    UNIMPLEMENTED: "unimplemented",
    /**
     * Internal errors. Means some invariants expected by underlying System has
     * been broken. If you see one of these errors, Something is very broken.
     */
    INTERNAL: "internal",
    /**
     * The service is currently unavailable. This is a most likely a transient
     * condition and may be corrected by retrying with a backoff.
     *
     * See litmus test above for deciding between FAILED_PRECONDITION, ABORTED,
     * and UNAVAILABLE.
     */
    UNAVAILABLE: "unavailable",
    /** Unrecoverable data loss or corruption. */
    DATA_LOSS: "data-loss"
};

/** An error returned by a Firestore operation. */ class F extends Error {
    /** @hideconstructor */
    constructor(t, e) {
        super(e), this.code = t, this.message = e, this.name = "FirebaseError", 
        // HACK: We write a toString property directly because Error is not a real
        // class and so inheritance does not work correctly. We could alternatively
        // do the same "back-door inheritance" trick that FirebaseError does.
        this.toString = () => `${this.name}: [code=${this.code}]: ${this.message}`;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable object representing an array of bytes.
 */ class O {
    /** @hideconstructor */
    constructor(t) {
        this.I = t;
    }
    /**
     * Creates a new `Bytes` object from the given Base64 string, converting it to
     * bytes.
     *
     * @param base64 - The Base64 string used to create the `Bytes` object.
     */    static fromBase64String(t) {
        try {
            return new O(N.fromBase64String(t));
        } catch (t) {
            throw new F($.INVALID_ARGUMENT, "Failed to construct data from Base64 string: " + t);
        }
    }
    /**
     * Creates a new `Bytes` object from the given Uint8Array.
     *
     * @param array - The Uint8Array used to create the `Bytes` object.
     */    static fromUint8Array(t) {
        return new O(N.fromUint8Array(t));
    }
    /**
     * Returns the underlying bytes as a Base64-encoded string.
     *
     * @returns The Base64-encoded string created from the `Bytes` object.
     */    toBase64() {
        return this.I.toBase64();
    }
    /**
     * Returns the underlying bytes in a new `Uint8Array`.
     *
     * @returns The Uint8Array created from the `Bytes` object.
     */    toUint8Array() {
        return this.I.toUint8Array();
    }
    /**
     * Returns a string representation of the `Bytes` object.
     *
     * @returns A string representation of the `Bytes` object.
     */    toString() {
        return "Bytes(base64: " + this.toBase64() + ")";
    }
    /**
     * Returns true if this `Bytes` object is equal to the provided one.
     *
     * @param other - The `Bytes` object to compare against.
     * @returns true if this `Bytes` object is equal to the provided one.
     */    isEqual(t) {
        return this.I.isEqual(t.I);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Simple wrapper around a nullable UID. Mostly exists to make code more
 * readable.
 */ class k {
    constructor(t) {
        this.uid = t;
    }
    m() {
        return null != this.uid;
    }
    /**
     * Returns a key representing this user, suitable for inclusion in a
     * dictionary.
     */    A() {
        return this.m() ? "uid:" + this.uid : "anonymous-user";
    }
    isEqual(t) {
        return t.uid === this.uid;
    }
}

/** A user with a null UID. */ k.UNAUTHENTICATED = new k(null), 
// TODO(mikelehen): Look into getting a proper uid-equivalent for
// non-FirebaseAuth providers.
k.R = new k("google-credentials-uid"), k.P = new k("first-party-uid");

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class M {
    constructor(t, e) {
        this.user = e, this.type = "OAuth", this.g = {}, 
        // Set the headers using Object Literal notation to avoid minification
        this.g.Authorization = `Bearer ${t}`;
    }
}

/** A CredentialsProvider that always yields an empty token. */ class L {
    constructor() {
        /**
         * Stores the listener registered with setChangeListener()
         * This isn't actually necessary since the UID never changes, but we use this
         * to verify the listen contract is adhered to in tests.
         */
        this.V = null;
    }
    getToken() {
        return Promise.resolve(null);
    }
    p() {}
    v(t) {
        this.V = t, 
        // Fire with initial user.
        t(k.UNAUTHENTICATED);
    }
    S() {
        this.V = null;
    }
}

class B {
    constructor(t) {
        /**
         * The auth token listener registered with FirebaseApp, retained here so we
         * can unregister it.
         */
        this.D = null, 
        /** Tracks the current User. */
        this.currentUser = k.UNAUTHENTICATED, this.C = !1, 
        /**
         * Counter used to detect if the token changed while a getToken request was
         * outstanding.
         */
        this.N = 0, 
        /** The listener registered with setChangeListener(). */
        this.V = null, this.forceRefresh = !1, this.D = () => {
            this.N++, this.currentUser = this.$(), this.C = !0, this.V && this.V(this.currentUser);
        }, this.N = 0, this.auth = t.getImmediate({
            optional: !0
        }), this.auth ? this.auth.addAuthTokenListener(this.D) : (
        // if auth is not available, invoke tokenListener once with null token
        this.D(null), t.get().then((t => {
            this.auth = t, this.D && 
            // tokenListener can be removed by removeChangeListener()
            this.auth.addAuthTokenListener(this.D);
        }), (() => {})));
    }
    getToken() {
        // Take note of the current value of the tokenCounter so that this method
        // can fail (with an ABORTED error) if there is a token change while the
        // request is outstanding.
        const t = this.N, e = this.forceRefresh;
        return this.forceRefresh = !1, this.auth ? this.auth.getToken(e).then((e => 
        // Cancel the request since the token changed while the request was
        // outstanding so the response is potentially for a previous user (which
        // user, we can't be sure).
        this.N !== t ? (R("FirebaseCredentialsProvider", "getToken aborted due to token change."), 
        this.getToken()) : e ? (p("string" == typeof e.accessToken), new M(e.accessToken, this.currentUser)) : null)) : Promise.resolve(null);
    }
    p() {
        this.forceRefresh = !0;
    }
    v(t) {
        this.V = t, 
        // Fire the initial event
        this.C && t(this.currentUser);
    }
    S() {
        this.auth && this.auth.removeAuthTokenListener(this.D), this.D = null, this.V = null;
    }
    // Auth.getUid() can return null even with a user logged in. It is because
    // getUid() is synchronous, but the auth code populating Uid is asynchronous.
    // This method should only be called in the AuthTokenListener callback
    // to guarantee to get the actual user.
    $() {
        const t = this.auth && this.auth.getUid();
        return p(null === t || "string" == typeof t), new k(t);
    }
}

/*
 * FirstPartyToken provides a fresh token each time its value
 * is requested, because if the token is too old, requests will be rejected.
 * Technically this may no longer be necessary since the SDK should gracefully
 * recover from unauthenticated errors (see b/33147818 for context), but it's
 * safer to keep the implementation as-is.
 */ class q {
    constructor(t, e) {
        this.F = t, this.O = e, this.type = "FirstParty", this.user = k.P;
    }
    get g() {
        const t = {
            "X-Goog-AuthUser": this.O
        }, e = this.F.auth.getAuthHeaderValueForFirstParty([]);
        // Use array notation to prevent minification
                return e && (t.Authorization = e), t;
    }
}

/*
 * Provides user credentials required for the Firestore JavaScript SDK
 * to authenticate the user, using technique that is only available
 * to applications hosted by Google.
 */ class U {
    constructor(t, e) {
        this.F = t, this.O = e;
    }
    getToken() {
        return Promise.resolve(new q(this.F, this.O));
    }
    v(t) {
        // Fire with initial uid.
        t(k.P);
    }
    S() {}
    p() {}
}

/**
 * Builds a CredentialsProvider depending on the type of
 * the credentials passed in.
 */
/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * `ListenSequence` is a monotonic sequence. It is initialized with a minimum value to
 * exceed. All subsequent calls to next will return increasing values. If provided with a
 * `SequenceNumberSyncer`, it will additionally bump its next value when told of a new value, as
 * well as write out sequence numbers that it produces via `next()`.
 */
class Q {
    constructor(t, e) {
        this.previousValue = t, e && (e.k = t => this.M(t), this.L = t => e.B(t));
    }
    M(t) {
        return this.previousValue = Math.max(t, this.previousValue), this.previousValue;
    }
    next() {
        const t = ++this.previousValue;
        return this.L && this.L(t), t;
    }
}

Q.q = -1;

/**
 * A `Timestamp` represents a point in time independent of any time zone or
 * calendar, represented as seconds and fractions of seconds at nanosecond
 * resolution in UTC Epoch time.
 *
 * It is encoded using the Proleptic Gregorian Calendar which extends the
 * Gregorian calendar backwards to year one. It is encoded assuming all minutes
 * are 60 seconds long, i.e. leap seconds are "smeared" so that no leap second
 * table is needed for interpretation. Range is from 0001-01-01T00:00:00Z to
 * 9999-12-31T23:59:59.999999999Z.
 *
 * @see https://github.com/google/protobuf/blob/master/src/google/protobuf/timestamp.proto
 */
class K {
    /**
     * Creates a new timestamp.
     *
     * @param seconds - The number of seconds of UTC time since Unix epoch
     *     1970-01-01T00:00:00Z. Must be from 0001-01-01T00:00:00Z to
     *     9999-12-31T23:59:59Z inclusive.
     * @param nanoseconds - The non-negative fractions of a second at nanosecond
     *     resolution. Negative second values with fractions must still have
     *     non-negative nanoseconds values that count forward in time. Must be
     *     from 0 to 999,999,999 inclusive.
     */
    constructor(t, e) {
        if (this.seconds = t, this.nanoseconds = e, e < 0) throw new F($.INVALID_ARGUMENT, "Timestamp nanoseconds out of range: " + e);
        if (e >= 1e9) throw new F($.INVALID_ARGUMENT, "Timestamp nanoseconds out of range: " + e);
        if (t < -62135596800) throw new F($.INVALID_ARGUMENT, "Timestamp seconds out of range: " + t);
        // This will break in the year 10,000.
                if (t >= 253402300800) throw new F($.INVALID_ARGUMENT, "Timestamp seconds out of range: " + t);
    }
    /**
     * Creates a new timestamp with the current date, with millisecond precision.
     *
     * @returns a new timestamp representing the current date.
     */    static now() {
        return K.fromMillis(Date.now());
    }
    /**
     * Creates a new timestamp from the given date.
     *
     * @param date - The date to initialize the `Timestamp` from.
     * @returns A new `Timestamp` representing the same point in time as the given
     *     date.
     */    static fromDate(t) {
        return K.fromMillis(t.getTime());
    }
    /**
     * Creates a new timestamp from the given number of milliseconds.
     *
     * @param milliseconds - Number of milliseconds since Unix epoch
     *     1970-01-01T00:00:00Z.
     * @returns A new `Timestamp` representing the same point in time as the given
     *     number of milliseconds.
     */    static fromMillis(t) {
        const e = Math.floor(t / 1e3);
        return new K(e, 1e6 * (t - 1e3 * e));
    }
    /**
     * Converts a `Timestamp` to a JavaScript `Date` object. This conversion causes
     * a loss of precision since `Date` objects only support millisecond precision.
     *
     * @returns JavaScript `Date` object representing the same point in time as
     *     this `Timestamp`, with millisecond precision.
     */    toDate() {
        return new Date(this.toMillis());
    }
    /**
     * Converts a `Timestamp` to a numeric timestamp (in milliseconds since
     * epoch). This operation causes a loss of precision.
     *
     * @returns The point in time corresponding to this timestamp, represented as
     *     the number of milliseconds since Unix epoch 1970-01-01T00:00:00Z.
     */    toMillis() {
        return 1e3 * this.seconds + this.nanoseconds / 1e6;
    }
    U(t) {
        return this.seconds === t.seconds ? D(this.nanoseconds, t.nanoseconds) : D(this.seconds, t.seconds);
    }
    /**
     * Returns true if this `Timestamp` is equal to the provided one.
     *
     * @param other - The `Timestamp` to compare against.
     * @returns true if this `Timestamp` is equal to the provided one.
     */    isEqual(t) {
        return t.seconds === this.seconds && t.nanoseconds === this.nanoseconds;
    }
    toString() {
        return "Timestamp(seconds=" + this.seconds + ", nanoseconds=" + this.nanoseconds + ")";
    }
    toJSON() {
        return {
            seconds: this.seconds,
            nanoseconds: this.nanoseconds
        };
    }
    /**
     * Converts this object to a primitive string, which allows Timestamp objects to be compared
     * using the `>`, `<=`, `>=` and `>` operators.
     */    valueOf() {
        // This method returns a string of the form <seconds>.<nanoseconds> where <seconds> is
        // translated to have a non-negative value and both <seconds> and <nanoseconds> are left-padded
        // with zeroes to be a consistent length. Strings with this format then have a lexiographical
        // ordering that matches the expected ordering. The <seconds> translation is done to avoid
        // having a leading negative sign (i.e. a leading '-' character) in its string representation,
        // which would affect its lexiographical ordering.
        const t = this.seconds - -62135596800;
        // Note: Up to 12 decimal digits are required to represent all valid 'seconds' values.
                return String(t).padStart(12, "0") + "." + String(this.nanoseconds).padStart(9, "0");
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A version of a document in Firestore. This corresponds to the version
 * timestamp, such as update_time or read_time.
 */ class W {
    constructor(t) {
        this.timestamp = t;
    }
    static K(t) {
        return new W(t);
    }
    static min() {
        return new W(new K(0, 0));
    }
    _(t) {
        return this.timestamp.U(t.timestamp);
    }
    isEqual(t) {
        return this.timestamp.isEqual(t.timestamp);
    }
    /** Returns a number representation of the version for use in spec tests. */    W() {
        // Convert to microseconds.
        return 1e6 * this.timestamp.seconds + this.timestamp.nanoseconds / 1e3;
    }
    toString() {
        return "SnapshotVersion(" + this.timestamp.toString() + ")";
    }
    j() {
        return this.timestamp;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Path represents an ordered sequence of string segments.
 */
class j {
    constructor(t, e, n) {
        void 0 === e ? e = 0 : e > t.length && V(), void 0 === n ? n = t.length - e : n > t.length - e && V(), 
        this.segments = t, this.offset = e, this.G = n;
    }
    get length() {
        return this.G;
    }
    isEqual(t) {
        return 0 === j.H(this, t);
    }
    child(t) {
        const e = this.segments.slice(this.offset, this.limit());
        return t instanceof j ? t.forEach((t => {
            e.push(t);
        })) : e.push(t), this.J(e);
    }
    /** The index of one past the last segment of the path. */    limit() {
        return this.offset + this.length;
    }
    Y(t) {
        return t = void 0 === t ? 1 : t, this.J(this.segments, this.offset + t, this.length - t);
    }
    X() {
        return this.J(this.segments, this.offset, this.length - 1);
    }
    Z() {
        return this.segments[this.offset];
    }
    tt() {
        return this.get(this.length - 1);
    }
    get(t) {
        return this.segments[this.offset + t];
    }
    et() {
        return 0 === this.length;
    }
    nt(t) {
        if (t.length < this.length) return !1;
        for (let e = 0; e < this.length; e++) if (this.get(e) !== t.get(e)) return !1;
        return !0;
    }
    st(t) {
        if (this.length + 1 !== t.length) return !1;
        for (let e = 0; e < this.length; e++) if (this.get(e) !== t.get(e)) return !1;
        return !0;
    }
    forEach(t) {
        for (let e = this.offset, n = this.limit(); e < n; e++) t(this.segments[e]);
    }
    it() {
        return this.segments.slice(this.offset, this.limit());
    }
    static H(t, e) {
        const n = Math.min(t.length, e.length);
        for (let s = 0; s < n; s++) {
            const n = t.get(s), i = e.get(s);
            if (n < i) return -1;
            if (n > i) return 1;
        }
        return t.length < e.length ? -1 : t.length > e.length ? 1 : 0;
    }
}

/**
 * A slash-separated path for navigating resources (documents and collections)
 * within Firestore.
 */ class G extends j {
    J(t, e, n) {
        return new G(t, e, n);
    }
    rt() {
        // NOTE: The client is ignorant of any path segments containing escape
        // sequences (e.g. __id123__) and just passes them through raw (they exist
        // for legacy reasons and should not be used frequently).
        return this.it().join("/");
    }
    toString() {
        return this.rt();
    }
    /**
     * Creates a resource path from the given slash-delimited string. If multiple
     * arguments are provided, all components are combined. Leading and trailing
     * slashes from all components are ignored.
     */    static ot(...t) {
        // NOTE: The client is ignorant of any path segments containing escape
        // sequences (e.g. __id123__) and just passes them through raw (they exist
        // for legacy reasons and should not be used frequently).
        const e = [];
        for (const n of t) {
            if (n.indexOf("//") >= 0) throw new F($.INVALID_ARGUMENT, `Invalid segment (${n}). Paths must not contain // in them.`);
            // Strip leading and traling slashed.
                        e.push(...n.split("/").filter((t => t.length > 0)));
        }
        return new G(e);
    }
    static ct() {
        return new G([]);
    }
}

const z = /^[_a-zA-Z][_a-zA-Z0-9]*$/;

/** A dot-separated path for navigating sub-objects within a document. */ class H extends j {
    J(t, e, n) {
        return new H(t, e, n);
    }
    /**
     * Returns true if the string could be used as a segment in a field path
     * without escaping.
     */    static at(t) {
        return z.test(t);
    }
    rt() {
        return this.it().map((t => (t = t.replace(/\\/g, "\\\\").replace(/`/g, "\\`"), H.at(t) || (t = "`" + t + "`"), 
        t))).join(".");
    }
    toString() {
        return this.rt();
    }
    /**
     * Returns true if this field references the key of a document.
     */    ut() {
        return 1 === this.length && "__name__" === this.get(0);
    }
    /**
     * The field designating the key of a document.
     */    static ht() {
        return new H([ "__name__" ]);
    }
    /**
     * Parses a field string from the given server-formatted string.
     *
     * - Splitting the empty string is not allowed (for now at least).
     * - Empty segments within the string (e.g. if there are two consecutive
     *   separators) are not allowed.
     *
     * TODO(b/37244157): we should make this more strict. Right now, it allows
     * non-identifier path components, even if they aren't escaped.
     */    static lt(t) {
        const e = [];
        let n = "", s = 0;
        const i = () => {
            if (0 === n.length) throw new F($.INVALID_ARGUMENT, `Invalid field path (${t}). Paths must not be empty, begin with '.', end with '.', or contain '..'`);
            e.push(n), n = "";
        };
        let r = !1;
        for (;s < t.length; ) {
            const e = t[s];
            if ("\\" === e) {
                if (s + 1 === t.length) throw new F($.INVALID_ARGUMENT, "Path has trailing escape character: " + t);
                const e = t[s + 1];
                if ("\\" !== e && "." !== e && "`" !== e) throw new F($.INVALID_ARGUMENT, "Path has invalid escape sequence: " + t);
                n += e, s += 2;
            } else "`" === e ? (r = !r, s++) : "." !== e || r ? (n += e, s++) : (i(), s++);
        }
        if (i(), r) throw new F($.INVALID_ARGUMENT, "Unterminated ` in path: " + t);
        return new H(e);
    }
    static ct() {
        return new H([]);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class J {
    constructor(t) {
        this.path = t;
    }
    static _t(t) {
        return new J(G.ot(t));
    }
    static ft(t) {
        return new J(G.ot(t).Y(5));
    }
    /** Returns true if the document is in the specified collectionId. */    dt(t) {
        return this.path.length >= 2 && this.path.get(this.path.length - 2) === t;
    }
    isEqual(t) {
        return null !== t && 0 === G.H(this.path, t.path);
    }
    toString() {
        return this.path.toString();
    }
    static H(t, e) {
        return G.H(t.path, e.path);
    }
    static wt(t) {
        return t.length % 2 == 0;
    }
    /**
     * Creates and returns a new document key with the given segments.
     *
     * @param segments - The segments of the path to the document
     * @returns A new instance of DocumentKey
     */    static Et(t) {
        return new J(new G(t.slice()));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function Y(t) {
    let e = 0;
    for (const n in t) Object.prototype.hasOwnProperty.call(t, n) && e++;
    return e;
}

function X(t, e) {
    for (const n in t) Object.prototype.hasOwnProperty.call(t, n) && e(n, t[n]);
}

function Z(t) {
    for (const e in t) if (Object.prototype.hasOwnProperty.call(t, e)) return !1;
    return !0;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Provides a set of fields that can be used to partially patch a document.
 * FieldMask is used in conjunction with ObjectValue.
 * Examples:
 *   foo - Overwrites foo entirely with the provided value. If foo is not
 *         present in the companion ObjectValue, the field is deleted.
 *   foo.bar - Overwrites only the field bar of the object foo.
 *             If foo is not an object, foo is replaced with an object
 *             containing foo
 */ class tt {
    constructor(t) {
        this.fields = t, 
        // TODO(dimond): validation of FieldMask
        // Sort the field mask to support `FieldMask.isEqual()` and assert below.
        t.sort(H.H);
    }
    /**
     * Verifies that `fieldPath` is included by at least one field in this field
     * mask.
     *
     * This is an O(n) operation, where `n` is the size of the field mask.
     */    Tt(t) {
        for (const e of this.fields) if (e.nt(t)) return !0;
        return !1;
    }
    isEqual(t) {
        return C(this.fields, t.fields, ((t, e) => t.isEqual(e)));
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// A RegExp matching ISO 8601 UTC timestamps with optional fraction.
const et = new RegExp(/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.(\d+))?Z$/);

/**
 * Converts the possible Proto values for a timestamp value into a "seconds and
 * nanos" representation.
 */ function nt(t) {
    // The json interface (for the browser) will return an iso timestamp string,
    // while the proto js library (for node) will return a
    // google.protobuf.Timestamp instance.
    if (p(!!t), "string" == typeof t) {
        // The date string can have higher precision (nanos) than the Date class
        // (millis), so we do some custom parsing here.
        // Parse the nanos right out of the string.
        let e = 0;
        const n = et.exec(t);
        if (p(!!n), n[1]) {
            // Pad the fraction out to 9 digits (nanos).
            let t = n[1];
            t = (t + "000000000").substr(0, 9), e = Number(t);
        }
        // Parse the date to get the seconds.
                const s = new Date(t);
        return {
            seconds: Math.floor(s.getTime() / 1e3),
            nanos: e
        };
    }
    return {
        seconds: st(t.seconds),
        nanos: st(t.nanos)
    };
}

/**
 * Converts the possible Proto types for numbers into a JavaScript number.
 * Returns 0 if the value is not numeric.
 */ function st(t) {
    // TODO(bjornick): Handle int64 greater than 53 bits.
    return "number" == typeof t ? t : "string" == typeof t ? Number(t) : 0;
}

/** Converts the possible Proto types for Blobs into a ByteString. */ function it(t) {
    return "string" == typeof t ? N.fromBase64String(t) : N.fromUint8Array(t);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents a locally-applied ServerTimestamp.
 *
 * Server Timestamps are backed by MapValues that contain an internal field
 * `__type__` with a value of `server_timestamp`. The previous value and local
 * write time are stored in its `__previous_value__` and `__local_write_time__`
 * fields respectively.
 *
 * Notes:
 * - ServerTimestampValue instances are created as the result of applying a
 *   transform. They can only exist in the local view of a document. Therefore
 *   they do not need to be parsed or serialized.
 * - When evaluated locally (e.g. for snapshot.data()), they by default
 *   evaluate to `null`. This behavior can be configured by passing custom
 *   FieldValueOptions to value().
 * - With respect to other ServerTimestampValues, they sort by their
 *   localWriteTime.
 */ function rt(t) {
    var e, n;
    return "server_timestamp" === (null === (n = ((null === (e = null == t ? void 0 : t.mapValue) || void 0 === e ? void 0 : e.fields) || {}).__type__) || void 0 === n ? void 0 : n.stringValue);
}

/**
 * Creates a new ServerTimestamp proto value (using the internal format).
 */
/**
 * Returns the value of the field before this ServerTimestamp was set.
 *
 * Preserving the previous values allows the user to display the last resoled
 * value until the backend responds with the timestamp.
 */
function ot(t) {
    const e = t.mapValue.fields.__previous_value__;
    return rt(e) ? ot(e) : e;
}

/**
 * Returns the local time at which this timestamp was first set.
 */ function ct(t) {
    const e = nt(t.mapValue.fields.__local_write_time__.timestampValue);
    return new K(e.seconds, e.nanos);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Sentinel value that sorts before any Mutation Batch ID. */
/**
 * Returns whether a variable is either undefined or null.
 */
function at(t) {
    return null == t;
}

/** Returns whether the value represents -0. */ function ut(t) {
    // Detect if the value is -0.0. Based on polyfill from
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
    return 0 === t && 1 / t == -1 / 0;
}

/**
 * Returns whether a value is an integer and in the safe integer range
 * @param value - The value to test for being an integer and in the safe range
 */ function ht(t) {
    return "number" == typeof t && Number.isInteger(t) && !ut(t) && t <= Number.MAX_SAFE_INTEGER && t >= Number.MIN_SAFE_INTEGER;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Extracts the backend's type order for the provided value. */ function lt(t) {
    return "nullValue" in t ? 0 /* NullValue */ : "booleanValue" in t ? 1 /* BooleanValue */ : "integerValue" in t || "doubleValue" in t ? 2 /* NumberValue */ : "timestampValue" in t ? 3 /* TimestampValue */ : "stringValue" in t ? 5 /* StringValue */ : "bytesValue" in t ? 6 /* BlobValue */ : "referenceValue" in t ? 7 /* RefValue */ : "geoPointValue" in t ? 8 /* GeoPointValue */ : "arrayValue" in t ? 9 /* ArrayValue */ : "mapValue" in t ? rt(t) ? 4 /* ServerTimestampValue */ : 10 /* ObjectValue */ : V();
}

/** Tests `left` and `right` for equality based on the backend semantics. */ function _t(t, e) {
    const n = lt(t);
    if (n !== lt(e)) return !1;
    switch (n) {
      case 0 /* NullValue */ :
        return !0;

      case 1 /* BooleanValue */ :
        return t.booleanValue === e.booleanValue;

      case 4 /* ServerTimestampValue */ :
        return ct(t).isEqual(ct(e));

      case 3 /* TimestampValue */ :
        return function(t, e) {
            if ("string" == typeof t.timestampValue && "string" == typeof e.timestampValue && t.timestampValue.length === e.timestampValue.length) 
            // Use string equality for ISO 8601 timestamps
            return t.timestampValue === e.timestampValue;
            const n = nt(t.timestampValue), s = nt(e.timestampValue);
            return n.seconds === s.seconds && n.nanos === s.nanos;
        }(t, e);

      case 5 /* StringValue */ :
        return t.stringValue === e.stringValue;

      case 6 /* BlobValue */ :
        return function(t, e) {
            return it(t.bytesValue).isEqual(it(e.bytesValue));
        }(t, e);

      case 7 /* RefValue */ :
        return t.referenceValue === e.referenceValue;

      case 8 /* GeoPointValue */ :
        return function(t, e) {
            return st(t.geoPointValue.latitude) === st(e.geoPointValue.latitude) && st(t.geoPointValue.longitude) === st(e.geoPointValue.longitude);
        }(t, e);

      case 2 /* NumberValue */ :
        return function(t, e) {
            if ("integerValue" in t && "integerValue" in e) return st(t.integerValue) === st(e.integerValue);
            if ("doubleValue" in t && "doubleValue" in e) {
                const n = st(t.doubleValue), s = st(e.doubleValue);
                return n === s ? ut(n) === ut(s) : isNaN(n) && isNaN(s);
            }
            return !1;
        }(t, e);

      case 9 /* ArrayValue */ :
        return C(t.arrayValue.values || [], e.arrayValue.values || [], _t);

      case 10 /* ObjectValue */ :
        return function(t, e) {
            const n = t.mapValue.fields || {}, s = e.mapValue.fields || {};
            if (Y(n) !== Y(s)) return !1;
            for (const t in n) if (n.hasOwnProperty(t) && (void 0 === s[t] || !_t(n[t], s[t]))) return !1;
            return !0;
        }
        /** Returns true if the ArrayValue contains the specified element. */ (t, e);

      default:
        return V();
    }
}

function ft(t, e) {
    return void 0 !== (t.values || []).find((t => _t(t, e)));
}

function dt(t, e) {
    const n = lt(t), s = lt(e);
    if (n !== s) return D(n, s);
    switch (n) {
      case 0 /* NullValue */ :
        return 0;

      case 1 /* BooleanValue */ :
        return D(t.booleanValue, e.booleanValue);

      case 2 /* NumberValue */ :
        return function(t, e) {
            const n = st(t.integerValue || t.doubleValue), s = st(e.integerValue || e.doubleValue);
            return n < s ? -1 : n > s ? 1 : n === s ? 0 : 
            // one or both are NaN.
            isNaN(n) ? isNaN(s) ? 0 : -1 : 1;
        }(t, e);

      case 3 /* TimestampValue */ :
        return wt(t.timestampValue, e.timestampValue);

      case 4 /* ServerTimestampValue */ :
        return wt(ct(t), ct(e));

      case 5 /* StringValue */ :
        return D(t.stringValue, e.stringValue);

      case 6 /* BlobValue */ :
        return function(t, e) {
            const n = it(t), s = it(e);
            return n._(s);
        }(t.bytesValue, e.bytesValue);

      case 7 /* RefValue */ :
        return function(t, e) {
            const n = t.split("/"), s = e.split("/");
            for (let t = 0; t < n.length && t < s.length; t++) {
                const e = D(n[t], s[t]);
                if (0 !== e) return e;
            }
            return D(n.length, s.length);
        }(t.referenceValue, e.referenceValue);

      case 8 /* GeoPointValue */ :
        return function(t, e) {
            const n = D(st(t.latitude), st(e.latitude));
            if (0 !== n) return n;
            return D(st(t.longitude), st(e.longitude));
        }(t.geoPointValue, e.geoPointValue);

      case 9 /* ArrayValue */ :
        return function(t, e) {
            const n = t.values || [], s = e.values || [];
            for (let t = 0; t < n.length && t < s.length; ++t) {
                const e = dt(n[t], s[t]);
                if (e) return e;
            }
            return D(n.length, s.length);
        }(t.arrayValue, e.arrayValue);

      case 10 /* ObjectValue */ :
        return function(t, e) {
            const n = t.fields || {}, s = Object.keys(n), i = e.fields || {}, r = Object.keys(i);
            // Even though MapValues are likely sorted correctly based on their insertion
            // order (e.g. when received from the backend), local modifications can bring
            // elements out of order. We need to re-sort the elements to ensure that
            // canonical IDs are independent of insertion order.
            s.sort(), r.sort();
            for (let t = 0; t < s.length && t < r.length; ++t) {
                const e = D(s[t], r[t]);
                if (0 !== e) return e;
                const o = dt(n[s[t]], i[r[t]]);
                if (0 !== o) return o;
            }
            return D(s.length, r.length);
        }
        /**
 * Generates the canonical ID for the provided field value (as used in Target
 * serialization).
 */ (t.mapValue, e.mapValue);

      default:
        throw V();
    }
}

function wt(t, e) {
    if ("string" == typeof t && "string" == typeof e && t.length === e.length) return D(t, e);
    const n = nt(t), s = nt(e), i = D(n.seconds, s.seconds);
    return 0 !== i ? i : D(n.nanos, s.nanos);
}

function Et(t) {
    return Tt(t);
}

function Tt(t) {
    return "nullValue" in t ? "null" : "booleanValue" in t ? "" + t.booleanValue : "integerValue" in t ? "" + t.integerValue : "doubleValue" in t ? "" + t.doubleValue : "timestampValue" in t ? function(t) {
        const e = nt(t);
        return `time(${e.seconds},${e.nanos})`;
    }(t.timestampValue) : "stringValue" in t ? t.stringValue : "bytesValue" in t ? it(t.bytesValue).toBase64() : "referenceValue" in t ? (n = t.referenceValue, 
    J.ft(n).toString()) : "geoPointValue" in t ? `geo(${(e = t.geoPointValue).latitude},${e.longitude})` : "arrayValue" in t ? function(t) {
        let e = "[", n = !0;
        for (const s of t.values || []) n ? n = !1 : e += ",", e += Tt(s);
        return e + "]";
    }
    /** Returns a reference value for the provided database and key. */ (t.arrayValue) : "mapValue" in t ? function(t) {
        // Iteration order in JavaScript is not guaranteed. To ensure that we generate
        // matching canonical IDs for identical maps, we need to sort the keys.
        const e = Object.keys(t.fields || {}).sort();
        let n = "{", s = !0;
        for (const i of e) s ? s = !1 : n += ",", n += `${i}:${Tt(t.fields[i])}`;
        return n + "}";
    }(t.mapValue) : V();
    var e, n;
}

function It(t, e) {
    return {
        referenceValue: `projects/${t.projectId}/databases/${t.database}/documents/${e.path.rt()}`
    };
}

/** Returns true if `value` is an IntegerValue . */ function mt(t) {
    return !!t && "integerValue" in t;
}

/** Returns true if `value` is a DoubleValue. */
/** Returns true if `value` is an ArrayValue. */
function At(t) {
    return !!t && "arrayValue" in t;
}

/** Returns true if `value` is a NullValue. */ function Rt(t) {
    return !!t && "nullValue" in t;
}

/** Returns true if `value` is NaN. */ function Pt(t) {
    return !!t && "doubleValue" in t && isNaN(Number(t.doubleValue));
}

/** Returns true if `value` is a MapValue. */ function yt(t) {
    return !!t && "mapValue" in t;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An ObjectValue represents a MapValue in the Firestore Proto and offers the
 * ability to add and remove fields (via the ObjectValueBuilder).
 */ class gt {
    constructor(t) {
        this.proto = t;
    }
    static empty() {
        return new gt({
            mapValue: {}
        });
    }
    /**
     * Returns the value at the given path or null.
     *
     * @param path - the path to search
     * @returns The value at the path or if there it doesn't exist.
     */    field(t) {
        if (t.et()) return this.proto;
        {
            let e = this.proto;
            for (let n = 0; n < t.length - 1; ++n) {
                if (!e.mapValue.fields) return null;
                if (e = e.mapValue.fields[t.get(n)], !yt(e)) return null;
            }
            return e = (e.mapValue.fields || {})[t.tt()], e || null;
        }
    }
    isEqual(t) {
        return _t(this.proto, t.proto);
    }
}

/**
 * An ObjectValueBuilder provides APIs to set and delete fields from an
 * ObjectValue.
 */ class Vt {
    /**
     * @param baseObject - The object to mutate.
     */
    constructor(t = gt.empty()) {
        this.It = t, 
        /** A map that contains the accumulated changes in this builder. */
        this.At = new Map;
    }
    /**
     * Sets the field to the provided value.
     *
     * @param path - The field path to set.
     * @param value - The value to set.
     * @returns The current Builder instance.
     */    set(t, e) {
        return this.Rt(t, e), this;
    }
    /**
     * Removes the field at the specified path. If there is no field at the
     * specified path, nothing is changed.
     *
     * @param path - The field path to remove.
     * @returns The current Builder instance.
     */    delete(t) {
        return this.Rt(t, null), this;
    }
    /**
     * Adds `value` to the overlay map at `path`. Creates nested map entries if
     * needed.
     */    Rt(t, e) {
        let n = this.At;
        for (let e = 0; e < t.length - 1; ++e) {
            const s = t.get(e);
            let i = n.get(s);
            i instanceof Map ? 
            // Re-use a previously created map
            n = i : i && 10 /* ObjectValue */ === lt(i) ? (
            // Convert the existing Protobuf MapValue into a map
            i = new Map(Object.entries(i.mapValue.fields || {})), n.set(s, i), n = i) : (
            // Create an empty map to represent the current nesting level
            i = new Map, n.set(s, i), n = i);
        }
        n.set(t.tt(), e);
    }
    /** Returns an ObjectValue with all mutations applied. */    Pt() {
        const t = this.yt(H.ct(), this.At);
        return null != t ? new gt(t) : this.It;
    }
    /**
     * Applies any overlays from `currentOverlays` that exist at `currentPath`
     * and returns the merged data at `currentPath` (or null if there were no
     * changes).
     *
     * @param currentPath - The path at the current nesting level. Can be set to
     * FieldValue.emptyPath() to represent the root.
     * @param currentOverlays - The overlays at the current nesting level in the
     * same format as `overlayMap`.
     * @returns The merged data at `currentPath` or null if no modifications
     * were applied.
     */    yt(t, e) {
        let n = !1;
        const s = this.It.field(t), i = yt(s) ? // If there is already data at the current path, base our
        Object.assign({}, s.mapValue.fields) : {};
        return e.forEach(((e, s) => {
            if (e instanceof Map) {
                const r = this.yt(t.child(s), e);
                null != r && (i[s] = r, n = !0);
            } else null !== e ? (i[s] = e, n = !0) : i.hasOwnProperty(s) && (delete i[s], n = !0);
        })), n ? {
            mapValue: {
                fields: i
            }
        } : null;
    }
}

/**
 * Returns a FieldMask built from all fields in a MapValue.
 */ function pt(t) {
    const e = [];
    return X(t.fields || {}, ((t, n) => {
        const s = new H([ t ]);
        if (yt(n)) {
            const t = pt(n.mapValue).fields;
            if (0 === t.length) 
            // Preserve the empty map by adding it to the FieldMask.
            e.push(s); else 
            // For nested and non-empty ObjectValues, add the FieldPath of the
            // leaf nodes.
            for (const n of t) e.push(s.child(n));
        } else 
        // For nested and non-empty ObjectValues, add the FieldPath of the leaf
        // nodes.
        e.push(s);
    })), new tt(e);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The result of a lookup for a given path may be an existing document or a
 * marker that this document does not exist at a given version.
 */ class bt {
    constructor(t, e) {
        this.key = t, this.version = e;
    }
}

/**
 * Represents a document in Firestore with a key, version, data and whether the
 * data has local mutations applied to it.
 */ class vt extends bt {
    constructor(t, e, n, s) {
        super(t, e), this.gt = n, this.Vt = !!s.Vt, this.hasCommittedMutations = !!s.hasCommittedMutations;
    }
    field(t) {
        return this.gt.field(t);
    }
    data() {
        return this.gt;
    }
    bt() {
        return this.gt.proto;
    }
    isEqual(t) {
        return t instanceof vt && this.key.isEqual(t.key) && this.version.isEqual(t.version) && this.Vt === t.Vt && this.hasCommittedMutations === t.hasCommittedMutations && this.gt.isEqual(t.gt);
    }
    toString() {
        return `Document(${this.key}, ${this.version}, ${this.gt.toString()}, {hasLocalMutations: ${this.Vt}}), {hasCommittedMutations: ${this.hasCommittedMutations}})`;
    }
    get hasPendingWrites() {
        return this.Vt || this.hasCommittedMutations;
    }
}

/**
 * Compares the value for field `field` in the provided documents. Throws if
 * the field does not exist in both documents.
 */
/**
 * A class representing a deleted document.
 * Version is set to 0 if we don't point to any specific time, otherwise it
 * denotes time we know it didn't exist at.
 */
class St extends bt {
    constructor(t, e, n) {
        super(t, e), this.hasCommittedMutations = !(!n || !n.hasCommittedMutations);
    }
    toString() {
        return `NoDocument(${this.key}, ${this.version})`;
    }
    get hasPendingWrites() {
        return this.hasCommittedMutations;
    }
    isEqual(t) {
        return t instanceof St && t.hasCommittedMutations === this.hasCommittedMutations && t.version.isEqual(this.version) && t.key.isEqual(this.key);
    }
}

/**
 * A class representing an existing document whose data is unknown (e.g. a
 * document that was updated without a known base document).
 */ class Dt extends bt {
    toString() {
        return `UnknownDocument(${this.key}, ${this.version})`;
    }
    get hasPendingWrites() {
        return !0;
    }
    isEqual(t) {
        return t instanceof Dt && t.version.isEqual(this.version) && t.key.isEqual(this.key);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Visible for testing
class Ct {
    constructor(t, e = null, n = [], s = [], i = null, r = null, o = null) {
        this.path = t, this.collectionGroup = e, this.orderBy = n, this.filters = s, this.limit = i, 
        this.startAt = r, this.endAt = o, this.vt = null;
    }
}

/**
 * Initializes a Target with a path and optional additional query constraints.
 * Path must currently be empty if this is a collection group query.
 *
 * NOTE: you should always construct `Target` from `Query.toTarget` instead of
 * using this factory method, because `Query` provides an implicit `orderBy`
 * property.
 */ function xt(t, e = null, n = [], s = [], i = null, r = null, o = null) {
    return new Ct(t, e, n, s, i, r, o);
}

function Nt(t) {
    const e = b(t);
    if (null === e.vt) {
        let t = e.path.rt();
        null !== e.collectionGroup && (t += "|cg:" + e.collectionGroup), t += "|f:", t += e.filters.map((t => Mt(t))).join(","), 
        t += "|ob:", t += e.orderBy.map((t => function(t) {
            // TODO(b/29183165): Make this collision robust.
            return t.field.rt() + t.dir;
        }(t))).join(","), at(e.limit) || (t += "|l:", t += e.limit), e.startAt && (t += "|lb:", 
        t += zt(e.startAt)), e.endAt && (t += "|ub:", t += zt(e.endAt)), e.vt = t;
    }
    return e.vt;
}

function $t(t) {
    let e = t.path.rt();
    return null !== t.collectionGroup && (e += " collectionGroup=" + t.collectionGroup), 
    t.filters.length > 0 && (e += `, filters: [${t.filters.map((t => {
        return `${(e = t).field.rt()} ${e.op} ${Et(e.value)}`;
        /** Returns a debug description for `filter`. */
        var e;
        /** Filter that matches on key fields (i.e. '__name__'). */    })).join(", ")}]`), 
    at(t.limit) || (e += ", limit: " + t.limit), t.orderBy.length > 0 && (e += `, orderBy: [${t.orderBy.map((t => function(t) {
        return `${t.field.rt()} (${t.dir})`;
    }(t))).join(", ")}]`), t.startAt && (e += ", startAt: " + zt(t.startAt)), t.endAt && (e += ", endAt: " + zt(t.endAt)), 
    `Target(${e})`;
}

function Ft(t, e) {
    if (t.limit !== e.limit) return !1;
    if (t.orderBy.length !== e.orderBy.length) return !1;
    for (let n = 0; n < t.orderBy.length; n++) if (!Jt(t.orderBy[n], e.orderBy[n])) return !1;
    if (t.filters.length !== e.filters.length) return !1;
    for (let i = 0; i < t.filters.length; i++) if (n = t.filters[i], s = e.filters[i], 
    n.op !== s.op || !n.field.isEqual(s.field) || !_t(n.value, s.value)) return !1;
    var n, s;
    return t.collectionGroup === e.collectionGroup && (!!t.path.isEqual(e.path) && (!!Xt(t.startAt, e.startAt) && Xt(t.endAt, e.endAt)));
}

function Ot(t) {
    return J.wt(t.path) && null === t.collectionGroup && 0 === t.filters.length;
}

class kt extends class {} {
    constructor(t, e, n) {
        super(), this.field = t, this.op = e, this.value = n;
    }
    /**
     * Creates a filter based on the provided arguments.
     */    static create(t, e, n) {
        return t.ut() ? "in" /* IN */ === e || "not-in" /* NOT_IN */ === e ? this.St(t, e, n) : new Lt(t, e, n) : "array-contains" /* ARRAY_CONTAINS */ === e ? new Qt(t, n) : "in" /* IN */ === e ? new Kt(t, n) : "not-in" /* NOT_IN */ === e ? new Wt(t, n) : "array-contains-any" /* ARRAY_CONTAINS_ANY */ === e ? new jt(t, n) : new kt(t, e, n);
    }
    static St(t, e, n) {
        return "in" /* IN */ === e ? new Bt(t, n) : new qt(t, n);
    }
    matches(t) {
        const e = t.field(this.field);
        // Types do not have to match in NOT_EQUAL filters.
                return "!=" /* NOT_EQUAL */ === this.op ? null !== e && this.Dt(dt(e, this.value)) : null !== e && lt(this.value) === lt(e) && this.Dt(dt(e, this.value));
        // Only compare types with matching backend order (such as double and int).
        }
    Dt(t) {
        switch (this.op) {
          case "<" /* LESS_THAN */ :
            return t < 0;

          case "<=" /* LESS_THAN_OR_EQUAL */ :
            return t <= 0;

          case "==" /* EQUAL */ :
            return 0 === t;

          case "!=" /* NOT_EQUAL */ :
            return 0 !== t;

          case ">" /* GREATER_THAN */ :
            return t > 0;

          case ">=" /* GREATER_THAN_OR_EQUAL */ :
            return t >= 0;

          default:
            return V();
        }
    }
    Ct() {
        return [ "<" /* LESS_THAN */ , "<=" /* LESS_THAN_OR_EQUAL */ , ">" /* GREATER_THAN */ , ">=" /* GREATER_THAN_OR_EQUAL */ , "!=" /* NOT_EQUAL */ , "not-in" /* NOT_IN */ ].indexOf(this.op) >= 0;
    }
}

function Mt(t) {
    // TODO(b/29183165): Technically, this won't be unique if two values have
    // the same description, such as the int 3 and the string "3". So we should
    // add the types in here somehow, too.
    return t.field.rt() + t.op.toString() + Et(t.value);
}

class Lt extends kt {
    constructor(t, e, n) {
        super(t, e, n), this.key = J.ft(n.referenceValue);
    }
    matches(t) {
        const e = J.H(t.key, this.key);
        return this.Dt(e);
    }
}

/** Filter that matches on key fields within an array. */ class Bt extends kt {
    constructor(t, e) {
        super(t, "in" /* IN */ , e), this.keys = Ut("in" /* IN */ , e);
    }
    matches(t) {
        return this.keys.some((e => e.isEqual(t.key)));
    }
}

/** Filter that matches on key fields not present within an array. */ class qt extends kt {
    constructor(t, e) {
        super(t, "not-in" /* NOT_IN */ , e), this.keys = Ut("not-in" /* NOT_IN */ , e);
    }
    matches(t) {
        return !this.keys.some((e => e.isEqual(t.key)));
    }
}

function Ut(t, e) {
    var n;
    return ((null === (n = e.arrayValue) || void 0 === n ? void 0 : n.values) || []).map((t => J.ft(t.referenceValue)));
}

/** A Filter that implements the array-contains operator. */ class Qt extends kt {
    constructor(t, e) {
        super(t, "array-contains" /* ARRAY_CONTAINS */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return At(e) && ft(e.arrayValue, this.value);
    }
}

/** A Filter that implements the IN operator. */ class Kt extends kt {
    constructor(t, e) {
        super(t, "in" /* IN */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return null !== e && ft(this.value.arrayValue, e);
    }
}

/** A Filter that implements the not-in operator. */ class Wt extends kt {
    constructor(t, e) {
        super(t, "not-in" /* NOT_IN */ , e);
    }
    matches(t) {
        if (ft(this.value.arrayValue, {
            nullValue: "NULL_VALUE"
        })) return !1;
        const e = t.field(this.field);
        return null !== e && !ft(this.value.arrayValue, e);
    }
}

/** A Filter that implements the array-contains-any operator. */ class jt extends kt {
    constructor(t, e) {
        super(t, "array-contains-any" /* ARRAY_CONTAINS_ANY */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return !(!At(e) || !e.arrayValue.values) && e.arrayValue.values.some((t => ft(this.value.arrayValue, t)));
    }
}

/**
 * Represents a bound of a query.
 *
 * The bound is specified with the given components representing a position and
 * whether it's just before or just after the position (relative to whatever the
 * query order is).
 *
 * The position represents a logical index position for a query. It's a prefix
 * of values for the (potentially implicit) order by clauses of a query.
 *
 * Bound provides a function to determine whether a document comes before or
 * after a bound. This is influenced by whether the position is just before or
 * just after the provided values.
 */ class Gt {
    constructor(t, e) {
        this.position = t, this.before = e;
    }
}

function zt(t) {
    // TODO(b/29183165): Make this collision robust.
    return `${t.before ? "b" : "a"}:${t.position.map((t => Et(t))).join(",")}`;
}

/**
 * An ordering on a field, in some Direction. Direction defaults to ASCENDING.
 */ class Ht {
    constructor(t, e = "asc" /* ASCENDING */) {
        this.field = t, this.dir = e;
    }
}

function Jt(t, e) {
    return t.dir === e.dir && t.field.isEqual(e.field);
}

/**
 * Returns true if a document sorts before a bound using the provided sort
 * order.
 */ function Yt(t, e, n) {
    let s = 0;
    for (let i = 0; i < t.position.length; i++) {
        const r = e[i], o = t.position[i];
        if (r.field.ut()) s = J.H(J.ft(o.referenceValue), n.key); else {
            s = dt(o, n.field(r.field));
        }
        if ("desc" /* DESCENDING */ === r.dir && (s *= -1), 0 !== s) break;
    }
    return t.before ? s <= 0 : s < 0;
}

function Xt(t, e) {
    if (null === t) return null === e;
    if (null === e) return !1;
    if (t.before !== e.before || t.position.length !== e.position.length) return !1;
    for (let n = 0; n < t.position.length; n++) {
        if (!_t(t.position[n], e.position[n])) return !1;
    }
    return !0;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Query encapsulates all the query attributes we support in the SDK. It can
 * be run against the LocalStore, as well as be converted to a `Target` to
 * query the RemoteStore results.
 *
 * Visible for testing.
 */ class Zt {
    /**
     * Initializes a Query with a path and optional additional query constraints.
     * Path must currently be empty if this is a collection group query.
     */
    constructor(t, e = null, n = [], s = [], i = null, r = "F" /* First */ , o = null, c = null) {
        this.path = t, this.collectionGroup = e, this.xt = n, this.filters = s, this.limit = i, 
        this.limitType = r, this.startAt = o, this.endAt = c, this.Nt = null, 
        // The corresponding `Target` of this `Query` instance.
        this.$t = null, this.startAt, this.endAt;
    }
}

/** Creates a new Query instance with the options provided. */ function te(t, e, n, s, i, r, o, c) {
    return new Zt(t, e, n, s, i, r, o, c);
}

/** Creates a new Query for a query that matches all documents at `path` */ function ee(t) {
    return new Zt(t);
}

/**
 * Helper to convert a collection group query into a collection query at a
 * specific path. This is used when executing collection group queries, since
 * we have to split the query into a set of collection queries at multiple
 * paths.
 */ function ne(t) {
    return !at(t.limit) && "F" /* First */ === t.limitType;
}

function se(t) {
    return !at(t.limit) && "L" /* Last */ === t.limitType;
}

function ie(t) {
    return t.xt.length > 0 ? t.xt[0].field : null;
}

function re(t) {
    for (const e of t.filters) if (e.Ct()) return e.field;
    return null;
}

/**
 * Checks if any of the provided Operators are included in the query and
 * returns the first one that is, or null if none are.
 */
/**
 * Returns whether the query matches a collection group rather than a specific
 * collection.
 */
function oe(t) {
    return null !== t.collectionGroup;
}

/**
 * Returns the implicit order by constraint that is used to execute the Query,
 * which can be different from the order by constraints the user provided (e.g.
 * the SDK and backend always orders by `__name__`).
 */ function ce(t) {
    const e = b(t);
    if (null === e.Nt) {
        e.Nt = [];
        const t = re(e), n = ie(e);
        if (null !== t && null === n) 
        // In order to implicitly add key ordering, we must also add the
        // inequality filter field for it to be a valid query.
        // Note that the default inequality field and key ordering is ascending.
        t.ut() || e.Nt.push(new Ht(t)), e.Nt.push(new Ht(H.ht(), "asc" /* ASCENDING */)); else {
            let t = !1;
            for (const n of e.xt) e.Nt.push(n), n.field.ut() && (t = !0);
            if (!t) {
                // The order of the implicit key ordering always matches the last
                // explicit order by
                const t = e.xt.length > 0 ? e.xt[e.xt.length - 1].dir : "asc" /* ASCENDING */;
                e.Nt.push(new Ht(H.ht(), t));
            }
        }
    }
    return e.Nt;
}

/**
 * Converts this `Query` instance to it's corresponding `Target` representation.
 */ function ae(t) {
    const e = b(t);
    if (!e.$t) if ("F" /* First */ === e.limitType) e.$t = xt(e.path, e.collectionGroup, ce(e), e.filters, e.limit, e.startAt, e.endAt); else {
        // Flip the orderBy directions since we want the last results
        const t = [];
        for (const n of ce(e)) {
            const e = "desc" /* DESCENDING */ === n.dir ? "asc" /* ASCENDING */ : "desc" /* DESCENDING */;
            t.push(new Ht(n.field, e));
        }
        // We need to swap the cursors to match the now-flipped query ordering.
                const n = e.endAt ? new Gt(e.endAt.position, !e.endAt.before) : null, s = e.startAt ? new Gt(e.startAt.position, !e.startAt.before) : null;
        // Now return as a LimitType.First query.
        e.$t = xt(e.path, e.collectionGroup, t, e.filters, e.limit, n, s);
    }
    return e.$t;
}

function ue(t, e, n) {
    return new Zt(t.path, t.collectionGroup, t.xt.slice(), t.filters.slice(), e, n, t.startAt, t.endAt);
}

function he(t, e) {
    return Ft(ae(t), ae(e)) && t.limitType === e.limitType;
}

// TODO(b/29183165): This is used to get a unique string from a query to, for
// example, use as a dictionary key, but the implementation is subject to
// collisions. Make it collision-free.
function le(t) {
    return `${Nt(ae(t))}|lt:${t.limitType}`;
}

function _e(t) {
    return `Query(target=${$t(ae(t))}; limitType=${t.limitType})`;
}

/** Returns whether `doc` matches the constraints of `query`. */ function fe(t, e) {
    return function(t, e) {
        const n = e.key.path;
        return null !== t.collectionGroup ? e.key.dt(t.collectionGroup) && t.path.nt(n) : J.wt(t.path) ? t.path.isEqual(n) : t.path.st(n);
    }
    /**
 * A document must have a value for every ordering clause in order to show up
 * in the results.
 */ (t, e) && function(t, e) {
        for (const n of t.xt) 
        // order by key always matches
        if (!n.field.ut() && null === e.field(n.field)) return !1;
        return !0;
    }(t, e) && function(t, e) {
        for (const n of t.filters) if (!n.matches(e)) return !1;
        return !0;
    }
    /** Makes sure a document is within the bounds, if provided. */ (t, e) && function(t, e) {
        if (t.startAt && !Yt(t.startAt, ce(t), e)) return !1;
        if (t.endAt && Yt(t.endAt, ce(t), e)) return !1;
        return !0;
    }
    /**
 * Returns a new comparator function that can be used to compare two documents
 * based on the Query's ordering constraint.
 */ (t, e);
}

function de(t) {
    return (e, n) => {
        let s = !1;
        for (const i of ce(t)) {
            const t = we(i, e, n);
            if (0 !== t) return t;
            s = s || i.field.ut();
        }
        return 0;
    };
}

function we(t, e, n) {
    const s = t.field.ut() ? J.H(e.key, n.key) : function(t, e, n) {
        const s = e.field(t), i = n.field(t);
        return null !== s && null !== i ? dt(s, i) : V();
    }(t.field, e, n);
    switch (t.dir) {
      case "asc" /* ASCENDING */ :
        return s;

      case "desc" /* DESCENDING */ :
        return -1 * s;

      default:
        return V();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable set of metadata that the local store tracks for each target.
 */ class Ee {
    constructor(
    /** The target being listened to. */
    t, 
    /**
     * The target ID to which the target corresponds; Assigned by the
     * LocalStore for user listens and by the SyncEngine for limbo watches.
     */
    e, 
    /** The purpose of the target. */
    n, 
    /**
     * The sequence number of the last transaction during which this target data
     * was modified.
     */
    s, 
    /** The latest snapshot version seen for this target. */
    i = W.min()
    /**
     * The maximum snapshot version at which the associated view
     * contained no limbo documents.
     */ , r = W.min()
    /**
     * An opaque, server-assigned token that allows watching a target to be
     * resumed after disconnecting without retransmitting all the data that
     * matches the target. The resume token essentially identifies a point in
     * time from which the server should resume sending results.
     */ , o = N.T) {
        this.target = t, this.targetId = e, this.Ft = n, this.sequenceNumber = s, this.Ot = i, 
        this.lastLimboFreeSnapshotVersion = r, this.resumeToken = o;
    }
    /** Creates a new target data instance with an updated sequence number. */    kt(t) {
        return new Ee(this.target, this.targetId, this.Ft, t, this.Ot, this.lastLimboFreeSnapshotVersion, this.resumeToken);
    }
    /**
     * Creates a new target data instance with an updated resume token and
     * snapshot version.
     */    Mt(t, e) {
        return new Ee(this.target, this.targetId, this.Ft, this.sequenceNumber, e, this.lastLimboFreeSnapshotVersion, t);
    }
    /**
     * Creates a new target data instance with an updated last limbo free
     * snapshot version number.
     */    Lt(t) {
        return new Ee(this.target, this.targetId, this.Ft, this.sequenceNumber, this.Ot, t, this.resumeToken);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Returns an DoubleValue for `value` that is encoded based the serializer's
 * `useProto3Json` setting.
 */ function Te(t, e) {
    if (t.Bt) {
        if (isNaN(e)) return {
            doubleValue: "NaN"
        };
        if (e === 1 / 0) return {
            doubleValue: "Infinity"
        };
        if (e === -1 / 0) return {
            doubleValue: "-Infinity"
        };
    }
    return {
        doubleValue: ut(e) ? "-0" : e
    };
}

/**
 * Returns an IntegerValue for `value`.
 */ function Ie(t) {
    return {
        integerValue: "" + t
    };
}

/**
 * Returns a value for a number that's appropriate to put into a proto.
 * The return value is an IntegerValue if it can safely represent the value,
 * otherwise a DoubleValue is returned.
 */ function me(t, e) {
    return ht(e) ? Ie(e) : Te(t, e);
}

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Used to represent a field transform on a mutation. */ class Ae {
    constructor() {
        // Make sure that the structural type of `TransformOperation` is unique.
        // See https://github.com/microsoft/TypeScript/issues/5451
        this.qt = void 0;
    }
}

/**
 * Computes the local transform result against the provided `previousValue`,
 * optionally using the provided localWriteTime.
 */ function Re(t, e, n) {
    return t instanceof ge ? function(t, e) {
        const n = {
            fields: {
                __type__: {
                    stringValue: "server_timestamp"
                },
                __local_write_time__: {
                    timestampValue: {
                        seconds: t.seconds,
                        nanos: t.nanoseconds
                    }
                }
            }
        };
        return e && (n.fields.__previous_value__ = e), {
            mapValue: n
        };
    }(n, e) : t instanceof Ve ? pe(t, e) : t instanceof be ? ve(t, e) : function(t, e) {
        // PORTING NOTE: Since JavaScript's integer arithmetic is limited to 53 bit
        // precision and resolves overflows by reducing precision, we do not
        // manually cap overflows at 2^63.
        const n = ye(t, e), s = De(n) + De(t.Ut);
        return mt(n) && mt(t.Ut) ? Ie(s) : Te(t.Qt, s);
    }(t, e);
}

/**
 * Computes a final transform result after the transform has been acknowledged
 * by the server, potentially using the server-provided transformResult.
 */ function Pe(t, e, n) {
    // The server just sends null as the transform result for array operations,
    // so we have to calculate a result the same as we do for local
    // applications.
    return t instanceof Ve ? pe(t, e) : t instanceof be ? ve(t, e) : n;
}

/**
 * If this transform operation is not idempotent, returns the base value to
 * persist for this transform. If a base value is returned, the transform
 * operation is always applied to this base value, even if document has
 * already been updated.
 *
 * Base values provide consistent behavior for non-idempotent transforms and
 * allow us to return the same latency-compensated value even if the backend
 * has already applied the transform operation. The base value is null for
 * idempotent transforms, as they can be re-played even if the backend has
 * already applied them.
 *
 * @returns a base value to store along with the mutation, or null for
 * idempotent transforms.
 */ function ye(t, e) {
    return t instanceof Se ? mt(n = e) || function(t) {
        return !!t && "doubleValue" in t;
    }
    /** Returns true if `value` is either an IntegerValue or a DoubleValue. */ (n) ? e : {
        integerValue: 0
    } : null;
    var n;
}

/** Transforms a value into a server-generated timestamp. */
class ge extends Ae {}

/** Transforms an array value via a union operation. */ class Ve extends Ae {
    constructor(t) {
        super(), this.elements = t;
    }
}

function pe(t, e) {
    const n = Ce(e);
    for (const e of t.elements) n.some((t => _t(t, e))) || n.push(e);
    return {
        arrayValue: {
            values: n
        }
    };
}

/** Transforms an array value via a remove operation. */ class be extends Ae {
    constructor(t) {
        super(), this.elements = t;
    }
}

function ve(t, e) {
    let n = Ce(e);
    for (const e of t.elements) n = n.filter((t => !_t(t, e)));
    return {
        arrayValue: {
            values: n
        }
    };
}

/**
 * Implements the backend semantics for locally computed NUMERIC_ADD (increment)
 * transforms. Converts all field values to integers or doubles, but unlike the
 * backend does not cap integer values at 2^63. Instead, JavaScript number
 * arithmetic is used and precision loss can occur for values greater than 2^53.
 */ class Se extends Ae {
    constructor(t, e) {
        super(), this.Qt = t, this.Ut = e;
    }
}

function De(t) {
    return st(t.integerValue || t.doubleValue);
}

function Ce(t) {
    return At(t) && t.arrayValue.values ? t.arrayValue.values.slice() : [];
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** A field path and the TransformOperation to perform upon it. */ class xe {
    constructor(t, e) {
        this.field = t, this.transform = e;
    }
}

function Ne(t, e) {
    return t.field.isEqual(e.field) && function(t, e) {
        return t instanceof Ve && e instanceof Ve || t instanceof be && e instanceof be ? C(t.elements, e.elements, _t) : t instanceof Se && e instanceof Se ? _t(t.Ut, e.Ut) : t instanceof ge && e instanceof ge;
    }(t.transform, e.transform);
}

/** The result of successfully applying a mutation to the backend. */
class $e {
    constructor(
    /**
     * The version at which the mutation was committed:
     *
     * - For most operations, this is the updateTime in the WriteResult.
     * - For deletes, the commitTime of the WriteResponse (because deletes are
     *   not stored and have no updateTime).
     *
     * Note that these versions can be different: No-op writes will not change
     * the updateTime even though the commitTime advances.
     */
    t, 
    /**
     * The resulting fields returned from the backend after a mutation
     * containing field transforms has been committed. Contains one FieldValue
     * for each FieldTransform that was in the mutation.
     *
     * Will be null if the mutation did not contain any field transforms.
     */
    e) {
        this.version = t, this.transformResults = e;
    }
}

/**
 * Encodes a precondition for a mutation. This follows the model that the
 * backend accepts with the special case of an explicit "empty" precondition
 * (meaning no precondition).
 */ class Fe {
    constructor(t, e) {
        this.updateTime = t, this.exists = e;
    }
    /** Creates a new empty Precondition. */    static Kt() {
        return new Fe;
    }
    /** Creates a new Precondition with an exists flag. */    static exists(t) {
        return new Fe(void 0, t);
    }
    /** Creates a new Precondition based on a version a document exists at. */    static updateTime(t) {
        return new Fe(t);
    }
    /** Returns whether this Precondition is empty. */    get Wt() {
        return void 0 === this.updateTime && void 0 === this.exists;
    }
    isEqual(t) {
        return this.exists === t.exists && (this.updateTime ? !!t.updateTime && this.updateTime.isEqual(t.updateTime) : !t.updateTime);
    }
}

/**
 * Returns true if the preconditions is valid for the given document
 * (or null if no document is available).
 */ function Oe(t, e) {
    return void 0 !== t.updateTime ? e instanceof vt && e.version.isEqual(t.updateTime) : void 0 === t.exists || t.exists === e instanceof vt;
}

/**
 * A mutation describes a self-contained change to a document. Mutations can
 * create, replace, delete, and update subsets of documents.
 *
 * Mutations not only act on the value of the document but also its version.
 *
 * For local mutations (mutations that haven't been committed yet), we preserve
 * the existing version for Set and Patch mutations. For Delete mutations, we
 * reset the version to 0.
 *
 * Here's the expected transition table.
 *
 * MUTATION           APPLIED TO            RESULTS IN
 *
 * SetMutation        Document(v3)          Document(v3)
 * SetMutation        NoDocument(v3)        Document(v0)
 * SetMutation        null                  Document(v0)
 * PatchMutation      Document(v3)          Document(v3)
 * PatchMutation      NoDocument(v3)        NoDocument(v3)
 * PatchMutation      null                  null
 * DeleteMutation     Document(v3)          NoDocument(v0)
 * DeleteMutation     NoDocument(v3)        NoDocument(v0)
 * DeleteMutation     null                  NoDocument(v0)
 *
 * For acknowledged mutations, we use the updateTime of the WriteResponse as
 * the resulting version for Set and Patch mutations. As deletes have no
 * explicit update time, we use the commitTime of the WriteResponse for
 * Delete mutations.
 *
 * If a mutation is acknowledged by the backend but fails the precondition check
 * locally, we return an `UnknownDocument` and rely on Watch to send us the
 * updated version.
 *
 * Field transforms are used only with Patch and Set Mutations. We use the
 * `updateTransforms` message to store transforms, rather than the `transforms`s
 * messages.
 *
 * ## Subclassing Notes
 *
 * Subclasses of Mutation need to implement applyToRemoteDocument() and
 * applyToLocalView() to implement the actual behavior of applying the mutation
 * to some source document.
 */ class ke {}

/**
 * Applies this mutation to the given MaybeDocument or null for the purposes
 * of computing a new remote document. If the input document doesn't match the
 * expected state (e.g. it is null or outdated), an `UnknownDocument` can be
 * returned.
 *
 * @param mutation - The mutation to apply.
 * @param maybeDoc - The document to mutate. The input document can be null if
 *     the client has no knowledge of the pre-mutation state of the document.
 * @param mutationResult - The result of applying the mutation from the backend.
 * @returns The mutated document. The returned document may be an
 *     UnknownDocument if the mutation could not be applied to the locally
 *     cached base document.
 */ function Me(t, e, n) {
    return t instanceof Qe ? function(t, e, n) {
        // Unlike applySetMutationToLocalView, if we're applying a mutation to a
        // remote document the server has accepted the mutation so the precondition
        // must have held.
        let s = t.value;
        if (n.transformResults) {
            const i = je(t.fieldTransforms, e, n.transformResults);
            s = ze(t.fieldTransforms, s, i);
        }
        return new vt(t.key, n.version, s, {
            hasCommittedMutations: !0
        });
    }(t, e, n) : t instanceof Ke ? function(t, e, n) {
        if (!Oe(t.jt, e)) 
        // Since the mutation was not rejected, we know that the precondition
        // matched on the backend. We therefore must not have the expected version
        // of the document in our cache and return an UnknownDocument with the
        // known updateTime.
        return new Dt(t.key, n.version);
        const s = n.transformResults ? je(t.fieldTransforms, e, n.transformResults) : [], i = We(t, e, s);
        return new vt(t.key, n.version, i, {
            hasCommittedMutations: !0
        });
    }(t, e, n) : function(t, e, n) {
        // Unlike applyToLocalView, if we're applying a mutation to a remote
        // document the server has accepted the mutation so the precondition must
        // have held.
        return new St(t.key, n.version, {
            hasCommittedMutations: !0
        });
    }(t, 0, n);
}

/**
 * Applies this mutation to the given MaybeDocument or null for the purposes
 * of computing the new local view of a document. Both the input and returned
 * documents can be null.
 *
 * @param mutation - The mutation to apply.
 * @param maybeDoc - The document to mutate. The input document can be null if
 *     the client has no knowledge of the pre-mutation state of the document.
 * @param localWriteTime - A timestamp indicating the local write time of the
 *     batch this mutation is a part of.
 * @returns The mutated document. The returned document may be null, but only
 *     if maybeDoc was null and the mutation would not create a new document.
 */ function Le(t, e, n) {
    return t instanceof Qe ? function(t, e, n) {
        if (!Oe(t.jt, e)) return e;
        let s = t.value;
        const i = Ge(t.fieldTransforms, n, e);
        s = ze(t.fieldTransforms, s, i);
        const r = Ue(e);
        return new vt(t.key, r, s, {
            Vt: !0
        });
    }
    /**
 * A mutation that modifies fields of the document at the given key with the
 * given values. The values are applied through a field mask:
 *
 *  * When a field is in both the mask and the values, the corresponding field
 *    is updated.
 *  * When a field is in neither the mask nor the values, the corresponding
 *    field is unmodified.
 *  * When a field is in the mask but not in the values, the corresponding field
 *    is deleted.
 *  * When a field is not in the mask but is in the values, the values map is
 *    ignored.
 */ (t, e, n) : t instanceof Ke ? function(t, e, n) {
        if (!Oe(t.jt, e)) return e;
        const s = Ue(e), i = Ge(t.fieldTransforms, n, e), r = We(t, e, i);
        return new vt(t.key, s, r, {
            Vt: !0
        });
    }
    /**
 * Patches the data of document if available or creates a new document. Note
 * that this does not check whether or not the precondition of this patch
 * holds.
 */ (t, e, n) : function(t, e) {
        if (!Oe(t.jt, e)) return e;
        return new St(t.key, W.min());
    }
    /**
 * A mutation that verifies the existence of the document at the given key with
 * the provided precondition.
 *
 * The `verify` operation is only used in Transactions, and this class serves
 * primarily to facilitate serialization into protos.
 */ (t, e);
}

/**
 * If this mutation is not idempotent, returns the base value to persist with
 * this mutation. If a base value is returned, the mutation is always applied
 * to this base value, even if document has already been updated.
 *
 * The base value is a sparse object that consists of only the document
 * fields for which this mutation contains a non-idempotent transformation
 * (e.g. a numeric increment). The provided value guarantees consistent
 * behavior for non-idempotent transforms and allow us to return the same
 * latency-compensated value even if the backend has already applied the
 * mutation. The base value is null for idempotent mutations, as they can be
 * re-played even if the backend has already applied them.
 *
 * @returns a base value to store along with the mutation, or null for
 * idempotent mutations.
 */ function Be(t, e) {
    return function(t, e) {
        let n = null;
        for (const s of t) {
            const t = e instanceof vt ? e.field(s.field) : void 0, i = ye(s.transform, t || null);
            null != i && (n = null == n ? (new Vt).set(s.field, i) : n.set(s.field, i));
        }
        return n ? n.Pt() : null;
    }(t.fieldTransforms, e);
}

function qe(t, e) {
    return t.type === e.type && (!!t.key.isEqual(e.key) && (!!t.jt.isEqual(e.jt) && (!!function(t, e) {
        return void 0 === t && void 0 === e || !(!t || !e) && C(t, e, ((t, e) => Ne(t, e)));
    }(t.fieldTransforms, e.fieldTransforms) && (0 /* Set */ === t.type ? t.value.isEqual(e.value) : 1 /* Patch */ !== t.type || t.data.isEqual(e.data) && t.Gt.isEqual(e.Gt)))));
}

/**
 * Returns the version from the given document for use as the result of a
 * mutation. Mutations are defined to return the version of the base document
 * only if it is an existing document. Deleted and unknown documents have a
 * post-mutation version of SnapshotVersion.min().
 */ function Ue(t) {
    return t instanceof vt ? t.version : W.min();
}

/**
 * A mutation that creates or replaces the document at the given key with the
 * object value contents.
 */ class Qe extends ke {
    constructor(t, e, n, s = []) {
        super(), this.key = t, this.value = e, this.jt = n, this.fieldTransforms = s, this.type = 0 /* Set */;
    }
}

class Ke extends ke {
    constructor(t, e, n, s, i = []) {
        super(), this.key = t, this.data = e, this.Gt = n, this.jt = s, this.fieldTransforms = i, 
        this.type = 1 /* Patch */;
    }
}

function We(t, e, n) {
    let s;
    return s = e instanceof vt ? e.data() : gt.empty(), s = function(t, e) {
        const n = new Vt(e);
        return t.Gt.fields.forEach((e => {
            if (!e.et()) {
                const s = t.data.field(e);
                null !== s ? n.set(e, s) : n.delete(e);
            }
        })), n.Pt();
    }
    /**
 * Creates a list of "transform results" (a transform result is a field value
 * representing the result of applying a transform) for use after a mutation
 * containing transforms has been acknowledged by the server.
 *
 * @param fieldTransforms - The field transforms to apply the result to.
 * @param maybeDoc - The current state of the document after applying all
 * previous mutations.
 * @param serverTransformResults - The transform results received by the server.
 * @returns The transform results list.
 */ (t, s), s = ze(t.fieldTransforms, s, n), s;
}

function je(t, e, n) {
    const s = [];
    p(t.length === n.length);
    for (let i = 0; i < n.length; i++) {
        const r = t[i], o = r.transform;
        let c = null;
        e instanceof vt && (c = e.field(r.field)), s.push(Pe(o, c, n[i]));
    }
    return s;
}

/**
 * Creates a list of "transform results" (a transform result is a field value
 * representing the result of applying a transform) for use when applying a
 * transform locally.
 *
 * @param fieldTransforms - The field transforms to apply the result to.
 * @param localWriteTime - The local time of the mutation (used to
 *     generate ServerTimestampValues).
 * @param maybeDoc - The current state of the document after applying all
 *     previous mutations.
 * @returns The transform results list.
 */ function Ge(t, e, n) {
    const s = [];
    for (const i of t) {
        const t = i.transform;
        let r = null;
        n instanceof vt && (r = n.field(i.field)), s.push(Re(t, r, e));
    }
    return s;
}

function ze(t, e, n) {
    const s = new Vt(e);
    for (let e = 0; e < t.length; e++) {
        const i = t[e];
        s.set(i.field, n[e]);
    }
    return s.Pt();
}

/** A mutation that deletes the document at the given key. */ class He extends ke {
    constructor(t, e) {
        super(), this.key = t, this.jt = e, this.type = 2 /* Delete */ , this.fieldTransforms = [];
    }
}

class Je extends ke {
    constructor(t, e) {
        super(), this.key = t, this.jt = e, this.type = 3 /* Verify */ , this.fieldTransforms = [];
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Ye {
    // TODO(b/33078163): just use simplest form of existence filter for now
    constructor(t) {
        this.count = t;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Error Codes describing the different ways GRPC can fail. These are copied
 * directly from GRPC's sources here:
 *
 * https://github.com/grpc/grpc/blob/bceec94ea4fc5f0085d81235d8e1c06798dc341a/include/grpc%2B%2B/impl/codegen/status_code_enum.h
 *
 * Important! The names of these identifiers matter because the string forms
 * are used for reverse lookups from the webchannel stream. Do NOT change the
 * names of these identifiers or change this into a const enum.
 */ var Xe, Ze;

/**
 * Determines whether an error code represents a permanent error when received
 * in response to a non-write operation.
 *
 * See isPermanentWriteError for classifying write errors.
 */
function tn(t) {
    switch (t) {
      case $.OK:
        return V();

      case $.CANCELLED:
      case $.UNKNOWN:
      case $.DEADLINE_EXCEEDED:
      case $.RESOURCE_EXHAUSTED:
      case $.INTERNAL:
      case $.UNAVAILABLE:
 // Unauthenticated means something went wrong with our token and we need
        // to retry with new credentials which will happen automatically.
              case $.UNAUTHENTICATED:
        return !1;

      case $.INVALID_ARGUMENT:
      case $.NOT_FOUND:
      case $.ALREADY_EXISTS:
      case $.PERMISSION_DENIED:
      case $.FAILED_PRECONDITION:
 // Aborted might be retried in some scenarios, but that is dependant on
        // the context and should handled individually by the calling code.
        // See https://cloud.google.com/apis/design/errors.
              case $.ABORTED:
      case $.OUT_OF_RANGE:
      case $.UNIMPLEMENTED:
      case $.DATA_LOSS:
        return !0;

      default:
        return V();
    }
}

/**
 * Determines whether an error code represents a permanent error when received
 * in response to a write operation.
 *
 * Write operations must be handled specially because as of b/119437764, ABORTED
 * errors on the write stream should be retried too (even though ABORTED errors
 * are not generally retryable).
 *
 * Note that during the initial handshake on the write stream an ABORTED error
 * signals that we should discard our stream token (i.e. it is permanent). This
 * means a handshake error should be classified with isPermanentError, above.
 */
/**
 * Maps an error Code from GRPC status code number, like 0, 1, or 14. These
 * are not the same as HTTP status codes.
 *
 * @returns The Code equivalent to the given GRPC status code. Fails if there
 *     is no match.
 */
function en(t) {
    if (void 0 === t) 
    // This shouldn't normally happen, but in certain error cases (like trying
    // to send invalid proto messages) we may get an error with no GRPC code.
    return P("GRPC error has no .code"), $.UNKNOWN;
    switch (t) {
      case Xe.OK:
        return $.OK;

      case Xe.CANCELLED:
        return $.CANCELLED;

      case Xe.UNKNOWN:
        return $.UNKNOWN;

      case Xe.DEADLINE_EXCEEDED:
        return $.DEADLINE_EXCEEDED;

      case Xe.RESOURCE_EXHAUSTED:
        return $.RESOURCE_EXHAUSTED;

      case Xe.INTERNAL:
        return $.INTERNAL;

      case Xe.UNAVAILABLE:
        return $.UNAVAILABLE;

      case Xe.UNAUTHENTICATED:
        return $.UNAUTHENTICATED;

      case Xe.INVALID_ARGUMENT:
        return $.INVALID_ARGUMENT;

      case Xe.NOT_FOUND:
        return $.NOT_FOUND;

      case Xe.ALREADY_EXISTS:
        return $.ALREADY_EXISTS;

      case Xe.PERMISSION_DENIED:
        return $.PERMISSION_DENIED;

      case Xe.FAILED_PRECONDITION:
        return $.FAILED_PRECONDITION;

      case Xe.ABORTED:
        return $.ABORTED;

      case Xe.OUT_OF_RANGE:
        return $.OUT_OF_RANGE;

      case Xe.UNIMPLEMENTED:
        return $.UNIMPLEMENTED;

      case Xe.DATA_LOSS:
        return $.DATA_LOSS;

      default:
        return V();
    }
}

/**
 * Converts an HTTP response's error status to the equivalent error code.
 *
 * @param status - An HTTP error response status ("FAILED_PRECONDITION",
 * "UNKNOWN", etc.)
 * @returns The equivalent Code. Non-matching responses are mapped to
 *     Code.UNKNOWN.
 */ (Ze = Xe || (Xe = {}))[Ze.OK = 0] = "OK", Ze[Ze.CANCELLED = 1] = "CANCELLED", 
Ze[Ze.UNKNOWN = 2] = "UNKNOWN", Ze[Ze.INVALID_ARGUMENT = 3] = "INVALID_ARGUMENT", 
Ze[Ze.DEADLINE_EXCEEDED = 4] = "DEADLINE_EXCEEDED", Ze[Ze.NOT_FOUND = 5] = "NOT_FOUND", 
Ze[Ze.ALREADY_EXISTS = 6] = "ALREADY_EXISTS", Ze[Ze.PERMISSION_DENIED = 7] = "PERMISSION_DENIED", 
Ze[Ze.UNAUTHENTICATED = 16] = "UNAUTHENTICATED", Ze[Ze.RESOURCE_EXHAUSTED = 8] = "RESOURCE_EXHAUSTED", 
Ze[Ze.FAILED_PRECONDITION = 9] = "FAILED_PRECONDITION", Ze[Ze.ABORTED = 10] = "ABORTED", 
Ze[Ze.OUT_OF_RANGE = 11] = "OUT_OF_RANGE", Ze[Ze.UNIMPLEMENTED = 12] = "UNIMPLEMENTED", 
Ze[Ze.INTERNAL = 13] = "INTERNAL", Ze[Ze.UNAVAILABLE = 14] = "UNAVAILABLE", Ze[Ze.DATA_LOSS = 15] = "DATA_LOSS";

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// An immutable sorted map implementation, based on a Left-leaning Red-Black
// tree.
class nn {
    constructor(t, e) {
        this.H = t, this.root = e || rn.EMPTY;
    }
    // Returns a copy of the map, with the specified key/value added or replaced.
    zt(t, e) {
        return new nn(this.H, this.root.zt(t, e, this.H).copy(null, null, rn.Ht, null, null));
    }
    // Returns a copy of the map, with the specified key removed.
    remove(t) {
        return new nn(this.H, this.root.remove(t, this.H).copy(null, null, rn.Ht, null, null));
    }
    // Returns the value of the node with the given key, or null.
    get(t) {
        let e = this.root;
        for (;!e.et(); ) {
            const n = this.H(t, e.key);
            if (0 === n) return e.value;
            n < 0 ? e = e.left : n > 0 && (e = e.right);
        }
        return null;
    }
    // Returns the index of the element in this sorted map, or -1 if it doesn't
    // exist.
    indexOf(t) {
        // Number of nodes that were pruned when descending right
        let e = 0, n = this.root;
        for (;!n.et(); ) {
            const s = this.H(t, n.key);
            if (0 === s) return e + n.left.size;
            s < 0 ? n = n.left : (
            // Count all nodes left of the node plus the node itself
            e += n.left.size + 1, n = n.right);
        }
        // Node not found
                return -1;
    }
    et() {
        return this.root.et();
    }
    // Returns the total number of nodes in the map.
    get size() {
        return this.root.size;
    }
    // Returns the minimum key in the map.
    Jt() {
        return this.root.Jt();
    }
    // Returns the maximum key in the map.
    Yt() {
        return this.root.Yt();
    }
    // Traverses the map in key order and calls the specified action function
    // for each key/value pair. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Xt(t) {
        return this.root.Xt(t);
    }
    forEach(t) {
        this.Xt(((e, n) => (t(e, n), !1)));
    }
    toString() {
        const t = [];
        return this.Xt(((e, n) => (t.push(`${e}:${n}`), !1))), `{${t.join(", ")}}`;
    }
    // Traverses the map in reverse key order and calls the specified action
    // function for each key/value pair. If action returns true, traversal is
    // aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Zt(t) {
        return this.root.Zt(t);
    }
    // Returns an iterator over the SortedMap.
    te() {
        return new sn(this.root, null, this.H, !1);
    }
    ee(t) {
        return new sn(this.root, t, this.H, !1);
    }
    ne() {
        return new sn(this.root, null, this.H, !0);
    }
    se(t) {
        return new sn(this.root, t, this.H, !0);
    }
}

 // end SortedMap
// An iterator over an LLRBNode.
class sn {
    constructor(t, e, n, s) {
        this.ie = s, this.re = [];
        let i = 1;
        for (;!t.et(); ) if (i = e ? n(t.key, e) : 1, 
        // flip the comparison if we're going in reverse
        s && (i *= -1), i < 0) 
        // This node is less than our start key. ignore it
        t = this.ie ? t.left : t.right; else {
            if (0 === i) {
                // This node is exactly equal to our start key. Push it on the stack,
                // but stop iterating;
                this.re.push(t);
                break;
            }
            // This node is greater than our start key, add it to the stack and move
            // to the next one
            this.re.push(t), t = this.ie ? t.right : t.left;
        }
    }
    oe() {
        let t = this.re.pop();
        const e = {
            key: t.key,
            value: t.value
        };
        if (this.ie) for (t = t.left; !t.et(); ) this.re.push(t), t = t.right; else for (t = t.right; !t.et(); ) this.re.push(t), 
        t = t.left;
        return e;
    }
    ce() {
        return this.re.length > 0;
    }
    ae() {
        if (0 === this.re.length) return null;
        const t = this.re[this.re.length - 1];
        return {
            key: t.key,
            value: t.value
        };
    }
}

 // end SortedMapIterator
// Represents a node in a Left-leaning Red-Black tree.
class rn {
    constructor(t, e, n, s, i) {
        this.key = t, this.value = e, this.color = null != n ? n : rn.RED, this.left = null != s ? s : rn.EMPTY, 
        this.right = null != i ? i : rn.EMPTY, this.size = this.left.size + 1 + this.right.size;
    }
    // Returns a copy of the current node, optionally replacing pieces of it.
    copy(t, e, n, s, i) {
        return new rn(null != t ? t : this.key, null != e ? e : this.value, null != n ? n : this.color, null != s ? s : this.left, null != i ? i : this.right);
    }
    et() {
        return !1;
    }
    // Traverses the tree in key order and calls the specified action function
    // for each node. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Xt(t) {
        return this.left.Xt(t) || t(this.key, this.value) || this.right.Xt(t);
    }
    // Traverses the tree in reverse key order and calls the specified action
    // function for each node. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Zt(t) {
        return this.right.Zt(t) || t(this.key, this.value) || this.left.Zt(t);
    }
    // Returns the minimum node in the tree.
    min() {
        return this.left.et() ? this : this.left.min();
    }
    // Returns the maximum key in the tree.
    Jt() {
        return this.min().key;
    }
    // Returns the maximum key in the tree.
    Yt() {
        return this.right.et() ? this.key : this.right.Yt();
    }
    // Returns new tree, with the key/value added.
    zt(t, e, n) {
        let s = this;
        const i = n(t, s.key);
        return s = i < 0 ? s.copy(null, null, null, s.left.zt(t, e, n), null) : 0 === i ? s.copy(null, e, null, null, null) : s.copy(null, null, null, null, s.right.zt(t, e, n)), 
        s.ue();
    }
    he() {
        if (this.left.et()) return rn.EMPTY;
        let t = this;
        return t.left.le() || t.left.left.le() || (t = t._e()), t = t.copy(null, null, null, t.left.he(), null), 
        t.ue();
    }
    // Returns new tree, with the specified item removed.
    remove(t, e) {
        let n, s = this;
        if (e(t, s.key) < 0) s.left.et() || s.left.le() || s.left.left.le() || (s = s._e()), 
        s = s.copy(null, null, null, s.left.remove(t, e), null); else {
            if (s.left.le() && (s = s.fe()), s.right.et() || s.right.le() || s.right.left.le() || (s = s.de()), 
            0 === e(t, s.key)) {
                if (s.right.et()) return rn.EMPTY;
                n = s.right.min(), s = s.copy(n.key, n.value, null, null, s.right.he());
            }
            s = s.copy(null, null, null, null, s.right.remove(t, e));
        }
        return s.ue();
    }
    le() {
        return this.color;
    }
    // Returns new tree after performing any needed rotations.
    ue() {
        let t = this;
        return t.right.le() && !t.left.le() && (t = t.we()), t.left.le() && t.left.left.le() && (t = t.fe()), 
        t.left.le() && t.right.le() && (t = t.Ee()), t;
    }
    _e() {
        let t = this.Ee();
        return t.right.left.le() && (t = t.copy(null, null, null, null, t.right.fe()), t = t.we(), 
        t = t.Ee()), t;
    }
    de() {
        let t = this.Ee();
        return t.left.left.le() && (t = t.fe(), t = t.Ee()), t;
    }
    we() {
        const t = this.copy(null, null, rn.RED, null, this.right.left);
        return this.right.copy(null, null, this.color, t, null);
    }
    fe() {
        const t = this.copy(null, null, rn.RED, this.left.right, null);
        return this.left.copy(null, null, this.color, null, t);
    }
    Ee() {
        const t = this.left.copy(null, null, !this.left.color, null, null), e = this.right.copy(null, null, !this.right.color, null, null);
        return this.copy(null, null, !this.color, t, e);
    }
    // For testing.
    Te() {
        const t = this.Ie();
        return Math.pow(2, t) <= this.size + 1;
    }
    // In a balanced RB tree, the black-depth (number of black nodes) from root to
    // leaves is equal on both sides.  This function verifies that or asserts.
    Ie() {
        if (this.le() && this.left.le()) throw V();
        if (this.right.le()) throw V();
        const t = this.left.Ie();
        if (t !== this.right.Ie()) throw V();
        return t + (this.le() ? 0 : 1);
    }
}

 // end LLRBNode
// Empty node is shared between all LLRB trees.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
rn.EMPTY = null, rn.RED = !0, rn.Ht = !1;

// end LLRBEmptyNode
rn.EMPTY = new 
// Represents an empty node (a leaf node in the Red-Black Tree).
class {
    constructor() {
        this.size = 0;
    }
    get key() {
        throw V();
    }
    get value() {
        throw V();
    }
    get color() {
        throw V();
    }
    get left() {
        throw V();
    }
    get right() {
        throw V();
    }
    // Returns a copy of the current node.
    copy(t, e, n, s, i) {
        return this;
    }
    // Returns a copy of the tree, with the specified key/value added.
    zt(t, e, n) {
        return new rn(t, e);
    }
    // Returns a copy of the tree, with the specified key removed.
    remove(t, e) {
        return this;
    }
    et() {
        return !0;
    }
    Xt(t) {
        return !1;
    }
    Zt(t) {
        return !1;
    }
    Jt() {
        return null;
    }
    Yt() {
        return null;
    }
    le() {
        return !1;
    }
    // For testing.
    Te() {
        return !0;
    }
    Ie() {
        return 0;
    }
};

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * SortedSet is an immutable (copy-on-write) collection that holds elements
 * in order specified by the provided comparator.
 *
 * NOTE: if provided comparator returns 0 for two elements, we consider them to
 * be equal!
 */
class on {
    constructor(t) {
        this.H = t, this.data = new nn(this.H);
    }
    has(t) {
        return null !== this.data.get(t);
    }
    first() {
        return this.data.Jt();
    }
    last() {
        return this.data.Yt();
    }
    get size() {
        return this.data.size;
    }
    indexOf(t) {
        return this.data.indexOf(t);
    }
    /** Iterates elements in order defined by "comparator" */    forEach(t) {
        this.data.Xt(((e, n) => (t(e), !1)));
    }
    /** Iterates over `elem`s such that: range[0] &lt;= elem &lt; range[1]. */    me(t, e) {
        const n = this.data.ee(t[0]);
        for (;n.ce(); ) {
            const s = n.oe();
            if (this.H(s.key, t[1]) >= 0) return;
            e(s.key);
        }
    }
    /**
     * Iterates over `elem`s such that: start &lt;= elem until false is returned.
     */    Ae(t, e) {
        let n;
        for (n = void 0 !== e ? this.data.ee(e) : this.data.te(); n.ce(); ) {
            if (!t(n.oe().key)) return;
        }
    }
    /** Finds the least element greater than or equal to `elem`. */    Re(t) {
        const e = this.data.ee(t);
        return e.ce() ? e.oe().key : null;
    }
    te() {
        return new cn(this.data.te());
    }
    ee(t) {
        return new cn(this.data.ee(t));
    }
    /** Inserts or updates an element */    add(t) {
        return this.copy(this.data.remove(t).zt(t, !0));
    }
    /** Deletes an element */    delete(t) {
        return this.has(t) ? this.copy(this.data.remove(t)) : this;
    }
    et() {
        return this.data.et();
    }
    Pe(t) {
        let e = this;
        // Make sure `result` always refers to the larger one of the two sets.
                return e.size < t.size && (e = t, t = this), t.forEach((t => {
            e = e.add(t);
        })), e;
    }
    isEqual(t) {
        if (!(t instanceof on)) return !1;
        if (this.size !== t.size) return !1;
        const e = this.data.te(), n = t.data.te();
        for (;e.ce(); ) {
            const t = e.oe().key, s = n.oe().key;
            if (0 !== this.H(t, s)) return !1;
        }
        return !0;
    }
    it() {
        const t = [];
        return this.forEach((e => {
            t.push(e);
        })), t;
    }
    toString() {
        const t = [];
        return this.forEach((e => t.push(e))), "SortedSet(" + t.toString() + ")";
    }
    copy(t) {
        const e = new on(this.H);
        return e.data = t, e;
    }
}

class cn {
    constructor(t) {
        this.ye = t;
    }
    oe() {
        return this.ye.oe().key;
    }
    ce() {
        return this.ye.ce();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const an = new nn(J.H);

function un() {
    return an;
}

function hn() {
    return un();
}

const ln = new nn(J.H);

function _n() {
    return ln;
}

const fn = new nn(J.H);

function dn() {
    return fn;
}

const wn = new on(J.H);

function En(...t) {
    let e = wn;
    for (const n of t) e = e.add(n);
    return e;
}

const Tn = new on(D);

function In() {
    return Tn;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * DocumentSet is an immutable (copy-on-write) collection that holds documents
 * in order specified by the provided comparator. We always add a document key
 * comparator on top of what is provided to guarantee document equality based on
 * the key.
 */ class mn {
    /** The default ordering is by key if the comparator is omitted */
    constructor(t) {
        // We are adding document key comparator to the end as it's the only
        // guaranteed unique property of a document.
        this.H = t ? (e, n) => t(e, n) || J.H(e.key, n.key) : (t, e) => J.H(t.key, e.key), 
        this.ge = _n(), this.Ve = new nn(this.H);
    }
    /**
     * Returns an empty copy of the existing DocumentSet, using the same
     * comparator.
     */    static pe(t) {
        return new mn(t.H);
    }
    has(t) {
        return null != this.ge.get(t);
    }
    get(t) {
        return this.ge.get(t);
    }
    first() {
        return this.Ve.Jt();
    }
    last() {
        return this.Ve.Yt();
    }
    et() {
        return this.Ve.et();
    }
    /**
     * Returns the index of the provided key in the document set, or -1 if the
     * document key is not present in the set;
     */    indexOf(t) {
        const e = this.ge.get(t);
        return e ? this.Ve.indexOf(e) : -1;
    }
    get size() {
        return this.Ve.size;
    }
    /** Iterates documents in order defined by "comparator" */    forEach(t) {
        this.Ve.Xt(((e, n) => (t(e), !1)));
    }
    /** Inserts or updates a document with the same key */    add(t) {
        // First remove the element if we have it.
        const e = this.delete(t.key);
        return e.copy(e.ge.zt(t.key, t), e.Ve.zt(t, null));
    }
    /** Deletes a document with a given key */    delete(t) {
        const e = this.get(t);
        return e ? this.copy(this.ge.remove(t), this.Ve.remove(e)) : this;
    }
    isEqual(t) {
        if (!(t instanceof mn)) return !1;
        if (this.size !== t.size) return !1;
        const e = this.Ve.te(), n = t.Ve.te();
        for (;e.ce(); ) {
            const t = e.oe().key, s = n.oe().key;
            if (!t.isEqual(s)) return !1;
        }
        return !0;
    }
    toString() {
        const t = [];
        return this.forEach((e => {
            t.push(e.toString());
        })), 0 === t.length ? "DocumentSet ()" : "DocumentSet (\n  " + t.join("  \n") + "\n)";
    }
    copy(t, e) {
        const n = new mn;
        return n.H = this.H, n.ge = t, n.Ve = e, n;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * DocumentChangeSet keeps track of a set of changes to docs in a query, merging
 * duplicate events for the same doc.
 */ class An {
    constructor() {
        this.be = new nn(J.H);
    }
    track(t) {
        const e = t.doc.key, n = this.be.get(e);
        n ? 
        // Merge the new change with the existing change.
        0 /* Added */ !== t.type && 3 /* Metadata */ === n.type ? this.be = this.be.zt(e, t) : 3 /* Metadata */ === t.type && 1 /* Removed */ !== n.type ? this.be = this.be.zt(e, {
            type: n.type,
            doc: t.doc
        }) : 2 /* Modified */ === t.type && 2 /* Modified */ === n.type ? this.be = this.be.zt(e, {
            type: 2 /* Modified */ ,
            doc: t.doc
        }) : 2 /* Modified */ === t.type && 0 /* Added */ === n.type ? this.be = this.be.zt(e, {
            type: 0 /* Added */ ,
            doc: t.doc
        }) : 1 /* Removed */ === t.type && 0 /* Added */ === n.type ? this.be = this.be.remove(e) : 1 /* Removed */ === t.type && 2 /* Modified */ === n.type ? this.be = this.be.zt(e, {
            type: 1 /* Removed */ ,
            doc: n.doc
        }) : 0 /* Added */ === t.type && 1 /* Removed */ === n.type ? this.be = this.be.zt(e, {
            type: 2 /* Modified */ ,
            doc: t.doc
        }) : 
        // This includes these cases, which don't make sense:
        // Added->Added
        // Removed->Removed
        // Modified->Added
        // Removed->Modified
        // Metadata->Added
        // Removed->Metadata
        V() : this.be = this.be.zt(e, t);
    }
    ve() {
        const t = [];
        return this.be.Xt(((e, n) => {
            t.push(n);
        })), t;
    }
}

class Rn {
    constructor(t, e, n, s, i, r, o, c) {
        this.query = t, this.docs = e, this.Se = n, this.docChanges = s, this.De = i, this.fromCache = r, 
        this.Ce = o, this.xe = c;
    }
    /** Returns a view snapshot as if all documents in the snapshot were added. */    static Ne(t, e, n, s) {
        const i = [];
        return e.forEach((t => {
            i.push({
                type: 0 /* Added */ ,
                doc: t
            });
        })), new Rn(t, e, mn.pe(e), i, n, s, 
        /* syncStateChanged= */ !0, 
        /* excludesMetadataChanges= */ !1);
    }
    get hasPendingWrites() {
        return !this.De.et();
    }
    isEqual(t) {
        if (!(this.fromCache === t.fromCache && this.Ce === t.Ce && this.De.isEqual(t.De) && he(this.query, t.query) && this.docs.isEqual(t.docs) && this.Se.isEqual(t.Se))) return !1;
        const e = this.docChanges, n = t.docChanges;
        if (e.length !== n.length) return !1;
        for (let t = 0; t < e.length; t++) if (e[t].type !== n[t].type || !e[t].doc.isEqual(n[t].doc)) return !1;
        return !0;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An event from the RemoteStore. It is split into targetChanges (changes to the
 * state or the set of documents in our watched targets) and documentUpdates
 * (changes to the actual documents).
 */ class Pn {
    constructor(
    /**
     * The snapshot version this event brings us up to, or MIN if not set.
     */
    t, 
    /**
     * A map from target to changes to the target. See TargetChange.
     */
    e, 
    /**
     * A set of targets that is known to be inconsistent. Listens for these
     * targets should be re-established without resume tokens.
     */
    n, 
    /**
     * A set of which documents have changed or been deleted, along with the
     * doc's new values (if not deleted).
     */
    s, 
    /**
     * A set of which document updates are due only to limbo resolution targets.
     */
    i) {
        this.Ot = t, this.$e = e, this.Fe = n, this.Oe = s, this.ke = i;
    }
    /**
     * HACK: Views require RemoteEvents in order to determine whether the view is
     * CURRENT, but secondary tabs don't receive remote events. So this method is
     * used to create a synthesized RemoteEvent that can be used to apply a
     * CURRENT status change to a View, for queries executed in a different tab.
     */
    // PORTING NOTE: Multi-tab only
    static Me(t, e) {
        const n = new Map;
        return n.set(t, yn.Le(t, e)), new Pn(W.min(), n, In(), un(), En());
    }
}

/**
 * A TargetChange specifies the set of changes for a specific target as part of
 * a RemoteEvent. These changes track which documents are added, modified or
 * removed, as well as the target's resume token and whether the target is
 * marked CURRENT.
 * The actual changes *to* documents are not part of the TargetChange since
 * documents may be part of multiple targets.
 */ class yn {
    constructor(
    /**
     * An opaque, server-assigned token that allows watching a query to be resumed
     * after disconnecting without retransmitting all the data that matches the
     * query. The resume token essentially identifies a point in time from which
     * the server should resume sending results.
     */
    t, 
    /**
     * The "current" (synced) status of this target. Note that "current"
     * has special meaning in the RPC protocol that implies that a target is
     * both up-to-date and consistent with the rest of the watch stream.
     */
    e, 
    /**
     * The set of documents that were newly assigned to this target as part of
     * this remote event.
     */
    n, 
    /**
     * The set of documents that were already assigned to this target but received
     * an update during this remote event.
     */
    s, 
    /**
     * The set of documents that were removed from this target as part of this
     * remote event.
     */
    i) {
        this.resumeToken = t, this.Be = e, this.qe = n, this.Ue = s, this.Qe = i;
    }
    /**
     * This method is used to create a synthesized TargetChanges that can be used to
     * apply a CURRENT status change to a View (for queries executed in a different
     * tab) or for new queries (to raise snapshots with correct CURRENT status).
     */    static Le(t, e) {
        return new yn(N.T, e, En(), En(), En());
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents a changed document and a list of target ids to which this change
 * applies.
 *
 * If document has been deleted NoDocument will be provided.
 */ class gn {
    constructor(
    /** The new document applies to all of these targets. */
    t, 
    /** The new document is removed from all of these targets. */
    e, 
    /** The key of the document for this change. */
    n, 
    /**
     * The new document or NoDocument if it was deleted. Is null if the
     * document went out of view without the server sending a new document.
     */
    s) {
        this.Ke = t, this.removedTargetIds = e, this.key = n, this.We = s;
    }
}

class Vn {
    constructor(t, e) {
        this.targetId = t, this.je = e;
    }
}

class pn {
    constructor(
    /** What kind of change occurred to the watch target. */
    t, 
    /** The target IDs that were added/removed/set. */
    e, 
    /**
     * An opaque, server-assigned token that allows watching a target to be
     * resumed after disconnecting without retransmitting all the data that
     * matches the target. The resume token essentially identifies a point in
     * time from which the server should resume sending results.
     */
    n = N.T
    /** An RPC error indicating why the watch failed. */ , s = null) {
        this.state = t, this.targetIds = e, this.resumeToken = n, this.cause = s;
    }
}

/** Tracks the internal state of a Watch target. */ class bn {
    constructor() {
        /**
         * The number of pending responses (adds or removes) that we are waiting on.
         * We only consider targets active that have no pending responses.
         */
        this.Ge = 0, 
        /**
         * Keeps track of the document changes since the last raised snapshot.
         *
         * These changes are continuously updated as we receive document updates and
         * always reflect the current set of changes against the last issued snapshot.
         */
        this.ze = Dn(), 
        /** See public getters for explanations of these fields. */
        this.He = N.T, this.Je = !1, 
        /**
         * Whether this target state should be included in the next snapshot. We
         * initialize to true so that newly-added targets are included in the next
         * RemoteEvent.
         */
        this.Ye = !0;
    }
    /**
     * Whether this target has been marked 'current'.
     *
     * 'Current' has special meaning in the RPC protocol: It implies that the
     * Watch backend has sent us all changes up to the point at which the target
     * was added and that the target is consistent with the rest of the watch
     * stream.
     */    get Be() {
        return this.Je;
    }
    /** The last resume token sent to us for this target. */    get resumeToken() {
        return this.He;
    }
    /** Whether this target has pending target adds or target removes. */    get Xe() {
        return 0 !== this.Ge;
    }
    /** Whether we have modified any state that should trigger a snapshot. */    get Ze() {
        return this.Ye;
    }
    /**
     * Applies the resume token to the TargetChange, but only when it has a new
     * value. Empty resumeTokens are discarded.
     */    tn(t) {
        t.l() > 0 && (this.Ye = !0, this.He = t);
    }
    /**
     * Creates a target change from the current set of changes.
     *
     * To reset the document changes after raising this snapshot, call
     * `clearPendingChanges()`.
     */    en() {
        let t = En(), e = En(), n = En();
        return this.ze.forEach(((s, i) => {
            switch (i) {
              case 0 /* Added */ :
                t = t.add(s);
                break;

              case 2 /* Modified */ :
                e = e.add(s);
                break;

              case 1 /* Removed */ :
                n = n.add(s);
                break;

              default:
                V();
            }
        })), new yn(this.He, this.Je, t, e, n);
    }
    /**
     * Resets the document changes and sets `hasPendingChanges` to false.
     */    nn() {
        this.Ye = !1, this.ze = Dn();
    }
    sn(t, e) {
        this.Ye = !0, this.ze = this.ze.zt(t, e);
    }
    rn(t) {
        this.Ye = !0, this.ze = this.ze.remove(t);
    }
    on() {
        this.Ge += 1;
    }
    cn() {
        this.Ge -= 1;
    }
    an() {
        this.Ye = !0, this.Je = !0;
    }
}

/**
 * A helper class to accumulate watch changes into a RemoteEvent.
 */
class vn {
    constructor(t) {
        this.un = t, 
        /** The internal state of all tracked targets. */
        this.hn = new Map, 
        /** Keeps track of the documents to update since the last raised snapshot. */
        this.ln = un(), 
        /** A mapping of document keys to their set of target IDs. */
        this._n = Sn(), 
        /**
         * A list of targets with existence filter mismatches. These targets are
         * known to be inconsistent and their listens needs to be re-established by
         * RemoteStore.
         */
        this.fn = new on(D);
    }
    /**
     * Processes and adds the DocumentWatchChange to the current set of changes.
     */    dn(t) {
        for (const e of t.Ke) t.We instanceof vt ? this.wn(e, t.We) : t.We instanceof St && this.En(e, t.key, t.We);
        for (const e of t.removedTargetIds) this.En(e, t.key, t.We);
    }
    /** Processes and adds the WatchTargetChange to the current set of changes. */    Tn(t) {
        this.In(t, (e => {
            const n = this.mn(e);
            switch (t.state) {
              case 0 /* NoChange */ :
                this.An(e) && n.tn(t.resumeToken);
                break;

              case 1 /* Added */ :
                // We need to decrement the number of pending acks needed from watch
                // for this targetId.
                n.cn(), n.Xe || 
                // We have a freshly added target, so we need to reset any state
                // that we had previously. This can happen e.g. when remove and add
                // back a target for existence filter mismatches.
                n.nn(), n.tn(t.resumeToken);
                break;

              case 2 /* Removed */ :
                // We need to keep track of removed targets to we can post-filter and
                // remove any target changes.
                // We need to decrement the number of pending acks needed from watch
                // for this targetId.
                n.cn(), n.Xe || this.removeTarget(e);
                break;

              case 3 /* Current */ :
                this.An(e) && (n.an(), n.tn(t.resumeToken));
                break;

              case 4 /* Reset */ :
                this.An(e) && (
                // Reset the target and synthesizes removes for all existing
                // documents. The backend will re-add any documents that still
                // match the target before it sends the next global snapshot.
                this.Rn(e), n.tn(t.resumeToken));
                break;

              default:
                V();
            }
        }));
    }
    /**
     * Iterates over all targetIds that the watch change applies to: either the
     * targetIds explicitly listed in the change or the targetIds of all currently
     * active targets.
     */    In(t, e) {
        t.targetIds.length > 0 ? t.targetIds.forEach(e) : this.hn.forEach(((t, n) => {
            this.An(n) && e(n);
        }));
    }
    /**
     * Handles existence filters and synthesizes deletes for filter mismatches.
     * Targets that are invalidated by filter mismatches are added to
     * `pendingTargetResets`.
     */    Pn(t) {
        const e = t.targetId, n = t.je.count, s = this.yn(e);
        if (s) {
            const t = s.target;
            if (Ot(t)) if (0 === n) {
                // The existence filter told us the document does not exist. We deduce
                // that this document does not exist and apply a deleted document to
                // our updates. Without applying this deleted document there might be
                // another query that will raise this document as part of a snapshot
                // until it is resolved, essentially exposing inconsistency between
                // queries.
                const n = new J(t.path);
                this.En(e, n, new St(n, W.min()));
            } else p(1 === n); else {
                this.gn(e) !== n && (
                // Existence filter mismatch: We reset the mapping and raise a new
                // snapshot with `isFromCache:true`.
                this.Rn(e), this.fn = this.fn.add(e));
            }
        }
    }
    /**
     * Converts the currently accumulated state into a remote event at the
     * provided snapshot version. Resets the accumulated changes before returning.
     */    Vn(t) {
        const e = new Map;
        this.hn.forEach(((n, s) => {
            const i = this.yn(s);
            if (i) {
                if (n.Be && Ot(i.target)) {
                    // Document queries for document that don't exist can produce an empty
                    // result set. To update our local cache, we synthesize a document
                    // delete if we have not previously received the document. This
                    // resolves the limbo state of the document, removing it from
                    // limboDocumentRefs.
                    // TODO(dimond): Ideally we would have an explicit lookup target
                    // instead resulting in an explicit delete message and we could
                    // remove this special logic.
                    const e = new J(i.target.path);
                    null !== this.ln.get(e) || this.pn(s, e) || this.En(s, e, new St(e, t));
                }
                n.Ze && (e.set(s, n.en()), n.nn());
            }
        }));
        let n = En();
        // We extract the set of limbo-only document updates as the GC logic
        // special-cases documents that do not appear in the target cache.
        
        // TODO(gsoltis): Expand on this comment once GC is available in the JS
        // client.
                this._n.forEach(((t, e) => {
            let s = !0;
            e.Ae((t => {
                const e = this.yn(t);
                return !e || 2 /* LimboResolution */ === e.Ft || (s = !1, !1);
            })), s && (n = n.add(t));
        }));
        const s = new Pn(t, e, this.fn, this.ln, n);
        return this.ln = un(), this._n = Sn(), this.fn = new on(D), s;
    }
    /**
     * Adds the provided document to the internal list of document updates and
     * its document key to the given target's mapping.
     */
    // Visible for testing.
    wn(t, e) {
        if (!this.An(t)) return;
        const n = this.pn(t, e.key) ? 2 /* Modified */ : 0 /* Added */;
        this.mn(t).sn(e.key, n), this.ln = this.ln.zt(e.key, e), this._n = this._n.zt(e.key, this.bn(e.key).add(t));
    }
    /**
     * Removes the provided document from the target mapping. If the
     * document no longer matches the target, but the document's state is still
     * known (e.g. we know that the document was deleted or we received the change
     * that caused the filter mismatch), the new document can be provided
     * to update the remote document cache.
     */
    // Visible for testing.
    En(t, e, n) {
        if (!this.An(t)) return;
        const s = this.mn(t);
        this.pn(t, e) ? s.sn(e, 1 /* Removed */) : 
        // The document may have entered and left the target before we raised a
        // snapshot, so we can just ignore the change.
        s.rn(e), this._n = this._n.zt(e, this.bn(e).delete(t)), n && (this.ln = this.ln.zt(e, n));
    }
    removeTarget(t) {
        this.hn.delete(t);
    }
    /**
     * Returns the current count of documents in the target. This includes both
     * the number of documents that the LocalStore considers to be part of the
     * target as well as any accumulated changes.
     */    gn(t) {
        const e = this.mn(t).en();
        return this.un.vn(t).size + e.qe.size - e.Qe.size;
    }
    /**
     * Increment the number of acks needed from watch before we can consider the
     * server to be 'in-sync' with the client's active targets.
     */    on(t) {
        this.mn(t).on();
    }
    mn(t) {
        let e = this.hn.get(t);
        return e || (e = new bn, this.hn.set(t, e)), e;
    }
    bn(t) {
        let e = this._n.get(t);
        return e || (e = new on(D), this._n = this._n.zt(t, e)), e;
    }
    /**
     * Verifies that the user is still interested in this target (by calling
     * `getTargetDataForTarget()`) and that we are not waiting for pending ADDs
     * from watch.
     */    An(t) {
        const e = null !== this.yn(t);
        return e || R("WatchChangeAggregator", "Detected inactive target", t), e;
    }
    /**
     * Returns the TargetData for an active target (i.e. a target that the user
     * is still interested in that has no outstanding target change requests).
     */    yn(t) {
        const e = this.hn.get(t);
        return e && e.Xe ? null : this.un.Sn(t);
    }
    /**
     * Resets the state of a Watch target to its initial state (e.g. sets
     * 'current' to false, clears the resume token and removes its target mapping
     * from all documents).
     */    Rn(t) {
        this.hn.set(t, new bn);
        this.un.vn(t).forEach((e => {
            this.En(t, e, /*updatedDocument=*/ null);
        }));
    }
    /**
     * Returns whether the LocalStore considers the document to be part of the
     * specified target.
     */    pn(t, e) {
        return this.un.vn(t).has(e);
    }
}

function Sn() {
    return new nn(J.H);
}

function Dn() {
    return new nn(J.H);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Cn = (() => {
    const t = {
        asc: "ASCENDING",
        desc: "DESCENDING"
    };
    return t;
})(), xn = (() => {
    const t = {
        "<": "LESS_THAN",
        "<=": "LESS_THAN_OR_EQUAL",
        ">": "GREATER_THAN",
        ">=": "GREATER_THAN_OR_EQUAL",
        "==": "EQUAL",
        "!=": "NOT_EQUAL",
        "array-contains": "ARRAY_CONTAINS",
        in: "IN",
        "not-in": "NOT_IN",
        "array-contains-any": "ARRAY_CONTAINS_ANY"
    };
    return t;
})();

/**
 * This class generates JsonObject values for the Datastore API suitable for
 * sending to either GRPC stub methods or via the JSON/HTTP REST API.
 *
 * The serializer supports both Protobuf.js and Proto3 JSON formats. By
 * setting `useProto3Json` to true, the serializer will use the Proto3 JSON
 * format.
 *
 * For a description of the Proto3 JSON format check
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 *
 * TODO(klimt): We can remove the databaseId argument if we keep the full
 * resource name in documents.
 */
class Nn {
    constructor(t, e) {
        this.t = t, this.Bt = e;
    }
}

/**
 * Returns a value for a Date that's appropriate to put into a proto.
 */
function $n(t, e) {
    if (t.Bt) {
        return `${new Date(1e3 * e.seconds).toISOString().replace(/\.\d*/, "").replace("Z", "")}.${("000000000" + e.nanoseconds).slice(-9)}Z`;
    }
    return {
        seconds: "" + e.seconds,
        nanos: e.nanoseconds
    };
}

/**
 * Returns a value for bytes that's appropriate to put in a proto.
 *
 * Visible for testing.
 */
function Fn(t, e) {
    return t.Bt ? e.toBase64() : e.toUint8Array();
}

/**
 * Returns a ByteString based on the proto string value.
 */ function On(t, e) {
    return $n(t, e.j());
}

function kn(t) {
    return p(!!t), W.K(function(t) {
        const e = nt(t);
        return new K(e.seconds, e.nanos);
    }(t));
}

function Mn(t, e) {
    return function(t) {
        return new G([ "projects", t.projectId, "databases", t.database ]);
    }(t).child("documents").child(e).rt();
}

function Ln(t) {
    const e = G.ot(t);
    return p(fs(e)), e;
}

function Bn(t, e) {
    return Mn(t.t, e.path);
}

function qn(t, e) {
    const n = Ln(e);
    if (n.get(1) !== t.t.projectId) throw new F($.INVALID_ARGUMENT, "Tried to deserialize key from different project: " + n.get(1) + " vs " + t.t.projectId);
    if (n.get(3) !== t.t.database) throw new F($.INVALID_ARGUMENT, "Tried to deserialize key from different database: " + n.get(3) + " vs " + t.t.database);
    return new J(Wn(n));
}

function Un(t, e) {
    return Mn(t.t, e);
}

function Qn(t) {
    const e = Ln(t);
    // In v1beta1 queries for collections at the root did not have a trailing
    // "/documents". In v1 all resource paths contain "/documents". Preserve the
    // ability to read the v1beta1 form for compatibility with queries persisted
    // in the local target cache.
        return 4 === e.length ? G.ct() : Wn(e);
}

function Kn(t) {
    return new G([ "projects", t.t.projectId, "databases", t.t.database ]).rt();
}

function Wn(t) {
    return p(t.length > 4 && "documents" === t.get(4)), t.Y(5);
}

/** Creates a Document proto from key and fields (but no create/update time) */ function jn(t, e, n) {
    return {
        name: Bn(t, e),
        fields: n.proto.mapValue.fields
    };
}

function Gn(t, e, n) {
    const s = qn(t, e.name), i = kn(e.updateTime), r = new gt({
        mapValue: {
            fields: e.fields
        }
    });
    return new vt(s, i, r, {
        hasCommittedMutations: !!n
    });
}

function zn(t, e) {
    return "found" in e ? function(t, e) {
        p(!!e.found), e.found.name, e.found.updateTime;
        const n = qn(t, e.found.name), s = kn(e.found.updateTime), i = new gt({
            mapValue: {
                fields: e.found.fields
            }
        });
        return new vt(n, s, i, {});
    }(t, e) : "missing" in e ? function(t, e) {
        p(!!e.missing), p(!!e.readTime);
        const n = qn(t, e.missing), s = kn(e.readTime);
        return new St(n, s);
    }(t, e) : V();
}

function Hn(t, e) {
    let n;
    if ("targetChange" in e) {
        e.targetChange;
        // proto3 default value is unset in JSON (undefined), so use 'NO_CHANGE'
        // if unset
        const s = function(t) {
            return "NO_CHANGE" === t ? 0 /* NoChange */ : "ADD" === t ? 1 /* Added */ : "REMOVE" === t ? 2 /* Removed */ : "CURRENT" === t ? 3 /* Current */ : "RESET" === t ? 4 /* Reset */ : V();
        }(e.targetChange.targetChangeType || "NO_CHANGE"), i = e.targetChange.targetIds || [], r = function(t, e) {
            return t.Bt ? (p(void 0 === e || "string" == typeof e), N.fromBase64String(e || "")) : (p(void 0 === e || e instanceof Uint8Array), 
            N.fromUint8Array(e || new Uint8Array));
        }(t, e.targetChange.resumeToken), o = e.targetChange.cause, c = o && function(t) {
            const e = void 0 === t.code ? $.UNKNOWN : en(t.code);
            return new F(e, t.message || "");
        }
        /**
 * Returns a value for a number (or null) that's appropriate to put into
 * a google.protobuf.Int32Value proto.
 * DO NOT USE THIS FOR ANYTHING ELSE.
 * This method cheats. It's typed as returning "number" because that's what
 * our generated proto interfaces say Int32Value must be. But GRPC actually
 * expects a { value: <number> } struct.
 */ (o);
        n = new pn(s, i, r, c || null);
    } else if ("documentChange" in e) {
        e.documentChange;
        const s = e.documentChange;
        s.document, s.document.name, s.document.updateTime;
        const i = qn(t, s.document.name), r = kn(s.document.updateTime), o = new gt({
            mapValue: {
                fields: s.document.fields
            }
        }), c = new vt(i, r, o, {}), a = s.targetIds || [], u = s.removedTargetIds || [];
        n = new gn(a, u, c.key, c);
    } else if ("documentDelete" in e) {
        e.documentDelete;
        const s = e.documentDelete;
        s.document;
        const i = qn(t, s.document), r = s.readTime ? kn(s.readTime) : W.min(), o = new St(i, r), c = s.removedTargetIds || [];
        n = new gn([], c, o.key, o);
    } else if ("documentRemove" in e) {
        e.documentRemove;
        const s = e.documentRemove;
        s.document;
        const i = qn(t, s.document), r = s.removedTargetIds || [];
        n = new gn([], r, i, null);
    } else {
        if (!("filter" in e)) return V();
        {
            e.filter;
            const t = e.filter;
            t.targetId;
            const s = t.count || 0, i = new Ye(s), r = t.targetId;
            n = new Vn(r, i);
        }
    }
    return n;
}

function Jn(t, e) {
    let n;
    if (e instanceof Qe) n = {
        update: jn(t, e.key, e.value)
    }; else if (e instanceof He) n = {
        delete: Bn(t, e.key)
    }; else if (e instanceof Ke) n = {
        update: jn(t, e.key, e.data),
        updateMask: _s(e.Gt)
    }; else {
        if (!(e instanceof Je)) return V();
        n = {
            verify: Bn(t, e.key)
        };
    }
    return e.fieldTransforms.length > 0 && (n.updateTransforms = e.fieldTransforms.map((t => function(t, e) {
        const n = e.transform;
        if (n instanceof ge) return {
            fieldPath: e.field.rt(),
            setToServerValue: "REQUEST_TIME"
        };
        if (n instanceof Ve) return {
            fieldPath: e.field.rt(),
            appendMissingElements: {
                values: n.elements
            }
        };
        if (n instanceof be) return {
            fieldPath: e.field.rt(),
            removeAllFromArray: {
                values: n.elements
            }
        };
        if (n instanceof Se) return {
            fieldPath: e.field.rt(),
            increment: n.Ut
        };
        throw V();
    }(0, t)))), e.jt.Wt || (n.currentDocument = function(t, e) {
        return void 0 !== e.updateTime ? {
            updateTime: On(t, e.updateTime)
        } : void 0 !== e.exists ? {
            exists: e.exists
        } : V();
    }(t, e.jt)), n;
}

function Yn(t, e) {
    const n = e.currentDocument ? function(t) {
        return void 0 !== t.updateTime ? Fe.updateTime(kn(t.updateTime)) : void 0 !== t.exists ? Fe.exists(t.exists) : Fe.Kt();
    }(e.currentDocument) : Fe.Kt(), s = e.updateTransforms ? e.updateTransforms.map((e => function(t, e) {
        let n = null;
        if ("setToServerValue" in e) p("REQUEST_TIME" === e.setToServerValue), n = new ge; else if ("appendMissingElements" in e) {
            const t = e.appendMissingElements.values || [];
            n = new Ve(t);
        } else if ("removeAllFromArray" in e) {
            const t = e.removeAllFromArray.values || [];
            n = new be(t);
        } else "increment" in e ? n = new Se(t, e.increment) : V();
        const s = H.lt(e.fieldPath);
        return new xe(s, n);
    }(t, e))) : [];
    if (e.update) {
        e.update.name;
        const i = qn(t, e.update.name), r = new gt({
            mapValue: {
                fields: e.update.fields
            }
        });
        if (e.updateMask) {
            const t = function(t) {
                const e = t.fieldPaths || [];
                return new tt(e.map((t => H.lt(t))));
            }(e.updateMask);
            return new Ke(i, r, t, n, s);
        }
        return new Qe(i, r, n, s);
    }
    if (e.delete) {
        const s = qn(t, e.delete);
        return new He(s, n);
    }
    if (e.verify) {
        const s = qn(t, e.verify);
        return new Je(s, n);
    }
    return V();
}

function Xn(t, e) {
    return t && t.length > 0 ? (p(void 0 !== e), t.map((t => function(t, e) {
        // NOTE: Deletes don't have an updateTime.
        let n = t.updateTime ? kn(t.updateTime) : kn(e);
        n.isEqual(W.min()) && (
        // The Firestore Emulator currently returns an update time of 0 for
        // deletes of non-existing documents (rather than null). This breaks the
        // test "get deleted doc while offline with source=cache" as NoDocuments
        // with version 0 are filtered by IndexedDb's RemoteDocumentCache.
        // TODO(#2149): Remove this when Emulator is fixed
        n = kn(e));
        let s = null;
        return t.transformResults && t.transformResults.length > 0 && (s = t.transformResults), 
        new $e(n, s);
    }(t, e)))) : [];
}

function Zn(t, e) {
    return {
        documents: [ Un(t, e.path) ]
    };
}

function ts(t, e) {
    // Dissect the path into parent, collectionId, and optional key filter.
    const n = {
        structuredQuery: {}
    }, s = e.path;
    null !== e.collectionGroup ? (n.parent = Un(t, s), n.structuredQuery.from = [ {
        collectionId: e.collectionGroup,
        allDescendants: !0
    } ]) : (n.parent = Un(t, s.X()), n.structuredQuery.from = [ {
        collectionId: s.tt()
    } ]);
    const i = function(t) {
        if (0 === t.length) return;
        const e = t.map((t => 
        // visible for testing
        function(t) {
            if ("==" /* EQUAL */ === t.op) {
                if (Pt(t.value)) return {
                    unaryFilter: {
                        field: as(t.field),
                        op: "IS_NAN"
                    }
                };
                if (Rt(t.value)) return {
                    unaryFilter: {
                        field: as(t.field),
                        op: "IS_NULL"
                    }
                };
            } else if ("!=" /* NOT_EQUAL */ === t.op) {
                if (Pt(t.value)) return {
                    unaryFilter: {
                        field: as(t.field),
                        op: "IS_NOT_NAN"
                    }
                };
                if (Rt(t.value)) return {
                    unaryFilter: {
                        field: as(t.field),
                        op: "IS_NOT_NULL"
                    }
                };
            }
            return {
                fieldFilter: {
                    field: as(t.field),
                    op: cs(t.op),
                    value: t.value
                }
            };
        }(t)));
        if (1 === e.length) return e[0];
        return {
            compositeFilter: {
                op: "AND",
                filters: e
            }
        };
    }(e.filters);
    i && (n.structuredQuery.where = i);
    const r = function(t) {
        if (0 === t.length) return;
        return t.map((t => 
        // visible for testing
        function(t) {
            return {
                field: as(t.field),
                direction: os(t.dir)
            };
        }(t)));
    }(e.orderBy);
    r && (n.structuredQuery.orderBy = r);
    const o = function(t, e) {
        return t.Bt || at(e) ? e : {
            value: e
        };
    }
    /**
 * Returns a number (or null) from a google.protobuf.Int32Value proto.
 */ (t, e.limit);
    return null !== o && (n.structuredQuery.limit = o), e.startAt && (n.structuredQuery.startAt = is(e.startAt)), 
    e.endAt && (n.structuredQuery.endAt = is(e.endAt)), n;
}

function es(t) {
    let e = Qn(t.parent);
    const n = t.structuredQuery, s = n.from ? n.from.length : 0;
    let i = null;
    if (s > 0) {
        p(1 === s);
        const t = n.from[0];
        t.allDescendants ? i = t.collectionId : e = e.child(t.collectionId);
    }
    let r = [];
    n.where && (r = ss(n.where));
    let o = [];
    n.orderBy && (o = n.orderBy.map((t => function(t) {
        return new Ht(us(t.field), 
        // visible for testing
        function(t) {
            switch (t) {
              case "ASCENDING":
                return "asc" /* ASCENDING */;

              case "DESCENDING":
                return "desc" /* DESCENDING */;

              default:
                return;
            }
        }
        // visible for testing
        (t.direction));
    }(t))));
    let c = null;
    n.limit && (c = function(t) {
        let e;
        return e = "object" == typeof t ? t.value : t, at(e) ? null : e;
    }(n.limit));
    let a = null;
    n.startAt && (a = rs(n.startAt));
    let u = null;
    return n.endAt && (u = rs(n.endAt)), te(e, i, o, r, c, "F" /* First */ , a, u);
}

function ns(t, e) {
    const n = function(t, e) {
        switch (e) {
          case 0 /* Listen */ :
            return null;

          case 1 /* ExistenceFilterMismatch */ :
            return "existence-filter-mismatch";

          case 2 /* LimboResolution */ :
            return "limbo-document";

          default:
            return V();
        }
    }(0, e.Ft);
    return null == n ? null : {
        "goog-listen-tags": n
    };
}

function ss(t) {
    return t ? void 0 !== t.unaryFilter ? [ ls(t) ] : void 0 !== t.fieldFilter ? [ hs(t) ] : void 0 !== t.compositeFilter ? t.compositeFilter.filters.map((t => ss(t))).reduce(((t, e) => t.concat(e))) : V() : [];
}

function is(t) {
    return {
        before: t.before,
        values: t.position
    };
}

function rs(t) {
    const e = !!t.before, n = t.values || [];
    return new Gt(n, e);
}

// visible for testing
function os(t) {
    return Cn[t];
}

function cs(t) {
    return xn[t];
}

function as(t) {
    return {
        fieldPath: t.rt()
    };
}

function us(t) {
    return H.lt(t.fieldPath);
}

function hs(t) {
    return kt.create(us(t.fieldFilter.field), function(t) {
        switch (t) {
          case "EQUAL":
            return "==" /* EQUAL */;

          case "NOT_EQUAL":
            return "!=" /* NOT_EQUAL */;

          case "GREATER_THAN":
            return ">" /* GREATER_THAN */;

          case "GREATER_THAN_OR_EQUAL":
            return ">=" /* GREATER_THAN_OR_EQUAL */;

          case "LESS_THAN":
            return "<" /* LESS_THAN */;

          case "LESS_THAN_OR_EQUAL":
            return "<=" /* LESS_THAN_OR_EQUAL */;

          case "ARRAY_CONTAINS":
            return "array-contains" /* ARRAY_CONTAINS */;

          case "IN":
            return "in" /* IN */;

          case "NOT_IN":
            return "not-in" /* NOT_IN */;

          case "ARRAY_CONTAINS_ANY":
            return "array-contains-any" /* ARRAY_CONTAINS_ANY */;

          case "OPERATOR_UNSPECIFIED":
          default:
            return V();
        }
    }(t.fieldFilter.op), t.fieldFilter.value);
}

function ls(t) {
    switch (t.unaryFilter.op) {
      case "IS_NAN":
        const e = us(t.unaryFilter.field);
        return kt.create(e, "==" /* EQUAL */ , {
            doubleValue: NaN
        });

      case "IS_NULL":
        const n = us(t.unaryFilter.field);
        return kt.create(n, "==" /* EQUAL */ , {
            nullValue: "NULL_VALUE"
        });

      case "IS_NOT_NAN":
        const s = us(t.unaryFilter.field);
        return kt.create(s, "!=" /* NOT_EQUAL */ , {
            doubleValue: NaN
        });

      case "IS_NOT_NULL":
        const i = us(t.unaryFilter.field);
        return kt.create(i, "!=" /* NOT_EQUAL */ , {
            nullValue: "NULL_VALUE"
        });

      case "OPERATOR_UNSPECIFIED":
      default:
        return V();
    }
}

function _s(t) {
    const e = [];
    return t.fields.forEach((t => e.push(t.rt()))), {
        fieldPaths: e
    };
}

function fs(t) {
    // Resource names have at least 4 components (project ID, database ID)
    return t.length >= 4 && "projects" === t.get(0) && "databases" === t.get(2);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class ds {
    constructor() {
        this.promise = new Promise(((t, e) => {
            this.resolve = t, this.reject = e;
        }));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * PersistencePromise is essentially a re-implementation of Promise except
 * it has a .next() method instead of .then() and .next() and .catch() callbacks
 * are executed synchronously when a PersistencePromise resolves rather than
 * asynchronously (Promise implementations use setImmediate() or similar).
 *
 * This is necessary to interoperate with IndexedDB which will automatically
 * commit transactions if control is returned to the event loop without
 * synchronously initiating another operation on the transaction.
 *
 * NOTE: .then() and .catch() only allow a single consumer, unlike normal
 * Promises.
 */ class ws {
    constructor(t) {
        // NOTE: next/catchCallback will always point to our own wrapper functions,
        // not the user's raw next() or catch() callbacks.
        this.Dn = null, this.Cn = null, 
        // When the operation resolves, we'll set result or error and mark isDone.
        this.result = void 0, this.error = void 0, this.xn = !1, 
        // Set to true when .then() or .catch() are called and prevents additional
        // chaining.
        this.Nn = !1, t((t => {
            this.xn = !0, this.result = t, this.Dn && 
            // value should be defined unless T is Void, but we can't express
            // that in the type system.
            this.Dn(t);
        }), (t => {
            this.xn = !0, this.error = t, this.Cn && this.Cn(t);
        }));
    }
    catch(t) {
        return this.next(void 0, t);
    }
    next(t, e) {
        return this.Nn && V(), this.Nn = !0, this.xn ? this.error ? this.$n(e, this.error) : this.Fn(t, this.result) : new ws(((n, s) => {
            this.Dn = e => {
                this.Fn(t, e).next(n, s);
            }, this.Cn = t => {
                this.$n(e, t).next(n, s);
            };
        }));
    }
    On() {
        return new Promise(((t, e) => {
            this.next(t, e);
        }));
    }
    kn(t) {
        try {
            const e = t();
            return e instanceof ws ? e : ws.resolve(e);
        } catch (t) {
            return ws.reject(t);
        }
    }
    Fn(t, e) {
        return t ? this.kn((() => t(e))) : ws.resolve(e);
    }
    $n(t, e) {
        return t ? this.kn((() => t(e))) : ws.reject(e);
    }
    static resolve(t) {
        return new ws(((e, n) => {
            e(t);
        }));
    }
    static reject(t) {
        return new ws(((e, n) => {
            n(t);
        }));
    }
    static Mn(
    // Accept all Promise types in waitFor().
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t) {
        return new ws(((e, n) => {
            let s = 0, i = 0, r = !1;
            t.forEach((t => {
                ++s, t.next((() => {
                    ++i, r && i === s && e();
                }), (t => n(t)));
            })), r = !0, i === s && e();
        }));
    }
    /**
     * Given an array of predicate functions that asynchronously evaluate to a
     * boolean, implements a short-circuiting `or` between the results. Predicates
     * will be evaluated until one of them returns `true`, then stop. The final
     * result will be whether any of them returned `true`.
     */    static Ln(t) {
        let e = ws.resolve(!1);
        for (const n of t) e = e.next((t => t ? ws.resolve(t) : n()));
        return e;
    }
    static forEach(t, e) {
        const n = [];
        return t.forEach(((t, s) => {
            n.push(e.call(this, t, s));
        })), this.Mn(n);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// References to `window` are guarded by SimpleDb.isAvailable()
/* eslint-disable no-restricted-globals */
/**
 * Wraps an IDBTransaction and exposes a store() method to get a handle to a
 * specific object store.
 */
class Es {
    constructor(t, e) {
        this.action = t, this.transaction = e, this.aborted = !1, 
        /**
         * A promise that resolves with the result of the IndexedDb transaction.
         */
        this.Bn = new ds, this.transaction.oncomplete = () => {
            this.Bn.resolve();
        }, this.transaction.onabort = () => {
            e.error ? this.Bn.reject(new ms(t, e.error)) : this.Bn.resolve();
        }, this.transaction.onerror = e => {
            const n = gs(e.target.error);
            this.Bn.reject(new ms(t, n));
        };
    }
    static open(t, e, n, s) {
        try {
            return new Es(e, t.transaction(s, n));
        } catch (t) {
            throw new ms(e, t);
        }
    }
    get qn() {
        return this.Bn.promise;
    }
    abort(t) {
        t && this.Bn.reject(t), this.aborted || (R("SimpleDb", "Aborting transaction:", t ? t.message : "Client-initiated abort"), 
        this.aborted = !0, this.transaction.abort());
    }
    /**
     * Returns a SimpleDbStore<KeyType, ValueType> for the specified store. All
     * operations performed on the SimpleDbStore happen within the context of this
     * transaction and it cannot be used anymore once the transaction is
     * completed.
     *
     * Note that we can't actually enforce that the KeyType and ValueType are
     * correct, but they allow type safety through the rest of the consuming code.
     */    store(t) {
        const e = this.transaction.objectStore(t);
        return new Rs(e);
    }
}

/**
 * Provides a wrapper around IndexedDb with a simplified interface that uses
 * Promise-like return values to chain operations. Real promises cannot be used
 * since .then() continuations are executed asynchronously (e.g. via
 * .setImmediate), which would cause IndexedDB to end the transaction.
 * See PersistencePromise for more details.
 */ class Ts {
    /*
     * Creates a new SimpleDb wrapper for IndexedDb database `name`.
     *
     * Note that `version` must not be a downgrade. IndexedDB does not support
     * downgrading the schema version. We currently do not support any way to do
     * versioning outside of IndexedDB's versioning mechanism, as only
     * version-upgrade transactions are allowed to do things like create
     * objectstores.
     */
    constructor(t, e, s) {
        this.name = t, this.version = e, this.Un = s;
        // NOTE: According to https://bugs.webkit.org/show_bug.cgi?id=197050, the
        // bug we're checking for should exist in iOS >= 12.2 and < 13, but for
        // whatever reason it's much harder to hit after 12.2 so we only proactively
        // log on 12.2.
        12.2 === Ts.Qn(n()) && P("Firestore persistence suffers from a bug in iOS 12.2 Safari that may cause your app to stop working. See https://stackoverflow.com/q/56496296/110915 for details and a potential workaround.");
    }
    /** Deletes the specified database. */    static delete(t) {
        return R("SimpleDb", "Removing database:", t), Ps(window.indexedDB.deleteDatabase(t)).On();
    }
    /** Returns true if IndexedDB is available in the current environment. */    static Kn() {
        if ("undefined" == typeof indexedDB) return !1;
        if (Ts.Wn()) return !0;
        // We extensively use indexed array values and compound keys,
        // which IE and Edge do not support. However, they still have indexedDB
        // defined on the window, so we need to check for them here and make sure
        // to return that persistence is not enabled for those browsers.
        // For tracking support of this feature, see here:
        // https://developer.microsoft.com/en-us/microsoft-edge/platform/status/indexeddbarraysandmultientrysupport/
        // Check the UA string to find out the browser.
                const t = n(), e = Ts.Qn(t), s = 0 < e && e < 10, i = Ts.jn(t), r = 0 < i && i < 4.5;
        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
        // Edge
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML,
        // like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
        // iOS Safari: Disable for users running iOS version < 10.
                return !(t.indexOf("MSIE ") > 0 || t.indexOf("Trident/") > 0 || t.indexOf("Edge/") > 0 || s || r);
    }
    /**
     * Returns true if the backing IndexedDB store is the Node IndexedDBShim
     * (see https://github.com/axemclion/IndexedDBShim).
     */    static Wn() {
        var t;
        return "undefined" != typeof process && "YES" === (null === (t = process.env) || void 0 === t ? void 0 : t.Gn);
    }
    /** Helper to get a typed SimpleDbStore from a transaction. */    static zn(t, e) {
        return t.store(e);
    }
    // visible for testing
    /** Parse User Agent to determine iOS version. Returns -1 if not found. */
    static Qn(t) {
        const e = t.match(/i(?:phone|pad|pod) os ([\d_]+)/i), n = e ? e[1].split("_").slice(0, 2).join(".") : "-1";
        return Number(n);
    }
    // visible for testing
    /** Parse User Agent to determine Android version. Returns -1 if not found. */
    static jn(t) {
        const e = t.match(/Android ([\d.]+)/i), n = e ? e[1].split(".").slice(0, 2).join(".") : "-1";
        return Number(n);
    }
    /**
     * Opens the specified database, creating or upgrading it if necessary.
     */    async Hn(t) {
        return this.db || (R("SimpleDb", "Opening database:", this.name), this.db = await new Promise(((e, n) => {
            // TODO(mikelehen): Investigate browser compatibility.
            // https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Using_IndexedDB
            // suggests IE9 and older WebKit browsers handle upgrade
            // differently. They expect setVersion, as described here:
            // https://developer.mozilla.org/en-US/docs/Web/API/IDBVersionChangeRequest/setVersion
            const s = indexedDB.open(this.name, this.version);
            s.onsuccess = t => {
                const n = t.target.result;
                e(n);
            }, s.onblocked = () => {
                n(new ms(t, "Cannot upgrade IndexedDB schema while another tab is open. Close all tabs that access Firestore and reload this page to proceed."));
            }, s.onerror = e => {
                const s = e.target.error;
                "VersionError" === s.name ? n(new F($.FAILED_PRECONDITION, "A newer version of the Firestore SDK was previously used and so the persisted data is not compatible with the version of the SDK you are now using. The SDK will operate with persistence disabled. If you need persistence, please re-upgrade to a newer version of the SDK or else clear the persisted IndexedDB data for your app to start fresh.")) : n(new ms(t, s));
            }, s.onupgradeneeded = t => {
                R("SimpleDb", 'Database "' + this.name + '" requires upgrade from version:', t.oldVersion);
                const e = t.target.result;
                this.Un.Jn(e, s.transaction, t.oldVersion, this.version).next((() => {
                    R("SimpleDb", "Database upgrade to version " + this.version + " complete");
                }));
            };
        }))), this.Yn && (this.db.onversionchange = t => this.Yn(t)), this.db;
    }
    Xn(t) {
        this.Yn = t, this.db && (this.db.onversionchange = e => t(e));
    }
    async runTransaction(t, e, n, s) {
        const i = "readonly" === e;
        let r = 0;
        for (;;) {
            ++r;
            try {
                this.db = await this.Hn(t);
                const e = Es.open(this.db, t, i ? "readonly" : "readwrite", n), r = s(e).catch((t => (
                // Abort the transaction if there was an error.
                e.abort(t), ws.reject(t)))).On();
                // As noted above, errors are propagated by aborting the transaction. So
                // we swallow any error here to avoid the browser logging it as unhandled.
                return r.catch((() => {})), 
                // Wait for the transaction to complete (i.e. IndexedDb's onsuccess event to
                // fire), but still return the original transactionFnResult back to the
                // caller.
                await e.qn, r;
            } catch (t) {
                // TODO(schmidt-sebastian): We could probably be smarter about this and
                // not retry exceptions that are likely unrecoverable (such as quota
                // exceeded errors).
                // Note: We cannot use an instanceof check for FirestoreException, since the
                // exception is wrapped in a generic error by our async/await handling.
                const e = "FirebaseError" !== t.name && r < 3;
                if (R("SimpleDb", "Transaction failed with error:", t.message, "Retrying:", e), 
                this.close(), !e) return Promise.reject(t);
            }
        }
    }
    close() {
        this.db && this.db.close(), this.db = void 0;
    }
}

/**
 * A controller for iterating over a key range or index. It allows an iterate
 * callback to delete the currently-referenced object, or jump to a new key
 * within the key range or index.
 */ class Is {
    constructor(t) {
        this.Zn = t, this.ts = !1, this.es = null;
    }
    get xn() {
        return this.ts;
    }
    get ns() {
        return this.es;
    }
    set cursor(t) {
        this.Zn = t;
    }
    /**
     * This function can be called to stop iteration at any point.
     */    done() {
        this.ts = !0;
    }
    /**
     * This function can be called to skip to that next key, which could be
     * an index or a primary key.
     */    ss(t) {
        this.es = t;
    }
    /**
     * Delete the current cursor value from the object store.
     *
     * NOTE: You CANNOT do this with a keysOnly query.
     */    delete() {
        return Ps(this.Zn.delete());
    }
}

/** An error that wraps exceptions that thrown during IndexedDB execution. */ class ms extends F {
    constructor(t, e) {
        super($.UNAVAILABLE, `IndexedDB transaction '${t}' failed: ${e}`), this.name = "IndexedDbTransactionError";
    }
}

/** Verifies whether `e` is an IndexedDbTransactionError. */ function As(t) {
    // Use name equality, as instanceof checks on errors don't work with errors
    // that wrap other errors.
    return "IndexedDbTransactionError" === t.name;
}

/**
 * A wrapper around an IDBObjectStore providing an API that:
 *
 * 1) Has generic KeyType / ValueType parameters to provide strongly-typed
 * methods for acting against the object store.
 * 2) Deals with IndexedDB's onsuccess / onerror event callbacks, making every
 * method return a PersistencePromise instead.
 * 3) Provides a higher-level API to avoid needing to do excessive wrapping of
 * intermediate IndexedDB types (IDBCursorWithValue, etc.)
 */ class Rs {
    constructor(t) {
        this.store = t;
    }
    put(t, e) {
        let n;
        return void 0 !== e ? (R("SimpleDb", "PUT", this.store.name, t, e), n = this.store.put(e, t)) : (R("SimpleDb", "PUT", this.store.name, "<auto-key>", t), 
        n = this.store.put(t)), Ps(n);
    }
    /**
     * Adds a new value into an Object Store and returns the new key. Similar to
     * IndexedDb's `add()`, this method will fail on primary key collisions.
     *
     * @param value - The object to write.
     * @returns The key of the value to add.
     */    add(t) {
        R("SimpleDb", "ADD", this.store.name, t, t);
        return Ps(this.store.add(t));
    }
    /**
     * Gets the object with the specified key from the specified store, or null
     * if no object exists with the specified key.
     *
     * @key The key of the object to get.
     * @returns The object with the specified key or null if no object exists.
     */    get(t) {
        // We're doing an unsafe cast to ValueType.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Ps(this.store.get(t)).next((e => (
        // Normalize nonexistence to null.
        void 0 === e && (e = null), R("SimpleDb", "GET", this.store.name, t, e), e)));
    }
    delete(t) {
        R("SimpleDb", "DELETE", this.store.name, t);
        return Ps(this.store.delete(t));
    }
    /**
     * If we ever need more of the count variants, we can add overloads. For now,
     * all we need is to count everything in a store.
     *
     * Returns the number of rows in the store.
     */    count() {
        R("SimpleDb", "COUNT", this.store.name);
        return Ps(this.store.count());
    }
    rs(t, e) {
        const n = this.cursor(this.options(t, e)), s = [];
        return this.os(n, ((t, e) => {
            s.push(e);
        })).next((() => s));
    }
    cs(t, e) {
        R("SimpleDb", "DELETE ALL", this.store.name);
        const n = this.options(t, e);
        n.us = !1;
        const s = this.cursor(n);
        return this.os(s, ((t, e, n) => n.delete()));
    }
    hs(t, e) {
        let n;
        e ? n = t : (n = {}, e = t);
        const s = this.cursor(n);
        return this.os(s, e);
    }
    /**
     * Iterates over a store, but waits for the given callback to complete for
     * each entry before iterating the next entry. This allows the callback to do
     * asynchronous work to determine if this iteration should continue.
     *
     * The provided callback should return `true` to continue iteration, and
     * `false` otherwise.
     */    ls(t) {
        const e = this.cursor({});
        return new ws(((n, s) => {
            e.onerror = t => {
                const e = gs(t.target.error);
                s(e);
            }, e.onsuccess = e => {
                const s = e.target.result;
                s ? t(s.primaryKey, s.value).next((t => {
                    t ? s.continue() : n();
                })) : n();
            };
        }));
    }
    os(t, e) {
        const n = [];
        return new ws(((s, i) => {
            t.onerror = t => {
                i(t.target.error);
            }, t.onsuccess = t => {
                const i = t.target.result;
                if (!i) return void s();
                const r = new Is(i), o = e(i.primaryKey, i.value, r);
                if (o instanceof ws) {
                    const t = o.catch((t => (r.done(), ws.reject(t))));
                    n.push(t);
                }
                r.xn ? s() : null === r.ns ? i.continue() : i.continue(r.ns);
            };
        })).next((() => ws.Mn(n)));
    }
    options(t, e) {
        let n;
        return void 0 !== t && ("string" == typeof t ? n = t : e = t), {
            index: n,
            range: e
        };
    }
    cursor(t) {
        let e = "next";
        if (t.reverse && (e = "prev"), t.index) {
            const n = this.store.index(t.index);
            return t.us ? n.openKeyCursor(t.range, e) : n.openCursor(t.range, e);
        }
        return this.store.openCursor(t.range, e);
    }
}

/**
 * Wraps an IDBRequest in a PersistencePromise, using the onsuccess / onerror
 * handlers to resolve / reject the PersistencePromise as appropriate.
 */ function Ps(t) {
    return new ws(((e, n) => {
        t.onsuccess = t => {
            const n = t.target.result;
            e(n);
        }, t.onerror = t => {
            const e = gs(t.target.error);
            n(e);
        };
    }));
}

// Guard so we only report the error once.
let ys = !1;

function gs(t) {
    const e = Ts.Qn(n());
    if (e >= 12.2 && e < 13) {
        const e = "An internal error was encountered in the Indexed Database server";
        if (t.message.indexOf(e) >= 0) {
            // Wrap error in a more descriptive one.
            const t = new F("internal", `IOS_INDEXEDDB_BUG1: IndexedDb has thrown '${e}'. This is likely due to an unavoidable bug in iOS. See https://stackoverflow.com/q/56496296/110915 for details and a potential workaround.`);
            return ys || (ys = !0, 
            // Throw a global exception outside of this promise chain, for the user to
            // potentially catch.
            setTimeout((() => {
                throw t;
            }), 0)), t;
        }
    }
    return t;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents an operation scheduled to be run in the future on an AsyncQueue.
 *
 * It is created via DelayedOperation.createAndSchedule().
 *
 * Supports cancellation (via cancel()) and early execution (via skipDelay()).
 *
 * Note: We implement `PromiseLike` instead of `Promise`, as the `Promise` type
 * in newer versions of TypeScript defines `finally`, which is not available in
 * IE.
 */
class Vs {
    constructor(t, e, n, s, i) {
        this._s = t, this.fs = e, this.ds = n, this.op = s, this.ws = i, this.Es = new ds, 
        this.then = this.Es.promise.then.bind(this.Es.promise), 
        // It's normal for the deferred promise to be canceled (due to cancellation)
        // and so we attach a dummy catch callback to avoid
        // 'UnhandledPromiseRejectionWarning' log spam.
        this.Es.promise.catch((t => {}));
    }
    /**
     * Creates and returns a DelayedOperation that has been scheduled to be
     * executed on the provided asyncQueue after the provided delayMs.
     *
     * @param asyncQueue - The queue to schedule the operation on.
     * @param id - A Timer ID identifying the type of operation this is.
     * @param delayMs - The delay (ms) before the operation should be scheduled.
     * @param op - The operation to run.
     * @param removalCallback - A callback to be called synchronously once the
     *   operation is executed or canceled, notifying the AsyncQueue to remove it
     *   from its delayedOperations list.
     *   PORTING NOTE: This exists to prevent making removeDelayedOperation() and
     *   the DelayedOperation class public.
     */    static Ts(t, e, n, s, i) {
        const r = Date.now() + n, o = new Vs(t, e, r, s, i);
        return o.start(n), o;
    }
    /**
     * Starts the timer. This is called immediately after construction by
     * createAndSchedule().
     */    start(t) {
        this.Is = setTimeout((() => this.As()), t);
    }
    /**
     * Queues the operation to run immediately (if it hasn't already been run or
     * canceled).
     */    Rs() {
        return this.As();
    }
    /**
     * Cancels the operation if it hasn't already been executed or canceled. The
     * promise will be rejected.
     *
     * As long as the operation has not yet been run, calling cancel() provides a
     * guarantee that the operation will not be run.
     */    cancel(t) {
        null !== this.Is && (this.clearTimeout(), this.Es.reject(new F($.CANCELLED, "Operation cancelled" + (t ? ": " + t : ""))));
    }
    As() {
        this._s.Ps((() => null !== this.Is ? (this.clearTimeout(), this.op().then((t => this.Es.resolve(t)))) : Promise.resolve()));
    }
    clearTimeout() {
        null !== this.Is && (this.ws(this), clearTimeout(this.Is), this.Is = null);
    }
}

/**
 * Returns a FirestoreError that can be surfaced to the user if the provided
 * error is an IndexedDbTransactionError. Re-throws the error otherwise.
 */ function ps(t, e) {
    if (P("AsyncQueue", `${e}: ${t}`), As(t)) return new F($.UNAVAILABLE, `${e}: ${t}`);
    throw t;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const bs = "The current tab is not in the required state to perform this operation. It might be necessary to refresh the browser tab.";

/**
 * A base class representing a persistence transaction, encapsulating both the
 * transaction's sequence numbers as well as a list of onCommitted listeners.
 *
 * When you call Persistence.runTransaction(), it will create a transaction and
 * pass it to your callback. You then pass it to any method that operates
 * on persistence.
 */ class vs {
    constructor() {
        this.ys = [];
    }
    gs(t) {
        this.ys.push(t);
    }
    Vs() {
        this.ys.forEach((t => t()));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Encodes a resource path into a IndexedDb-compatible string form.
 */
function Ss(t) {
    let e = "";
    for (let n = 0; n < t.length; n++) e.length > 0 && (e = Cs(e)), e = Ds(t.get(n), e);
    return Cs(e);
}

/** Encodes a single segment of a resource path into the given result */ function Ds(t, e) {
    let n = e;
    const s = t.length;
    for (let e = 0; e < s; e++) {
        const s = t.charAt(e);
        switch (s) {
          case "\0":
            n += "";
            break;

          case "":
            n += "";
            break;

          default:
            n += s;
        }
    }
    return n;
}

/** Encodes a path separator into the given result */ function Cs(t) {
    return t + "";
}

/**
 * Decodes the given IndexedDb-compatible string form of a resource path into
 * a ResourcePath instance. Note that this method is not suitable for use with
 * decoding resource names from the server; those are One Platform format
 * strings.
 */ function xs(t) {
    // Event the empty path must encode as a path of at least length 2. A path
    // with exactly 2 must be the empty path.
    const e = t.length;
    if (p(e >= 2), 2 === e) return p("" === t.charAt(0) && "" === t.charAt(1)), G.ct();
    // Escape characters cannot exist past the second-to-last position in the
    // source value.
        const n = e - 2, s = [];
    let i = "";
    for (let r = 0; r < e; ) {
        // The last two characters of a valid encoded path must be a separator, so
        // there must be an end to this segment.
        const e = t.indexOf("", r);
        (e < 0 || e > n) && V();
        switch (t.charAt(e + 1)) {
          case "":
            const n = t.substring(r, e);
            let o;
            0 === i.length ? 
            // Avoid copying for the common case of a segment that excludes \0
            // and \001
            o = n : (i += n, o = i, i = ""), s.push(o);
            break;

          case "":
            i += t.substring(r, e), i += "\0";
            break;

          case "":
            // The escape character can be used in the output to encode itself.
            i += t.substring(r, e + 1);
            break;

          default:
            V();
        }
        r = e + 2;
    }
    return new G(s);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Schema Version for the Web client:
 * 1.  Initial version including Mutation Queue, Query Cache, and Remote
 *     Document Cache
 * 2.  Used to ensure a targetGlobal object exists and add targetCount to it. No
 *     longer required because migration 3 unconditionally clears it.
 * 3.  Dropped and re-created Query Cache to deal with cache corruption related
 *     to limbo resolution. Addresses
 *     https://github.com/firebase/firebase-ios-sdk/issues/1548
 * 4.  Multi-Tab Support.
 * 5.  Removal of held write acks.
 * 6.  Create document global for tracking document cache size.
 * 7.  Ensure every cached document has a sentinel row with a sequence number.
 * 8.  Add collection-parent index for Collection Group queries.
 * 9.  Change RemoteDocumentChanges store to be keyed by readTime rather than
 *     an auto-incrementing ID. This is required for Index-Free queries.
 * 10. Rewrite the canonical IDs to the explicit Protobuf-based format.
 * 11. Add bundles and named_queries for bundle support.
 */
/**
 * Wrapper class to store timestamps (seconds and nanos) in IndexedDb objects.
 */
class Ns {
    constructor(t, e) {
        this.seconds = t, this.nanoseconds = e;
    }
}

/**
 * A singleton object to be stored in the 'owner' store in IndexedDb.
 *
 * A given database can have a single primary tab assigned at a given time. That
 * tab must validate that it is still holding the primary lease before every
 * operation that requires locked access. The primary tab should regularly
 * write an updated timestamp to this lease to prevent other tabs from
 * "stealing" the primary lease
 */ class $s {
    constructor(t, 
    /** Whether to allow shared access from multiple tabs. */
    e, n) {
        this.ownerId = t, this.allowTabSynchronization = e, this.leaseTimestampMs = n;
    }
}

/**
 * Name of the IndexedDb object store.
 *
 * Note that the name 'owner' is chosen to ensure backwards compatibility with
 * older clients that only supported single locked access to the persistence
 * layer.
 */ $s.store = "owner", 
/**
 * The key string used for the single object that exists in the
 * DbPrimaryClient store.
 */
$s.key = "owner";

/**
 * An object to be stored in the 'mutationQueues' store in IndexedDb.
 *
 * Each user gets a single queue of MutationBatches to apply to the server.
 * DbMutationQueue tracks the metadata about the queue.
 */
class Fs {
    constructor(
    /**
     * The normalized user ID to which this queue belongs.
     */
    t, 
    /**
     * An identifier for the highest numbered batch that has been acknowledged
     * by the server. All MutationBatches in this queue with batchIds less
     * than or equal to this value are considered to have been acknowledged by
     * the server.
     *
     * NOTE: this is deprecated and no longer used by the code.
     */
    e, 
    /**
     * A stream token that was previously sent by the server.
     *
     * See StreamingWriteRequest in datastore.proto for more details about
     * usage.
     *
     * After sending this token, earlier tokens may not be used anymore so
     * only a single stream token is retained.
     *
     * NOTE: this is deprecated and no longer used by the code.
     */
    n) {
        this.userId = t, this.lastAcknowledgedBatchId = e, this.lastStreamToken = n;
    }
}

/** Name of the IndexedDb object store.  */ Fs.store = "mutationQueues", 
/** Keys are automatically assigned via the userId property. */
Fs.keyPath = "userId";

/**
 * An object to be stored in the 'mutations' store in IndexedDb.
 *
 * Represents a batch of user-level mutations intended to be sent to the server
 * in a single write. Each user-level batch gets a separate DbMutationBatch
 * with a new batchId.
 */
class Os {
    constructor(
    /**
     * The normalized user ID to which this batch belongs.
     */
    t, 
    /**
     * An identifier for this batch, allocated using an auto-generated key.
     */
    e, 
    /**
     * The local write time of the batch, stored as milliseconds since the
     * epoch.
     */
    n, 
    /**
     * A list of "mutations" that represent a partial base state from when this
     * write batch was initially created. During local application of the write
     * batch, these baseMutations are applied prior to the real writes in order
     * to override certain document fields from the remote document cache. This
     * is necessary in the case of non-idempotent writes (e.g. `increment()`
     * transforms) to make sure that the local view of the modified documents
     * doesn't flicker if the remote document cache receives the result of the
     * non-idempotent write before the write is removed from the queue.
     *
     * These mutations are never sent to the backend.
     */
    s, 
    /**
     * A list of mutations to apply. All mutations will be applied atomically.
     *
     * Mutations are serialized via toMutation().
     */
    i) {
        this.userId = t, this.batchId = e, this.localWriteTimeMs = n, this.baseMutations = s, 
        this.mutations = i;
    }
}

/** Name of the IndexedDb object store.  */ Os.store = "mutations", 
/** Keys are automatically assigned via the userId, batchId properties. */
Os.keyPath = "batchId", 
/** The index name for lookup of mutations by user. */
Os.userMutationsIndex = "userMutationsIndex", 
/** The user mutations index is keyed by [userId, batchId] pairs. */
Os.userMutationsKeyPath = [ "userId", "batchId" ];

/**
 * An object to be stored in the 'documentMutations' store in IndexedDb.
 *
 * A manually maintained index of all the mutation batches that affect a given
 * document key. The rows in this table are references based on the contents of
 * DbMutationBatch.mutations.
 */
class ks {
    constructor() {}
    /**
     * Creates a [userId] key for use in the DbDocumentMutations index to iterate
     * over all of a user's document mutations.
     */    static prefixForUser(t) {
        return [ t ];
    }
    /**
     * Creates a [userId, encodedPath] key for use in the DbDocumentMutations
     * index to iterate over all at document mutations for a given path or lower.
     */    static prefixForPath(t, e) {
        return [ t, Ss(e) ];
    }
    /**
     * Creates a full index key of [userId, encodedPath, batchId] for inserting
     * and deleting into the DbDocumentMutations index.
     */    static key(t, e, n) {
        return [ t, Ss(e), n ];
    }
}

ks.store = "documentMutations", 
/**
 * Because we store all the useful information for this store in the key,
 * there is no useful information to store as the value. The raw (unencoded)
 * path cannot be stored because IndexedDb doesn't store prototype
 * information.
 */
ks.PLACEHOLDER = new ks;

/**
 * Represents the known absence of a document at a particular version.
 * Stored in IndexedDb as part of a DbRemoteDocument object.
 */
class Ms {
    constructor(t, e) {
        this.path = t, this.readTime = e;
    }
}

/**
 * Represents a document that is known to exist but whose data is unknown.
 * Stored in IndexedDb as part of a DbRemoteDocument object.
 */ class Ls {
    constructor(t, e) {
        this.path = t, this.version = e;
    }
}

/**
 * An object to be stored in the 'remoteDocuments' store in IndexedDb.
 * It represents either:
 *
 * - A complete document.
 * - A "no document" representing a document that is known not to exist (at
 * some version).
 * - An "unknown document" representing a document that is known to exist (at
 * some version) but whose contents are unknown.
 *
 * Note: This is the persisted equivalent of a MaybeDocument and could perhaps
 * be made more general if necessary.
 */ class Bs {
    // TODO: We are currently storing full document keys almost three times
    // (once as part of the primary key, once - partly - as `parentPath` and once
    // inside the encoded documents). During our next migration, we should
    // rewrite the primary key as parentPath + document ID which would allow us
    // to drop one value.
    constructor(
    /**
     * Set to an instance of DbUnknownDocument if the data for a document is
     * not known, but it is known that a document exists at the specified
     * version (e.g. it had a successful update applied to it)
     */
    t, 
    /**
     * Set to an instance of a DbNoDocument if it is known that no document
     * exists.
     */
    e, 
    /**
     * Set to an instance of a Document if there's a cached version of the
     * document.
     */
    n, 
    /**
     * Documents that were written to the remote document store based on
     * a write acknowledgment are marked with `hasCommittedMutations`. These
     * documents are potentially inconsistent with the backend's copy and use
     * the write's commit version as their document version.
     */
    s, 
    /**
     * When the document was read from the backend. Undefined for data written
     * prior to schema version 9.
     */
    i, 
    /**
     * The path of the collection this document is part of. Undefined for data
     * written prior to schema version 9.
     */
    r) {
        this.unknownDocument = t, this.noDocument = e, this.document = n, this.hasCommittedMutations = s, 
        this.readTime = i, this.parentPath = r;
    }
}

Bs.store = "remoteDocuments", 
/**
 * An index that provides access to all entries sorted by read time (which
 * corresponds to the last modification time of each row).
 *
 * This index is used to provide a changelog for Multi-Tab.
 */
Bs.readTimeIndex = "readTimeIndex", Bs.readTimeIndexPath = "readTime", 
/**
 * An index that provides access to documents in a collection sorted by read
 * time.
 *
 * This index is used to allow the RemoteDocumentCache to fetch newly changed
 * documents in a collection.
 */
Bs.collectionReadTimeIndex = "collectionReadTimeIndex", Bs.collectionReadTimeIndexPath = [ "parentPath", "readTime" ];

/**
 * Contains a single entry that has metadata about the remote document cache.
 */
class qs {
    /**
     * @param byteSize - Approximately the total size in bytes of all the
     * documents in the document cache.
     */
    constructor(t) {
        this.byteSize = t;
    }
}

qs.store = "remoteDocumentGlobal", qs.key = "remoteDocumentGlobalKey";

/**
 * An object to be stored in the 'targets' store in IndexedDb.
 *
 * This is based on and should be kept in sync with the proto used in the iOS
 * client.
 *
 * Each query the client listens to against the server is tracked on disk so
 * that the query can be efficiently resumed on restart.
 */
class Us {
    constructor(
    /**
     * An auto-generated sequential numeric identifier for the query.
     *
     * Queries are stored using their canonicalId as the key, but these
     * canonicalIds can be quite long so we additionally assign a unique
     * queryId which can be used by referenced data structures (e.g.
     * indexes) to minimize the on-disk cost.
     */
    t, 
    /**
     * The canonical string representing this query. This is not unique.
     */
    e, 
    /**
     * The last readTime received from the Watch Service for this query.
     *
     * This is the same value as TargetChange.read_time in the protos.
     */
    n, 
    /**
     * An opaque, server-assigned token that allows watching a query to be
     * resumed after disconnecting without retransmitting all the data
     * that matches the query. The resume token essentially identifies a
     * point in time from which the server should resume sending results.
     *
     * This is related to the snapshotVersion in that the resumeToken
     * effectively also encodes that value, but the resumeToken is opaque
     * and sometimes encodes additional information.
     *
     * A consequence of this is that the resumeToken should be used when
     * asking the server to reason about where this client is in the watch
     * stream, but the client should use the snapshotVersion for its own
     * purposes.
     *
     * This is the same value as TargetChange.resume_token in the protos.
     */
    s, 
    /**
     * A sequence number representing the last time this query was
     * listened to, used for garbage collection purposes.
     *
     * Conventionally this would be a timestamp value, but device-local
     * clocks are unreliable and they must be able to create new listens
     * even while disconnected. Instead this should be a monotonically
     * increasing number that's incremented on each listen call.
     *
     * This is different from the queryId since the queryId is an
     * immutable identifier assigned to the Query on first use while
     * lastListenSequenceNumber is updated every time the query is
     * listened to.
     */
    i, 
    /**
     * Denotes the maximum snapshot version at which the associated query view
     * contained no limbo documents.  Undefined for data written prior to
     * schema version 9.
     */
    r, 
    /**
     * The query for this target.
     *
     * Because canonical ids are not unique we must store the actual query. We
     * use the proto to have an object we can persist without having to
     * duplicate translation logic to and from a `Query` object.
     */
    o) {
        this.targetId = t, this.canonicalId = e, this.readTime = n, this.resumeToken = s, 
        this.lastListenSequenceNumber = i, this.lastLimboFreeSnapshotVersion = r, this.query = o;
    }
}

Us.store = "targets", 
/** Keys are automatically assigned via the targetId property. */
Us.keyPath = "targetId", 
/** The name of the queryTargets index. */
Us.queryTargetsIndexName = "queryTargetsIndex", 
/**
 * The index of all canonicalIds to the targets that they match. This is not
 * a unique mapping because canonicalId does not promise a unique name for all
 * possible queries, so we append the targetId to make the mapping unique.
 */
Us.queryTargetsKeyPath = [ "canonicalId", "targetId" ];

/**
 * An object representing an association between a target and a document, or a
 * sentinel row marking the last sequence number at which a document was used.
 * Each document cached must have a corresponding sentinel row before lru
 * garbage collection is enabled.
 *
 * The target associations and sentinel rows are co-located so that orphaned
 * documents and their sequence numbers can be identified efficiently via a scan
 * of this store.
 */
class Qs {
    constructor(
    /**
     * The targetId identifying a target or 0 for a sentinel row.
     */
    t, 
    /**
     * The path to the document, as encoded in the key.
     */
    e, 
    /**
     * If this is a sentinel row, this should be the sequence number of the last
     * time the document specified by `path` was used. Otherwise, it should be
     * `undefined`.
     */
    n) {
        this.targetId = t, this.path = e, this.sequenceNumber = n;
    }
}

/** Name of the IndexedDb object store.  */ Qs.store = "targetDocuments", 
/** Keys are automatically assigned via the targetId, path properties. */
Qs.keyPath = [ "targetId", "path" ], 
/** The index name for the reverse index. */
Qs.documentTargetsIndex = "documentTargetsIndex", 
/** We also need to create the reverse index for these properties. */
Qs.documentTargetsKeyPath = [ "path", "targetId" ];

/**
 * A record of global state tracked across all Targets, tracked separately
 * to avoid the need for extra indexes.
 *
 * This should be kept in-sync with the proto used in the iOS client.
 */
class Ks {
    constructor(
    /**
     * The highest numbered target id across all targets.
     *
     * See DbTarget.targetId.
     */
    t, 
    /**
     * The highest numbered lastListenSequenceNumber across all targets.
     *
     * See DbTarget.lastListenSequenceNumber.
     */
    e, 
    /**
     * A global snapshot version representing the last consistent snapshot we
     * received from the backend. This is monotonically increasing and any
     * snapshots received from the backend prior to this version (e.g. for
     * targets resumed with a resumeToken) should be suppressed (buffered)
     * until the backend has caught up to this snapshot version again. This
     * prevents our cache from ever going backwards in time.
     */
    n, 
    /**
     * The number of targets persisted.
     */
    s) {
        this.highestTargetId = t, this.highestListenSequenceNumber = e, this.lastRemoteSnapshotVersion = n, 
        this.targetCount = s;
    }
}

/**
 * The key string used for the single object that exists in the
 * DbTargetGlobal store.
 */ Ks.key = "targetGlobalKey", Ks.store = "targetGlobal";

/**
 * An object representing an association between a Collection id (e.g. 'messages')
 * to a parent path (e.g. '/chats/123') that contains it as a (sub)collection.
 * This is used to efficiently find all collections to query when performing
 * a Collection Group query.
 */
class Ws {
    constructor(
    /**
     * The collectionId (e.g. 'messages')
     */
    t, 
    /**
     * The path to the parent (either a document location or an empty path for
     * a root-level collection).
     */
    e) {
        this.collectionId = t, this.parent = e;
    }
}

/** Name of the IndexedDb object store. */ Ws.store = "collectionParents", 
/** Keys are automatically assigned via the collectionId, parent properties. */
Ws.keyPath = [ "collectionId", "parent" ];

/**
 * A record of the metadata state of each client.
 *
 * PORTING NOTE: This is used to synchronize multi-tab state and does not need
 * to be ported to iOS or Android.
 */
class js {
    constructor(
    // Note: Previous schema versions included a field
    // "lastProcessedDocumentChangeId". Don't use anymore.
    /** The auto-generated client id assigned at client startup. */
    t, 
    /** The last time this state was updated. */
    e, 
    /** Whether the client's network connection is enabled. */
    n, 
    /** Whether this client is running in a foreground tab. */
    s) {
        this.clientId = t, this.updateTimeMs = e, this.networkEnabled = n, this.inForeground = s;
    }
}

/** Name of the IndexedDb object store. */ js.store = "clientMetadata", 
/** Keys are automatically assigned via the clientId properties. */
js.keyPath = "clientId";

/**
 * A object representing a bundle loaded by the SDK.
 */
class Gs {
    constructor(
    /** The ID of the loaded bundle. */
    t, 
    /** The create time of the loaded bundle. */
    e, 
    /** The schema version of the loaded bundle. */
    n) {
        this.bundleId = t, this.createTime = e, this.version = n;
    }
}

/** Name of the IndexedDb object store. */ Gs.store = "bundles", Gs.keyPath = "bundleId";

/**
 * A object representing a named query loaded by the SDK via a bundle.
 */
class zs {
    constructor(
    /** The name of the query. */
    t, 
    /** The read time of the results saved in the bundle from the named query. */
    e, 
    /** The query saved in the bundle. */
    n) {
        this.name = t, this.readTime = e, this.bundledQuery = n;
    }
}

/** Name of the IndexedDb object store. */ zs.store = "namedQueries", zs.keyPath = "name";

// Visible for testing
const Hs = [ ...[ ...[ ...[ ...[ Fs.store, Os.store, ks.store, Bs.store, Us.store, $s.store, Ks.store, Qs.store ], js.store ], qs.store ], Ws.store ], Gs.store, zs.store ];

// V2 is no longer usable (see comment at top of file)
// Visible for testing
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Js extends vs {
    constructor(t, e) {
        super(), this.ps = t, this.bs = e;
    }
}

function Ys(t, e) {
    const n = b(t);
    return Ts.zn(n.ps, e);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A batch of mutations that will be sent as one unit to the backend.
 */ class Xs {
    /**
     * @param batchId - The unique ID of this mutation batch.
     * @param localWriteTime - The original write time of this mutation.
     * @param baseMutations - Mutations that are used to populate the base
     * values when this mutation is applied locally. This can be used to locally
     * overwrite values that are persisted in the remote document cache. Base
     * mutations are never sent to the backend.
     * @param mutations - The user-provided mutations in this mutation batch.
     * User-provided mutations are applied both locally and remotely on the
     * backend.
     */
    constructor(t, e, n, s) {
        this.batchId = t, this.vs = e, this.baseMutations = n, this.mutations = s;
    }
    /**
     * Applies all the mutations in this MutationBatch to the specified document
     * to create a new remote document
     *
     * @param docKey - The key of the document to apply mutations to.
     * @param maybeDoc - The document to apply mutations to.
     * @param batchResult - The result of applying the MutationBatch to the
     * backend.
     */    Ss(t, e, n) {
        const s = n.Ds;
        for (let n = 0; n < this.mutations.length; n++) {
            const i = this.mutations[n];
            if (i.key.isEqual(t)) {
                e = Me(i, e, s[n]);
            }
        }
        return e;
    }
    /**
     * Computes the local view of a document given all the mutations in this
     * batch.
     *
     * @param docKey - The key of the document to apply mutations to.
     * @param maybeDoc - The document to apply mutations to.
     */    Cs(t, e) {
        // First, apply the base state. This allows us to apply non-idempotent
        // transform against a consistent set of values.
        for (const n of this.baseMutations) n.key.isEqual(t) && (e = Le(n, e, this.vs));
        // Second, apply all user-provided mutations.
                for (const n of this.mutations) n.key.isEqual(t) && (e = Le(n, e, this.vs));
        return e;
    }
    /**
     * Computes the local view for all provided documents given the mutations in
     * this batch.
     */    xs(t) {
        // TODO(mrschmidt): This implementation is O(n^2). If we apply the mutations
        // directly (as done in `applyToLocalView()`), we can reduce the complexity
        // to O(n).
        let e = t;
        return this.mutations.forEach((n => {
            const s = this.Cs(n.key, t.get(n.key));
            s && (e = e.zt(n.key, s));
        })), e;
    }
    keys() {
        return this.mutations.reduce(((t, e) => t.add(e.key)), En());
    }
    isEqual(t) {
        return this.batchId === t.batchId && C(this.mutations, t.mutations, ((t, e) => qe(t, e))) && C(this.baseMutations, t.baseMutations, ((t, e) => qe(t, e)));
    }
}

/** The result of applying a mutation batch to the backend. */ class Zs {
    constructor(t, e, n, 
    /**
     * A pre-computed mapping from each mutated document to the resulting
     * version.
     */
    s) {
        this.batch = t, this.Ns = e, this.Ds = n, this.$s = s;
    }
    /**
     * Creates a new MutationBatchResult for the given batch and results. There
     * must be one result for each mutation in the batch. This static factory
     * caches a document=&gt;version mapping (docVersions).
     */    static from(t, e, n) {
        p(t.mutations.length === n.length);
        let s = dn();
        const i = t.mutations;
        for (let t = 0; t < i.length; t++) s = s.zt(i[t].key, n[t].version);
        return new Zs(t, e, n, s);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Serializer for values stored in the LocalStore. */ class ti {
    constructor(t) {
        this.Fs = t;
    }
}

/** Decodes a remote document from storage locally to a Document. */ function ei(t, e) {
    if (e.document) return Gn(t.Fs, e.document, !!e.hasCommittedMutations);
    if (e.noDocument) {
        const t = J.Et(e.noDocument.path), n = oi(e.noDocument.readTime);
        return new St(t, n, {
            hasCommittedMutations: !!e.hasCommittedMutations
        });
    }
    if (e.unknownDocument) {
        const t = J.Et(e.unknownDocument.path), n = oi(e.unknownDocument.version);
        return new Dt(t, n);
    }
    return V();
}

/** Encodes a document for storage locally. */ function ni(t, e, n) {
    const s = si(n), i = e.key.path.X().it();
    if (e instanceof vt) {
        const n = function(t, e) {
            return {
                name: Bn(t, e.key),
                fields: e.bt().mapValue.fields,
                updateTime: $n(t, e.version.j())
            };
        }(t.Fs, e), r = e.hasCommittedMutations;
        return new Bs(
        /* unknownDocument= */ null, 
        /* noDocument= */ null, n, r, s, i);
    }
    if (e instanceof St) {
        const t = e.key.path.it(), n = ri(e.version), r = e.hasCommittedMutations;
        return new Bs(
        /* unknownDocument= */ null, new Ms(t, n), 
        /* document= */ null, r, s, i);
    }
    if (e instanceof Dt) {
        const t = e.key.path.it(), n = ri(e.version);
        return new Bs(new Ls(t, n), 
        /* noDocument= */ null, 
        /* document= */ null, 
        /* hasCommittedMutations= */ !0, s, i);
    }
    return V();
}

function si(t) {
    const e = t.j();
    return [ e.seconds, e.nanoseconds ];
}

function ii(t) {
    const e = new K(t[0], t[1]);
    return W.K(e);
}

function ri(t) {
    const e = t.j();
    return new Ns(e.seconds, e.nanoseconds);
}

function oi(t) {
    const e = new K(t.seconds, t.nanoseconds);
    return W.K(e);
}

/** Encodes a batch of mutations into a DbMutationBatch for local storage. */
/** Decodes a DbMutationBatch into a MutationBatch */
function ci(t, e) {
    const n = (e.baseMutations || []).map((e => Yn(t.Fs, e)));
    // Squash old transform mutations into existing patch or set mutations.
    // The replacement of representing `transforms` with `update_transforms`
    // on the SDK means that old `transform` mutations stored in IndexedDB need
    // to be updated to `update_transforms`.
    // TODO(b/174608374): Remove this code once we perform a schema migration.
        for (let t = 0; t < e.mutations.length - 1; ++t) {
        const n = e.mutations[t];
        if (t + 1 < e.mutations.length && void 0 !== e.mutations[t + 1].transform) {
            const s = e.mutations[t + 1];
            n.updateTransforms = s.transform.fieldTransforms, e.mutations.splice(t + 1, 1), 
            ++t;
        }
    }
    const s = e.mutations.map((e => Yn(t.Fs, e))), i = K.fromMillis(e.localWriteTimeMs);
    return new Xs(e.batchId, i, n, s);
}

/** Decodes a DbTarget into TargetData */ function ai(t) {
    const e = oi(t.readTime), n = void 0 !== t.lastLimboFreeSnapshotVersion ? oi(t.lastLimboFreeSnapshotVersion) : W.min();
    let s;
    var i;
    return void 0 !== t.query.documents ? (p(1 === (i = t.query).documents.length), 
    s = ae(ee(Qn(i.documents[0])))) : s = function(t) {
        return ae(es(t));
    }(t.query), new Ee(s, t.targetId, 0 /* Listen */ , t.lastListenSequenceNumber, e, n, N.fromBase64String(t.resumeToken));
}

/** Encodes TargetData into a DbTarget for storage locally. */ function ui(t, e) {
    const n = ri(e.Ot), s = ri(e.lastLimboFreeSnapshotVersion);
    let i;
    i = Ot(e.target) ? Zn(t.Fs, e.target) : ts(t.Fs, e.target);
    // We can't store the resumeToken as a ByteString in IndexedDb, so we
    // convert it to a base64 string for storage.
        const r = e.resumeToken.toBase64();
    // lastListenSequenceNumber is always 0 until we do real GC.
        return new Us(e.targetId, Nt(e.target), n, r, e.sequenceNumber, s, i);
}

/**
 * A helper function for figuring out what kind of query has been stored.
 */
/**
 * Encodes a `BundledQuery` from bundle proto to a Query object.
 *
 * This reconstructs the original query used to build the bundle being loaded,
 * including features exists only in SDKs (for example: limit-to-last).
 */
function hi(t) {
    const e = es({
        parent: t.parent,
        structuredQuery: t.structuredQuery
    });
    return "LAST" === t.limitType ? ue(e, e.limit, "L" /* Last */) : e;
}

/** Encodes a NamedQuery proto object to a NamedQuery model object. */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class li {
    Os(t, e) {
        return _i(t).get(e).next((t => {
            if (t) return {
                id: (e = t).bundleId,
                createTime: oi(e.createTime),
                version: e.version
            };
            /** Encodes a DbBundle to a BundleMetadata object. */
            var e;
            /** Encodes a BundleMetadata to a DbBundle. */        }));
    }
    ks(t, e) {
        return _i(t).put({
            bundleId: (n = e).id,
            createTime: ri(kn(n.createTime)),
            version: n.version
        });
        var n;
        /** Encodes a DbNamedQuery to a NamedQuery. */    }
    Ms(t, e) {
        return fi(t).get(e).next((t => {
            if (t) return {
                name: (e = t).name,
                query: hi(e.bundledQuery),
                readTime: oi(e.readTime)
            };
            var e;
            /** Encodes a NamedQuery from a bundle proto to a DbNamedQuery. */        }));
    }
    Ls(t, e) {
        return fi(t).put(function(t) {
            return {
                name: t.name,
                readTime: ri(kn(t.readTime)),
                bundledQuery: t.bundledQuery
            };
        }(e));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the bundles object store.
 */ function _i(t) {
    return Ys(t, Gs.store);
}

/**
 * Helper to get a typed SimpleDbStore for the namedQueries object store.
 */ function fi(t) {
    return Ys(t, zs.store);
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An in-memory implementation of IndexManager.
 */ class di {
    constructor() {
        this.Bs = new wi;
    }
    qs(t, e) {
        return this.Bs.add(e), ws.resolve();
    }
    Us(t, e) {
        return ws.resolve(this.Bs.getEntries(e));
    }
}

/**
 * Internal implementation of the collection-parent index exposed by MemoryIndexManager.
 * Also used for in-memory caching by IndexedDbIndexManager and initial index population
 * in indexeddb_schema.ts
 */ class wi {
    constructor() {
        this.index = {};
    }
    // Returns false if the entry already existed.
    add(t) {
        const e = t.tt(), n = t.X(), s = this.index[e] || new on(G.H), i = !s.has(n);
        return this.index[e] = s.add(n), i;
    }
    has(t) {
        const e = t.tt(), n = t.X(), s = this.index[e];
        return s && s.has(n);
    }
    getEntries(t) {
        return (this.index[t] || new on(G.H)).it();
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A persisted implementation of IndexManager.
 */ class Ei {
    constructor() {
        /**
         * An in-memory copy of the index entries we've already written since the SDK
         * launched. Used to avoid re-writing the same entry repeatedly.
         *
         * This is *NOT* a complete cache of what's in persistence and so can never be used to
         * satisfy reads.
         */
        this.Qs = new wi;
    }
    /**
     * Adds a new entry to the collection parent index.
     *
     * Repeated calls for the same collectionPath should be avoided within a
     * transaction as IndexedDbIndexManager only caches writes once a transaction
     * has been committed.
     */    qs(t, e) {
        if (!this.Qs.has(e)) {
            const n = e.tt(), s = e.X();
            t.gs((() => {
                // Add the collection to the in memory cache only if the transaction was
                // successfully committed.
                this.Qs.add(e);
            }));
            const i = {
                collectionId: n,
                parent: Ss(s)
            };
            return Ti(t).put(i);
        }
        return ws.resolve();
    }
    Us(t, e) {
        const n = [], s = IDBKeyRange.bound([ e, "" ], [ x(e), "" ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        return Ti(t).rs(s).next((t => {
            for (const s of t) {
                // This collectionId guard shouldn't be necessary (and isn't as long
                // as we're running in a real browser), but there's a bug in
                // indexeddbshim that breaks our range in our tests running in node:
                // https://github.com/axemclion/IndexedDBShim/issues/334
                if (s.collectionId !== e) break;
                n.push(xs(s.parent));
            }
            return n;
        }));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the collectionParents
 * document store.
 */ function Ti(t) {
    return Ys(t, Ws.store);
}

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Ii = {
    Ks: !1,
    Ws: 0,
    js: 0,
    Gs: 0
};

class mi {
    constructor(
    // When we attempt to collect, we will only do so if the cache size is greater than this
    // threshold. Passing `COLLECTION_DISABLED` here will cause collection to always be skipped.
    t, 
    // The percentage of sequence numbers that we will attempt to collect
    e, 
    // A cap on the total number of sequence numbers that will be collected. This prevents
    // us from collecting a huge number of sequence numbers if the cache has grown very large.
    n) {
        this.zs = t, this.Hs = e, this.Js = n;
    }
    static Ys(t) {
        return new mi(t, mi.Xs, mi.Zs);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Verifies the error thrown by a LocalStore operation. If a LocalStore
 * operation fails because the primary lease has been taken by another client,
 * we ignore the error (the persistence layer will immediately call
 * `applyPrimaryLease` to propagate the primary state change). All other errors
 * are re-thrown.
 *
 * @param err - An error returned by a LocalStore operation.
 * @returns A Promise that resolves after we recovered, or the original error.
 */
async function Ai(t) {
    if (t.code !== $.FAILED_PRECONDITION || t.message !== bs) throw t;
    R("LocalStore", "Unexpectedly lost primary lease");
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A map implementation that uses objects as keys. Objects must have an
 * associated equals function and must be immutable. Entries in the map are
 * stored together with the key being produced from the mapKeyFn. This map
 * automatically handles collisions of keys.
 */ mi.Xs = 10, mi.Zs = 1e3, mi.ti = new mi(41943040, mi.Xs, mi.Zs), mi.ei = new mi(-1, 0, 0);

class Ri {
    constructor(t, e) {
        this.ni = t, this.si = e, 
        /**
         * The inner map for a key/value pair. Due to the possibility of collisions we
         * keep a list of entries that we do a linear search through to find an actual
         * match. Note that collisions should be rare, so we still expect near
         * constant time lookups in practice.
         */
        this.ii = {};
    }
    /** Get a value for this key, or undefined if it does not exist. */    get(t) {
        const e = this.ni(t), n = this.ii[e];
        if (void 0 !== n) for (const [e, s] of n) if (this.si(e, t)) return s;
    }
    has(t) {
        return void 0 !== this.get(t);
    }
    /** Put this key and value in the map. */    set(t, e) {
        const n = this.ni(t), s = this.ii[n];
        if (void 0 !== s) {
            for (let n = 0; n < s.length; n++) if (this.si(s[n][0], t)) return void (s[n] = [ t, e ]);
            s.push([ t, e ]);
        } else this.ii[n] = [ [ t, e ] ];
    }
    /**
     * Remove this key from the map. Returns a boolean if anything was deleted.
     */    delete(t) {
        const e = this.ni(t), n = this.ii[e];
        if (void 0 === n) return !1;
        for (let s = 0; s < n.length; s++) if (this.si(n[s][0], t)) return 1 === n.length ? delete this.ii[e] : n.splice(s, 1), 
        !0;
        return !1;
    }
    forEach(t) {
        X(this.ii, ((e, n) => {
            for (const [e, s] of n) t(e, s);
        }));
    }
    et() {
        return Z(this.ii);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An in-memory buffer of entries to be written to a RemoteDocumentCache.
 * It can be used to batch up a set of changes to be written to the cache, but
 * additionally supports reading entries back with the `getEntry()` method,
 * falling back to the underlying RemoteDocumentCache if no entry is
 * buffered.
 *
 * Entries added to the cache *must* be read first. This is to facilitate
 * calculating the size delta of the pending changes.
 *
 * PORTING NOTE: This class was implemented then removed from other platforms.
 * If byte-counting ends up being needed on the other platforms, consider
 * porting this class as part of that implementation work.
 */ class Pi {
    constructor() {
        // A mapping of document key to the new cache entry that should be written (or null if any
        // existing cache entry should be removed).
        this.ri = new Ri((t => t.toString()), ((t, e) => t.isEqual(e))), this.oi = !1;
    }
    ci(t) {
        const e = this.ri.get(t);
        return e ? e.readTime : W.min();
    }
    /**
     * Buffers a `RemoteDocumentCache.addEntry()` call.
     *
     * You can only modify documents that have already been retrieved via
     * `getEntry()/getEntries()` (enforced via IndexedDbs `apply()`).
     */    ai(t, e) {
        this.ui(), this.ri.set(t.key, {
            hi: t,
            readTime: e
        });
    }
    /**
     * Buffers a `RemoteDocumentCache.removeEntry()` call.
     *
     * You can only remove documents that have already been retrieved via
     * `getEntry()/getEntries()` (enforced via IndexedDbs `apply()`).
     */    li(t, e = null) {
        this.ui(), this.ri.set(t, {
            hi: null,
            readTime: e
        });
    }
    /**
     * Looks up an entry in the cache. The buffered changes will first be checked,
     * and if no buffered change applies, this will forward to
     * `RemoteDocumentCache.getEntry()`.
     *
     * @param transaction - The transaction in which to perform any persistence
     *     operations.
     * @param documentKey - The key of the entry to look up.
     * @returns The cached Document or NoDocument entry, or null if we have
     *     nothing cached.
     */    _i(t, e) {
        this.ui();
        const n = this.ri.get(e);
        return void 0 !== n ? ws.resolve(n.hi) : this.fi(t, e);
    }
    /**
     * Looks up several entries in the cache, forwarding to
     * `RemoteDocumentCache.getEntry()`.
     *
     * @param transaction - The transaction in which to perform any persistence
     *     operations.
     * @param documentKeys - The keys of the entries to look up.
     * @returns A map of cached `Document`s or `NoDocument`s, indexed by key. If
     *     an entry cannot be found, the corresponding key will be mapped to a
     *     null value.
     */    getEntries(t, e) {
        return this.di(t, e);
    }
    /**
     * Applies buffered changes to the underlying RemoteDocumentCache, using
     * the provided transaction.
     */    apply(t) {
        return this.ui(), this.oi = !0, this.wi(t);
    }
    /** Helper to assert this.changes is not null  */    ui() {}
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Delete a mutation batch and the associated document mutations.
 * @returns A PersistencePromise of the document mutations that were removed.
 */ function yi(t, e, n) {
    const s = t.store(Os.store), i = t.store(ks.store), r = [], o = IDBKeyRange.only(n.batchId);
    let c = 0;
    const a = s.hs({
        range: o
    }, ((t, e, n) => (c++, n.delete())));
    r.push(a.next((() => {
        p(1 === c);
    })));
    const u = [];
    for (const t of n.mutations) {
        const s = ks.key(e, t.key.path, n.batchId);
        r.push(i.delete(s)), u.push(t.key);
    }
    return ws.Mn(r).next((() => u));
}

/**
 * Returns an approximate size for the given document.
 */ function gi(t) {
    let e;
    if (t.document) e = t.document; else if (t.unknownDocument) e = t.unknownDocument; else {
        if (!t.noDocument) throw V();
        e = t.noDocument;
    }
    return JSON.stringify(e).length;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** A mutation queue for a specific user, backed by IndexedDB. */ class Vi {
    constructor(
    /**
     * The normalized userId (e.g. null UID => "" userId) used to store /
     * retrieve mutations.
     */
    t, e, n, s) {
        this.userId = t, this.Qt = e, this.Ei = n, this.Ti = s, 
        /**
         * Caches the document keys for pending mutation batches. If the mutation
         * has been removed from IndexedDb, the cached value may continue to
         * be used to retrieve the batch's document keys. To remove a cached value
         * locally, `removeCachedMutationKeys()` should be invoked either directly
         * or through `removeMutationBatches()`.
         *
         * With multi-tab, when the primary client acknowledges or rejects a mutation,
         * this cache is used by secondary clients to invalidate the local
         * view of the documents that were previously affected by the mutation.
         */
        // PORTING NOTE: Multi-tab only.
        this.Ii = {};
    }
    /**
     * Creates a new mutation queue for the given user.
     * @param user - The user for which to create a mutation queue.
     * @param serializer - The serializer to use when persisting to IndexedDb.
     */    static mi(t, e, n, s) {
        // TODO(mcg): Figure out what constraints there are on userIDs
        // In particular, are there any reserved characters? are empty ids allowed?
        // For the moment store these together in the same mutations table assuming
        // that empty userIDs aren't allowed.
        p("" !== t.uid);
        const i = t.m() ? t.uid : "";
        return new Vi(i, e, n, s);
    }
    Ai(t) {
        let e = !0;
        const n = IDBKeyRange.bound([ this.userId, Number.NEGATIVE_INFINITY ], [ this.userId, Number.POSITIVE_INFINITY ]);
        return bi(t).hs({
            index: Os.userMutationsIndex,
            range: n
        }, ((t, n, s) => {
            e = !1, s.done();
        })).next((() => e));
    }
    Ri(t, e, n, s) {
        const i = vi(t), r = bi(t);
        // The IndexedDb implementation in Chrome (and Firefox) does not handle
        // compound indices that include auto-generated keys correctly. To ensure
        // that the index entry is added correctly in all browsers, we perform two
        // writes: The first write is used to retrieve the next auto-generated Batch
        // ID, and the second write populates the index and stores the actual
        // mutation batch.
        // See: https://bugs.chromium.org/p/chromium/issues/detail?id=701972
        // We write an empty object to obtain key
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return r.add({}).next((o => {
            p("number" == typeof o);
            const c = new Xs(o, e, n, s), a = function(t, e, n) {
                const s = n.baseMutations.map((e => Jn(t.Fs, e))), i = n.mutations.map((e => Jn(t.Fs, e)));
                return new Os(e, n.batchId, n.vs.toMillis(), s, i);
            }(this.Qt, this.userId, c), u = [];
            let h = new on(((t, e) => D(t.rt(), e.rt())));
            for (const t of s) {
                const e = ks.key(this.userId, t.key.path, o);
                h = h.add(t.key.path.X()), u.push(r.put(a)), u.push(i.put(e, ks.PLACEHOLDER));
            }
            return h.forEach((e => {
                u.push(this.Ei.qs(t, e));
            })), t.gs((() => {
                this.Ii[o] = c.keys();
            })), ws.Mn(u).next((() => c));
        }));
    }
    Pi(t, e) {
        return bi(t).get(e).next((t => t ? (p(t.userId === this.userId), ci(this.Qt, t)) : null));
    }
    /**
     * Returns the document keys for the mutation batch with the given batchId.
     * For primary clients, this method returns `null` after
     * `removeMutationBatches()` has been called. Secondary clients return a
     * cached result until `removeCachedMutationKeys()` is invoked.
     */
    // PORTING NOTE: Multi-tab only.
    yi(t, e) {
        return this.Ii[e] ? ws.resolve(this.Ii[e]) : this.Pi(t, e).next((t => {
            if (t) {
                const n = t.keys();
                return this.Ii[e] = n, n;
            }
            return null;
        }));
    }
    gi(t, e) {
        const n = e + 1, s = IDBKeyRange.lowerBound([ this.userId, n ]);
        let i = null;
        return bi(t).hs({
            index: Os.userMutationsIndex,
            range: s
        }, ((t, e, s) => {
            e.userId === this.userId && (p(e.batchId >= n), i = ci(this.Qt, e)), s.done();
        })).next((() => i));
    }
    Vi(t) {
        const e = IDBKeyRange.upperBound([ this.userId, Number.POSITIVE_INFINITY ]);
        let n = -1;
        return bi(t).hs({
            index: Os.userMutationsIndex,
            range: e,
            reverse: !0
        }, ((t, e, s) => {
            n = e.batchId, s.done();
        })).next((() => n));
    }
    pi(t) {
        const e = IDBKeyRange.bound([ this.userId, -1 ], [ this.userId, Number.POSITIVE_INFINITY ]);
        return bi(t).rs(Os.userMutationsIndex, e).next((t => t.map((t => ci(this.Qt, t)))));
    }
    bi(t, e) {
        // Scan the document-mutation index starting with a prefix starting with
        // the given documentKey.
        const n = ks.prefixForPath(this.userId, e.path), s = IDBKeyRange.lowerBound(n), i = [];
        return vi(t).hs({
            range: s
        }, ((n, s, r) => {
            const [o, c, a] = n, u = xs(c);
            // Only consider rows matching exactly the specific key of
            // interest. Note that because we order by path first, and we
            // order terminators before path separators, we'll encounter all
            // the index rows for documentKey contiguously. In particular, all
            // the rows for documentKey will occur before any rows for
            // documents nested in a subcollection beneath documentKey so we
            // can stop as soon as we hit any such row.
                        if (o === this.userId && e.path.isEqual(u)) 
            // Look up the mutation batch in the store.
            return bi(t).get(a).next((t => {
                if (!t) throw V();
                p(t.userId === this.userId), i.push(ci(this.Qt, t));
            }));
            r.done();
        })).next((() => i));
    }
    vi(t, e) {
        let n = new on(D);
        const s = [];
        return e.forEach((e => {
            const i = ks.prefixForPath(this.userId, e.path), r = IDBKeyRange.lowerBound(i), o = vi(t).hs({
                range: r
            }, ((t, s, i) => {
                const [r, o, c] = t, a = xs(o);
                // Only consider rows matching exactly the specific key of
                // interest. Note that because we order by path first, and we
                // order terminators before path separators, we'll encounter all
                // the index rows for documentKey contiguously. In particular, all
                // the rows for documentKey will occur before any rows for
                // documents nested in a subcollection beneath documentKey so we
                // can stop as soon as we hit any such row.
                                r === this.userId && e.path.isEqual(a) ? n = n.add(c) : i.done();
            }));
            s.push(o);
        })), ws.Mn(s).next((() => this.Si(t, n)));
    }
    Di(t, e) {
        const n = e.path, s = n.length + 1, i = ks.prefixForPath(this.userId, n), r = IDBKeyRange.lowerBound(i);
        // Collect up unique batchIDs encountered during a scan of the index. Use a
        // SortedSet to accumulate batch IDs so they can be traversed in order in a
        // scan of the main table.
        let o = new on(D);
        return vi(t).hs({
            range: r
        }, ((t, e, i) => {
            const [r, c, a] = t, u = xs(c);
            r === this.userId && n.nt(u) ? 
            // Rows with document keys more than one segment longer than the
            // query path can't be matches. For example, a query on 'rooms'
            // can't match the document /rooms/abc/messages/xyx.
            // TODO(mcg): we'll need a different scanner when we implement
            // ancestor queries.
            u.length === s && (o = o.add(a)) : i.done();
        })).next((() => this.Si(t, o)));
    }
    Si(t, e) {
        const n = [], s = [];
        // TODO(rockwood): Implement this using iterate.
        return e.forEach((e => {
            s.push(bi(t).get(e).next((t => {
                if (null === t) throw V();
                p(t.userId === this.userId), n.push(ci(this.Qt, t));
            })));
        })), ws.Mn(s).next((() => n));
    }
    Ci(t, e) {
        return yi(t.ps, this.userId, e).next((n => (t.gs((() => {
            this.xi(e.batchId);
        })), ws.forEach(n, (e => this.Ti.Ni(t, e))))));
    }
    /**
     * Clears the cached keys for a mutation batch. This method should be
     * called by secondary clients after they process mutation updates.
     *
     * Note that this method does not have to be called from primary clients as
     * the corresponding cache entries are cleared when an acknowledged or
     * rejected batch is removed from the mutation queue.
     */
    // PORTING NOTE: Multi-tab only
    xi(t) {
        delete this.Ii[t];
    }
    $i(t) {
        return this.Ai(t).next((e => {
            if (!e) return ws.resolve();
            // Verify that there are no entries in the documentMutations index if
            // the queue is empty.
                        const n = IDBKeyRange.lowerBound(ks.prefixForUser(this.userId)), s = [];
            return vi(t).hs({
                range: n
            }, ((t, e, n) => {
                if (t[0] === this.userId) {
                    const e = xs(t[1]);
                    s.push(e);
                } else n.done();
            })).next((() => {
                p(0 === s.length);
            }));
        }));
    }
    Fi(t, e) {
        return pi(t, this.userId, e);
    }
    // PORTING NOTE: Multi-tab only (state is held in memory in other clients).
    /** Returns the mutation queue's metadata from IndexedDb. */
    Oi(t) {
        return Si(t).get(this.userId).next((t => t || new Fs(this.userId, -1, 
        /*lastStreamToken=*/ "")));
    }
}

/**
 * @returns true if the mutation queue for the given user contains a pending
 *         mutation for the given key.
 */ function pi(t, e, n) {
    const s = ks.prefixForPath(e, n.path), i = s[1], r = IDBKeyRange.lowerBound(s);
    let o = !1;
    return vi(t).hs({
        range: r,
        us: !0
    }, ((t, n, s) => {
        const [r, c, /*batchID*/ a] = t;
        r === e && c === i && (o = !0), s.done();
    })).next((() => o));
}

/** Returns true if any mutation queue contains the given document. */
/**
 * Helper to get a typed SimpleDbStore for the mutations object store.
 */
function bi(t) {
    return Ys(t, Os.store);
}

/**
 * Helper to get a typed SimpleDbStore for the mutationQueues object store.
 */ function vi(t) {
    return Ys(t, ks.store);
}

/**
 * Helper to get a typed SimpleDbStore for the mutationQueues object store.
 */ function Si(t) {
    return Ys(t, Fs.store);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Offset to ensure non-overlapping target ids. */
/**
 * Generates monotonically increasing target IDs for sending targets to the
 * watch stream.
 *
 * The client constructs two generators, one for the target cache, and one for
 * for the sync engine (to generate limbo documents targets). These
 * generators produce non-overlapping IDs (by using even and odd IDs
 * respectively).
 *
 * By separating the target ID space, the query cache can generate target IDs
 * that persist across client restarts, while sync engine can independently
 * generate in-memory target IDs that are transient and can be reused after a
 * restart.
 */
class Di {
    constructor(t) {
        this.ki = t;
    }
    next() {
        return this.ki += 2, this.ki;
    }
    static Mi() {
        // The target cache generator must return '2' in its first call to `next()`
        // as there is no differentiation in the protocol layer between an unset
        // number and the number '0'. If we were to sent a target with target ID
        // '0', the backend would consider it unset and replace it with its own ID.
        return new Di(0);
    }
    static Li() {
        // Sync engine assigns target IDs for limbo document detection.
        return new Di(-1);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Ci {
    constructor(t, e) {
        this.Ti = t, this.Qt = e;
    }
    // PORTING NOTE: We don't cache global metadata for the target cache, since
    // some of it (in particular `highestTargetId`) can be modified by secondary
    // tabs. We could perhaps be more granular (and e.g. still cache
    // `lastRemoteSnapshotVersion` in memory) but for simplicity we currently go
    // to IndexedDb whenever we need to read metadata. We can revisit if it turns
    // out to have a meaningful performance impact.
    Bi(t) {
        return this.qi(t).next((e => {
            const n = new Di(e.highestTargetId);
            return e.highestTargetId = n.next(), this.Ui(t, e).next((() => e.highestTargetId));
        }));
    }
    Qi(t) {
        return this.qi(t).next((t => W.K(new K(t.lastRemoteSnapshotVersion.seconds, t.lastRemoteSnapshotVersion.nanoseconds))));
    }
    Ki(t) {
        return this.qi(t).next((t => t.highestListenSequenceNumber));
    }
    Wi(t, e, n) {
        return this.qi(t).next((s => (s.highestListenSequenceNumber = e, n && (s.lastRemoteSnapshotVersion = n.j()), 
        e > s.highestListenSequenceNumber && (s.highestListenSequenceNumber = e), this.Ui(t, s))));
    }
    ji(t, e) {
        return this.Gi(t, e).next((() => this.qi(t).next((n => (n.targetCount += 1, this.zi(e, n), 
        this.Ui(t, n))))));
    }
    Hi(t, e) {
        return this.Gi(t, e);
    }
    Ji(t, e) {
        return this.Yi(t, e.targetId).next((() => xi(t).delete(e.targetId))).next((() => this.qi(t))).next((e => (p(e.targetCount > 0), 
        e.targetCount -= 1, this.Ui(t, e))));
    }
    /**
     * Drops any targets with sequence number less than or equal to the upper bound, excepting those
     * present in `activeTargetIds`. Document associations for the removed targets are also removed.
     * Returns the number of targets removed.
     */    Xi(t, e, n) {
        let s = 0;
        const i = [];
        return xi(t).hs(((r, o) => {
            const c = ai(o);
            c.sequenceNumber <= e && null === n.get(c.targetId) && (s++, i.push(this.Ji(t, c)));
        })).next((() => ws.Mn(i))).next((() => s));
    }
    /**
     * Call provided function with each `TargetData` that we have cached.
     */    In(t, e) {
        return xi(t).hs(((t, n) => {
            const s = ai(n);
            e(s);
        }));
    }
    qi(t) {
        return Ni(t).get(Ks.key).next((t => (p(null !== t), t)));
    }
    Ui(t, e) {
        return Ni(t).put(Ks.key, e);
    }
    Gi(t, e) {
        return xi(t).put(ui(this.Qt, e));
    }
    /**
     * In-place updates the provided metadata to account for values in the given
     * TargetData. Saving is done separately. Returns true if there were any
     * changes to the metadata.
     */    zi(t, e) {
        let n = !1;
        return t.targetId > e.highestTargetId && (e.highestTargetId = t.targetId, n = !0), 
        t.sequenceNumber > e.highestListenSequenceNumber && (e.highestListenSequenceNumber = t.sequenceNumber, 
        n = !0), n;
    }
    Zi(t) {
        return this.qi(t).next((t => t.targetCount));
    }
    tr(t, e) {
        // Iterating by the canonicalId may yield more than one result because
        // canonicalId values are not required to be unique per target. This query
        // depends on the queryTargets index to be efficient.
        const n = Nt(e), s = IDBKeyRange.bound([ n, Number.NEGATIVE_INFINITY ], [ n, Number.POSITIVE_INFINITY ]);
        let i = null;
        return xi(t).hs({
            range: s,
            index: Us.queryTargetsIndexName
        }, ((t, n, s) => {
            const r = ai(n);
            // After finding a potential match, check that the target is
            // actually equal to the requested target.
                        Ft(e, r.target) && (i = r, s.done());
        })).next((() => i));
    }
    er(t, e, n) {
        // PORTING NOTE: The reverse index (documentsTargets) is maintained by
        // IndexedDb.
        const s = [], i = $i(t);
        return e.forEach((e => {
            const r = Ss(e.path);
            s.push(i.put(new Qs(n, r))), s.push(this.Ti.nr(t, n, e));
        })), ws.Mn(s);
    }
    sr(t, e, n) {
        // PORTING NOTE: The reverse index (documentsTargets) is maintained by
        // IndexedDb.
        const s = $i(t);
        return ws.forEach(e, (e => {
            const i = Ss(e.path);
            return ws.Mn([ s.delete([ n, i ]), this.Ti.ir(t, n, e) ]);
        }));
    }
    Yi(t, e) {
        const n = $i(t), s = IDBKeyRange.bound([ e ], [ e + 1 ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        return n.delete(s);
    }
    rr(t, e) {
        const n = IDBKeyRange.bound([ e ], [ e + 1 ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0), s = $i(t);
        let i = En();
        return s.hs({
            range: n,
            us: !0
        }, ((t, e, n) => {
            const s = xs(t[1]), r = new J(s);
            i = i.add(r);
        })).next((() => i));
    }
    Fi(t, e) {
        const n = Ss(e.path), s = IDBKeyRange.bound([ n ], [ x(n) ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        let i = 0;
        return $i(t).hs({
            index: Qs.documentTargetsIndex,
            us: !0,
            range: s
        }, (([t, e], n, s) => {
            // Having a sentinel row for a document does not count as containing that document;
            // For the target cache, containing the document means the document is part of some
            // target.
            0 !== t && (i++, s.done());
        })).next((() => i > 0));
    }
    /**
     * Looks up a TargetData entry by target ID.
     *
     * @param targetId - The target ID of the TargetData entry to look up.
     * @returns The cached TargetData entry, or null if the cache has no entry for
     * the target.
     */
    // PORTING NOTE: Multi-tab only.
    Sn(t, e) {
        return xi(t).get(e).next((t => t ? ai(t) : null));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the queries object store.
 */ function xi(t) {
    return Ys(t, Us.store);
}

/**
 * Helper to get a typed SimpleDbStore for the target globals object store.
 */ function Ni(t) {
    return Ys(t, Ks.store);
}

/**
 * Helper to get a typed SimpleDbStore for the document target object store.
 */ function $i(t) {
    return Ys(t, Qs.store);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function Fi([t, e], [n, s]) {
    const i = D(t, n);
    return 0 === i ? D(e, s) : i;
}

/**
 * Used to calculate the nth sequence number. Keeps a rolling buffer of the
 * lowest n values passed to `addElement`, and finally reports the largest of
 * them in `maxValue`.
 */ class Oi {
    constructor(t) {
        this.cr = t, this.buffer = new on(Fi), this.ar = 0;
    }
    ur() {
        return ++this.ar;
    }
    hr(t) {
        const e = [ t, this.ur() ];
        if (this.buffer.size < this.cr) this.buffer = this.buffer.add(e); else {
            const t = this.buffer.last();
            Fi(e, t) < 0 && (this.buffer = this.buffer.delete(t).add(e));
        }
    }
    get maxValue() {
        // Guaranteed to be non-empty. If we decide we are not collecting any
        // sequence numbers, nthSequenceNumber below short-circuits. If we have
        // decided that we are collecting n sequence numbers, it's because n is some
        // percentage of the existing sequence numbers. That means we should never
        // be in a situation where we are collecting sequence numbers but don't
        // actually have any.
        return this.buffer.last()[0];
    }
}

/**
 * This class is responsible for the scheduling of LRU garbage collection. It handles checking
 * whether or not GC is enabled, as well as which delay to use before the next run.
 */ class ki {
    constructor(t, e) {
        this.lr = t, this._s = e, this._r = !1, this.dr = null;
    }
    start(t) {
        -1 !== this.lr.params.zs && this.wr(t);
    }
    stop() {
        this.dr && (this.dr.cancel(), this.dr = null);
    }
    get Er() {
        return null !== this.dr;
    }
    wr(t) {
        const e = this._r ? 3e5 : 6e4;
        R("LruGarbageCollector", `Garbage collection scheduled in ${e}ms`), this.dr = this._s.Tr("lru_garbage_collection" /* LruGarbageCollection */ , e, (async () => {
            this.dr = null, this._r = !0;
            try {
                await t.Ir(this.lr);
            } catch (t) {
                As(t) ? R("LruGarbageCollector", "Ignoring IndexedDB error during garbage collection: ", t) : await Ai(t);
            }
            await this.wr(t);
        }));
    }
}

/** Implements the steps for LRU garbage collection. */ class Mi {
    constructor(t, e) {
        this.mr = t, this.params = e;
    }
    Ar(t, e) {
        return this.mr.Rr(t).next((t => Math.floor(e / 100 * t)));
    }
    Pr(t, e) {
        if (0 === e) return ws.resolve(Q.q);
        const n = new Oi(e);
        return this.mr.In(t, (t => n.hr(t.sequenceNumber))).next((() => this.mr.yr(t, (t => n.hr(t))))).next((() => n.maxValue));
    }
    Xi(t, e, n) {
        return this.mr.Xi(t, e, n);
    }
    gr(t, e) {
        return this.mr.gr(t, e);
    }
    Vr(t, e) {
        return -1 === this.params.zs ? (R("LruGarbageCollector", "Garbage collection skipped; disabled"), 
        ws.resolve(Ii)) : this.pr(t).next((n => n < this.params.zs ? (R("LruGarbageCollector", `Garbage collection skipped; Cache size ${n} is lower than threshold ${this.params.zs}`), 
        Ii) : this.br(t, e)));
    }
    pr(t) {
        return this.mr.pr(t);
    }
    br(t, n) {
        let s, i, r, o, c, a, u;
        const h = Date.now();
        return this.Ar(t, this.params.Hs).next((e => (
        // Cap at the configured max
        e > this.params.Js ? (R("LruGarbageCollector", `Capping sequence numbers to collect down to the maximum of ${this.params.Js} from ${e}`), 
        i = this.params.Js) : i = e, o = Date.now(), this.Pr(t, i)))).next((e => (s = e, 
        c = Date.now(), this.Xi(t, s, n)))).next((e => (r = e, a = Date.now(), this.gr(t, s)))).next((t => {
            if (u = Date.now(), A() <= e.DEBUG) {
                R("LruGarbageCollector", `LRU Garbage Collection\n\tCounted targets in ${o - h}ms\n\tDetermined least recently used ${i} in ` + (c - o) + "ms\n" + `\tRemoved ${r} targets in ` + (a - c) + "ms\n" + `\tRemoved ${t} documents in ` + (u - a) + "ms\n" + `Total Duration: ${u - h}ms`);
            }
            return ws.resolve({
                Ks: !0,
                Ws: i,
                js: r,
                Gs: t
            });
        }));
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Provides LRU functionality for IndexedDB persistence. */
class Li {
    constructor(t, e) {
        this.db = t, this.lr = function(t, e) {
            return new Mi(t, e);
        }(this, e);
    }
    Rr(t) {
        const e = this.vr(t);
        return this.db.Sr().Zi(t).next((t => e.next((e => t + e))));
    }
    vr(t) {
        let e = 0;
        return this.yr(t, (t => {
            e++;
        })).next((() => e));
    }
    In(t, e) {
        return this.db.Sr().In(t, e);
    }
    yr(t, e) {
        return this.Dr(t, ((t, n) => e(n)));
    }
    nr(t, e, n) {
        return Bi(t, n);
    }
    ir(t, e, n) {
        return Bi(t, n);
    }
    Xi(t, e, n) {
        return this.db.Sr().Xi(t, e, n);
    }
    Ni(t, e) {
        return Bi(t, e);
    }
    /**
     * Returns true if anything would prevent this document from being garbage
     * collected, given that the document in question is not present in any
     * targets and has a sequence number less than or equal to the upper bound for
     * the collection run.
     */    Cr(t, e) {
        return function(t, e) {
            let n = !1;
            return Si(t).ls((s => pi(t, s, e).next((t => (t && (n = !0), ws.resolve(!t)))))).next((() => n));
        }(t, e);
    }
    gr(t, e) {
        const n = this.db.$r().Nr(), s = [];
        let i = 0;
        return this.Dr(t, ((r, o) => {
            if (o <= e) {
                const e = this.Cr(t, r).next((e => {
                    if (!e) 
                    // Our size accounting requires us to read all documents before
                    // removing them.
                    return i++, n._i(t, r).next((() => (n.li(r), $i(t).delete([ 0, Ss(r.path) ]))));
                }));
                s.push(e);
            }
        })).next((() => ws.Mn(s))).next((() => n.apply(t))).next((() => i));
    }
    removeTarget(t, e) {
        const n = e.kt(t.bs);
        return this.db.Sr().Hi(t, n);
    }
    Fr(t, e) {
        return Bi(t, e);
    }
    /**
     * Call provided function for each document in the cache that is 'orphaned'. Orphaned
     * means not a part of any target, so the only entry in the target-document index for
     * that document will be the sentinel row (targetId 0), which will also have the sequence
     * number for the last time the document was accessed.
     */    Dr(t, e) {
        const n = $i(t);
        let s, i = Q.q;
        return n.hs({
            index: Qs.documentTargetsIndex
        }, (([t, n], {path: r, sequenceNumber: o}) => {
            0 === t ? (
            // if nextToReport is valid, report it, this is a new key so the
            // last one must not be a member of any targets.
            i !== Q.q && e(new J(xs(s)), i), 
            // set nextToReport to be this sequence number. It's the next one we
            // might report, if we don't find any targets for this document.
            // Note that the sequence number must be defined when the targetId
            // is 0.
            i = o, s = r) : 
            // set nextToReport to be invalid, we know we don't need to report
            // this one since we found a target for it.
            i = Q.q;
        })).next((() => {
            // Since we report sequence numbers after getting to the next key, we
            // need to check if the last key we iterated over was an orphaned
            // document and report it.
            i !== Q.q && e(new J(xs(s)), i);
        }));
    }
    pr(t) {
        return this.db.$r().Or(t);
    }
}

function Bi(t, e) {
    return $i(t).put(
    /**
 * @returns A value suitable for writing a sentinel row in the target-document
 * store.
 */
    function(t, e) {
        return new Qs(0, Ss(t.path), e);
    }(e, t.bs));
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The RemoteDocumentCache for IndexedDb. To construct, invoke
 * `newIndexedDbRemoteDocumentCache()`.
 */ class qi {
    /**
     * @param serializer - The document serializer.
     * @param indexManager - The query indexes that need to be maintained.
     */
    constructor(t, e) {
        this.Qt = t, this.Ei = e;
    }
    /**
     * Adds the supplied entries to the cache.
     *
     * All calls of `addEntry` are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()` to ensure proper accounting of metadata.
     */    ai(t, e, n) {
        return Ki(t).put(Wi(e), n);
    }
    /**
     * Removes a document from the cache.
     *
     * All calls of `removeEntry`  are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()` to ensure proper accounting of metadata.
     */    li(t, e) {
        const n = Ki(t), s = Wi(e);
        return n.delete(s);
    }
    /**
     * Updates the current cache size.
     *
     * Callers to `addEntry()` and `removeEntry()` *must* call this afterwards to update the
     * cache's metadata.
     */    updateMetadata(t, e) {
        return this.getMetadata(t).next((n => (n.byteSize += e, this.kr(t, n))));
    }
    _i(t, e) {
        return Ki(t).get(Wi(e)).next((t => this.Mr(t)));
    }
    /**
     * Looks up an entry in the cache.
     *
     * @param documentKey - The key of the entry to look up.
     * @returns The cached MaybeDocument entry and its size, or null if we have
     * nothing cached.
     */    Lr(t, e) {
        return Ki(t).get(Wi(e)).next((t => {
            const e = this.Mr(t);
            return e ? {
                hi: e,
                size: gi(t)
            } : null;
        }));
    }
    getEntries(t, e) {
        let n = hn();
        return this.Br(t, e, ((t, e) => {
            const s = this.Mr(e);
            n = n.zt(t, s);
        })).next((() => n));
    }
    /**
     * Looks up several entries in the cache.
     *
     * @param documentKeys - The set of keys entries to look up.
     * @returns A map of MaybeDocuments indexed by key (if a document cannot be
     *     found, the key will be mapped to null) and a map of sizes indexed by
     *     key (zero if the key cannot be found).
     */    qr(t, e) {
        let n = hn(), s = new nn(J.H);
        return this.Br(t, e, ((t, e) => {
            const i = this.Mr(e);
            i ? (n = n.zt(t, i), s = s.zt(t, gi(e))) : (n = n.zt(t, null), s = s.zt(t, 0));
        })).next((() => ({
            Ur: n,
            Qr: s
        })));
    }
    Br(t, e, n) {
        if (e.et()) return ws.resolve();
        const s = IDBKeyRange.bound(e.first().path.it(), e.last().path.it()), i = e.te();
        let r = i.oe();
        return Ki(t).hs({
            range: s
        }, ((t, e, s) => {
            const o = J.Et(t);
            // Go through keys not found in cache.
                        for (;r && J.H(r, o) < 0; ) n(r, null), r = i.oe();
            r && r.isEqual(o) && (
            // Key found in cache.
            n(r, e), r = i.ce() ? i.oe() : null), 
            // Skip to the next key (if there is one).
            r ? s.ss(r.path.it()) : s.done();
        })).next((() => {
            // The rest of the keys are not in the cache. One case where `iterate`
            // above won't go through them is when the cache is empty.
            for (;r; ) n(r, null), r = i.ce() ? i.oe() : null;
        }));
    }
    Kr(t, e, n) {
        let s = _n();
        const i = e.path.length + 1, r = {};
        if (n.isEqual(W.min())) {
            // Documents are ordered by key, so we can use a prefix scan to narrow
            // down the documents we need to match the query against.
            const t = e.path.it();
            r.range = IDBKeyRange.lowerBound(t);
        } else {
            // Execute an index-free query and filter by read time. This is safe
            // since all document changes to queries that have a
            // lastLimboFreeSnapshotVersion (`sinceReadTime`) have a read time set.
            const t = e.path.it(), s = si(n);
            r.range = IDBKeyRange.lowerBound([ t, s ], 
            /* open= */ !0), r.index = Bs.collectionReadTimeIndex;
        }
        return Ki(t).hs(r, ((t, n, r) => {
            // The query is actually returning any path that starts with the query
            // path prefix which may include documents in subcollections. For
            // example, a query on 'rooms' will return rooms/abc/messages/xyx but we
            // shouldn't match it. Fix this by discarding rows with document keys
            // more than one segment longer than the query path.
            if (t.length !== i) return;
            const o = ei(this.Qt, n);
            e.path.nt(o.key.path) ? o instanceof vt && fe(e, o) && (s = s.zt(o.key, o)) : r.done();
        })).next((() => s));
    }
    Nr(t) {
        return new Ui(this, !!t && t.Wr);
    }
    Or(t) {
        return this.getMetadata(t).next((t => t.byteSize));
    }
    getMetadata(t) {
        return Qi(t).get(qs.key).next((t => (p(!!t), t)));
    }
    kr(t, e) {
        return Qi(t).put(qs.key, e);
    }
    /**
     * Decodes `remoteDoc` and returns the document (or null, if the document
     * corresponds to the format used for sentinel deletes).
     */    Mr(t) {
        if (t) {
            const e = ei(this.Qt, t);
            return e instanceof St && e.version.isEqual(W.min()) ? null : e;
        }
        return null;
    }
}

/**
 * Creates a new IndexedDbRemoteDocumentCache.
 *
 * @param serializer - The document serializer.
 * @param indexManager - The query indexes that need to be maintained.
 */
/**
 * Handles the details of adding and updating documents in the IndexedDbRemoteDocumentCache.
 *
 * Unlike the MemoryRemoteDocumentChangeBuffer, the IndexedDb implementation computes the size
 * delta for all submitted changes. This avoids having to re-read all documents from IndexedDb
 * when we apply the changes.
 */
class Ui extends Pi {
    /**
     * @param documentCache - The IndexedDbRemoteDocumentCache to apply the changes to.
     * @param trackRemovals - Whether to create sentinel deletes that can be tracked by
     * `getNewDocumentChanges()`.
     */
    constructor(t, e) {
        super(), this.jr = t, this.Wr = e, 
        // A map of document sizes prior to applying the changes in this buffer.
        this.Gr = new Ri((t => t.toString()), ((t, e) => t.isEqual(e)));
    }
    wi(t) {
        const e = [];
        let n = 0, s = new on(((t, e) => D(t.rt(), e.rt())));
        return this.ri.forEach(((i, r) => {
            const o = this.Gr.get(i);
            if (r.hi) {
                const c = ni(this.jr.Qt, r.hi, this.ci(i));
                s = s.add(i.path.X());
                const a = gi(c);
                n += a - o, e.push(this.jr.ai(t, i, c));
            } else if (n -= o, this.Wr) {
                // In order to track removals, we store a "sentinel delete" in the
                // RemoteDocumentCache. This entry is represented by a NoDocument
                // with a version of 0 and ignored by `maybeDecodeDocument()` but
                // preserved in `getNewDocumentChanges()`.
                const n = ni(this.jr.Qt, new St(i, W.min()), this.ci(i));
                e.push(this.jr.ai(t, i, n));
            } else e.push(this.jr.li(t, i));
        })), s.forEach((n => {
            e.push(this.jr.Ei.qs(t, n));
        })), e.push(this.jr.updateMetadata(t, n)), ws.Mn(e);
    }
    fi(t, e) {
        // Record the size of everything we load from the cache so we can compute a delta later.
        return this.jr.Lr(t, e).next((t => null === t ? (this.Gr.set(e, 0), null) : (this.Gr.set(e, t.size), 
        t.hi)));
    }
    di(t, e) {
        // Record the size of everything we load from the cache so we can compute
        // a delta later.
        return this.jr.qr(t, e).next((({Ur: t, Qr: e}) => (
        // Note: `getAllFromCache` returns two maps instead of a single map from
        // keys to `DocumentSizeEntry`s. This is to allow returning the
        // `NullableMaybeDocumentMap` directly, without a conversion.
        e.forEach(((t, e) => {
            this.Gr.set(t, e);
        })), t)));
    }
}

function Qi(t) {
    return Ys(t, qs.store);
}

/**
 * Helper to get a typed SimpleDbStore for the remoteDocuments object store.
 */ function Ki(t) {
    return Ys(t, Bs.store);
}

function Wi(t) {
    return t.path.it();
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Performs database creation and schema upgrades. */ class ji {
    constructor(t) {
        this.Qt = t;
    }
    /**
     * Performs database creation and schema upgrades.
     *
     * Note that in production, this method is only ever used to upgrade the schema
     * to SCHEMA_VERSION. Different values of toVersion are only used for testing
     * and local feature development.
     */    Jn(t, e, n, s) {
        p(n < s && n >= 0 && s <= 11);
        const i = new Es("createOrUpgrade", e);
        n < 1 && s >= 1 && (function(t) {
            t.createObjectStore($s.store);
        }(t), function(t) {
            t.createObjectStore(Fs.store, {
                keyPath: Fs.keyPath
            });
            t.createObjectStore(Os.store, {
                keyPath: Os.keyPath,
                autoIncrement: !0
            }).createIndex(Os.userMutationsIndex, Os.userMutationsKeyPath, {
                unique: !0
            }), t.createObjectStore(ks.store);
        }
        /**
 * Upgrade function to migrate the 'mutations' store from V1 to V3. Loads
 * and rewrites all data.
 */ (t), Gi(t), function(t) {
            t.createObjectStore(Bs.store);
        }(t));
        // Migration 2 to populate the targetGlobal object no longer needed since
        // migration 3 unconditionally clears it.
                let r = ws.resolve();
        return n < 3 && s >= 3 && (
        // Brand new clients don't need to drop and recreate--only clients that
        // potentially have corrupt data.
        0 !== n && (!function(t) {
            t.deleteObjectStore(Qs.store), t.deleteObjectStore(Us.store), t.deleteObjectStore(Ks.store);
        }(t), Gi(t)), r = r.next((() => 
        /**
 * Creates the target global singleton row.
 *
 * @param txn - The version upgrade transaction for indexeddb
 */
        function(t) {
            const e = t.store(Ks.store), n = new Ks(
            /*highestTargetId=*/ 0, 
            /*lastListenSequenceNumber=*/ 0, W.min().j(), 
            /*targetCount=*/ 0);
            return e.put(Ks.key, n);
        }
        /**
 * Creates indices on the RemoteDocuments store used for both multi-tab
 * and Index-Free queries.
 */ (i)))), n < 4 && s >= 4 && (0 !== n && (
        // Schema version 3 uses auto-generated keys to generate globally unique
        // mutation batch IDs (this was previously ensured internally by the
        // client). To migrate to the new schema, we have to read all mutations
        // and write them back out. We preserve the existing batch IDs to guarantee
        // consistency with other object stores. Any further mutation batch IDs will
        // be auto-generated.
        r = r.next((() => function(t, e) {
            return e.store(Os.store).rs().next((n => {
                t.deleteObjectStore(Os.store);
                t.createObjectStore(Os.store, {
                    keyPath: Os.keyPath,
                    autoIncrement: !0
                }).createIndex(Os.userMutationsIndex, Os.userMutationsKeyPath, {
                    unique: !0
                });
                const s = e.store(Os.store), i = n.map((t => s.put(t)));
                return ws.Mn(i);
            }));
        }(t, i)))), r = r.next((() => {
            !function(t) {
                t.createObjectStore(js.store, {
                    keyPath: js.keyPath
                });
            }(t);
        }))), n < 5 && s >= 5 && (r = r.next((() => this.zr(i)))), n < 6 && s >= 6 && (r = r.next((() => (function(t) {
            t.createObjectStore(qs.store);
        }(t), this.Hr(i))))), n < 7 && s >= 7 && (r = r.next((() => this.Jr(i)))), n < 8 && s >= 8 && (r = r.next((() => this.Yr(t, i)))), 
        n < 9 && s >= 9 && (r = r.next((() => {
            // Multi-Tab used to manage its own changelog, but this has been moved
            // to the DbRemoteDocument object store itself. Since the previous change
            // log only contained transient data, we can drop its object store.
            !function(t) {
                t.objectStoreNames.contains("remoteDocumentChanges") && t.deleteObjectStore("remoteDocumentChanges");
            }(t), function(t) {
                const e = t.objectStore(Bs.store);
                e.createIndex(Bs.readTimeIndex, Bs.readTimeIndexPath, {
                    unique: !1
                }), e.createIndex(Bs.collectionReadTimeIndex, Bs.collectionReadTimeIndexPath, {
                    unique: !1
                });
            }(e);
        }))), n < 10 && s >= 10 && (r = r.next((() => this.Xr(i)))), n < 11 && s >= 11 && (r = r.next((() => {
            !function(t) {
                t.createObjectStore(Gs.store, {
                    keyPath: Gs.keyPath
                });
            }(t), function(t) {
                t.createObjectStore(zs.store, {
                    keyPath: zs.keyPath
                });
            }
            /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t);
        }))), r;
    }
    Hr(t) {
        let e = 0;
        return t.store(Bs.store).hs(((t, n) => {
            e += gi(n);
        })).next((() => {
            const n = new qs(e);
            return t.store(qs.store).put(qs.key, n);
        }));
    }
    zr(t) {
        const e = t.store(Fs.store), n = t.store(Os.store);
        return e.rs().next((e => ws.forEach(e, (e => {
            const s = IDBKeyRange.bound([ e.userId, -1 ], [ e.userId, e.lastAcknowledgedBatchId ]);
            return n.rs(Os.userMutationsIndex, s).next((n => ws.forEach(n, (n => {
                p(n.userId === e.userId);
                const s = ci(this.Qt, n);
                return yi(t, e.userId, s).next((() => {}));
            }))));
        }))));
    }
    /**
     * Ensures that every document in the remote document cache has a corresponding sentinel row
     * with a sequence number. Missing rows are given the most recently used sequence number.
     */    Jr(t) {
        const e = t.store(Qs.store), n = t.store(Bs.store);
        return t.store(Ks.store).get(Ks.key).next((t => {
            const s = [];
            return n.hs(((n, i) => {
                const r = new G(n), o = function(t) {
                    return [ 0, Ss(t) ];
                }(r);
                s.push(e.get(o).next((n => n ? ws.resolve() : (n => e.put(new Qs(0, Ss(n), t.highestListenSequenceNumber)))(r))));
            })).next((() => ws.Mn(s)));
        }));
    }
    Yr(t, e) {
        // Create the index.
        t.createObjectStore(Ws.store, {
            keyPath: Ws.keyPath
        });
        const n = e.store(Ws.store), s = new wi, i = t => {
            if (s.add(t)) {
                const e = t.tt(), s = t.X();
                return n.put({
                    collectionId: e,
                    parent: Ss(s)
                });
            }
        };
        // Helper to add an index entry iff we haven't already written it.
                // Index existing remote documents.
        return e.store(Bs.store).hs({
            us: !0
        }, ((t, e) => {
            const n = new G(t);
            return i(n.X());
        })).next((() => e.store(ks.store).hs({
            us: !0
        }, (([t, e, n], s) => {
            const r = xs(e);
            return i(r.X());
        }))));
    }
    Xr(t) {
        const e = t.store(Us.store);
        return e.hs(((t, n) => {
            const s = ai(n), i = ui(this.Qt, s);
            return e.put(i);
        }));
    }
}

function Gi(t) {
    t.createObjectStore(Qs.store, {
        keyPath: Qs.keyPath
    }).createIndex(Qs.documentTargetsIndex, Qs.documentTargetsKeyPath, {
        unique: !0
    });
    // NOTE: This is unique only because the TargetId is the suffix.
    t.createObjectStore(Us.store, {
        keyPath: Us.keyPath
    }).createIndex(Us.queryTargetsIndexName, Us.queryTargetsKeyPath, {
        unique: !0
    }), t.createObjectStore(Ks.store);
}

const zi = "Failed to obtain exclusive access to the persistence layer. To allow shared access, multi-tab synchronization has to be enabled in all tabs. If you are using `experimentalForceOwningTab:true`, make sure that only one tab has persistence enabled at any given time.";

/**
 * Oldest acceptable age in milliseconds for client metadata before the client
 * is considered inactive and its associated data is garbage collected.
 */
/**
 * An IndexedDB-backed instance of Persistence. Data is stored persistently
 * across sessions.
 *
 * On Web only, the Firestore SDKs support shared access to its persistence
 * layer. This allows multiple browser tabs to read and write to IndexedDb and
 * to synchronize state even without network connectivity. Shared access is
 * currently optional and not enabled unless all clients invoke
 * `enablePersistence()` with `{synchronizeTabs:true}`.
 *
 * In multi-tab mode, if multiple clients are active at the same time, the SDK
 * will designate one client as the “primary client”. An effort is made to pick
 * a visible, network-connected and active client, and this client is
 * responsible for letting other clients know about its presence. The primary
 * client writes a unique client-generated identifier (the client ID) to
 * IndexedDb’s “owner” store every 4 seconds. If the primary client fails to
 * update this entry, another client can acquire the lease and take over as
 * primary.
 *
 * Some persistence operations in the SDK are designated as primary-client only
 * operations. This includes the acknowledgment of mutations and all updates of
 * remote documents. The effects of these operations are written to persistence
 * and then broadcast to other tabs via LocalStorage (see
 * `WebStorageSharedClientState`), which then refresh their state from
 * persistence.
 *
 * Similarly, the primary client listens to notifications sent by secondary
 * clients to discover persistence changes written by secondary clients, such as
 * the addition of new mutations and query targets.
 *
 * If multi-tab is not enabled and another tab already obtained the primary
 * lease, IndexedDbPersistence enters a failed state and all subsequent
 * operations will automatically fail.
 *
 * Additionally, there is an optimization so that when a tab is closed, the
 * primary lease is released immediately (this is especially important to make
 * sure that a refreshed tab is able to immediately re-acquire the primary
 * lease). Unfortunately, IndexedDB cannot be reliably used in window.unload
 * since it is an asynchronous API. So in addition to attempting to give up the
 * lease, the leaseholder writes its client ID to a "zombiedClient" entry in
 * LocalStorage which acts as an indicator that another tab should go ahead and
 * take the primary lease immediately regardless of the current lease timestamp.
 *
 * TODO(b/114226234): Remove `synchronizeTabs` section when multi-tab is no
 * longer optional.
 */
class Hi {
    constructor(
    /**
     * Whether to synchronize the in-memory state of multiple tabs and share
     * access to local persistence.
     */
    t, e, n, s, i, r, o, c, a, 
    /**
     * If set to true, forcefully obtains database access. Existing tabs will
     * no longer be able to access IndexedDB.
     */
    u) {
        if (this.allowTabSynchronization = t, this.persistenceKey = e, this.clientId = n, 
        this.Zr = i, this.window = r, this.document = o, this.eo = a, this.no = u, this.so = null, 
        this.io = !1, this.isPrimary = !1, this.networkEnabled = !0, 
        /** Our window.unload handler, if registered. */
        this.ro = null, this.inForeground = !1, 
        /** Our 'visibilitychange' listener if registered. */
        this.oo = null, 
        /** The client metadata refresh task. */
        this.co = null, 
        /** The last time we garbage collected the client metadata object store. */
        this.ao = Number.NEGATIVE_INFINITY, 
        /** A listener to notify on primary state changes. */
        this.uo = t => Promise.resolve(), !Hi.Kn()) throw new F($.UNIMPLEMENTED, "This platform is either missing IndexedDB or is known to have an incomplete implementation. Offline persistence has been disabled.");
        this.Ti = new Li(this, s), this.ho = e + "main", this.Qt = new ti(c), this.lo = new Ts(this.ho, 11, new ji(this.Qt)), 
        this._o = new Ci(this.Ti, this.Qt), this.Ei = new Ei, this.fo = function(t, e) {
            return new qi(t, e);
        }
        /**
 * Returns the set of documents that have changed since the specified read
 * time.
 */
        // PORTING NOTE: This is only used for multi-tab synchronization.
        (this.Qt, this.Ei), this.wo = new li, this.window && this.window.localStorage ? this.Eo = this.window.localStorage : (this.Eo = null, 
        !1 === u && P("IndexedDbPersistence", "LocalStorage is unavailable. As a result, persistence may not work reliably. In particular enablePersistence() could fail immediately after refreshing the page."));
    }
    /**
     * Attempt to start IndexedDb persistence.
     *
     * @returns Whether persistence was enabled.
     */    start() {
        // NOTE: This is expected to fail sometimes (in the case of another tab
        // already having the persistence lock), so it's the first thing we should
        // do.
        return this.To().then((() => {
            if (!this.isPrimary && !this.allowTabSynchronization) 
            // Fail `start()` if `synchronizeTabs` is disabled and we cannot
            // obtain the primary lease.
            throw new F($.FAILED_PRECONDITION, zi);
            return this.Io(), this.mo(), this.Ao(), this.runTransaction("getHighestListenSequenceNumber", "readonly", (t => this._o.Ki(t)));
        })).then((t => {
            this.so = new Q(t, this.eo);
        })).then((() => {
            this.io = !0;
        })).catch((t => (this.lo && this.lo.close(), Promise.reject(t))));
    }
    /**
     * Registers a listener that gets called when the primary state of the
     * instance changes. Upon registering, this listener is invoked immediately
     * with the current primary state.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Ro(t) {
        return this.uo = async e => {
            if (this.Er) return t(e);
        }, t(this.isPrimary);
    }
    /**
     * Registers a listener that gets called when the database receives a
     * version change event indicating that it has deleted.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Po(t) {
        this.lo.Xn((async e => {
            // Check if an attempt is made to delete IndexedDB.
            null === e.newVersion && await t();
        }));
    }
    /**
     * Adjusts the current network state in the client's metadata, potentially
     * affecting the primary lease.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    yo(t) {
        this.networkEnabled !== t && (this.networkEnabled = t, 
        // Schedule a primary lease refresh for immediate execution. The eventual
        // lease update will be propagated via `primaryStateListener`.
        this.Zr.Ps((async () => {
            this.Er && await this.To();
        })));
    }
    /**
     * Updates the client metadata in IndexedDb and attempts to either obtain or
     * extend the primary lease for the local client. Asynchronously notifies the
     * primary state listener if the client either newly obtained or released its
     * primary lease.
     */    To() {
        return this.runTransaction("updateClientMetadataAndTryBecomePrimary", "readwrite", (t => Yi(t).put(new js(this.clientId, Date.now(), this.networkEnabled, this.inForeground)).next((() => {
            if (this.isPrimary) return this.Vo(t).next((t => {
                t || (this.isPrimary = !1, this.Zr.po((() => this.uo(!1))));
            }));
        })).next((() => this.bo(t))).next((e => this.isPrimary && !e ? this.vo(t).next((() => !1)) : !!e && this.So(t).next((() => !0)))))).catch((t => {
            if (As(t)) 
            // Proceed with the existing state. Any subsequent access to
            // IndexedDB will verify the lease.
            return R("IndexedDbPersistence", "Failed to extend owner lease: ", t), this.isPrimary;
            if (!this.allowTabSynchronization) throw t;
            return R("IndexedDbPersistence", "Releasing owner lease after error during lease refresh", t), 
            /* isPrimary= */ !1;
        })).then((t => {
            this.isPrimary !== t && this.Zr.po((() => this.uo(t))), this.isPrimary = t;
        }));
    }
    Vo(t) {
        return Ji(t).get($s.key).next((t => ws.resolve(this.Do(t))));
    }
    Co(t) {
        return Yi(t).delete(this.clientId);
    }
    /**
     * If the garbage collection threshold has passed, prunes the
     * RemoteDocumentChanges and the ClientMetadata store based on the last update
     * time of all clients.
     */    async xo() {
        if (this.isPrimary && !this.No(this.ao, 18e5)) {
            this.ao = Date.now();
            const t = await this.runTransaction("maybeGarbageCollectMultiClientState", "readwrite-primary", (t => {
                const e = Ys(t, js.store);
                return e.rs().next((t => {
                    const n = this.$o(t, 18e5), s = t.filter((t => -1 === n.indexOf(t)));
                    // Delete metadata for clients that are no longer considered active.
                    return ws.forEach(s, (t => e.delete(t.clientId))).next((() => s));
                }));
            })).catch((() => []));
            // Delete potential leftover entries that may continue to mark the
            // inactive clients as zombied in LocalStorage.
            // Ideally we'd delete the IndexedDb and LocalStorage zombie entries for
            // the client atomically, but we can't. So we opt to delete the IndexedDb
            // entries first to avoid potentially reviving a zombied client.
                        if (this.Eo) for (const e of t) this.Eo.removeItem(this.Fo(e.clientId));
        }
    }
    /**
     * Schedules a recurring timer to update the client metadata and to either
     * extend or acquire the primary lease if the client is eligible.
     */    Ao() {
        this.co = this.Zr.Tr("client_metadata_refresh" /* ClientMetadataRefresh */ , 4e3, (() => this.To().then((() => this.xo())).then((() => this.Ao()))));
    }
    /** Checks whether `client` is the local client. */    Do(t) {
        return !!t && t.ownerId === this.clientId;
    }
    /**
     * Evaluate the state of all active clients and determine whether the local
     * client is or can act as the holder of the primary lease. Returns whether
     * the client is eligible for the lease, but does not actually acquire it.
     * May return 'false' even if there is no active leaseholder and another
     * (foreground) client should become leaseholder instead.
     */    bo(t) {
        if (this.no) return ws.resolve(!0);
        return Ji(t).get($s.key).next((e => {
            // A client is eligible for the primary lease if:
            // - its network is enabled and the client's tab is in the foreground.
            // - its network is enabled and no other client's tab is in the
            //   foreground.
            // - every clients network is disabled and the client's tab is in the
            //   foreground.
            // - every clients network is disabled and no other client's tab is in
            //   the foreground.
            // - the `forceOwningTab` setting was passed in.
            if (null !== e && this.No(e.leaseTimestampMs, 5e3) && !this.Oo(e.ownerId)) {
                if (this.Do(e) && this.networkEnabled) return !0;
                if (!this.Do(e)) {
                    if (!e.allowTabSynchronization) 
                    // Fail the `canActAsPrimary` check if the current leaseholder has
                    // not opted into multi-tab synchronization. If this happens at
                    // client startup, we reject the Promise returned by
                    // `enablePersistence()` and the user can continue to use Firestore
                    // with in-memory persistence.
                    // If this fails during a lease refresh, we will instead block the
                    // AsyncQueue from executing further operations. Note that this is
                    // acceptable since mixing & matching different `synchronizeTabs`
                    // settings is not supported.
                    // TODO(b/114226234): Remove this check when `synchronizeTabs` can
                    // no longer be turned off.
                    throw new F($.FAILED_PRECONDITION, zi);
                    return !1;
                }
            }
            return !(!this.networkEnabled || !this.inForeground) || Yi(t).rs().next((t => void 0 === this.$o(t, 5e3).find((t => {
                if (this.clientId !== t.clientId) {
                    const e = !this.networkEnabled && t.networkEnabled, n = !this.inForeground && t.inForeground, s = this.networkEnabled === t.networkEnabled;
                    if (e || n && s) return !0;
                }
                return !1;
            }))));
        })).next((t => (this.isPrimary !== t && R("IndexedDbPersistence", `Client ${t ? "is" : "is not"} eligible for a primary lease.`), 
        t)));
    }
    async ko() {
        // The shutdown() operations are idempotent and can be called even when
        // start() aborted (e.g. because it couldn't acquire the persistence lease).
        this.io = !1, this.Mo(), this.co && (this.co.cancel(), this.co = null), this.Lo(), 
        this.Bo(), 
        // Use `SimpleDb.runTransaction` directly to avoid failing if another tab
        // has obtained the primary lease.
        await this.lo.runTransaction("shutdown", "readwrite", [ $s.store, js.store ], (t => {
            const e = new Js(t, Q.q);
            return this.vo(e).next((() => this.Co(e)));
        })), this.lo.close(), 
        // Remove the entry marking the client as zombied from LocalStorage since
        // we successfully deleted its metadata from IndexedDb.
        this.qo();
    }
    /**
     * Returns clients that are not zombied and have an updateTime within the
     * provided threshold.
     */    $o(t, e) {
        return t.filter((t => this.No(t.updateTimeMs, e) && !this.Oo(t.clientId)));
    }
    /**
     * Returns the IDs of the clients that are currently active. If multi-tab
     * is not supported, returns an array that only contains the local client's
     * ID.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Uo() {
        return this.runTransaction("getActiveClients", "readonly", (t => Yi(t).rs().next((t => this.$o(t, 18e5).map((t => t.clientId))))));
    }
    get Er() {
        return this.io;
    }
    Qo(t) {
        return Vi.mi(t, this.Qt, this.Ei, this.Ti);
    }
    Sr() {
        return this._o;
    }
    $r() {
        return this.fo;
    }
    Ko() {
        return this.Ei;
    }
    Wo() {
        return this.wo;
    }
    runTransaction(t, e, n) {
        R("IndexedDbPersistence", "Starting transaction:", t);
        const s = "readonly" === e ? "readonly" : "readwrite";
        let i;
        // Do all transactions as readwrite against all object stores, since we
        // are the only reader/writer.
                return this.lo.runTransaction(t, s, Hs, (s => (i = new Js(s, this.so ? this.so.next() : Q.q), 
        "readwrite-primary" === e ? this.Vo(i).next((t => !!t || this.bo(i))).next((e => {
            if (!e) throw P(`Failed to obtain primary lease for action '${t}'.`), this.isPrimary = !1, 
            this.Zr.po((() => this.uo(!1))), new F($.FAILED_PRECONDITION, bs);
            return n(i);
        })).next((t => this.So(i).next((() => t)))) : this.jo(i).next((() => n(i)))))).then((t => (i.Vs(), 
        t)));
    }
    /**
     * Verifies that the current tab is the primary leaseholder or alternatively
     * that the leaseholder has opted into multi-tab synchronization.
     */
    // TODO(b/114226234): Remove this check when `synchronizeTabs` can no longer
    // be turned off.
    jo(t) {
        return Ji(t).get($s.key).next((t => {
            if (null !== t && this.No(t.leaseTimestampMs, 5e3) && !this.Oo(t.ownerId) && !this.Do(t) && !(this.no || this.allowTabSynchronization && t.allowTabSynchronization)) throw new F($.FAILED_PRECONDITION, zi);
        }));
    }
    /**
     * Obtains or extends the new primary lease for the local client. This
     * method does not verify that the client is eligible for this lease.
     */    So(t) {
        const e = new $s(this.clientId, this.allowTabSynchronization, Date.now());
        return Ji(t).put($s.key, e);
    }
    static Kn() {
        return Ts.Kn();
    }
    /** Checks the primary lease and removes it if we are the current primary. */    vo(t) {
        const e = Ji(t);
        return e.get($s.key).next((t => this.Do(t) ? (R("IndexedDbPersistence", "Releasing primary lease."), 
        e.delete($s.key)) : ws.resolve()));
    }
    /** Verifies that `updateTimeMs` is within `maxAgeMs`. */    No(t, e) {
        const n = Date.now();
        return !(t < n - e) && (!(t > n) || (P(`Detected an update time that is in the future: ${t} > ${n}`), 
        !1));
    }
    Io() {
        null !== this.document && "function" == typeof this.document.addEventListener && (this.oo = () => {
            this.Zr.Ps((() => (this.inForeground = "visible" === this.document.visibilityState, 
            this.To())));
        }, this.document.addEventListener("visibilitychange", this.oo), this.inForeground = "visible" === this.document.visibilityState);
    }
    Lo() {
        this.oo && (this.document.removeEventListener("visibilitychange", this.oo), this.oo = null);
    }
    /**
     * Attaches a window.unload handler that will synchronously write our
     * clientId to a "zombie client id" location in LocalStorage. This can be used
     * by tabs trying to acquire the primary lease to determine that the lease
     * is no longer valid even if the timestamp is recent. This is particularly
     * important for the refresh case (so the tab correctly re-acquires the
     * primary lease). LocalStorage is used for this rather than IndexedDb because
     * it is a synchronous API and so can be used reliably from  an unload
     * handler.
     */    mo() {
        var t;
        "function" == typeof (null === (t = this.window) || void 0 === t ? void 0 : t.addEventListener) && (this.ro = () => {
            // Note: In theory, this should be scheduled on the AsyncQueue since it
            // accesses internal state. We execute this code directly during shutdown
            // to make sure it gets a chance to run.
            this.Mo(), this.Zr.Ps((() => this.ko()));
        }, this.window.addEventListener("unload", this.ro));
    }
    Bo() {
        this.ro && (this.window.removeEventListener("unload", this.ro), this.ro = null);
    }
    /**
     * Returns whether a client is "zombied" based on its LocalStorage entry.
     * Clients become zombied when their tab closes without running all of the
     * cleanup logic in `shutdown()`.
     */    Oo(t) {
        var e;
        try {
            const n = null !== (null === (e = this.Eo) || void 0 === e ? void 0 : e.getItem(this.Fo(t)));
            return R("IndexedDbPersistence", `Client '${t}' ${n ? "is" : "is not"} zombied in LocalStorage`), 
            n;
        } catch (t) {
            // Gracefully handle if LocalStorage isn't working.
            return P("IndexedDbPersistence", "Failed to get zombied client id.", t), !1;
        }
    }
    /**
     * Record client as zombied (a client that had its tab closed). Zombied
     * clients are ignored during primary tab selection.
     */    Mo() {
        if (this.Eo) try {
            this.Eo.setItem(this.Fo(this.clientId), String(Date.now()));
        } catch (t) {
            // Gracefully handle if LocalStorage isn't available / working.
            P("Failed to set zombie client id.", t);
        }
    }
    /** Removes the zombied client entry if it exists. */    qo() {
        if (this.Eo) try {
            this.Eo.removeItem(this.Fo(this.clientId));
        } catch (t) {
            // Ignore
        }
    }
    Fo(t) {
        return `firestore_zombie_${this.persistenceKey}_${t}`;
    }
}

/**
 * Helper to get a typed SimpleDbStore for the primary client object store.
 */ function Ji(t) {
    return Ys(t, $s.store);
}

/**
 * Helper to get a typed SimpleDbStore for the client metadata object store.
 */ function Yi(t) {
    return Ys(t, js.store);
}

/**
 * Generates a string used as a prefix when storing data in IndexedDB and
 * LocalStorage.
 */ function Xi(t, e) {
    // Use two different prefix formats:
    //   * firestore / persistenceKey / projectID . databaseID / ...
    //   * firestore / persistenceKey / projectID / ...
    // projectIDs are DNS-compatible names and cannot contain dots
    // so there's no danger of collisions.
    let n = t.projectId;
    return t.o || (n += "." + t.database), "firestore/" + e + "/" + n + "/";
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Zi {
    constructor(t, e) {
        this.progress = t, this.Go = e;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A readonly view of the local state of all documents we're tracking (i.e. we
 * have a cached version in remoteDocumentCache or local mutations for the
 * document). The view is computed by applying the mutations in the
 * MutationQueue to the RemoteDocumentCache.
 */ class tr {
    constructor(t, e, n) {
        this.fo = t, this.zo = e, this.Ei = n;
    }
    /**
     * Get the local view of the document identified by `key`.
     *
     * @returns Local view of the document or null if we don't have any cached
     * state for it.
     */    Ho(t, e) {
        return this.zo.bi(t, e).next((n => this.Jo(t, e, n)));
    }
    /** Internal version of `getDocument` that allows reusing batches. */    Jo(t, e, n) {
        return this.fo._i(t, e).next((t => {
            for (const s of n) t = s.Cs(e, t);
            return t;
        }));
    }
    // Returns the view of the given `docs` as they would appear after applying
    // all mutations in the given `batches`.
    Yo(t, e, n) {
        let s = hn();
        return e.forEach(((t, e) => {
            for (const s of n) e = s.Cs(t, e);
            s = s.zt(t, e);
        })), s;
    }
    /**
     * Gets the local view of the documents identified by `keys`.
     *
     * If we don't have cached state for a document in `keys`, a NoDocument will
     * be stored for that key in the resulting set.
     */    Xo(t, e) {
        return this.fo.getEntries(t, e).next((e => this.Zo(t, e)));
    }
    /**
     * Similar to `getDocuments`, but creates the local view from the given
     * `baseDocs` without retrieving documents from the local store.
     */    Zo(t, e) {
        return this.zo.vi(t, e).next((n => {
            const s = this.Yo(t, e, n);
            let i = un();
            return s.forEach(((t, e) => {
                // TODO(http://b/32275378): Don't conflate missing / deleted.
                e || (e = new St(t, W.min())), i = i.zt(t, e);
            })), i;
        }));
    }
    /**
     * Performs a query against the local view of all documents.
     *
     * @param transaction - The persistence transaction.
     * @param query - The query to match documents against.
     * @param sinceReadTime - If not set to SnapshotVersion.min(), return only
     *     documents that have been read since this snapshot version (exclusive).
     */    Kr(t, e, n) {
        /**
 * Returns whether the query matches a single document by path (rather than a
 * collection).
 */
        return function(t) {
            return J.wt(t.path) && null === t.collectionGroup && 0 === t.filters.length;
        }(e) ? this.tc(t, e.path) : oe(e) ? this.ec(t, e, n) : this.nc(t, e, n);
    }
    tc(t, e) {
        // Just do a simple document lookup.
        return this.Ho(t, new J(e)).next((t => {
            let e = _n();
            return t instanceof vt && (e = e.zt(t.key, t)), e;
        }));
    }
    ec(t, e, n) {
        const s = e.collectionGroup;
        let i = _n();
        return this.Ei.Us(t, s).next((r => ws.forEach(r, (r => {
            const o = function(t, e) {
                return new Zt(e, 
                /*collectionGroup=*/ null, t.xt.slice(), t.filters.slice(), t.limit, t.limitType, t.startAt, t.endAt);
            }
            /**
 * Returns true if this query does not specify any query constraints that
 * could remove results.
 */ (e, r.child(s));
            return this.nc(t, o, n).next((t => {
                t.forEach(((t, e) => {
                    i = i.zt(t, e);
                }));
            }));
        })).next((() => i))));
    }
    nc(t, e, n) {
        // Query the remote documents and overlay mutations.
        let s, i;
        return this.fo.Kr(t, e, n).next((n => (s = n, this.zo.Di(t, e)))).next((e => (i = e, 
        this.sc(t, i, s).next((t => {
            s = t;
            for (const t of i) for (const e of t.mutations) {
                const n = e.key, i = Le(e, s.get(n), t.vs);
                s = i instanceof vt ? s.zt(n, i) : s.remove(n);
            }
        }))))).next((() => (
        // Finally, filter out any documents that don't actually match
        // the query.
        s.forEach(((t, n) => {
            fe(e, n) || (s = s.remove(t));
        })), s)));
    }
    sc(t, e, n) {
        let s = En();
        for (const t of e) for (const e of t.mutations) e instanceof Ke && null === n.get(e.key) && (s = s.add(e.key));
        let i = n;
        return this.fo.getEntries(t, s).next((t => (t.forEach(((t, e) => {
            null !== e && e instanceof vt && (i = i.zt(t, e));
        })), i)));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A set of changes to what documents are currently in view and out of view for
 * a given query. These changes are sent to the LocalStore by the View (via
 * the SyncEngine) and are used to pin / unpin documents as appropriate.
 */ class er {
    constructor(t, e, n, s) {
        this.targetId = t, this.fromCache = e, this.ic = n, this.rc = s;
    }
    static oc(t, e) {
        let n = En(), s = En();
        for (const t of e.docChanges) switch (t.type) {
          case 0 /* Added */ :
            n = n.add(t.doc.key);
            break;

          case 1 /* Removed */ :
            s = s.add(t.doc.key);
 // do nothing
                }
        return new er(t, e.fromCache, n, s);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A query engine that takes advantage of the target document mapping in the
 * QueryCache. Query execution is optimized by only reading the documents that
 * previously matched a query plus any documents that were edited after the
 * query was last listened to.
 *
 * There are some cases when this optimization is not guaranteed to produce
 * the same results as full collection scans. In these cases, query
 * processing falls back to full scans. These cases are:
 *
 * - Limit queries where a document that matched the query previously no longer
 *   matches the query.
 *
 * - Limit queries where a document edit may cause the document to sort below
 *   another document that is in the local cache.
 *
 * - Queries that have never been CURRENT or free of limbo documents.
 */ class nr {
    /** Sets the document view to query against. */
    cc(t) {
        this.ac = t;
    }
    /** Returns all local documents matching the specified query. */    Kr(t, n, s, i) {
        // Queries that match all documents don't benefit from using
        // key-based lookups. It is more efficient to scan all documents in a
        // collection, rather than to perform individual lookups.
        return function(t) {
            return 0 === t.filters.length && null === t.limit && null == t.startAt && null == t.endAt && (0 === t.xt.length || 1 === t.xt.length && t.xt[0].field.ut());
        }(n) || s.isEqual(W.min()) ? this.uc(t, n) : this.ac.Xo(t, i).next((r => {
            const o = this.hc(n, r);
            return (ne(n) || se(n)) && this.lc(n.limitType, o, i, s) ? this.uc(t, n) : (A() <= e.DEBUG && R("QueryEngine", "Re-using previous result from %s to execute query: %s", s.toString(), _e(n)), 
            this.ac.Kr(t, n, s).next((t => (
            // We merge `previousResults` into `updateResults`, since
            // `updateResults` is already a DocumentMap. If a document is
            // contained in both lists, then its contents are the same.
            o.forEach((e => {
                t = t.zt(e.key, e);
            })), t))));
        }));
        // Queries that have never seen a snapshot without limbo free documents
        // should also be run as a full collection scan.
        }
    /** Applies the query filter and sorting to the provided documents.  */    hc(t, e) {
        // Sort the documents and re-apply the query filter since previously
        // matching documents do not necessarily still match the query.
        let n = new on(de(t));
        return e.forEach(((e, s) => {
            s instanceof vt && fe(t, s) && (n = n.add(s));
        })), n;
    }
    /**
     * Determines if a limit query needs to be refilled from cache, making it
     * ineligible for index-free execution.
     *
     * @param sortedPreviousResults - The documents that matched the query when it
     * was last synchronized, sorted by the query's comparator.
     * @param remoteKeys - The document keys that matched the query at the last
     * snapshot.
     * @param limboFreeSnapshotVersion - The version of the snapshot when the
     * query was last synchronized.
     */    lc(t, e, n, s) {
        // The query needs to be refilled if a previously matching document no
        // longer matches.
        if (n.size !== e.size) return !0;
        // Limit queries are not eligible for index-free query execution if there is
        // a potential that an older document from cache now sorts before a document
        // that was previously part of the limit. This, however, can only happen if
        // the document at the edge of the limit goes out of limit.
        // If a document that is not the limit boundary sorts differently,
        // the boundary of the limit itself did not change and documents from cache
        // will continue to be "rejected" by this boundary. Therefore, we can ignore
        // any modifications that don't affect the last document.
                const i = "F" /* First */ === t ? e.last() : e.first();
        return !!i && (i.hasPendingWrites || i.version._(s) > 0);
    }
    uc(t, n) {
        return A() <= e.DEBUG && R("QueryEngine", "Using full collection scan to execute query:", _e(n)), 
        this.ac.Kr(t, n, W.min());
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Implements `LocalStore` interface.
 *
 * Note: some field defined in this class might have public access level, but
 * the class is not exported so they are only accessible from this module.
 * This is useful to implement optional features (like bundles) in free
 * functions, such that they are tree-shakeable.
 */
class sr {
    constructor(
    /** Manages our in-memory or durable persistence. */
    t, e, n, s) {
        this.persistence = t, this._c = e, this.Qt = s, 
        /**
         * Maps a targetID to data about its target.
         *
         * PORTING NOTE: We are using an immutable data structure on Web to make re-runs
         * of `applyRemoteEvent()` idempotent.
         */
        this.fc = new nn(D), 
        /** Maps a target to its targetID. */
        // TODO(wuandy): Evaluate if TargetId can be part of Target.
        this.dc = new Ri((t => Nt(t)), Ft), 
        /**
         * The read time of the last entry processed by `getNewDocumentChanges()`.
         *
         * PORTING NOTE: This is only used for multi-tab synchronization.
         */
        this.wc = W.min(), this.zo = t.Qo(n), this.Ec = t.$r(), this._o = t.Sr(), this.Tc = new tr(this.Ec, this.zo, this.persistence.Ko()), 
        this.wo = t.Wo(), this._c.cc(this.Tc);
    }
    Ir(t) {
        return this.persistence.runTransaction("Collect garbage", "readwrite-primary", (e => t.Vr(e, this.fc)));
    }
}

function ir(
/** Manages our in-memory or durable persistence. */
t, e, n, s) {
    return new sr(t, e, n, s);
}

/**
 * Tells the LocalStore that the currently authenticated user has changed.
 *
 * In response the local store switches the mutation queue to the new user and
 * returns any resulting document changes.
 */
// PORTING NOTE: Android and iOS only return the documents affected by the
// change.
async function rr(t, e) {
    const n = b(t);
    let s = n.zo, i = n.Tc;
    const r = await n.persistence.runTransaction("Handle user change", "readonly", (t => {
        // Swap out the mutation queue, grabbing the pending mutation batches
        // before and after.
        let r;
        return n.zo.pi(t).next((o => (r = o, s = n.persistence.Qo(e), 
        // Recreate our LocalDocumentsView using the new
        // MutationQueue.
        i = new tr(n.Ec, s, n.persistence.Ko()), s.pi(t)))).next((e => {
            const n = [], s = [];
            // Union the old/new changed keys.
            let o = En();
            for (const t of r) {
                n.push(t.batchId);
                for (const e of t.mutations) o = o.add(e.key);
            }
            for (const t of e) {
                s.push(t.batchId);
                for (const e of t.mutations) o = o.add(e.key);
            }
            // Return the set of all (potentially) changed documents and the list
            // of mutation batch IDs that were affected by change.
                        return i.Xo(t, o).next((t => ({
                Ic: t,
                mc: n,
                Ac: s
            })));
        }));
    }));
    return n.zo = s, n.Tc = i, n._c.cc(n.Tc), r;
}

/* Accepts locally generated Mutations and commit them to storage. */
/**
 * Acknowledges the given batch.
 *
 * On the happy path when a batch is acknowledged, the local store will
 *
 *  + remove the batch from the mutation queue;
 *  + apply the changes to the remote document cache;
 *  + recalculate the latency compensated view implied by those changes (there
 *    may be mutations in the queue that affect the documents but haven't been
 *    acknowledged yet); and
 *  + give the changed documents back the sync engine
 *
 * @returns The resulting (modified) documents.
 */
function or(t, e) {
    const n = b(t);
    return n.persistence.runTransaction("Acknowledge batch", "readwrite-primary", (t => {
        const s = e.batch.keys(), i = n.Ec.Nr({
            Wr: !0
        });
        return function(t, e, n, s) {
            const i = n.batch, r = i.keys();
            let o = ws.resolve();
            return r.forEach((t => {
                o = o.next((() => s._i(e, t))).next((e => {
                    let r = e;
                    const o = n.$s.get(t);
                    p(null !== o), (!r || r.version._(o) < 0) && (r = i.Ss(t, r, n), r && 
                    // We use the commitVersion as the readTime rather than the
                    // document's updateTime since the updateTime is not advanced
                    // for updates that do not modify the underlying document.
                    s.ai(r, n.Ns));
                }));
            })), o.next((() => t.zo.Ci(e, i)));
        }
        /** Returns the local view of the documents affected by a mutation batch. */
        // PORTING NOTE: Multi-Tab only.
        (n, t, e, i).next((() => i.apply(t))).next((() => n.zo.$i(t))).next((() => n.Tc.Xo(t, s)));
    }));
}

/**
 * Removes mutations from the MutationQueue for the specified batch;
 * LocalDocuments will be recalculated.
 *
 * @returns The resulting modified documents.
 */
/**
 * Returns the last consistent snapshot processed (used by the RemoteStore to
 * determine whether to buffer incoming snapshots from the backend).
 */
function cr(t) {
    const e = b(t);
    return e.persistence.runTransaction("Get last remote snapshot version", "readonly", (t => e._o.Qi(t)));
}

/**
 * Updates the "ground-state" (remote) documents. We assume that the remote
 * event reflects any write batches that have been acknowledged or rejected
 * (i.e. we do not re-apply local mutations to updates from this event).
 *
 * LocalDocuments are re-calculated if there are remaining mutations in the
 * queue.
 */ function ar(t, e) {
    const n = b(t), s = e.Ot;
    let i = n.fc;
    return n.persistence.runTransaction("Apply remote event", "readwrite-primary", (t => {
        const r = n.Ec.Nr({
            Wr: !0
        });
        // Reset newTargetDataByTargetMap in case this transaction gets re-run.
                i = n.fc;
        const o = [];
        e.$e.forEach(((e, r) => {
            const c = i.get(r);
            if (!c) return;
            // Only update the remote keys if the target is still active. This
            // ensures that we can persist the updated target data along with
            // the updated assignment.
                        o.push(n._o.sr(t, e.Qe, r).next((() => n._o.er(t, e.qe, r))));
            const a = e.resumeToken;
            // Update the resume token if the change includes one.
                        if (a.l() > 0) {
                const u = c.Mt(a, s).kt(t.bs);
                i = i.zt(r, u), 
                // Update the target data if there are target changes (or if
                // sufficient time has passed since the last update).
                /**
 * Returns true if the newTargetData should be persisted during an update of
 * an active target. TargetData should always be persisted when a target is
 * being released and should not call this function.
 *
 * While the target is active, TargetData updates can be omitted when nothing
 * about the target has changed except metadata like the resume token or
 * snapshot version. Occasionally it's worth the extra write to prevent these
 * values from getting too stale after a crash, but this doesn't have to be
 * too frequent.
 */
                function(t, e, n) {
                    // Always persist target data if we don't already have a resume token.
                    if (p(e.resumeToken.l() > 0), 0 === t.resumeToken.l()) return !0;
                    // Don't allow resume token changes to be buffered indefinitely. This
                    // allows us to be reasonably up-to-date after a crash and avoids needing
                    // to loop over all active queries on shutdown. Especially in the browser
                    // we may not get time to do anything interesting while the current tab is
                    // closing.
                                        if (e.Ot.W() - t.Ot.W() >= 3e8) return !0;
                    // Otherwise if the only thing that has changed about a target is its resume
                    // token it's not worth persisting. Note that the RemoteStore keeps an
                    // in-memory view of the currently active targets which includes the current
                    // resume token, so stream failure or user changes will still use an
                    // up-to-date resume token regardless of what we do here.
                                        return n.qe.size + n.Ue.size + n.Qe.size > 0;
                }
                /**
 * Notifies local store of the changed views to locally pin documents.
 */ (c, u, e) && o.push(n._o.Hi(t, u));
            }
        }));
        let c = un();
        // HACK: The only reason we allow a null snapshot version is so that we
        // can synthesize remote events when we get permission denied errors while
        // trying to resolve the state of a locally cached document that is in
        // limbo.
        if (e.Oe.forEach(((s, i) => {
            e.ke.has(s) && o.push(n.persistence.Ti.Fr(t, s));
        })), 
        // Each loop iteration only affects its "own" doc, so it's safe to get all the remote
        // documents in advance in a single call.
        o.push(ur(t, r, e.Oe, s, void 0).next((t => {
            c = t;
        }))), !s.isEqual(W.min())) {
            const e = n._o.Qi(t).next((e => n._o.Wi(t, t.bs, s)));
            o.push(e);
        }
        return ws.Mn(o).next((() => r.apply(t))).next((() => n.Tc.Zo(t, c)));
    })).then((t => (n.fc = i, t)));
}

/**
 * Populates document change buffer with documents from backend or a bundle.
 * Returns the document changes resulting from applying those documents.
 *
 * @param txn - Transaction to use to read existing documents from storage.
 * @param documentBuffer - Document buffer to collect the resulted changes to be
 *        applied to storage.
 * @param documents - Documents to be applied.
 * @param globalVersion - A `SnapshotVersion` representing the read time if all
 *        documents have the same read time.
 * @param documentVersions - A DocumentKey-to-SnapshotVersion map if documents
 *        have their own read time.
 *
 * Note: this function will use `documentVersions` if it is defined;
 * when it is not defined, resorts to `globalVersion`.
 */ function ur(t, e, n, s, 
// TODO(wuandy): We could add `readTime` to MaybeDocument instead to remove
// this parameter.
i) {
    let r = En();
    return n.forEach((t => r = r.add(t))), e.getEntries(t, r).next((t => {
        let r = un();
        return n.forEach(((n, o) => {
            const c = t.get(n), a = (null == i ? void 0 : i.get(n)) || s;
            // Note: The order of the steps below is important, since we want
            // to ensure that rejected limbo resolutions (which fabricate
            // NoDocuments with SnapshotVersion.min()) never add documents to
            // cache.
            o instanceof St && o.version.isEqual(W.min()) ? (
            // NoDocuments with SnapshotVersion.min() are used in manufactured
            // events. We remove these documents from cache since we lost
            // access.
            e.li(n, a), r = r.zt(n, o)) : null == c || o.version._(c.version) > 0 || 0 === o.version._(c.version) && c.hasPendingWrites ? (e.ai(o, a), 
            r = r.zt(n, o)) : R("LocalStore", "Ignoring outdated watch update for ", n, ". Current version:", c.version, " Watch version:", o.version);
        })), r;
    }));
}

/**
 * Gets the mutation batch after the passed in batchId in the mutation queue
 * or null if empty.
 * @param afterBatchId - If provided, the batch to search after.
 * @returns The next mutation or null if there wasn't one.
 */
function hr(t, e) {
    const n = b(t);
    return n.persistence.runTransaction("Get next mutation batch", "readonly", (t => (void 0 === e && (e = -1), 
    n.zo.gi(t, e))));
}

/**
 * Reads the current value of a Document with a given key or null if not
 * found - used for testing.
 */
/**
 * Assigns the given target an internal ID so that its results can be pinned so
 * they don't get GC'd. A target must be allocated in the local store before
 * the store can be used to manage its view.
 *
 * Allocating an already allocated `Target` will return the existing `TargetData`
 * for that `Target`.
 */
function lr(t, e) {
    const n = b(t);
    return n.persistence.runTransaction("Allocate target", "readwrite", (t => {
        let s;
        return n._o.tr(t, e).next((i => i ? (
        // This target has been listened to previously, so reuse the
        // previous targetID.
        // TODO(mcg): freshen last accessed date?
        s = i, ws.resolve(s)) : n._o.Bi(t).next((i => (s = new Ee(e, i, 0 /* Listen */ , t.bs), 
        n._o.ji(t, s).next((() => s)))))));
    })).then((t => {
        // If Multi-Tab is enabled, the existing target data may be newer than
        // the in-memory data
        const s = n.fc.get(t.targetId);
        return (null === s || t.Ot._(s.Ot) > 0) && (n.fc = n.fc.zt(t.targetId, t), n.dc.set(e, t.targetId)), 
        t;
    }));
}

/**
 * Returns the TargetData as seen by the LocalStore, including updates that may
 * have not yet been persisted to the TargetCache.
 */
// Visible for testing.
/**
 * Unpins all the documents associated with the given target. If
 * `keepPersistedTargetData` is set to false and Eager GC enabled, the method
 * directly removes the associated target data from the target cache.
 *
 * Releasing a non-existing `Target` is a no-op.
 */
// PORTING NOTE: `keepPersistedTargetData` is multi-tab only.
async function _r(t, e, n) {
    const s = b(t), i = s.fc.get(e), r = n ? "readwrite" : "readwrite-primary";
    try {
        n || await s.persistence.runTransaction("Release target", r, (t => s.persistence.Ti.removeTarget(t, i)));
    } catch (t) {
        if (!As(t)) throw t;
        // All `releaseTarget` does is record the final metadata state for the
        // target, but we've been recording this periodically during target
        // activity. If we lose this write this could cause a very slight
        // difference in the order of target deletion during GC, but we
        // don't define exact LRU semantics so this is acceptable.
        R("LocalStore", `Failed to update sequence numbers for target ${e}: ${t}`);
    }
    s.fc = s.fc.remove(e), s.dc.delete(i.target);
}

/**
 * Runs the specified query against the local store and returns the results,
 * potentially taking advantage of query data from previous executions (such
 * as the set of remote keys).
 *
 * @param usePreviousResults - Whether results from previous executions can
 * be used to optimize this query execution.
 */ function fr(t, e, n) {
    const s = b(t);
    let i = W.min(), r = En();
    return s.persistence.runTransaction("Execute query", "readonly", (t => function(t, e, n) {
        const s = b(t), i = s.dc.get(n);
        return void 0 !== i ? ws.resolve(s.fc.get(i)) : s._o.tr(e, n);
    }(s, t, ae(e)).next((e => {
        if (e) return i = e.lastLimboFreeSnapshotVersion, s._o.rr(t, e.targetId).next((t => {
            r = t;
        }));
    })).next((() => s._c.Kr(t, e, n ? i : W.min(), n ? r : En()))).next((t => ({
        documents: t,
        Rc: r
    })))));
}

// PORTING NOTE: Multi-Tab only.
function dr(t, e) {
    const n = b(t), s = b(n._o), i = n.fc.get(e);
    return i ? Promise.resolve(i.target) : n.persistence.runTransaction("Get target data", "readonly", (t => s.Sn(t, e).next((t => t ? t.target : null))));
}

/**
 * Returns the set of documents that have been updated since the last call.
 * If this is the first call, returns the set of changes since client
 * initialization. Further invocations will return document that have changed
 * since the prior call.
 */
// PORTING NOTE: Multi-Tab only.
function wr(t) {
    const e = b(t);
    return e.persistence.runTransaction("Get new document changes", "readonly", (t => function(t, e, n) {
        const s = b(t);
        let i = un(), r = si(n);
        const o = Ki(e), c = IDBKeyRange.lowerBound(r, !0);
        return o.hs({
            index: Bs.readTimeIndex,
            range: c
        }, ((t, e) => {
            // Unlike `getEntry()` and others, `getNewDocumentChanges()` parses
            // the documents directly since we want to keep sentinel deletes.
            const n = ei(s.Qt, e);
            i = i.zt(n.key, n), r = e.readTime;
        })).next((() => ({
            Go: i,
            readTime: ii(r)
        })));
    }
    /**
 * Returns the read time of the most recently read document in the cache, or
 * SnapshotVersion.min() if not available.
 */
    // PORTING NOTE: This is only used for multi-tab synchronization.
    (e.Ec, t, e.wc))).then((({Go: t, readTime: n}) => (e.wc = n, t)));
}

/**
 * Reads the newest document change from persistence and moves the internal
 * synchronization marker forward so that calls to `getNewDocumentChanges()`
 * only return changes that happened after client initialization.
 */
// PORTING NOTE: Multi-Tab only.
async function Er(t) {
    const e = b(t);
    return e.persistence.runTransaction("Synchronize last document change read time", "readonly", (t => function(t) {
        const e = Ki(t);
        // If there are no existing entries, we return SnapshotVersion.min().
                let n = W.min();
        return e.hs({
            index: Bs.readTimeIndex,
            reverse: !0
        }, ((t, e, s) => {
            e.readTime && (n = ii(e.readTime)), s.done();
        })).next((() => n));
    }(t))).then((t => {
        e.wc = t;
    }));
}

/**
 * Creates a new target using the given bundle name, which will be used to
 * hold the keys of all documents from the bundle in query-document mappings.
 * This ensures that the loaded documents do not get garbage collected
 * right away.
 */
/**
 * Applies the documents from a bundle to the "ground-state" (remote)
 * documents.
 *
 * LocalDocuments are re-calculated if there are remaining mutations in the
 * queue.
 */
async function Tr(t, e, n, s) {
    const i = b(t);
    let r = En(), o = un(), c = dn();
    for (const t of n) {
        const n = e.Pc(t.metadata.name);
        t.document && (r = r.add(n)), o = o.zt(n, e.yc(t)), c = c.zt(n, e.gc(t.metadata.readTime));
    }
    const a = i.Ec.Nr({
        Wr: !0
    }), u = await lr(i, function(t) {
        // It is OK that the path used for the query is not valid, because this will
        // not be read and queried.
        return ae(ee(G.ot(`__bundle__/docs/${t}`)));
    }(s));
    // Allocates a target to hold all document keys from the bundle, such that
    // they will not get garbage collected right away.
        return i.persistence.runTransaction("Apply bundle documents", "readwrite", (t => ur(t, a, o, W.min(), c).next((e => (a.apply(t), 
    e))).next((e => i._o.Yi(t, u.targetId).next((() => i._o.er(t, r, u.targetId))).next((() => i.Tc.Zo(t, e)))))));
}

/**
 * Returns a promise of a boolean to indicate if the given bundle has already
 * been loaded and the create time is newer than the current loading bundle.
 */
/**
 * Saves the given `NamedQuery` to local persistence.
 */
async function Ir(t, e, n = En()) {
    // Allocate a target for the named query such that it can be resumed
    // from associated read time if users use it to listen.
    // NOTE: this also means if no corresponding target exists, the new target
    // will remain active and will not get collected, unless users happen to
    // unlisten the query somehow.
    const s = await lr(t, ae(hi(e.bundledQuery))), i = b(t);
    return i.persistence.runTransaction("Save named query", "readwrite", (t => {
        const r = kn(e.readTime);
        // Simply save the query itself if it is older than what the SDK already
        // has.
                if (s.Ot._(r) >= 0) return i.wo.Ls(t, e);
        // Update existing target data because the query from the bundle is newer.
                const o = s.Mt(N.T, r);
        return i.fc = i.fc.zt(o.targetId, o), i._o.Hi(t, o).next((() => i._o.Yi(t, s.targetId))).next((() => i._o.er(t, n, s.targetId))).next((() => i.wo.Ls(t, e)));
    }));
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class mr {
    constructor(t) {
        this.Qt = t, this.Vc = new Map, this.bc = new Map;
    }
    Os(t, e) {
        return ws.resolve(this.Vc.get(e));
    }
    ks(t, e) {
        /** Decodes a BundleMetadata proto into a BundleMetadata object. */
        var n;
        return this.Vc.set(e.id, {
            id: (n = e).id,
            version: n.version,
            createTime: kn(n.createTime)
        }), ws.resolve();
    }
    Ms(t, e) {
        return ws.resolve(this.bc.get(e));
    }
    Ls(t, e) {
        return this.bc.set(e.name, function(t) {
            return {
                name: t.name,
                query: hi(t.bundledQuery),
                readTime: kn(t.readTime)
            };
        }(e)), ws.resolve();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A collection of references to a document from some kind of numbered entity
 * (either a target ID or batch ID). As references are added to or removed from
 * the set corresponding events are emitted to a registered garbage collector.
 *
 * Each reference is represented by a DocumentReference object. Each of them
 * contains enough information to uniquely identify the reference. They are all
 * stored primarily in a set sorted by key. A document is considered garbage if
 * there's no references in that set (this can be efficiently checked thanks to
 * sorting by key).
 *
 * ReferenceSet also keeps a secondary set that contains references sorted by
 * IDs. This one is used to efficiently implement removal of all references by
 * some target ID.
 */ class Ar {
    constructor() {
        // A set of outstanding references to a document sorted by key.
        this.vc = new on(Rr.Sc), 
        // A set of outstanding references to a document sorted by target id.
        this.Dc = new on(Rr.Cc);
    }
    /** Returns true if the reference set contains no references. */    et() {
        return this.vc.et();
    }
    /** Adds a reference to the given document key for the given ID. */    nr(t, e) {
        const n = new Rr(t, e);
        this.vc = this.vc.add(n), this.Dc = this.Dc.add(n);
    }
    /** Add references to the given document keys for the given ID. */    xc(t, e) {
        t.forEach((t => this.nr(t, e)));
    }
    /**
     * Removes a reference to the given document key for the given
     * ID.
     */    ir(t, e) {
        this.Nc(new Rr(t, e));
    }
    $c(t, e) {
        t.forEach((t => this.ir(t, e)));
    }
    /**
     * Clears all references with a given ID. Calls removeRef() for each key
     * removed.
     */    Fc(t) {
        const e = new J(new G([])), n = new Rr(e, t), s = new Rr(e, t + 1), i = [];
        return this.Dc.me([ n, s ], (t => {
            this.Nc(t), i.push(t.key);
        })), i;
    }
    Oc() {
        this.vc.forEach((t => this.Nc(t)));
    }
    Nc(t) {
        this.vc = this.vc.delete(t), this.Dc = this.Dc.delete(t);
    }
    kc(t) {
        const e = new J(new G([])), n = new Rr(e, t), s = new Rr(e, t + 1);
        let i = En();
        return this.Dc.me([ n, s ], (t => {
            i = i.add(t.key);
        })), i;
    }
    Fi(t) {
        const e = new Rr(t, 0), n = this.vc.Re(e);
        return null !== n && t.isEqual(n.key);
    }
}

class Rr {
    constructor(t, e) {
        this.key = t, this.Mc = e;
    }
    /** Compare by key then by ID */    static Sc(t, e) {
        return J.H(t.key, e.key) || D(t.Mc, e.Mc);
    }
    /** Compare by ID then by key */    static Cc(t, e) {
        return D(t.Mc, e.Mc) || J.H(t.key, e.key);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Pr {
    constructor(t, e) {
        this.Ei = t, this.Ti = e, 
        /**
         * The set of all mutations that have been sent but not yet been applied to
         * the backend.
         */
        this.zo = [], 
        /** Next value to use when assigning sequential IDs to each mutation batch. */
        this.Lc = 1, 
        /** An ordered mapping between documents and the mutations batch IDs. */
        this.Bc = new on(Rr.Sc);
    }
    Ai(t) {
        return ws.resolve(0 === this.zo.length);
    }
    Ri(t, e, n, s) {
        const i = this.Lc;
        if (this.Lc++, this.zo.length > 0) {
            this.zo[this.zo.length - 1];
        }
        const r = new Xs(i, e, n, s);
        this.zo.push(r);
        // Track references by document key and index collection parents.
        for (const e of s) this.Bc = this.Bc.add(new Rr(e.key, i)), this.Ei.qs(t, e.key.path.X());
        return ws.resolve(r);
    }
    Pi(t, e) {
        return ws.resolve(this.qc(e));
    }
    gi(t, e) {
        const n = e + 1, s = this.Uc(n), i = s < 0 ? 0 : s;
        // The requested batchId may still be out of range so normalize it to the
        // start of the queue.
                return ws.resolve(this.zo.length > i ? this.zo[i] : null);
    }
    Vi() {
        return ws.resolve(0 === this.zo.length ? -1 : this.Lc - 1);
    }
    pi(t) {
        return ws.resolve(this.zo.slice());
    }
    bi(t, e) {
        const n = new Rr(e, 0), s = new Rr(e, Number.POSITIVE_INFINITY), i = [];
        return this.Bc.me([ n, s ], (t => {
            const e = this.qc(t.Mc);
            i.push(e);
        })), ws.resolve(i);
    }
    vi(t, e) {
        let n = new on(D);
        return e.forEach((t => {
            const e = new Rr(t, 0), s = new Rr(t, Number.POSITIVE_INFINITY);
            this.Bc.me([ e, s ], (t => {
                n = n.add(t.Mc);
            }));
        })), ws.resolve(this.Qc(n));
    }
    Di(t, e) {
        // Use the query path as a prefix for testing if a document matches the
        // query.
        const n = e.path, s = n.length + 1;
        // Construct a document reference for actually scanning the index. Unlike
        // the prefix the document key in this reference must have an even number of
        // segments. The empty segment can be used a suffix of the query path
        // because it precedes all other segments in an ordered traversal.
        let i = n;
        J.wt(i) || (i = i.child(""));
        const r = new Rr(new J(i), 0);
        // Find unique batchIDs referenced by all documents potentially matching the
        // query.
                let o = new on(D);
        return this.Bc.Ae((t => {
            const e = t.key.path;
            return !!n.nt(e) && (
            // Rows with document keys more than one segment longer than the query
            // path can't be matches. For example, a query on 'rooms' can't match
            // the document /rooms/abc/messages/xyx.
            // TODO(mcg): we'll need a different scanner when we implement
            // ancestor queries.
            e.length === s && (o = o.add(t.Mc)), !0);
        }), r), ws.resolve(this.Qc(o));
    }
    Qc(t) {
        // Construct an array of matching batches, sorted by batchID to ensure that
        // multiple mutations affecting the same document key are applied in order.
        const e = [];
        return t.forEach((t => {
            const n = this.qc(t);
            null !== n && e.push(n);
        })), e;
    }
    Ci(t, e) {
        p(0 === this.Kc(e.batchId, "removed")), this.zo.shift();
        let n = this.Bc;
        return ws.forEach(e.mutations, (s => {
            const i = new Rr(s.key, e.batchId);
            return n = n.delete(i), this.Ti.Ni(t, s.key);
        })).next((() => {
            this.Bc = n;
        }));
    }
    xi(t) {
        // No-op since the memory mutation queue does not maintain a separate cache.
    }
    Fi(t, e) {
        const n = new Rr(e, 0), s = this.Bc.Re(n);
        return ws.resolve(e.isEqual(s && s.key));
    }
    $i(t) {
        return this.zo.length, ws.resolve();
    }
    /**
     * Finds the index of the given batchId in the mutation queue and asserts that
     * the resulting index is within the bounds of the queue.
     *
     * @param batchId - The batchId to search for
     * @param action - A description of what the caller is doing, phrased in passive
     * form (e.g. "acknowledged" in a routine that acknowledges batches).
     */    Kc(t, e) {
        return this.Uc(t);
    }
    /**
     * Finds the index of the given batchId in the mutation queue. This operation
     * is O(1).
     *
     * @returns The computed index of the batch with the given batchId, based on
     * the state of the queue. Note this index can be negative if the requested
     * batchId has already been remvoed from the queue or past the end of the
     * queue if the batchId is larger than the last added batch.
     */    Uc(t) {
        if (0 === this.zo.length) 
        // As an index this is past the end of the queue
        return 0;
        // Examine the front of the queue to figure out the difference between the
        // batchId and indexes in the array. Note that since the queue is ordered
        // by batchId, if the first batch has a larger batchId then the requested
        // batchId doesn't exist in the queue.
                return t - this.zo[0].batchId;
    }
    /**
     * A version of lookupMutationBatch that doesn't return a promise, this makes
     * other functions that uses this code easier to read and more efficent.
     */    qc(t) {
        const e = this.Uc(t);
        if (e < 0 || e >= this.zo.length) return null;
        return this.zo[e];
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The memory-only RemoteDocumentCache for IndexedDb. To construct, invoke
 * `newMemoryRemoteDocumentCache()`.
 */
class yr {
    /**
     * @param sizer - Used to assess the size of a document. For eager GC, this is
     * expected to just return 0 to avoid unnecessarily doing the work of
     * calculating the size.
     */
    constructor(t, e) {
        this.Ei = t, this.Wc = e, 
        /** Underlying cache of documents and their read times. */
        this.docs = new nn(J.H), 
        /** Size of all cached documents. */
        this.size = 0;
    }
    /**
     * Adds the supplied entry to the cache and updates the cache size as appropriate.
     *
     * All calls of `addEntry`  are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()`.
     */    ai(t, e, n) {
        const s = e.key, i = this.docs.get(s), r = i ? i.size : 0, o = this.Wc(e);
        return this.docs = this.docs.zt(s, {
            hi: e,
            size: o,
            readTime: n
        }), this.size += o - r, this.Ei.qs(t, s.path.X());
    }
    /**
     * Removes the specified entry from the cache and updates the cache size as appropriate.
     *
     * All calls of `removeEntry` are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()`.
     */    li(t) {
        const e = this.docs.get(t);
        e && (this.docs = this.docs.remove(t), this.size -= e.size);
    }
    _i(t, e) {
        const n = this.docs.get(e);
        return ws.resolve(n ? n.hi : null);
    }
    getEntries(t, e) {
        let n = hn();
        return e.forEach((t => {
            const e = this.docs.get(t);
            n = n.zt(t, e ? e.hi : null);
        })), ws.resolve(n);
    }
    Kr(t, e, n) {
        let s = _n();
        // Documents are ordered by key, so we can use a prefix scan to narrow down
        // the documents we need to match the query against.
                const i = new J(e.path.child("")), r = this.docs.ee(i);
        for (;r.ce(); ) {
            const {key: t, value: {hi: i, readTime: o}} = r.oe();
            if (!e.path.nt(t.path)) break;
            o._(n) <= 0 || i instanceof vt && fe(e, i) && (s = s.zt(i.key, i));
        }
        return ws.resolve(s);
    }
    jc(t, e) {
        return ws.forEach(this.docs, (t => e(t)));
    }
    Nr(t) {
        // `trackRemovals` is ignores since the MemoryRemoteDocumentCache keeps
        // a separate changelog and does not need special handling for removals.
        return new gr(this);
    }
    Or(t) {
        return ws.resolve(this.size);
    }
}

/**
 * Creates a new memory-only RemoteDocumentCache.
 *
 * @param indexManager - A class that manages collection group indices.
 * @param sizer - Used to assess the size of a document. For eager GC, this is
 * expected to just return 0 to avoid unnecessarily doing the work of
 * calculating the size.
 */
/**
 * Handles the details of adding and updating documents in the MemoryRemoteDocumentCache.
 */
class gr extends Pi {
    constructor(t) {
        super(), this.jr = t;
    }
    wi(t) {
        const e = [];
        return this.ri.forEach(((n, s) => {
            s && s.hi ? e.push(this.jr.ai(t, s.hi, this.ci(n))) : this.jr.li(n);
        })), ws.Mn(e);
    }
    fi(t, e) {
        return this.jr._i(t, e);
    }
    di(t, e) {
        return this.jr.getEntries(t, e);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Vr {
    constructor(t) {
        this.persistence = t, 
        /**
         * Maps a target to the data about that target
         */
        this.Gc = new Ri((t => Nt(t)), Ft), 
        /** The last received snapshot version. */
        this.lastRemoteSnapshotVersion = W.min(), 
        /** The highest numbered target ID encountered. */
        this.highestTargetId = 0, 
        /** The highest sequence number encountered. */
        this.zc = 0, 
        /**
         * A ordered bidirectional mapping between documents and the remote target
         * IDs.
         */
        this.Hc = new Ar, this.targetCount = 0, this.Jc = Di.Mi();
    }
    In(t, e) {
        return this.Gc.forEach(((t, n) => e(n))), ws.resolve();
    }
    Qi(t) {
        return ws.resolve(this.lastRemoteSnapshotVersion);
    }
    Ki(t) {
        return ws.resolve(this.zc);
    }
    Bi(t) {
        return this.highestTargetId = this.Jc.next(), ws.resolve(this.highestTargetId);
    }
    Wi(t, e, n) {
        return n && (this.lastRemoteSnapshotVersion = n), e > this.zc && (this.zc = e), 
        ws.resolve();
    }
    Gi(t) {
        this.Gc.set(t.target, t);
        const e = t.targetId;
        e > this.highestTargetId && (this.Jc = new Di(e), this.highestTargetId = e), t.sequenceNumber > this.zc && (this.zc = t.sequenceNumber);
    }
    ji(t, e) {
        return this.Gi(e), this.targetCount += 1, ws.resolve();
    }
    Hi(t, e) {
        return this.Gi(e), ws.resolve();
    }
    Ji(t, e) {
        return this.Gc.delete(e.target), this.Hc.Fc(e.targetId), this.targetCount -= 1, 
        ws.resolve();
    }
    Xi(t, e, n) {
        let s = 0;
        const i = [];
        return this.Gc.forEach(((r, o) => {
            o.sequenceNumber <= e && null === n.get(o.targetId) && (this.Gc.delete(r), i.push(this.Yi(t, o.targetId)), 
            s++);
        })), ws.Mn(i).next((() => s));
    }
    Zi(t) {
        return ws.resolve(this.targetCount);
    }
    tr(t, e) {
        const n = this.Gc.get(e) || null;
        return ws.resolve(n);
    }
    er(t, e, n) {
        return this.Hc.xc(e, n), ws.resolve();
    }
    sr(t, e, n) {
        this.Hc.$c(e, n);
        const s = this.persistence.Ti, i = [];
        return s && e.forEach((e => {
            i.push(s.Ni(t, e));
        })), ws.Mn(i);
    }
    Yi(t, e) {
        return this.Hc.Fc(e), ws.resolve();
    }
    rr(t, e) {
        const n = this.Hc.kc(e);
        return ws.resolve(n);
    }
    Fi(t, e) {
        return ws.resolve(this.Hc.Fi(e));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A memory-backed instance of Persistence. Data is stored only in RAM and
 * not persisted across sessions.
 */
class pr {
    /**
     * The constructor accepts a factory for creating a reference delegate. This
     * allows both the delegate and this instance to have strong references to
     * each other without having nullable fields that would then need to be
     * checked or asserted on every access.
     */
    constructor(t, e) {
        this.Yc = {}, this.so = new Q(0), this.io = !1, this.io = !0, this.Ti = t(this), 
        this._o = new Vr(this);
        this.Ei = new di, this.fo = function(t, e) {
            return new yr(t, e);
        }(this.Ei, (t => this.Ti.Xc(t))), this.Qt = new ti(e), this.wo = new mr(this.Qt);
    }
    start() {
        return Promise.resolve();
    }
    ko() {
        // No durable state to ensure is closed on shutdown.
        return this.io = !1, Promise.resolve();
    }
    get Er() {
        return this.io;
    }
    Po() {
        // No op.
    }
    yo() {
        // No op.
    }
    Ko() {
        return this.Ei;
    }
    Qo(t) {
        let e = this.Yc[t.A()];
        return e || (e = new Pr(this.Ei, this.Ti), this.Yc[t.A()] = e), e;
    }
    Sr() {
        return this._o;
    }
    $r() {
        return this.fo;
    }
    Wo() {
        return this.wo;
    }
    runTransaction(t, e, n) {
        R("MemoryPersistence", "Starting transaction:", t);
        const s = new br(this.so.next());
        return this.Ti.Zc(), n(s).next((t => this.Ti.ta(s).next((() => t)))).On().then((t => (s.Vs(), 
        t)));
    }
    ea(t, e) {
        return ws.Ln(Object.values(this.Yc).map((n => () => n.Fi(t, e))));
    }
}

/**
 * Memory persistence is not actually transactional, but future implementations
 * may have transaction-scoped state.
 */ class br extends vs {
    constructor(t) {
        super(), this.bs = t;
    }
}

class vr {
    constructor(t) {
        this.persistence = t, 
        /** Tracks all documents that are active in Query views. */
        this.na = new Ar, 
        /** The list of documents that are potentially GCed after each transaction. */
        this.sa = null;
    }
    static ia(t) {
        return new vr(t);
    }
    get ra() {
        if (this.sa) return this.sa;
        throw V();
    }
    nr(t, e, n) {
        return this.na.nr(n, e), this.ra.delete(n.toString()), ws.resolve();
    }
    ir(t, e, n) {
        return this.na.ir(n, e), this.ra.add(n.toString()), ws.resolve();
    }
    Ni(t, e) {
        return this.ra.add(e.toString()), ws.resolve();
    }
    removeTarget(t, e) {
        this.na.Fc(e.targetId).forEach((t => this.ra.add(t.toString())));
        const n = this.persistence.Sr();
        return n.rr(t, e.targetId).next((t => {
            t.forEach((t => this.ra.add(t.toString())));
        })).next((() => n.Ji(t, e)));
    }
    Zc() {
        this.sa = new Set;
    }
    ta(t) {
        // Remove newly orphaned documents.
        const e = this.persistence.$r().Nr();
        return ws.forEach(this.ra, (n => {
            const s = J._t(n);
            return this.oa(t, s).next((t => {
                t || e.li(s);
            }));
        })).next((() => (this.sa = null, e.apply(t))));
    }
    Fr(t, e) {
        return this.oa(t, e).next((t => {
            t ? this.ra.delete(e.toString()) : this.ra.add(e.toString());
        }));
    }
    Xc(t) {
        // For eager GC, we don't care about the document size, there are no size thresholds.
        return 0;
    }
    oa(t, e) {
        return ws.Ln([ () => ws.resolve(this.na.Fi(e)), () => this.persistence.Sr().Fi(t, e), () => this.persistence.ea(t, e) ]);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// The format of the LocalStorage key that stores the client state is:
//     firestore_clients_<persistence_prefix>_<instance_key>
/** Assembles the key for a client state in WebStorage */
function Sr(t, e) {
    return `firestore_clients_${t}_${e}`;
}

// The format of the WebStorage key that stores the mutation state is:
//     firestore_mutations_<persistence_prefix>_<batch_id>
//     (for unauthenticated users)
// or: firestore_mutations_<persistence_prefix>_<batch_id>_<user_uid>

// 'user_uid' is last to avoid needing to escape '_' characters that it might
// contain.
/** Assembles the key for a mutation batch in WebStorage */
function Dr(t, e, n) {
    let s = `firestore_mutations_${t}_${n}`;
    return e.m() && (s += `_${e.uid}`), s;
}

// The format of the WebStorage key that stores a query target's metadata is:
//     firestore_targets_<persistence_prefix>_<target_id>
/** Assembles the key for a query state in WebStorage */
function Cr(t, e) {
    return `firestore_targets_${t}_${e}`;
}

// The WebStorage prefix that stores the primary tab's online state. The
// format of the key is:
//     firestore_online_state_<persistence_prefix>
/**
 * Holds the state of a mutation batch, including its user ID, batch ID and
 * whether the batch is 'pending', 'acknowledged' or 'rejected'.
 */
// Visible for testing
class xr {
    constructor(t, e, n, s) {
        this.user = t, this.batchId = e, this.state = n, this.error = s;
    }
    /**
     * Parses a MutationMetadata from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static ca(t, e, n) {
        const s = JSON.parse(n);
        let i, r = "object" == typeof s && -1 !== [ "pending", "acknowledged", "rejected" ].indexOf(s.state) && (void 0 === s.error || "object" == typeof s.error);
        return r && s.error && (r = "string" == typeof s.error.message && "string" == typeof s.error.code, 
        r && (i = new F(s.error.code, s.error.message))), r ? new xr(t, e, s.state, i) : (P("SharedClientState", `Failed to parse mutation state for ID '${e}': ${n}`), 
        null);
    }
    aa() {
        const t = {
            state: this.state,
            updateTimeMs: Date.now()
        };
        return this.error && (t.error = {
            code: this.error.code,
            message: this.error.message
        }), JSON.stringify(t);
    }
}

/**
 * Holds the state of a query target, including its target ID and whether the
 * target is 'not-current', 'current' or 'rejected'.
 */
// Visible for testing
class Nr {
    constructor(t, e, n) {
        this.targetId = t, this.state = e, this.error = n;
    }
    /**
     * Parses a QueryTargetMetadata from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static ca(t, e) {
        const n = JSON.parse(e);
        let s, i = "object" == typeof n && -1 !== [ "not-current", "current", "rejected" ].indexOf(n.state) && (void 0 === n.error || "object" == typeof n.error);
        return i && n.error && (i = "string" == typeof n.error.message && "string" == typeof n.error.code, 
        i && (s = new F(n.error.code, n.error.message))), i ? new Nr(t, n.state, s) : (P("SharedClientState", `Failed to parse target state for ID '${t}': ${e}`), 
        null);
    }
    aa() {
        const t = {
            state: this.state,
            updateTimeMs: Date.now()
        };
        return this.error && (t.error = {
            code: this.error.code,
            message: this.error.message
        }), JSON.stringify(t);
    }
}

/**
 * This class represents the immutable ClientState for a client read from
 * WebStorage, containing the list of active query targets.
 */ class $r {
    constructor(t, e) {
        this.clientId = t, this.activeTargetIds = e;
    }
    /**
     * Parses a RemoteClientState from the JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static ca(t, e) {
        const n = JSON.parse(e);
        let s = "object" == typeof n && n.activeTargetIds instanceof Array, i = In();
        for (let t = 0; s && t < n.activeTargetIds.length; ++t) s = ht(n.activeTargetIds[t]), 
        i = i.add(n.activeTargetIds[t]);
        return s ? new $r(t, i) : (P("SharedClientState", `Failed to parse client data for instance '${t}': ${e}`), 
        null);
    }
}

/**
 * This class represents the online state for all clients participating in
 * multi-tab. The online state is only written to by the primary client, and
 * used in secondary clients to update their query views.
 */ class Fr {
    constructor(t, e) {
        this.clientId = t, this.onlineState = e;
    }
    /**
     * Parses a SharedOnlineState from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static ca(t) {
        const e = JSON.parse(t);
        return "object" == typeof e && -1 !== [ "Unknown", "Online", "Offline" ].indexOf(e.onlineState) && "string" == typeof e.clientId ? new Fr(e.clientId, e.onlineState) : (P("SharedClientState", `Failed to parse online state: ${t}`), 
        null);
    }
}

/**
 * Metadata state of the local client. Unlike `RemoteClientState`, this class is
 * mutable and keeps track of all pending mutations, which allows us to
 * update the range of pending mutation batch IDs as new mutations are added or
 * removed.
 *
 * The data in `LocalClientState` is not read from WebStorage and instead
 * updated via its instance methods. The updated state can be serialized via
 * `toWebStorageJSON()`.
 */
// Visible for testing.
class Or {
    constructor() {
        this.activeTargetIds = In();
    }
    ua(t) {
        this.activeTargetIds = this.activeTargetIds.add(t);
    }
    ha(t) {
        this.activeTargetIds = this.activeTargetIds.delete(t);
    }
    /**
     * Converts this entry into a JSON-encoded format we can use for WebStorage.
     * Does not encode `clientId` as it is part of the key in WebStorage.
     */    aa() {
        const t = {
            activeTargetIds: this.activeTargetIds.it(),
            updateTimeMs: Date.now()
        };
        return JSON.stringify(t);
    }
}

/**
 * `WebStorageSharedClientState` uses WebStorage (window.localStorage) as the
 * backing store for the SharedClientState. It keeps track of all active
 * clients and supports modifications of the local client's data.
 */ class kr {
    constructor(t, e, n, s, i) {
        this.window = t, this.Zr = e, this.persistenceKey = n, this.la = s, this._a = null, 
        this.fa = null, this.k = null, this.da = this.wa.bind(this), this.Ea = new nn(D), 
        this.Er = !1, 
        /**
         * Captures WebStorage events that occur before `start()` is called. These
         * events are replayed once `WebStorageSharedClientState` is started.
         */
        this.Ta = [];
        // Escape the special characters mentioned here:
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
        const r = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        this.storage = this.window.localStorage, this.currentUser = i, this.Ia = Sr(this.persistenceKey, this.la), 
        this.ma = 
        /** Assembles the key for the current sequence number. */
        function(t) {
            return `firestore_sequence_number_${t}`;
        }
        /**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (this.persistenceKey), this.Ea = this.Ea.zt(this.la, new Or), this.Aa = new RegExp(`^firestore_clients_${r}_([^_]*)$`), 
        this.Ra = new RegExp(`^firestore_mutations_${r}_(\\d+)(?:_(.*))?$`), this.Pa = new RegExp(`^firestore_targets_${r}_(\\d+)$`), 
        this.ya = 
        /** Assembles the key for the online state of the primary tab. */
        function(t) {
            return `firestore_online_state_${t}`;
        }
        // The WebStorage prefix that plays as a event to indicate the remote documents
        // might have changed due to some secondary tabs loading a bundle.
        // format of the key is:
        //     firestore_bundle_loaded_<persistenceKey>
        (this.persistenceKey), this.ga = function(t) {
            return `firestore_bundle_loaded_${t}`;
        }
        // The WebStorage key prefix for the key that stores the last sequence number allocated. The key
        // looks like 'firestore_sequence_number_<persistence_prefix>'.
        (this.persistenceKey), 
        // Rather than adding the storage observer during start(), we add the
        // storage observer during initialization. This ensures that we collect
        // events before other components populate their initial state (during their
        // respective start() calls). Otherwise, we might for example miss a
        // mutation that is added after LocalStore's start() processed the existing
        // mutations but before we observe WebStorage events.
        this.window.addEventListener("storage", this.da);
    }
    /** Returns 'true' if WebStorage is available in the current environment. */    static Kn(t) {
        return !(!t || !t.localStorage);
    }
    async start() {
        // Retrieve the list of existing clients to backfill the data in
        // SharedClientState.
        const t = await this._a.Uo();
        for (const e of t) {
            if (e === this.la) continue;
            const t = this.getItem(Sr(this.persistenceKey, e));
            if (t) {
                const n = $r.ca(e, t);
                n && (this.Ea = this.Ea.zt(n.clientId, n));
            }
        }
        this.Va();
        // Check if there is an existing online state and call the callback handler
        // if applicable.
        const e = this.storage.getItem(this.ya);
        if (e) {
            const t = this.pa(e);
            t && this.ba(t);
        }
        for (const t of this.Ta) this.wa(t);
        this.Ta = [], 
        // Register a window unload hook to remove the client metadata entry from
        // WebStorage even if `shutdown()` was not called.
        this.window.addEventListener("unload", (() => this.ko())), this.Er = !0;
    }
    B(t) {
        this.setItem(this.ma, JSON.stringify(t));
    }
    va() {
        return this.Sa(this.Ea);
    }
    Da(t) {
        let e = !1;
        return this.Ea.forEach(((n, s) => {
            s.activeTargetIds.has(t) && (e = !0);
        })), e;
    }
    Ca(t) {
        this.xa(t, "pending");
    }
    Na(t, e, n) {
        this.xa(t, e, n), 
        // Once a final mutation result is observed by other clients, they no longer
        // access the mutation's metadata entry. Since WebStorage replays events
        // in order, it is safe to delete the entry right after updating it.
        this.$a(t);
    }
    Fa(t) {
        let e = "not-current";
        // Lookup an existing query state if the target ID was already registered
        // by another tab
                if (this.Da(t)) {
            const n = this.storage.getItem(Cr(this.persistenceKey, t));
            if (n) {
                const s = Nr.ca(t, n);
                s && (e = s.state);
            }
        }
        return this.Oa.ua(t), this.Va(), e;
    }
    ka(t) {
        this.Oa.ha(t), this.Va();
    }
    Ma(t) {
        return this.Oa.activeTargetIds.has(t);
    }
    La(t) {
        this.removeItem(Cr(this.persistenceKey, t));
    }
    Ba(t, e, n) {
        this.qa(t, e, n);
    }
    Ua(t, e, n) {
        e.forEach((t => {
            this.$a(t);
        })), this.currentUser = t, n.forEach((t => {
            this.Ca(t);
        }));
    }
    Qa(t) {
        this.Ka(t);
    }
    Wa() {
        this.ja();
    }
    ko() {
        this.Er && (this.window.removeEventListener("storage", this.da), this.removeItem(this.Ia), 
        this.Er = !1);
    }
    getItem(t) {
        const e = this.storage.getItem(t);
        return R("SharedClientState", "READ", t, e), e;
    }
    setItem(t, e) {
        R("SharedClientState", "SET", t, e), this.storage.setItem(t, e);
    }
    removeItem(t) {
        R("SharedClientState", "REMOVE", t), this.storage.removeItem(t);
    }
    wa(t) {
        // Note: The function is typed to take Event to be interface-compatible with
        // `Window.addEventListener`.
        const e = t;
        if (e.storageArea === this.storage) {
            if (R("SharedClientState", "EVENT", e.key, e.newValue), e.key === this.Ia) return void P("Received WebStorage notification for local change. Another client might have garbage-collected our state");
            this.Zr.po((async () => {
                if (this.Er) {
                    if (null !== e.key) if (this.Aa.test(e.key)) {
                        if (null == e.newValue) {
                            const t = this.Ga(e.key);
                            return this.za(t, null);
                        }
                        {
                            const t = this.Ha(e.key, e.newValue);
                            if (t) return this.za(t.clientId, t);
                        }
                    } else if (this.Ra.test(e.key)) {
                        if (null !== e.newValue) {
                            const t = this.Ja(e.key, e.newValue);
                            if (t) return this.Ya(t);
                        }
                    } else if (this.Pa.test(e.key)) {
                        if (null !== e.newValue) {
                            const t = this.Xa(e.key, e.newValue);
                            if (t) return this.Za(t);
                        }
                    } else if (e.key === this.ya) {
                        if (null !== e.newValue) {
                            const t = this.pa(e.newValue);
                            if (t) return this.ba(t);
                        }
                    } else if (e.key === this.ma) {
                        const t = function(t) {
                            let e = Q.q;
                            if (null != t) try {
                                const n = JSON.parse(t);
                                p("number" == typeof n), e = n;
                            } catch (t) {
                                P("SharedClientState", "Failed to read sequence number from WebStorage", t);
                            }
                            return e;
                        }
                        /**
 * `MemorySharedClientState` is a simple implementation of SharedClientState for
 * clients using memory persistence. The state in this class remains fully
 * isolated and no synchronization is performed.
 */ (e.newValue);
                        t !== Q.q && this.k(t);
                    } else if (e.key === this.ga) return this._a.tu();
                } else this.Ta.push(e);
            }));
        }
    }
    get Oa() {
        return this.Ea.get(this.la);
    }
    Va() {
        this.setItem(this.Ia, this.Oa.aa());
    }
    xa(t, e, n) {
        const s = new xr(this.currentUser, t, e, n), i = Dr(this.persistenceKey, this.currentUser, t);
        this.setItem(i, s.aa());
    }
    $a(t) {
        const e = Dr(this.persistenceKey, this.currentUser, t);
        this.removeItem(e);
    }
    Ka(t) {
        const e = {
            clientId: this.la,
            onlineState: t
        };
        this.storage.setItem(this.ya, JSON.stringify(e));
    }
    qa(t, e, n) {
        const s = Cr(this.persistenceKey, t), i = new Nr(t, e, n);
        this.setItem(s, i.aa());
    }
    ja() {
        this.setItem(this.ga, "value-not-used");
    }
    /**
     * Parses a client state key in WebStorage. Returns null if the key does not
     * match the expected key format.
     */    Ga(t) {
        const e = this.Aa.exec(t);
        return e ? e[1] : null;
    }
    /**
     * Parses a client state in WebStorage. Returns 'null' if the value could not
     * be parsed.
     */    Ha(t, e) {
        const n = this.Ga(t);
        return $r.ca(n, e);
    }
    /**
     * Parses a mutation batch state in WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    Ja(t, e) {
        const n = this.Ra.exec(t), s = Number(n[1]), i = void 0 !== n[2] ? n[2] : null;
        return xr.ca(new k(i), s, e);
    }
    /**
     * Parses a query target state from WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    Xa(t, e) {
        const n = this.Pa.exec(t), s = Number(n[1]);
        return Nr.ca(s, e);
    }
    /**
     * Parses an online state from WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    pa(t) {
        return Fr.ca(t);
    }
    async Ya(t) {
        if (t.user.uid === this.currentUser.uid) return this._a.eu(t.batchId, t.state, t.error);
        R("SharedClientState", `Ignoring mutation for non-active user ${t.user.uid}`);
    }
    Za(t) {
        return this._a.nu(t.targetId, t.state, t.error);
    }
    za(t, e) {
        const n = e ? this.Ea.zt(t, e) : this.Ea.remove(t), s = this.Sa(this.Ea), i = this.Sa(n), r = [], o = [];
        return i.forEach((t => {
            s.has(t) || r.push(t);
        })), s.forEach((t => {
            i.has(t) || o.push(t);
        })), this._a.su(r, o).then((() => {
            this.Ea = n;
        }));
    }
    ba(t) {
        // We check whether the client that wrote this online state is still active
        // by comparing its client ID to the list of clients kept active in
        // IndexedDb. If a client does not update their IndexedDb client state
        // within 5 seconds, it is considered inactive and we don't emit an online
        // state event.
        this.Ea.get(t.clientId) && this.fa(t.onlineState);
    }
    Sa(t) {
        let e = In();
        return t.forEach(((t, n) => {
            e = e.Pe(n.activeTargetIds);
        })), e;
    }
}

class Mr {
    constructor() {
        this.iu = new Or, this.ru = {}, this.fa = null, this.k = null;
    }
    Ca(t) {
        // No op.
    }
    Na(t, e, n) {
        // No op.
    }
    Fa(t) {
        return this.iu.ua(t), this.ru[t] || "not-current";
    }
    Ba(t, e, n) {
        this.ru[t] = e;
    }
    ka(t) {
        this.iu.ha(t);
    }
    Ma(t) {
        return this.iu.activeTargetIds.has(t);
    }
    La(t) {
        delete this.ru[t];
    }
    va() {
        return this.iu.activeTargetIds;
    }
    Da(t) {
        return this.iu.activeTargetIds.has(t);
    }
    start() {
        return this.iu = new Or, Promise.resolve();
    }
    Ua(t, e, n) {
        // No op.
    }
    Qa(t) {
        // No op.
    }
    ko() {}
    B(t) {}
    Wa() {
        // No op.
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Lr {
    ou(t) {
        // No-op.
    }
    ko() {
        // No-op.
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// References to `window` are guarded by BrowserConnectivityMonitor.isAvailable()
/* eslint-disable no-restricted-globals */
/**
 * Browser implementation of ConnectivityMonitor.
 */
class Br {
    constructor() {
        this.cu = () => this.au(), this.uu = () => this.hu(), this.lu = [], this._u();
    }
    ou(t) {
        this.lu.push(t);
    }
    ko() {
        window.removeEventListener("online", this.cu), window.removeEventListener("offline", this.uu);
    }
    _u() {
        window.addEventListener("online", this.cu), window.addEventListener("offline", this.uu);
    }
    au() {
        R("ConnectivityMonitor", "Network connectivity changed: AVAILABLE");
        for (const t of this.lu) t(0 /* AVAILABLE */);
    }
    hu() {
        R("ConnectivityMonitor", "Network connectivity changed: UNAVAILABLE");
        for (const t of this.lu) t(1 /* UNAVAILABLE */);
    }
    // TODO(chenbrian): Consider passing in window either into this component or
    // here for testing via FakeWindow.
    /** Checks that all used attributes of window are available. */
    static Kn() {
        return "undefined" != typeof window && void 0 !== window.addEventListener && void 0 !== window.removeEventListener;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const qr = {
    BatchGetDocuments: "batchGet",
    Commit: "commit",
    RunQuery: "runQuery"
};

/**
 * Maps RPC names to the corresponding REST endpoint name.
 *
 * We use array notation to avoid mangling.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Provides a simple helper class that implements the Stream interface to
 * bridge to other implementations that are streams but do not implement the
 * interface. The stream callbacks are invoked with the callOn... methods.
 */
class Ur {
    constructor(t) {
        this.fu = t.fu, this.du = t.du;
    }
    wu(t) {
        this.Eu = t;
    }
    Tu(t) {
        this.Iu = t;
    }
    onMessage(t) {
        this.mu = t;
    }
    close() {
        this.du();
    }
    send(t) {
        this.fu(t);
    }
    Au() {
        this.Eu();
    }
    Ru(t) {
        this.Iu(t);
    }
    Pu(t) {
        this.mu(t);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Qr extends 
/**
 * Base class for all Rest-based connections to the backend (WebChannel and
 * HTTP).
 */
class {
    constructor(t) {
        this.yu = t, this.t = t.t;
        const e = t.ssl ? "https" : "http";
        this.gu = e + "://" + t.host, this.Vu = "projects/" + this.t.projectId + "/databases/" + this.t.database + "/documents";
    }
    pu(t, e, n, s) {
        const i = this.bu(t, e);
        R("RestConnection", "Sending: ", i, n);
        const r = {};
        return this.vu(r, s), this.Su(t, i, r, n).then((t => (R("RestConnection", "Received: ", t), 
        t)), (e => {
            throw y("RestConnection", `${t} failed with error: `, e, "url: ", i, "request:", n), 
            e;
        }));
    }
    Du(t, e, n, s) {
        // The REST API automatically aggregates all of the streamed results, so we
        // can just use the normal invoke() method.
        return this.pu(t, e, n, s);
    }
    /**
     * Modifies the headers for a request, adding any authorization token if
     * present and any additional headers for the request.
     */    vu(t, e) {
        if (t["X-Goog-Api-Client"] = "gl-js/ fire/8.2.7", 
        // Content-Type: text/plain will avoid preflight requests which might
        // mess with CORS and redirects by proxies. If we add custom headers
        // we will need to change this code to potentially use the $httpOverwrite
        // parameter supported by ESF to avoid triggering preflight requests.
        t["Content-Type"] = "text/plain", e) for (const n in e.g) e.g.hasOwnProperty(n) && (t[n] = e.g[n]);
    }
    bu(t, e) {
        const n = qr[t];
        return `${this.gu}/v1/${e}:${n}`;
    }
} {
    constructor(t) {
        super(t), this.forceLongPolling = t.forceLongPolling, this.i = t.i;
    }
    Su(t, e, n, s) {
        return new Promise(((i, r) => {
            const o = new u;
            o.listenOnce(h.COMPLETE, (() => {
                try {
                    switch (o.getLastErrorCode()) {
                      case l.NO_ERROR:
                        const e = o.getResponseJson();
                        R("Connection", "XHR received:", JSON.stringify(e)), i(e);
                        break;

                      case l.TIMEOUT:
                        R("Connection", 'RPC "' + t + '" timed out'), r(new F($.DEADLINE_EXCEEDED, "Request time out"));
                        break;

                      case l.HTTP_ERROR:
                        const n = o.getStatus();
                        if (R("Connection", 'RPC "' + t + '" failed with status:', n, "response text:", o.getResponseText()), 
                        n > 0) {
                            const t = o.getResponseJson().error;
                            if (t && t.status && t.message) {
                                const e = function(t) {
                                    const e = t.toLowerCase().replace(/_/g, "-");
                                    return Object.values($).indexOf(e) >= 0 ? e : $.UNKNOWN;
                                }(t.status);
                                r(new F(e, t.message));
                            } else r(new F($.UNKNOWN, "Server responded with status " + o.getStatus()));
                        } else 
                        // If we received an HTTP_ERROR but there's no status code,
                        // it's most probably a connection issue
                        r(new F($.UNAVAILABLE, "Connection failed."));
                        break;

                      default:
                        V();
                    }
                } finally {
                    R("Connection", 'RPC "' + t + '" completed.');
                }
            }));
            const c = JSON.stringify(s);
            o.send(e, "POST", c, n, 15);
        }));
    }
    Cu(t, e) {
        const n = [ this.gu, "/", "google.firestore.v1.Firestore", "/", t, "/channel" ], u = _(), h = f(), l = {
            // Required for backend stickiness, routing behavior is based on this
            // parameter.
            httpSessionIdParam: "gsessionid",
            initMessageHeaders: {},
            messageUrlParams: {
                // This param is used to improve routing and project isolation by the
                // backend and must be included in every request.
                database: `projects/${this.t.projectId}/databases/${this.t.database}`
            },
            sendRawJson: !0,
            supportsCrossDomainXhr: !0,
            internalChannelParams: {
                // Override the default timeout (randomized between 10-20 seconds) since
                // a large write batch on a slow internet connection may take a long
                // time to send to the backend. Rather than have WebChannel impose a
                // tight timeout which could lead to infinite timeouts and retries, we
                // set it very large (5-10 minutes) and rely on the browser's builtin
                // timeouts to kick in if the request isn't working.
                forwardChannelRequestTimeoutMs: 6e5
            },
            forceLongPolling: this.forceLongPolling,
            detectBufferingProxy: this.i
        };
        this.vu(l.initMessageHeaders, e), 
        // Sending the custom headers we just added to request.initMessageHeaders
        // (Authorization, etc.) will trigger the browser to make a CORS preflight
        // request because the XHR will no longer meet the criteria for a "simple"
        // CORS request:
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#Simple_requests
        // Therefore to avoid the CORS preflight request (an extra network
        // roundtrip), we use the httpHeadersOverwriteParam option to specify that
        // the headers should instead be encoded into a special "$httpHeaders" query
        // parameter, which is recognized by the webchannel backend. This is
        // formally defined here:
        // https://github.com/google/closure-library/blob/b0e1815b13fb92a46d7c9b3c30de5d6a396a3245/closure/goog/net/rpc/httpcors.js#L32
        // TODO(b/145624756): There is a backend bug where $httpHeaders isn't respected if the request
        // doesn't have an Origin header. So we have to exclude a few browser environments that are
        // known to (sometimes) not include an Origin. See
        // https://github.com/firebase/firebase-js-sdk/issues/1491.
        s() || i() || r() || o() || c() || a() || (l.httpHeadersOverwriteParam = "$httpHeaders");
        const T = n.join("");
        R("Connection", "Creating WebChannel: " + T, l);
        const I = u.createWebChannel(T, l);
        // WebChannel supports sending the first message with the handshake - saving
        // a network round trip. However, it will have to call send in the same
        // JS event loop as open. In order to enforce this, we delay actually
        // opening the WebChannel until send is called. Whether we have called
        // open is tracked with this variable.
                let m = !1, A = !1;
        // A flag to determine whether the stream was closed (by us or through an
        // error/close event) to avoid delivering multiple close events or sending
        // on a closed stream
                const P = new Ur({
            fu: t => {
                A ? R("Connection", "Not sending because WebChannel is closed:", t) : (m || (R("Connection", "Opening WebChannel transport."), 
                I.open(), m = !0), R("Connection", "WebChannel sending:", t), I.send(t));
            },
            du: () => I.close()
        }), g = (t, e, n) => {
            // TODO(dimond): closure typing seems broken because WebChannel does
            // not implement goog.events.Listenable
            t.listen(e, (t => {
                try {
                    n(t);
                } catch (t) {
                    setTimeout((() => {
                        throw t;
                    }), 0);
                }
            }));
        };
        // Closure events are guarded and exceptions are swallowed, so catch any
        // exception and rethrow using a setTimeout so they become visible again.
        // Note that eventually this function could go away if we are confident
        // enough the code is exception free.
                return g(I, d.EventType.OPEN, (() => {
            A || R("Connection", "WebChannel transport opened.");
        })), g(I, d.EventType.CLOSE, (() => {
            A || (A = !0, R("Connection", "WebChannel transport closed"), P.Ru());
        })), g(I, d.EventType.ERROR, (t => {
            A || (A = !0, y("Connection", "WebChannel transport errored:", t), P.Ru(new F($.UNAVAILABLE, "The operation could not be completed")));
        })), g(I, d.EventType.MESSAGE, (t => {
            var e;
            if (!A) {
                const n = t.data[0];
                p(!!n);
                // TODO(b/35143891): There is a bug in One Platform that caused errors
                // (and only errors) to be wrapped in an extra array. To be forward
                // compatible with the bug we need to check either condition. The latter
                // can be removed once the fix has been rolled out.
                // Use any because msgData.error is not typed.
                const s = n, i = s.error || (null === (e = s[0]) || void 0 === e ? void 0 : e.error);
                if (i) {
                    R("Connection", "WebChannel received error:", i);
                    // error.status will be a string like 'OK' or 'NOT_FOUND'.
                    const t = i.status;
                    let e = 
                    /**
 * Maps an error Code from a GRPC status identifier like 'NOT_FOUND'.
 *
 * @returns The Code equivalent to the given status string or undefined if
 *     there is no match.
 */
                    function(t) {
                        // lookup by string
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const e = Xe[t];
                        if (void 0 !== e) return en(e);
                    }(t), n = i.message;
                    void 0 === e && (e = $.INTERNAL, n = "Unknown error status: " + t + " with message " + i.message), 
                    // Mark closed so no further events are propagated
                    A = !0, P.Ru(new F(e, n)), I.close();
                } else R("Connection", "WebChannel received:", n), P.Pu(n);
            }
        })), g(h, w.STAT_EVENT, (t => {
            t.stat === E.PROXY ? R("Connection", "Detected buffering proxy") : t.stat === E.NOPROXY && R("Connection", "Detected no buffering proxy");
        })), setTimeout((() => {
            // Technically we could/should wait for the WebChannel opened event,
            // but because we want to send the first message with the WebChannel
            // handshake we pretend the channel opened here (asynchronously), and
            // then delay the actual open until the first message is sent.
            P.Au();
        }), 0), P;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Initializes the WebChannelConnection for the browser. */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** The Platform's 'window' implementation or null if not available. */
function Kr() {
    // `window` is not always available, e.g. in ReactNative and WebWorkers.
    // eslint-disable-next-line no-restricted-globals
    return "undefined" != typeof window ? window : null;
}

/** The Platform's 'document' implementation or null if not available. */ function Wr() {
    // `document` is not always available, e.g. in ReactNative and WebWorkers.
    // eslint-disable-next-line no-restricted-globals
    return "undefined" != typeof document ? document : null;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function jr(t) {
    return new Nn(t, /* useProto3Json= */ !0);
}

/**
 * An instance of the Platform's 'TextEncoder' implementation.
 */
/**
 * A helper for running delayed tasks following an exponential backoff curve
 * between attempts.
 *
 * Each delay is made up of a "base" delay which follows the exponential
 * backoff curve, and a +/- 50% "jitter" that is calculated and added to the
 * base delay. This prevents clients from accidentally synchronizing their
 * delays causing spikes of load to the backend.
 */
class Gr {
    constructor(
    /**
     * The AsyncQueue to run backoff operations on.
     */
    t, 
    /**
     * The ID to use when scheduling backoff operations on the AsyncQueue.
     */
    e, 
    /**
     * The initial delay (used as the base delay on the first retry attempt).
     * Note that jitter will still be applied, so the actual delay could be as
     * little as 0.5*initialDelayMs.
     */
    n = 1e3
    /**
     * The multiplier to use to determine the extended base delay after each
     * attempt.
     */ , s = 1.5
    /**
     * The maximum base delay after which no further backoff is performed.
     * Note that jitter will still be applied, so the actual delay could be as
     * much as 1.5*maxDelayMs.
     */ , i = 6e4) {
        this.Zr = t, this.fs = e, this.xu = n, this.Nu = s, this.$u = i, this.Fu = 0, this.Ou = null, 
        /** The last backoff attempt, as epoch milliseconds. */
        this.ku = Date.now(), this.reset();
    }
    /**
     * Resets the backoff delay.
     *
     * The very next backoffAndWait() will have no delay. If it is called again
     * (i.e. due to an error), initialDelayMs (plus jitter) will be used, and
     * subsequent ones will increase according to the backoffFactor.
     */    reset() {
        this.Fu = 0;
    }
    /**
     * Resets the backoff delay to the maximum delay (e.g. for use after a
     * RESOURCE_EXHAUSTED error).
     */    Mu() {
        this.Fu = this.$u;
    }
    /**
     * Returns a promise that resolves after currentDelayMs, and increases the
     * delay for any subsequent attempts. If there was a pending backoff operation
     * already, it will be canceled.
     */    Lu(t) {
        // Cancel any pending backoff operation.
        this.cancel();
        // First schedule using the current base (which may be 0 and should be
        // honored as such).
        const e = Math.floor(this.Fu + this.Bu()), n = Math.max(0, Date.now() - this.ku), s = Math.max(0, e - n);
        // Guard against lastAttemptTime being in the future due to a clock change.
                s > 0 && R("ExponentialBackoff", `Backing off for ${s} ms (base delay: ${this.Fu} ms, delay with jitter: ${e} ms, last attempt: ${n} ms ago)`), 
        this.Ou = this.Zr.Tr(this.fs, s, (() => (this.ku = Date.now(), t()))), 
        // Apply backoff factor to determine next delay and ensure it is within
        // bounds.
        this.Fu *= this.Nu, this.Fu < this.xu && (this.Fu = this.xu), this.Fu > this.$u && (this.Fu = this.$u);
    }
    qu() {
        null !== this.Ou && (this.Ou.Rs(), this.Ou = null);
    }
    cancel() {
        null !== this.Ou && (this.Ou.cancel(), this.Ou = null);
    }
    /** Returns a random value in the range [-currentBaseMs/2, currentBaseMs/2] */    Bu() {
        return (Math.random() - .5) * this.Fu;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A PersistentStream is an abstract base class that represents a streaming RPC
 * to the Firestore backend. It's built on top of the connections own support
 * for streaming RPCs, and adds several critical features for our clients:
 *
 *   - Exponential backoff on failure
 *   - Authentication via CredentialsProvider
 *   - Dispatching all callbacks into the shared worker queue
 *   - Closing idle streams after 60 seconds of inactivity
 *
 * Subclasses of PersistentStream implement serialization of models to and
 * from the JSON representation of the protocol buffers for a specific
 * streaming RPC.
 *
 * ## Starting and Stopping
 *
 * Streaming RPCs are stateful and need to be start()ed before messages can
 * be sent and received. The PersistentStream will call the onOpen() function
 * of the listener once the stream is ready to accept requests.
 *
 * Should a start() fail, PersistentStream will call the registered onClose()
 * listener with a FirestoreError indicating what went wrong.
 *
 * A PersistentStream can be started and stopped repeatedly.
 *
 * Generic types:
 *  SendType: The type of the outgoing message of the underlying
 *    connection stream
 *  ReceiveType: The type of the incoming message of the underlying
 *    connection stream
 *  ListenerType: The type of the listener that will be used for callbacks
 */
class zr {
    constructor(t, e, n, s, i, r) {
        this.Zr = t, this.Uu = n, this.Qu = s, this.Ku = i, this.listener = r, this.state = 0 /* Initial */ , 
        /**
         * A close count that's incremented every time the stream is closed; used by
         * getCloseGuardedDispatcher() to invalidate callbacks that happen after
         * close.
         */
        this.Wu = 0, this.ju = null, this.stream = null, this.Gu = new Gr(t, e);
    }
    /**
     * Returns true if start() has been called and no error has occurred. True
     * indicates the stream is open or in the process of opening (which
     * encompasses respecting backoff, getting auth tokens, and starting the
     * actual RPC). Use isOpen() to determine if the stream is open and ready for
     * outbound requests.
     */    zu() {
        return 1 /* Starting */ === this.state || 2 /* Open */ === this.state || 4 /* Backoff */ === this.state;
    }
    /**
     * Returns true if the underlying RPC is open (the onOpen() listener has been
     * called) and the stream is ready for outbound requests.
     */    Hu() {
        return 2 /* Open */ === this.state;
    }
    /**
     * Starts the RPC. Only allowed if isStarted() returns false. The stream is
     * not immediately ready for use: onOpen() will be invoked when the RPC is
     * ready for outbound requests, at which point isOpen() will return true.
     *
     * When start returns, isStarted() will return true.
     */    start() {
        3 /* Error */ !== this.state ? this.auth() : this.Ju();
    }
    /**
     * Stops the RPC. This call is idempotent and allowed regardless of the
     * current isStarted() state.
     *
     * When stop returns, isStarted() and isOpen() will both return false.
     */    async stop() {
        this.zu() && await this.close(0 /* Initial */);
    }
    /**
     * After an error the stream will usually back off on the next attempt to
     * start it. If the error warrants an immediate restart of the stream, the
     * sender can use this to indicate that the receiver should not back off.
     *
     * Each error will call the onClose() listener. That function can decide to
     * inhibit backoff if required.
     */    Yu() {
        this.state = 0 /* Initial */ , this.Gu.reset();
    }
    /**
     * Marks this stream as idle. If no further actions are performed on the
     * stream for one minute, the stream will automatically close itself and
     * notify the stream's onClose() handler with Status.OK. The stream will then
     * be in a !isStarted() state, requiring the caller to start the stream again
     * before further use.
     *
     * Only streams that are in state 'Open' can be marked idle, as all other
     * states imply pending network operations.
     */    Xu() {
        // Starts the idle time if we are in state 'Open' and are not yet already
        // running a timer (in which case the previous idle timeout still applies).
        this.Hu() && null === this.ju && (this.ju = this.Zr.Tr(this.Uu, 6e4, (() => this.Zu())));
    }
    /** Sends a message to the underlying stream. */    th(t) {
        this.eh(), this.stream.send(t);
    }
    /** Called by the idle timer when the stream should close due to inactivity. */    async Zu() {
        if (this.Hu()) 
        // When timing out an idle stream there's no reason to force the stream into backoff when
        // it restarts so set the stream state to Initial instead of Error.
        return this.close(0 /* Initial */);
    }
    /** Marks the stream as active again. */    eh() {
        this.ju && (this.ju.cancel(), this.ju = null);
    }
    /**
     * Closes the stream and cleans up as necessary:
     *
     * * closes the underlying GRPC stream;
     * * calls the onClose handler with the given 'error';
     * * sets internal stream state to 'finalState';
     * * adjusts the backoff timer based on the error
     *
     * A new stream can be opened by calling start().
     *
     * @param finalState - the intended state of the stream after closing.
     * @param error - the error the connection was closed with.
     */    async close(t, e) {
        // Cancel any outstanding timers (they're guaranteed not to execute).
        this.eh(), this.Gu.cancel(), 
        // Invalidates any stream-related callbacks (e.g. from auth or the
        // underlying stream), guaranteeing they won't execute.
        this.Wu++, 3 /* Error */ !== t ? 
        // If this is an intentional close ensure we don't delay our next connection attempt.
        this.Gu.reset() : e && e.code === $.RESOURCE_EXHAUSTED ? (
        // Log the error. (Probably either 'quota exceeded' or 'max queue length reached'.)
        P(e.toString()), P("Using maximum backoff delay to prevent overloading the backend."), 
        this.Gu.Mu()) : e && e.code === $.UNAUTHENTICATED && 
        // "unauthenticated" error means the token was rejected. Try force refreshing it in case it
        // just expired.
        this.Ku.p(), 
        // Clean up the underlying stream because we are no longer interested in events.
        null !== this.stream && (this.nh(), this.stream.close(), this.stream = null), 
        // This state must be assigned before calling onClose() to allow the callback to
        // inhibit backoff or otherwise manipulate the state in its non-started state.
        this.state = t, 
        // Notify the listener that the stream closed.
        await this.listener.Tu(e);
    }
    /**
     * Can be overridden to perform additional cleanup before the stream is closed.
     * Calling super.tearDown() is not required.
     */    nh() {}
    auth() {
        this.state = 1 /* Starting */;
        const t = this.sh(this.Wu), e = this.Wu;
        // TODO(mikelehen): Just use dispatchIfNotClosed, but see TODO below.
                this.Ku.getToken().then((t => {
            // Stream can be stopped while waiting for authentication.
            // TODO(mikelehen): We really should just use dispatchIfNotClosed
            // and let this dispatch onto the queue, but that opened a spec test can
            // of worms that I don't want to deal with in this PR.
            this.Wu === e && 
            // Normally we'd have to schedule the callback on the AsyncQueue.
            // However, the following calls are safe to be called outside the
            // AsyncQueue since they don't chain asynchronous calls
            this.ih(t);
        }), (e => {
            t((() => {
                const t = new F($.UNKNOWN, "Fetching auth token failed: " + e.message);
                return this.rh(t);
            }));
        }));
    }
    ih(t) {
        const e = this.sh(this.Wu);
        this.stream = this.oh(t), this.stream.wu((() => {
            e((() => (this.state = 2 /* Open */ , this.listener.wu())));
        })), this.stream.Tu((t => {
            e((() => this.rh(t)));
        })), this.stream.onMessage((t => {
            e((() => this.onMessage(t)));
        }));
    }
    Ju() {
        this.state = 4 /* Backoff */ , this.Gu.Lu((async () => {
            this.state = 0 /* Initial */ , this.start();
        }));
    }
    // Visible for tests
    rh(t) {
        // In theory the stream could close cleanly, however, in our current model
        // we never expect this to happen because if we stop a stream ourselves,
        // this callback will never be called. To prevent cases where we retry
        // without a backoff accidentally, we set the stream to error in all cases.
        return R("PersistentStream", `close with error: ${t}`), this.stream = null, this.close(3 /* Error */ , t);
    }
    /**
     * Returns a "dispatcher" function that dispatches operations onto the
     * AsyncQueue but only runs them if closeCount remains unchanged. This allows
     * us to turn auth / stream callbacks into no-ops if the stream is closed /
     * re-opened, etc.
     */    sh(t) {
        return e => {
            this.Zr.Ps((() => this.Wu === t ? e() : (R("PersistentStream", "stream callback skipped by getCloseGuardedDispatcher."), 
            Promise.resolve())));
        };
    }
}

/**
 * A PersistentStream that implements the Listen RPC.
 *
 * Once the Listen stream has called the onOpen() listener, any number of
 * listen() and unlisten() calls can be made to control what changes will be
 * sent from the server for ListenResponses.
 */ class Hr extends zr {
    constructor(t, e, n, s, i) {
        super(t, "listen_stream_connection_backoff" /* ListenStreamConnectionBackoff */ , "listen_stream_idle" /* ListenStreamIdle */ , e, n, i), 
        this.Qt = s;
    }
    oh(t) {
        return this.Qu.Cu("Listen", t);
    }
    onMessage(t) {
        // A successful response means the stream is healthy
        this.Gu.reset();
        const e = Hn(this.Qt, t), n = function(t) {
            // We have only reached a consistent snapshot for the entire stream if there
            // is a read_time set and it applies to all targets (i.e. the list of
            // targets is empty). The backend is guaranteed to send such responses.
            if (!("targetChange" in t)) return W.min();
            const e = t.targetChange;
            return e.targetIds && e.targetIds.length ? W.min() : e.readTime ? kn(e.readTime) : W.min();
        }(t);
        return this.listener.ah(e, n);
    }
    /**
     * Registers interest in the results of the given target. If the target
     * includes a resumeToken it will be included in the request. Results that
     * affect the target will be streamed back as WatchChange messages that
     * reference the targetId.
     */    uh(t) {
        const e = {};
        e.database = Kn(this.Qt), e.addTarget = function(t, e) {
            let n;
            const s = e.target;
            return n = Ot(s) ? {
                documents: Zn(t, s)
            } : {
                query: ts(t, s)
            }, n.targetId = e.targetId, e.resumeToken.l() > 0 ? n.resumeToken = Fn(t, e.resumeToken) : e.Ot._(W.min()) > 0 && (
            // TODO(wuandy): Consider removing above check because it is most likely true.
            // Right now, many tests depend on this behaviour though (leaving min() out
            // of serialization).
            n.readTime = $n(t, e.Ot.j())), n;
        }(this.Qt, t);
        const n = ns(this.Qt, t);
        n && (e.labels = n), this.th(e);
    }
    /**
     * Unregisters interest in the results of the target associated with the
     * given targetId.
     */    hh(t) {
        const e = {};
        e.database = Kn(this.Qt), e.removeTarget = t, this.th(e);
    }
}

/**
 * A Stream that implements the Write RPC.
 *
 * The Write RPC requires the caller to maintain special streamToken
 * state in between calls, to help the server understand which responses the
 * client has processed by the time the next request is made. Every response
 * will contain a streamToken; this value must be passed to the next
 * request.
 *
 * After calling start() on this stream, the next request must be a handshake,
 * containing whatever streamToken is on hand. Once a response to this
 * request is received, all pending mutations may be submitted. When
 * submitting multiple batches of mutations at the same time, it's
 * okay to use the same streamToken for the calls to writeMutations.
 *
 * TODO(b/33271235): Use proto types
 */ class Jr extends zr {
    constructor(t, e, n, s, i) {
        super(t, "write_stream_connection_backoff" /* WriteStreamConnectionBackoff */ , "write_stream_idle" /* WriteStreamIdle */ , e, n, i), 
        this.Qt = s, this.lh = !1;
    }
    /**
     * Tracks whether or not a handshake has been successfully exchanged and
     * the stream is ready to accept mutations.
     */    get _h() {
        return this.lh;
    }
    // Override of PersistentStream.start
    start() {
        this.lh = !1, this.lastStreamToken = void 0, super.start();
    }
    nh() {
        this.lh && this.fh([]);
    }
    oh(t) {
        return this.Qu.Cu("Write", t);
    }
    onMessage(t) {
        if (
        // Always capture the last stream token.
        p(!!t.streamToken), this.lastStreamToken = t.streamToken, this.lh) {
            // A successful first write response means the stream is healthy,
            // Note, that we could consider a successful handshake healthy, however,
            // the write itself might be causing an error we want to back off from.
            this.Gu.reset();
            const e = Xn(t.writeResults, t.commitTime), n = kn(t.commitTime);
            return this.listener.dh(n, e);
        }
        // The first response is always the handshake response
        return p(!t.writeResults || 0 === t.writeResults.length), this.lh = !0, this.listener.wh();
    }
    /**
     * Sends an initial streamToken to the server, performing the handshake
     * required to make the StreamingWrite RPC work. Subsequent
     * calls should wait until onHandshakeComplete was called.
     */    Eh() {
        // TODO(dimond): Support stream resumption. We intentionally do not set the
        // stream token on the handshake, ignoring any stream token we might have.
        const t = {};
        t.database = Kn(this.Qt), this.th(t);
    }
    /** Sends a group of mutations to the Firestore backend to apply. */    fh(t) {
        const e = {
            streamToken: this.lastStreamToken,
            writes: t.map((t => Jn(this.Qt, t)))
        };
        this.th(e);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Datastore and its related methods are a wrapper around the external Google
 * Cloud Datastore grpc API, which provides an interface that is more convenient
 * for the rest of the client SDK architecture to consume.
 */
/**
 * An implementation of Datastore that exposes additional state for internal
 * consumption.
 */
class Yr extends class {} {
    constructor(t, e, n) {
        super(), this.credentials = t, this.Qu = e, this.Qt = n, this.Th = !1;
    }
    Ih() {
        if (this.Th) throw new F($.FAILED_PRECONDITION, "The client has already been terminated.");
    }
    /** Gets an auth token and invokes the provided RPC. */    pu(t, e, n) {
        return this.Ih(), this.credentials.getToken().then((s => this.Qu.pu(t, e, n, s))).catch((t => {
            throw t.code === $.UNAUTHENTICATED && this.credentials.p(), t;
        }));
    }
    /** Gets an auth token and invokes the provided RPC with streamed results. */    Du(t, e, n) {
        return this.Ih(), this.credentials.getToken().then((s => this.Qu.Du(t, e, n, s))).catch((t => {
            throw t.code === $.UNAUTHENTICATED && this.credentials.p(), t;
        }));
    }
    terminate() {
        this.Th = !1;
    }
}

// TODO(firestorexp): Make sure there is only one Datastore instance per
// firestore-exp client.
/**
 * A component used by the RemoteStore to track the OnlineState (that is,
 * whether or not the client as a whole should be considered to be online or
 * offline), implementing the appropriate heuristics.
 *
 * In particular, when the client is trying to connect to the backend, we
 * allow up to MAX_WATCH_STREAM_FAILURES within ONLINE_STATE_TIMEOUT_MS for
 * a connection to succeed. If we have too many failures or the timeout elapses,
 * then we set the OnlineState to Offline, and the client will behave as if
 * it is offline (get()s will return cached data, etc.).
 */
class Xr {
    constructor(t, e) {
        this._s = t, this.fa = e, 
        /** The current OnlineState. */
        this.state = "Unknown" /* Unknown */ , 
        /**
         * A count of consecutive failures to open the stream. If it reaches the
         * maximum defined by MAX_WATCH_STREAM_FAILURES, we'll set the OnlineState to
         * Offline.
         */
        this.mh = 0, 
        /**
         * A timer that elapses after ONLINE_STATE_TIMEOUT_MS, at which point we
         * transition from OnlineState.Unknown to OnlineState.Offline without waiting
         * for the stream to actually fail (MAX_WATCH_STREAM_FAILURES times).
         */
        this.Ah = null, 
        /**
         * Whether the client should log a warning message if it fails to connect to
         * the backend (initially true, cleared after a successful stream, or if we've
         * logged the message already).
         */
        this.Rh = !0;
    }
    /**
     * Called by RemoteStore when a watch stream is started (including on each
     * backoff attempt).
     *
     * If this is the first attempt, it sets the OnlineState to Unknown and starts
     * the onlineStateTimer.
     */    Ph() {
        0 === this.mh && (this.yh("Unknown" /* Unknown */), this.Ah = this._s.Tr("online_state_timeout" /* OnlineStateTimeout */ , 1e4, (() => (this.Ah = null, 
        this.gh("Backend didn't respond within 10 seconds."), this.yh("Offline" /* Offline */), 
        Promise.resolve()))));
    }
    /**
     * Updates our OnlineState as appropriate after the watch stream reports a
     * failure. The first failure moves us to the 'Unknown' state. We then may
     * allow multiple failures (based on MAX_WATCH_STREAM_FAILURES) before we
     * actually transition to the 'Offline' state.
     */    Vh(t) {
        "Online" /* Online */ === this.state ? this.yh("Unknown" /* Unknown */) : (this.mh++, 
        this.mh >= 1 && (this.ph(), this.gh(`Connection failed 1 times. Most recent error: ${t.toString()}`), 
        this.yh("Offline" /* Offline */)));
    }
    /**
     * Explicitly sets the OnlineState to the specified state.
     *
     * Note that this resets our timers / failure counters, etc. used by our
     * Offline heuristics, so must not be used in place of
     * handleWatchStreamStart() and handleWatchStreamFailure().
     */    set(t) {
        this.ph(), this.mh = 0, "Online" /* Online */ === t && (
        // We've connected to watch at least once. Don't warn the developer
        // about being offline going forward.
        this.Rh = !1), this.yh(t);
    }
    yh(t) {
        t !== this.state && (this.state = t, this.fa(t));
    }
    gh(t) {
        const e = `Could not reach Cloud Firestore backend. ${t}\nThis typically indicates that your device does not have a healthy Internet connection at the moment. The client will operate in offline mode until it is able to successfully connect to the backend.`;
        this.Rh ? (P(e), this.Rh = !1) : R("OnlineStateTracker", e);
    }
    ph() {
        null !== this.Ah && (this.Ah.cancel(), this.Ah = null);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Zr {
    constructor(
    /**
     * The local store, used to fill the write pipeline with outbound mutations.
     */
    t, 
    /** The client-side proxy for interacting with the backend. */
    e, n, s, i) {
        this.bh = t, this.Sh = e, this._s = n, this.Dh = {}, 
        /**
         * A list of up to MAX_PENDING_WRITES writes that we have fetched from the
         * LocalStore via fillWritePipeline() and have or will send to the write
         * stream.
         *
         * Whenever writePipeline.length > 0 the RemoteStore will attempt to start or
         * restart the write stream. When the stream is established the writes in the
         * pipeline will be sent in order.
         *
         * Writes remain in writePipeline until they are acknowledged by the backend
         * and thus will automatically be re-sent if the stream is interrupted /
         * restarted before they're acknowledged.
         *
         * Write responses from the backend are linked to their originating request
         * purely based on order, and so we can just shift() writes from the front of
         * the writePipeline as we receive responses.
         */
        this.Ch = [], 
        /**
         * A mapping of watched targets that the client cares about tracking and the
         * user has explicitly called a 'listen' for this target.
         *
         * These targets may or may not have been sent to or acknowledged by the
         * server. On re-establishing the listen stream, these targets should be sent
         * to the server. The targets removed with unlistens are removed eagerly
         * without waiting for confirmation from the listen stream.
         */
        this.xh = new Map, 
        /**
         * A set of reasons for why the RemoteStore may be offline. If empty, the
         * RemoteStore may start its network connections.
         */
        this.Nh = new Set, 
        /**
         * Event handlers that get called when the network is disabled or enabled.
         *
         * PORTING NOTE: These functions are used on the Web client to create the
         * underlying streams (to support tree-shakeable streams). On Android and iOS,
         * the streams are created during construction of RemoteStore.
         */
        this.$h = [], this.Fh = i, this.Fh.ou((t => {
            n.Ps((async () => {
                // Porting Note: Unlike iOS, `restartNetwork()` is called even when the
                // network becomes unreachable as we don't have any other way to tear
                // down our streams.
                ao(this) && (R("RemoteStore", "Restarting streams for network reachability change."), 
                await async function(t) {
                    const e = b(t);
                    e.Nh.add(4 /* ConnectivityChange */), await eo(e), e.Oh.set("Unknown" /* Unknown */), 
                    e.Nh.delete(4 /* ConnectivityChange */), await to(e);
                }(this));
            }));
        })), this.Oh = new Xr(n, s);
    }
}

async function to(t) {
    if (ao(t)) for (const e of t.$h) await e(/* enabled= */ !0);
}

/**
 * Temporarily disables the network. The network can be re-enabled using
 * enableNetwork().
 */ async function eo(t) {
    for (const e of t.$h) await e(/* enabled= */ !1);
}

/**
 * Starts new listen for the given target. Uses resume token if provided. It
 * is a no-op if the target of given `TargetData` is already being listened to.
 */
function no(t, e) {
    const n = b(t);
    n.xh.has(e.targetId) || (
    // Mark this as something the client is currently listening for.
    n.xh.set(e.targetId, e), co(n) ? 
    // The listen will be sent in onWatchStreamOpen
    oo(n) : po(n).Hu() && io(n, e));
}

/**
 * Removes the listen from server. It is a no-op if the given target id is
 * not being listened to.
 */ function so(t, e) {
    const n = b(t), s = po(n);
    n.xh.delete(e), s.Hu() && ro(n, e), 0 === n.xh.size && (s.Hu() ? s.Xu() : ao(n) && 
    // Revert to OnlineState.Unknown if the watch stream is not open and we
    // have no listeners, since without any listens to send we cannot
    // confirm if the stream is healthy and upgrade to OnlineState.Online.
    n.Oh.set("Unknown" /* Unknown */));
}

/**
 * We need to increment the the expected number of pending responses we're due
 * from watch so we wait for the ack to process any messages from this target.
 */ function io(t, e) {
    t.kh.on(e.targetId), po(t).uh(e);
}

/**
 * We need to increment the expected number of pending responses we're due
 * from watch so we wait for the removal on the server before we process any
 * messages from this target.
 */ function ro(t, e) {
    t.kh.on(e), po(t).hh(e);
}

function oo(t) {
    t.kh = new vn({
        vn: e => t.Dh.vn(e),
        Sn: e => t.xh.get(e) || null
    }), po(t).start(), t.Oh.Ph();
}

/**
 * Returns whether the watch stream should be started because it's necessary
 * and has not yet been started.
 */ function co(t) {
    return ao(t) && !po(t).zu() && t.xh.size > 0;
}

function ao(t) {
    return 0 === b(t).Nh.size;
}

function uo(t) {
    t.kh = void 0;
}

async function ho(t) {
    t.xh.forEach(((e, n) => {
        io(t, e);
    }));
}

async function lo(t, e) {
    uo(t), 
    // If we still need the watch stream, retry the connection.
    co(t) ? (t.Oh.Vh(e), oo(t)) : 
    // No need to restart watch stream because there are no active targets.
    // The online state is set to unknown because there is no active attempt
    // at establishing a connection
    t.Oh.set("Unknown" /* Unknown */);
}

async function _o(t, e, n) {
    if (
    // Mark the client as online since we got a message from the server
    t.Oh.set("Online" /* Online */), e instanceof pn && 2 /* Removed */ === e.state && e.cause) 
    // There was an error on a target, don't wait for a consistent snapshot
    // to raise events
    try {
        await 
        /** Handles an error on a target */
        async function(t, e) {
            const n = e.cause;
            for (const s of e.targetIds) 
            // A watched target might have been removed already.
            t.xh.has(s) && (await t.Dh.Mh(s, n), t.xh.delete(s), t.kh.removeTarget(s));
        }
        /**
 * Attempts to fill our write pipeline with writes from the LocalStore.
 *
 * Called internally to bootstrap or refill the write pipeline and by
 * SyncEngine whenever there are new mutations to process.
 *
 * Starts the write stream if necessary.
 */ (t, e);
    } catch (n) {
        R("RemoteStore", "Failed to remove targets %s: %s ", e.targetIds.join(","), n), 
        await fo(t, n);
    } else if (e instanceof gn ? t.kh.dn(e) : e instanceof Vn ? t.kh.Pn(e) : t.kh.Tn(e), 
    !n.isEqual(W.min())) try {
        const e = await cr(t.bh);
        n._(e) >= 0 && 
        // We have received a target change with a global snapshot if the snapshot
        // version is not equal to SnapshotVersion.min().
        await 
        /**
 * Takes a batch of changes from the Datastore, repackages them as a
 * RemoteEvent, and passes that on to the listener, which is typically the
 * SyncEngine.
 */
        function(t, e) {
            const n = t.kh.Vn(e);
            // Update in-memory resume tokens. LocalStore will update the
            // persistent view of these when applying the completed RemoteEvent.
                        return n.$e.forEach(((n, s) => {
                if (n.resumeToken.l() > 0) {
                    const i = t.xh.get(s);
                    // A watched target might have been removed already.
                                        i && t.xh.set(s, i.Mt(n.resumeToken, e));
                }
            })), 
            // Re-establish listens for the targets that have been invalidated by
            // existence filter mismatches.
            n.Fe.forEach((e => {
                const n = t.xh.get(e);
                if (!n) 
                // A watched target might have been removed already.
                return;
                // Clear the resume token for the target, since we're in a known mismatch
                // state.
                                t.xh.set(e, n.Mt(N.T, n.Ot)), 
                // Cause a hard reset by unwatching and rewatching immediately, but
                // deliberately don't send a resume token so that we get a full update.
                ro(t, e);
                // Mark the target we send as being on behalf of an existence filter
                // mismatch, but don't actually retain that in listenTargets. This ensures
                // that we flag the first re-listen this way without impacting future
                // listens of this target (that might happen e.g. on reconnect).
                const s = new Ee(n.target, e, 1 /* ExistenceFilterMismatch */ , n.sequenceNumber);
                io(t, s);
            })), t.Dh.Lh(n);
        }(t, n);
    } catch (e) {
        R("RemoteStore", "Failed to raise snapshot:", e), await fo(t, e);
    }
}

/**
 * Recovery logic for IndexedDB errors that takes the network offline until
 * `op` succeeds. Retries are scheduled with backoff using
 * `enqueueRetryable()`. If `op()` is not provided, IndexedDB access is
 * validated via a generic operation.
 *
 * The returned Promise is resolved once the network is disabled and before
 * any retry attempt.
 */ async function fo(t, e, n) {
    if (!As(e)) throw e;
    t.Nh.add(1 /* IndexedDbFailed */), 
    // Disable network and raise offline snapshots
    await eo(t), t.Oh.set("Offline" /* Offline */), n || (
    // Use a simple read operation to determine if IndexedDB recovered.
    // Ideally, we would expose a health check directly on SimpleDb, but
    // RemoteStore only has access to persistence through LocalStore.
    n = () => cr(t.bh)), 
    // Probe IndexedDB periodically and re-enable network
    t._s.po((async () => {
        R("RemoteStore", "Retrying IndexedDB access"), await n(), t.Nh.delete(1 /* IndexedDbFailed */), 
        await to(t);
    }));
}

/**
 * Executes `op`. If `op` fails, takes the network offline until `op`
 * succeeds. Returns after the first attempt.
 */ function wo(t, e) {
    return e().catch((n => fo(t, n, e)));
}

async function Eo(t) {
    const e = b(t), n = bo(e);
    let s = e.Ch.length > 0 ? e.Ch[e.Ch.length - 1].batchId : -1;
    for (;To(e); ) try {
        const t = await hr(e.bh, s);
        if (null === t) {
            0 === e.Ch.length && n.Xu();
            break;
        }
        s = t.batchId, Io(e, t);
    } catch (t) {
        await fo(e, t);
    }
    mo(e) && Ao(e);
}

/**
 * Returns true if we can add to the write pipeline (i.e. the network is
 * enabled and the write pipeline is not full).
 */ function To(t) {
    return ao(t) && t.Ch.length < 10;
}

/**
 * Queues additional writes to be sent to the write stream, sending them
 * immediately if the write stream is established.
 */ function Io(t, e) {
    t.Ch.push(e);
    const n = bo(t);
    n.Hu() && n._h && n.fh(e.mutations);
}

function mo(t) {
    return ao(t) && !bo(t).zu() && t.Ch.length > 0;
}

function Ao(t) {
    bo(t).start();
}

async function Ro(t) {
    bo(t).Eh();
}

async function Po(t) {
    const e = bo(t);
    // Send the write pipeline now that the stream is established.
        for (const n of t.Ch) e.fh(n.mutations);
}

async function yo(t, e, n) {
    const s = t.Ch.shift(), i = Zs.from(s, e, n);
    await wo(t, (() => t.Dh.Bh(i))), 
    // It's possible that with the completion of this mutation another
    // slot has freed up.
    await Eo(t);
}

async function go(t, e) {
    // If the write stream closed after the write handshake completes, a write
    // operation failed and we fail the pending operation.
    e && bo(t)._h && 
    // This error affects the actual write.
    await async function(t, e) {
        // Only handle permanent errors here. If it's transient, just let the retry
        // logic kick in.
        if (n = e.code, tn(n) && n !== $.ABORTED) {
            // This was a permanent error, the request itself was the problem
            // so it's not going to succeed if we resend it.
            const n = t.Ch.shift();
            // In this case it's also unlikely that the server itself is melting
            // down -- this was just a bad request so inhibit backoff on the next
            // restart.
                        bo(t).Yu(), await wo(t, (() => t.Dh.qh(n.batchId, e))), 
            // It's possible that with the completion of this mutation
            // another slot has freed up.
            await Eo(t);
        }
        var n;
    }(t, e), 
    // The write stream might have been started by refilling the write
    // pipeline for failed writes
    mo(t) && Ao(t);
}

/**
 * Toggles the network state when the client gains or loses its primary lease.
 */
async function Vo(t, e) {
    const n = b(t);
    e ? (n.Nh.delete(2 /* IsSecondary */), await to(n)) : e || (n.Nh.add(2 /* IsSecondary */), 
    await eo(n), n.Oh.set("Unknown" /* Unknown */));
}

/**
 * If not yet initialized, registers the WatchStream and its network state
 * callback with `remoteStoreImpl`. Returns the existing stream if one is
 * already available.
 *
 * PORTING NOTE: On iOS and Android, the WatchStream gets registered on startup.
 * This is not done on Web to allow it to be tree-shaken.
 */ function po(t) {
    return t.Uh || (
    // Create stream (but note that it is not started yet).
    t.Uh = function(t, e, n) {
        const s = b(t);
        return s.Ih(), new Hr(e, s.Qu, s.credentials, s.Qt, n);
    }
    /**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t.Sh, t._s, {
        wu: ho.bind(null, t),
        Tu: lo.bind(null, t),
        ah: _o.bind(null, t)
    }), t.$h.push((async e => {
        e ? (t.Uh.Yu(), co(t) ? oo(t) : t.Oh.set("Unknown" /* Unknown */)) : (await t.Uh.stop(), 
        uo(t));
    }))), t.Uh;
}

/**
 * If not yet initialized, registers the WriteStream and its network state
 * callback with `remoteStoreImpl`. Returns the existing stream if one is
 * already available.
 *
 * PORTING NOTE: On iOS and Android, the WriteStream gets registered on startup.
 * This is not done on Web to allow it to be tree-shaken.
 */ function bo(t) {
    return t.Qh || (
    // Create stream (but note that it is not started yet).
    t.Qh = function(t, e, n) {
        const s = b(t);
        return s.Ih(), new Jr(e, s.Qu, s.credentials, s.Qt, n);
    }(t.Sh, t._s, {
        wu: Ro.bind(null, t),
        Tu: go.bind(null, t),
        wh: Po.bind(null, t),
        dh: yo.bind(null, t)
    }), t.$h.push((async e => {
        e ? (t.Qh.Yu(), 
        // This will start the write stream if necessary.
        await Eo(t)) : (await t.Qh.stop(), t.Ch.length > 0 && (R("RemoteStore", `Stopping write stream with ${t.Ch.length} pending writes`), 
        t.Ch = []));
    }))), t.Qh;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Holds the listeners and the last received ViewSnapshot for a query being
 * tracked by EventManager.
 */ class vo {
    constructor() {
        this.Kh = void 0, this.listeners = [];
    }
}

class So {
    constructor() {
        this.queries = new Ri((t => le(t)), he), this.onlineState = "Unknown" /* Unknown */ , 
        this.Wh = new Set;
    }
}

async function Do(t, e) {
    const n = b(t), s = e.query;
    let i = !1, r = n.queries.get(s);
    if (r || (i = !0, r = new vo), i) try {
        r.Kh = await n.jh(s);
    } catch (t) {
        const n = ps(t, `Initialization of query '${_e(e.query)}' failed`);
        return void e.onError(n);
    }
    n.queries.set(s, r), r.listeners.push(e);
    // Run global snapshot listeners if a consistent snapshot has been emitted.
    e.Gh(n.onlineState);
    if (r.Kh) {
        e.zh(r.Kh) && $o(n);
    }
}

async function Co(t, e) {
    const n = b(t), s = e.query;
    let i = !1;
    const r = n.queries.get(s);
    if (r) {
        const t = r.listeners.indexOf(e);
        t >= 0 && (r.listeners.splice(t, 1), i = 0 === r.listeners.length);
    }
    if (i) return n.queries.delete(s), n.Hh(s);
}

function xo(t, e) {
    const n = b(t);
    let s = !1;
    for (const t of e) {
        const e = t.query, i = n.queries.get(e);
        if (i) {
            for (const e of i.listeners) e.zh(t) && (s = !0);
            i.Kh = t;
        }
    }
    s && $o(n);
}

function No(t, e, n) {
    const s = b(t), i = s.queries.get(e);
    if (i) for (const t of i.listeners) t.onError(n);
    // Remove all listeners. NOTE: We don't need to call syncEngine.unlisten()
    // after an error.
        s.queries.delete(e);
}

// Call all global snapshot listeners that have been set.
function $o(t) {
    t.Wh.forEach((t => {
        t.next();
    }));
}

/**
 * QueryListener takes a series of internal view snapshots and determines
 * when to raise the event.
 *
 * It uses an Observer to dispatch events.
 */ class Fo {
    constructor(t, e, n) {
        this.query = t, this.Jh = e, 
        /**
         * Initial snapshots (e.g. from cache) may not be propagated to the wrapped
         * observer. This flag is set to true once we've actually raised an event.
         */
        this.Yh = !1, this.Xh = null, this.onlineState = "Unknown" /* Unknown */ , this.options = n || {};
    }
    /**
     * Applies the new ViewSnapshot to this listener, raising a user-facing event
     * if applicable (depending on what changed, whether the user has opted into
     * metadata-only changes, etc.). Returns true if a user-facing event was
     * indeed raised.
     */    zh(t) {
        if (!this.options.includeMetadataChanges) {
            // Remove the metadata only changes.
            const e = [];
            for (const n of t.docChanges) 3 /* Metadata */ !== n.type && e.push(n);
            t = new Rn(t.query, t.docs, t.Se, e, t.De, t.fromCache, t.Ce, 
            /* excludesMetadataChanges= */ !0);
        }
        let e = !1;
        return this.Yh ? this.Zh(t) && (this.Jh.next(t), e = !0) : this.tl(t, this.onlineState) && (this.el(t), 
        e = !0), this.Xh = t, e;
    }
    onError(t) {
        this.Jh.error(t);
    }
    /** Returns whether a snapshot was raised. */    Gh(t) {
        this.onlineState = t;
        let e = !1;
        return this.Xh && !this.Yh && this.tl(this.Xh, t) && (this.el(this.Xh), e = !0), 
        e;
    }
    tl(t, e) {
        // Always raise the first event when we're synced
        if (!t.fromCache) return !0;
        // NOTE: We consider OnlineState.Unknown as online (it should become Offline
        // or Online if we wait long enough).
                const n = "Offline" /* Offline */ !== e;
        // Don't raise the event if we're online, aren't synced yet (checked
        // above) and are waiting for a sync.
                return (!this.options.nl || !n) && (!t.docs.et() || "Offline" /* Offline */ === e);
        // Raise data from cache if we have any documents or we are offline
        }
    Zh(t) {
        // We don't need to handle includeDocumentMetadataChanges here because
        // the Metadata only changes have already been stripped out if needed.
        // At this point the only changes we will see are the ones we should
        // propagate.
        if (t.docChanges.length > 0) return !0;
        const e = this.Xh && this.Xh.hasPendingWrites !== t.hasPendingWrites;
        return !(!t.Ce && !e) && !0 === this.options.includeMetadataChanges;
        // Generally we should have hit one of the cases above, but it's possible
        // to get here if there were only metadata docChanges and they got
        // stripped out.
        }
    el(t) {
        t = Rn.Ne(t.query, t.docs, t.De, t.fromCache), this.Yh = !0, this.Jh.next(t);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A complete element in the bundle stream, together with the byte length it
 * occupies in the stream.
 */ class Oo {
    constructor(t, 
    // How many bytes this element takes to store in the bundle.
    e) {
        this.payload = t, this.byteLength = e;
    }
    sl() {
        return "metadata" in this.payload;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Helper to convert objects from bundles to model objects in the SDK.
 */ class ko {
    constructor(t) {
        this.Qt = t;
    }
    Pc(t) {
        return qn(this.Qt, t);
    }
    /**
     * Converts a BundleDocument to a MaybeDocument.
     */    yc(t) {
        return t.metadata.exists ? Gn(this.Qt, t.document, !1) : new St(this.Pc(t.metadata.name), this.gc(t.metadata.readTime));
    }
    gc(t) {
        return kn(t);
    }
}

/**
 * A class to process the elements from a bundle, load them into local
 * storage and provide progress update while loading.
 */ class Mo {
    constructor(t, e, n) {
        this.il = t, this.bh = e, this.Qt = n, 
        /** Batched queries to be saved into storage */
        this.queries = [], 
        /** Batched documents to be saved into storage */
        this.documents = [], this.progress = Lo(t);
    }
    /**
     * Adds an element from the bundle to the loader.
     *
     * Returns a new progress if adding the element leads to a new progress,
     * otherwise returns null.
     */    rl(t) {
        this.progress.bytesLoaded += t.byteLength;
        let e = this.progress.documentsLoaded;
        return t.payload.namedQuery ? this.queries.push(t.payload.namedQuery) : t.payload.documentMetadata ? (this.documents.push({
            metadata: t.payload.documentMetadata
        }), t.payload.documentMetadata.exists || ++e) : t.payload.document && (this.documents[this.documents.length - 1].document = t.payload.document, 
        ++e), e !== this.progress.documentsLoaded ? (this.progress.documentsLoaded = e, 
        Object.assign({}, this.progress)) : null;
    }
    ol(t) {
        const e = new Map, n = new ko(this.Qt);
        for (const s of t) if (s.metadata.queries) {
            const t = n.Pc(s.metadata.name);
            for (const n of s.metadata.queries) {
                const s = (e.get(n) || En()).add(t);
                e.set(n, s);
            }
        }
        return e;
    }
    /**
     * Update the progress to 'Success' and return the updated progress.
     */    async complete() {
        const t = await Tr(this.bh, new ko(this.Qt), this.documents, this.il.id), e = this.ol(this.documents);
        for (const t of this.queries) await Ir(this.bh, t, e.get(t.name));
        return this.progress.taskState = "Success", new Zi(Object.assign({}, this.progress), t);
    }
}

/**
 * Returns a `LoadBundleTaskProgress` representing the initial progress of
 * loading a bundle.
 */ function Lo(t) {
    return {
        taskState: "Running",
        documentsLoaded: 0,
        bytesLoaded: 0,
        totalDocuments: t.totalDocuments,
        totalBytes: t.totalBytes
    };
}

/**
 * Returns a `LoadBundleTaskProgress` representing the progress that the loading
 * has succeeded.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Bo {
    constructor(t) {
        this.key = t;
    }
}

class qo {
    constructor(t) {
        this.key = t;
    }
}

/**
 * View is responsible for computing the final merged truth of what docs are in
 * a query. It gets notified of local and remote changes to docs, and applies
 * the query filters and limits to determine the most correct possible results.
 */ class Uo {
    constructor(t, 
    /** Documents included in the remote target */
    e) {
        this.query = t, this.cl = e, this.al = null, 
        /**
         * A flag whether the view is current with the backend. A view is considered
         * current after it has seen the current flag from the backend and did not
         * lose consistency within the watch stream (e.g. because of an existence
         * filter mismatch).
         */
        this.Be = !1, 
        /** Documents in the view but not in the remote target */
        this.ul = En(), 
        /** Document Keys that have local changes */
        this.De = En(), this.hl = de(t), this.ll = new mn(this.hl);
    }
    /**
     * The set of remote documents that the server has told us belongs to the target associated with
     * this view.
     */    get _l() {
        return this.cl;
    }
    /**
     * Iterates over a set of doc changes, applies the query limit, and computes
     * what the new results should be, what the changes were, and whether we may
     * need to go back to the local cache for more results. Does not make any
     * changes to the view.
     * @param docChanges - The doc changes to apply to this view.
     * @param previousChanges - If this is being called with a refill, then start
     *        with this set of docs and changes instead of the current view.
     * @returns a new set of docs, changes, and refill flag.
     */    fl(t, e) {
        const n = e ? e.dl : new An, s = e ? e.ll : this.ll;
        let i = e ? e.De : this.De, r = s, o = !1;
        // Track the last doc in a (full) limit. This is necessary, because some
        // update (a delete, or an update moving a doc past the old limit) might
        // mean there is some other document in the local cache that either should
        // come (1) between the old last limit doc and the new last document, in the
        // case of updates, or (2) after the new last document, in the case of
        // deletes. So we keep this doc at the old limit to compare the updates to.
        // Note that this should never get used in a refill (when previousChanges is
        // set), because there will only be adds -- no deletes or updates.
        const c = ne(this.query) && s.size === this.query.limit ? s.last() : null, a = se(this.query) && s.size === this.query.limit ? s.first() : null;
        // Drop documents out to meet limit/limitToLast requirement.
        if (t.Xt(((t, e) => {
            const u = s.get(t);
            let h = e instanceof vt ? e : null;
            h && (h = fe(this.query, h) ? h : null);
            const l = !!u && this.De.has(u.key), _ = !!h && (h.Vt || 
            // We only consider committed mutations for documents that were
            // mutated during the lifetime of the view.
            this.De.has(h.key) && h.hasCommittedMutations);
            let f = !1;
            // Calculate change
                        if (u && h) {
                u.data().isEqual(h.data()) ? l !== _ && (n.track({
                    type: 3 /* Metadata */ ,
                    doc: h
                }), f = !0) : this.wl(u, h) || (n.track({
                    type: 2 /* Modified */ ,
                    doc: h
                }), f = !0, (c && this.hl(h, c) > 0 || a && this.hl(h, a) < 0) && (
                // This doc moved from inside the limit to outside the limit.
                // That means there may be some other doc in the local cache
                // that should be included instead.
                o = !0));
            } else !u && h ? (n.track({
                type: 0 /* Added */ ,
                doc: h
            }), f = !0) : u && !h && (n.track({
                type: 1 /* Removed */ ,
                doc: u
            }), f = !0, (c || a) && (
            // A doc was removed from a full limit query. We'll need to
            // requery from the local cache to see if we know about some other
            // doc that should be in the results.
            o = !0));
            f && (h ? (r = r.add(h), i = _ ? i.add(t) : i.delete(t)) : (r = r.delete(t), i = i.delete(t)));
        })), ne(this.query) || se(this.query)) for (;r.size > this.query.limit; ) {
            const t = ne(this.query) ? r.last() : r.first();
            r = r.delete(t.key), i = i.delete(t.key), n.track({
                type: 1 /* Removed */ ,
                doc: t
            });
        }
        return {
            ll: r,
            dl: n,
            lc: o,
            De: i
        };
    }
    wl(t, e) {
        // We suppress the initial change event for documents that were modified as
        // part of a write acknowledgment (e.g. when the value of a server transform
        // is applied) as Watch will send us the same document again.
        // By suppressing the event, we only raise two user visible events (one with
        // `hasPendingWrites` and the final state of the document) instead of three
        // (one with `hasPendingWrites`, the modified document with
        // `hasPendingWrites` and the final state of the document).
        return t.Vt && e.hasCommittedMutations && !e.Vt;
    }
    /**
     * Updates the view with the given ViewDocumentChanges and optionally updates
     * limbo docs and sync state from the provided target change.
     * @param docChanges - The set of changes to make to the view's docs.
     * @param updateLimboDocuments - Whether to update limbo documents based on
     *        this change.
     * @param targetChange - A target change to apply for computing limbo docs and
     *        sync state.
     * @returns A new ViewChange with the given docs, changes, and sync state.
     */
    // PORTING NOTE: The iOS/Android clients always compute limbo document changes.
    wi(t, e, n) {
        const s = this.ll;
        this.ll = t.ll, this.De = t.De;
        // Sort changes based on type and query comparator
        const i = t.dl.ve();
        i.sort(((t, e) => function(t, e) {
            const n = t => {
                switch (t) {
                  case 0 /* Added */ :
                    return 1;

                  case 2 /* Modified */ :
                  case 3 /* Metadata */ :
                    // A metadata change is converted to a modified change at the public
                    // api layer.  Since we sort by document key and then change type,
                    // metadata and modified changes must be sorted equivalently.
                    return 2;

                  case 1 /* Removed */ :
                    return 0;

                  default:
                    return V();
                }
            };
            return n(t) - n(e);
        }
        /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t.type, e.type) || this.hl(t.doc, e.doc))), this.El(n);
        const r = e ? this.Tl() : [], o = 0 === this.ul.size && this.Be ? 1 /* Synced */ : 0 /* Local */ , c = o !== this.al;
        if (this.al = o, 0 !== i.length || c) {
            return {
                snapshot: new Rn(this.query, t.ll, s, i, t.De, 0 /* Local */ === o, c, 
                /* excludesMetadataChanges= */ !1),
                Il: r
            };
        }
        // no changes
        return {
            Il: r
        };
    }
    /**
     * Applies an OnlineState change to the view, potentially generating a
     * ViewChange if the view's syncState changes as a result.
     */    Gh(t) {
        return this.Be && "Offline" /* Offline */ === t ? (
        // If we're offline, set `current` to false and then call applyChanges()
        // to refresh our syncState and generate a ViewChange as appropriate. We
        // are guaranteed to get a new TargetChange that sets `current` back to
        // true once the client is back online.
        this.Be = !1, this.wi({
            ll: this.ll,
            dl: new An,
            De: this.De,
            lc: !1
        }, 
        /* updateLimboDocuments= */ !1)) : {
            Il: []
        };
    }
    /**
     * Returns whether the doc for the given key should be in limbo.
     */    ml(t) {
        // If the remote end says it's part of this query, it's not in limbo.
        return !this.cl.has(t) && (
        // The local store doesn't think it's a result, so it shouldn't be in limbo.
        !!this.ll.has(t) && !this.ll.get(t).Vt);
    }
    /**
     * Updates syncedDocuments, current, and limbo docs based on the given change.
     * Returns the list of changes to which docs are in limbo.
     */    El(t) {
        t && (t.qe.forEach((t => this.cl = this.cl.add(t))), t.Ue.forEach((t => {})), t.Qe.forEach((t => this.cl = this.cl.delete(t))), 
        this.Be = t.Be);
    }
    Tl() {
        // We can only determine limbo documents when we're in-sync with the server.
        if (!this.Be) return [];
        // TODO(klimt): Do this incrementally so that it's not quadratic when
        // updating many documents.
                const t = this.ul;
        this.ul = En(), this.ll.forEach((t => {
            this.ml(t.key) && (this.ul = this.ul.add(t.key));
        }));
        // Diff the new limbo docs with the old limbo docs.
        const e = [];
        return t.forEach((t => {
            this.ul.has(t) || e.push(new qo(t));
        })), this.ul.forEach((n => {
            t.has(n) || e.push(new Bo(n));
        })), e;
    }
    /**
     * Update the in-memory state of the current view with the state read from
     * persistence.
     *
     * We update the query view whenever a client's primary status changes:
     * - When a client transitions from primary to secondary, it can miss
     *   LocalStorage updates and its query views may temporarily not be
     *   synchronized with the state on disk.
     * - For secondary to primary transitions, the client needs to update the list
     *   of `syncedDocuments` since secondary clients update their query views
     *   based purely on synthesized RemoteEvents.
     *
     * @param queryResult.documents - The documents that match the query according
     * to the LocalStore.
     * @param queryResult.remoteKeys - The keys of the documents that match the
     * query according to the backend.
     *
     * @returns The ViewChange that resulted from this synchronization.
     */
    // PORTING NOTE: Multi-tab only.
    Al(t) {
        this.cl = t.Rc, this.ul = En();
        const e = this.fl(t.documents);
        return this.wi(e, /*updateLimboDocuments=*/ !0);
    }
    /**
     * Returns a view snapshot as if this query was just listened to. Contains
     * a document add for every existing document and the `fromCache` and
     * `hasPendingWrites` status of the already established view.
     */
    // PORTING NOTE: Multi-tab only.
    Rl() {
        return Rn.Ne(this.query, this.ll, this.De, 0 /* Local */ === this.al);
    }
}

/**
 * QueryView contains all of the data that SyncEngine needs to keep track of for
 * a particular query.
 */
class Qo {
    constructor(
    /**
     * The query itself.
     */
    t, 
    /**
     * The target number created by the client that is used in the watch
     * stream to identify this query.
     */
    e, 
    /**
     * The view is responsible for computing the final merged truth of what
     * docs are in the query. It gets notified of local and remote changes,
     * and applies the query filters and limits to determine the most correct
     * possible results.
     */
    n) {
        this.query = t, this.targetId = e, this.view = n;
    }
}

/** Tracks a limbo resolution. */ class Ko {
    constructor(t) {
        this.key = t, 
        /**
         * Set to true once we've received a document. This is used in
         * getRemoteKeysForTarget() and ultimately used by WatchChangeAggregator to
         * decide whether it needs to manufacture a delete event for the target once
         * the target is CURRENT.
         */
        this.Pl = !1;
    }
}

/**
 * An implementation of `SyncEngine` coordinating with other parts of SDK.
 *
 * The parts of SyncEngine that act as a callback to RemoteStore need to be
 * registered individually. This is done in `syncEngineWrite()` and
 * `syncEngineListen()` (as well as `applyPrimaryState()`) as these methods
 * serve as entry points to RemoteStore's functionality.
 *
 * Note: some field defined in this class might have public access level, but
 * the class is not exported so they are only accessible from this module.
 * This is useful to implement optional features (like bundles) in free
 * functions, such that they are tree-shakeable.
 */ class Wo {
    constructor(t, e, n, 
    // PORTING NOTE: Manages state synchronization in multi-tab environments.
    s, i, r) {
        this.bh = t, this.yl = e, this.gl = n, this.Vl = s, this.currentUser = i, this.pl = r, 
        this.bl = {}, this.vl = new Ri((t => le(t)), he), this.Sl = new Map, 
        /**
         * The keys of documents that are in limbo for which we haven't yet started a
         * limbo resolution query. The strings in this set are the result of calling
         * `key.path.canonicalString()` where `key` is a `DocumentKey` object.
         *
         * The `Set` type was chosen because it provides efficient lookup and removal
         * of arbitrary elements and it also maintains insertion order, providing the
         * desired queue-like FIFO semantics.
         */
        this.Dl = new Set, 
        /**
         * Keeps track of the target ID for each document that is in limbo with an
         * active target.
         */
        this.Cl = new nn(J.H), 
        /**
         * Keeps track of the information about an active limbo resolution for each
         * active target ID that was started for the purpose of limbo resolution.
         */
        this.xl = new Map, this.Nl = new Ar, 
        /** Stores user completion handlers, indexed by User and BatchId. */
        this.$l = {}, 
        /** Stores user callbacks waiting for all pending writes to be acknowledged. */
        this.Fl = new Map, this.Ol = Di.Li(), this.onlineState = "Unknown" /* Unknown */ , 
        // The primary state is set to `true` or `false` immediately after Firestore
        // startup. In the interim, a client should only be considered primary if
        // `isPrimary` is true.
        this.kl = void 0;
    }
    get Ml() {
        return !0 === this.kl;
    }
}

/**
 * Initiates the new listen, resolves promise when listen enqueued to the
 * server. All the subsequent view snapshots or errors are sent to the
 * subscribed handlers. Returns the initial snapshot.
 */
async function jo(t, e) {
    const n = Rc(t);
    let s, i;
    const r = n.vl.get(e);
    if (r) 
    // PORTING NOTE: With Multi-Tab Web, it is possible that a query view
    // already exists when EventManager calls us for the first time. This
    // happens when the primary tab is already listening to this query on
    // behalf of another tab and the user of the primary also starts listening
    // to the query. EventManager will not have an assigned target ID in this
    // case and calls `listen` to obtain this ID.
    s = r.targetId, n.Vl.Fa(s), i = r.view.Rl(); else {
        const t = await lr(n.bh, ae(e)), r = n.Vl.Fa(t.targetId);
        s = t.targetId, i = await Go(n, e, s, "current" === r), n.Ml && no(n.yl, t);
    }
    return i;
}

/**
 * Registers a view for a previously unknown query and computes its initial
 * snapshot.
 */ async function Go(t, e, n, s) {
    // PORTING NOTE: On Web only, we inject the code that registers new Limbo
    // targets based on view changes. This allows us to only depend on Limbo
    // changes when user code includes queries.
    t.Ll = (e, n, s) => async function(t, e, n, s) {
        let i = e.view.fl(n);
        i.lc && (
        // The query has a limit and some docs were removed, so we need
        // to re-run the query against the local store to make sure we
        // didn't lose any good docs that had been past the limit.
        i = await fr(t.bh, e.query, 
        /* usePreviousResults= */ !1).then((({documents: t}) => e.view.fl(t, i))));
        const r = s && s.$e.get(e.targetId), o = e.view.wi(i, 
        /* updateLimboDocuments= */ t.Ml, r);
        return oc(t, e.targetId, o.Il), o.snapshot;
    }(t, e, n, s);
    const i = await fr(t.bh, e, 
    /* usePreviousResults= */ !0), r = new Uo(e, i.Rc), o = r.fl(i.documents), c = yn.Le(n, s && "Offline" /* Offline */ !== t.onlineState), a = r.wi(o, 
    /* updateLimboDocuments= */ t.Ml, c);
    oc(t, n, a.Il);
    const u = new Qo(e, n, r);
    return t.vl.set(e, u), t.Sl.has(n) ? t.Sl.get(n).push(e) : t.Sl.set(n, [ e ]), a.snapshot;
}

/** Stops listening to the query. */ async function zo(t, e) {
    const n = b(t), s = n.vl.get(e), i = n.Sl.get(s.targetId);
    if (i.length > 1) return n.Sl.set(s.targetId, i.filter((t => !he(t, e)))), void n.vl.delete(e);
    // No other queries are mapped to the target, clean up the query and the target.
        if (n.Ml) {
        // We need to remove the local query target first to allow us to verify
        // whether any other client is still interested in this target.
        n.Vl.ka(s.targetId);
        n.Vl.Da(s.targetId) || await _r(n.bh, s.targetId, 
        /*keepPersistedTargetData=*/ !1).then((() => {
            n.Vl.La(s.targetId), so(n.yl, s.targetId), ic(n, s.targetId);
        })).catch(Ai);
    } else ic(n, s.targetId), await _r(n.bh, s.targetId, 
    /*keepPersistedTargetData=*/ !0);
}

/**
 * Initiates the write of local mutation batch which involves adding the
 * writes to the mutation queue, notifying the remote store about new
 * mutations and raising events for any changes this write caused.
 *
 * The promise returned by this call is resolved when the above steps
 * have completed, *not* when the write was acked by the backend. The
 * userCallback is resolved once the write was acked/rejected by the
 * backend (or failed locally for any other reason).
 */ async function Ho(t, e, n) {
    const s = Pc(t);
    try {
        const t = await function(t, e) {
            const n = b(t), s = K.now(), i = e.reduce(((t, e) => t.add(e.key)), En());
            let r;
            return n.persistence.runTransaction("Locally write mutations", "readwrite", (t => n.Tc.Xo(t, i).next((i => {
                r = i;
                // For non-idempotent mutations (such as `FieldValue.increment()`),
                // we record the base state in a separate patch mutation. This is
                // later used to guarantee consistent values and prevents flicker
                // even if the backend sends us an update that already includes our
                // transform.
                const o = [];
                for (const t of e) {
                    const e = Be(t, r.get(t.key));
                    null != e && 
                    // NOTE: The base state should only be applied if there's some
                    // existing document to override, so use a Precondition of
                    // exists=true
                    o.push(new Ke(t.key, e, pt(e.proto.mapValue), Fe.exists(!0)));
                }
                return n.zo.Ri(t, s, o, e);
            })))).then((t => {
                const e = t.xs(r);
                return {
                    batchId: t.batchId,
                    ri: e
                };
            }));
        }(s.bh, e);
        s.Vl.Ca(t.batchId), function(t, e, n) {
            let s = t.$l[t.currentUser.A()];
            s || (s = new nn(D));
            s = s.zt(e, n), t.$l[t.currentUser.A()] = s;
        }
        /**
 * Resolves or rejects the user callback for the given batch and then discards
 * it.
 */ (s, t.batchId, n), await uc(s, t.ri), await Eo(s.yl);
    } catch (t) {
        // If we can't persist the mutation, we reject the user callback and
        // don't send the mutation. The user can then retry the write.
        const e = ps(t, "Failed to persist write");
        n.reject(e);
    }
}

/**
 * Applies one remote event to the sync engine, notifying any views of the
 * changes, and releasing any pending mutation batches that would become
 * visible because of the snapshot version the remote event contains.
 */ async function Jo(t, e) {
    const n = b(t);
    try {
        const t = await ar(n.bh, e);
        // Update `receivedDocument` as appropriate for any limbo targets.
                e.$e.forEach(((t, e) => {
            const s = n.xl.get(e);
            s && (
            // Since this is a limbo resolution lookup, it's for a single document
            // and it could be added, modified, or removed, but not a combination.
            p(t.qe.size + t.Ue.size + t.Qe.size <= 1), t.qe.size > 0 ? s.Pl = !0 : t.Ue.size > 0 ? p(s.Pl) : t.Qe.size > 0 && (p(s.Pl), 
            s.Pl = !1));
        })), await uc(n, t, e);
    } catch (t) {
        await Ai(t);
    }
}

/**
 * Applies an OnlineState change to the sync engine and notifies any views of
 * the change.
 */ function Yo(t, e, n) {
    const s = b(t);
    // If we are the secondary client, we explicitly ignore the remote store's
    // online state (the local client may go offline, even though the primary
    // tab remains online) and only apply the primary tab's online state from
    // SharedClientState.
        if (s.Ml && 0 /* RemoteStore */ === n || !s.Ml && 1 /* SharedClientState */ === n) {
        const t = [];
        s.vl.forEach(((n, s) => {
            const i = s.view.Gh(e);
            i.snapshot && t.push(i.snapshot);
        })), function(t, e) {
            const n = b(t);
            n.onlineState = e;
            let s = !1;
            n.queries.forEach(((t, n) => {
                for (const t of n.listeners) 
                // Run global snapshot listeners if a consistent snapshot has been emitted.
                t.Gh(e) && (s = !0);
            })), s && $o(n);
        }(s.gl, e), t.length && s.bl.ah(t), s.onlineState = e, s.Ml && s.Vl.Qa(e);
    }
}

/**
 * Rejects the listen for the given targetID. This can be triggered by the
 * backend for any active target.
 *
 * @param syncEngine - The sync engine implementation.
 * @param targetId - The targetID corresponds to one previously initiated by the
 * user as part of TargetData passed to listen() on RemoteStore.
 * @param err - A description of the condition that has forced the rejection.
 * Nearly always this will be an indication that the user is no longer
 * authorized to see the data matching the target.
 */ async function Xo(t, e, n) {
    const s = b(t);
    // PORTING NOTE: Multi-tab only.
        s.Vl.Ba(e, "rejected", n);
    const i = s.xl.get(e), r = i && i.key;
    if (r) {
        // TODO(klimt): We really only should do the following on permission
        // denied errors, but we don't have the cause code here.
        // It's a limbo doc. Create a synthetic event saying it was deleted.
        // This is kind of a hack. Ideally, we would have a method in the local
        // store to purge a document. However, it would be tricky to keep all of
        // the local store's invariants with another method.
        let t = new nn(J.H);
        t = t.zt(r, new St(r, W.min()));
        const n = En().add(r), i = new Pn(W.min(), 
        /* targetChanges= */ new Map, 
        /* targetMismatches= */ new on(D), t, n);
        await Jo(s, i), 
        // Since this query failed, we won't want to manually unlisten to it.
        // We only remove it from bookkeeping after we successfully applied the
        // RemoteEvent. If `applyRemoteEvent()` throws, we want to re-listen to
        // this query when the RemoteStore restarts the Watch stream, which should
        // re-trigger the target failure.
        s.Cl = s.Cl.remove(r), s.xl.delete(e), ac(s);
    } else await _r(s.bh, e, 
    /* keepPersistedTargetData */ !1).then((() => ic(s, e, n))).catch(Ai);
}

async function Zo(t, e) {
    const n = b(t), s = e.batch.batchId;
    try {
        const t = await or(n.bh, e);
        // The local store may or may not be able to apply the write result and
        // raise events immediately (depending on whether the watcher is caught
        // up), so we raise user callbacks first so that they consistently happen
        // before listen events.
                sc(n, s, /*error=*/ null), nc(n, s), n.Vl.Na(s, "acknowledged"), await uc(n, t);
    } catch (t) {
        await Ai(t);
    }
}

async function tc(t, e, n) {
    const s = b(t);
    try {
        const t = await function(t, e) {
            const n = b(t);
            return n.persistence.runTransaction("Reject batch", "readwrite-primary", (t => {
                let s;
                return n.zo.Pi(t, e).next((e => (p(null !== e), s = e.keys(), n.zo.Ci(t, e)))).next((() => n.zo.$i(t))).next((() => n.Tc.Xo(t, s)));
            }));
        }
        /**
 * Returns the largest (latest) batch id in mutation queue that is pending
 * server response.
 *
 * Returns `BATCHID_UNKNOWN` if the queue is empty.
 */ (s.bh, e);
        // The local store may or may not be able to apply the write result and
        // raise events immediately (depending on whether the watcher is caught up),
        // so we raise user callbacks first so that they consistently happen before
        // listen events.
                sc(s, e, n), nc(s, e), s.Vl.Na(e, "rejected", n), await uc(s, t);
    } catch (n) {
        await Ai(n);
    }
}

/**
 * Registers a user callback that resolves when all pending mutations at the moment of calling
 * are acknowledged .
 */ async function ec(t, e) {
    const n = b(t);
    ao(n.yl) || R("SyncEngine", "The network is disabled. The task returned by 'awaitPendingWrites()' will not complete until the network is enabled.");
    try {
        const t = await function(t) {
            const e = b(t);
            return e.persistence.runTransaction("Get highest unacknowledged batch id", "readonly", (t => e.zo.Vi(t)));
        }(n.bh);
        if (-1 === t) 
        // Trigger the callback right away if there is no pending writes at the moment.
        return void e.resolve();
        const s = n.Fl.get(t) || [];
        s.push(e), n.Fl.set(t, s);
    } catch (t) {
        const n = ps(t, "Initialization of waitForPendingWrites() operation failed");
        e.reject(n);
    }
}

/**
 * Triggers the callbacks that are waiting for this batch id to get acknowledged by server,
 * if there are any.
 */ function nc(t, e) {
    (t.Fl.get(e) || []).forEach((t => {
        t.resolve();
    })), t.Fl.delete(e);
}

/** Reject all outstanding callbacks waiting for pending writes to complete. */ function sc(t, e, n) {
    const s = b(t);
    let i = s.$l[s.currentUser.A()];
    // NOTE: Mutations restored from persistence won't have callbacks, so it's
    // okay for there to be no callback for this ID.
        if (i) {
        const t = i.get(e);
        t && (n ? t.reject(n) : t.resolve(), i = i.remove(e)), s.$l[s.currentUser.A()] = i;
    }
}

function ic(t, e, n = null) {
    t.Vl.ka(e);
    for (const s of t.Sl.get(e)) t.vl.delete(s), n && t.bl.Bl(s, n);
    if (t.Sl.delete(e), t.Ml) {
        t.Nl.Fc(e).forEach((e => {
            t.Nl.Fi(e) || 
            // We removed the last reference for this key
            rc(t, e);
        }));
    }
}

function rc(t, e) {
    t.Dl.delete(e.path.rt());
    // It's possible that the target already got removed because the query failed. In that case,
    // the key won't exist in `limboTargetsByKey`. Only do the cleanup if we still have the target.
    const n = t.Cl.get(e);
    null !== n && (so(t.yl, n), t.Cl = t.Cl.remove(e), t.xl.delete(n), ac(t));
}

function oc(t, e, n) {
    for (const s of n) if (s instanceof Bo) t.Nl.nr(s.key, e), cc(t, s); else if (s instanceof qo) {
        R("SyncEngine", "Document no longer in limbo: " + s.key), t.Nl.ir(s.key, e);
        t.Nl.Fi(s.key) || 
        // We removed the last reference for this key
        rc(t, s.key);
    } else V();
}

function cc(t, e) {
    const n = e.key, s = n.path.rt();
    t.Cl.get(n) || t.Dl.has(s) || (R("SyncEngine", "New document in limbo: " + n), t.Dl.add(s), 
    ac(t));
}

/**
 * Starts listens for documents in limbo that are enqueued for resolution,
 * subject to a maximum number of concurrent resolutions.
 *
 * Without bounding the number of concurrent resolutions, the server can fail
 * with "resource exhausted" errors which can lead to pathological client
 * behavior as seen in https://github.com/firebase/firebase-js-sdk/issues/2683.
 */ function ac(t) {
    for (;t.Dl.size > 0 && t.Cl.size < t.pl; ) {
        const e = t.Dl.values().next().value;
        t.Dl.delete(e);
        const n = new J(G.ot(e)), s = t.Ol.next();
        t.xl.set(s, new Ko(n)), t.Cl = t.Cl.zt(n, s), no(t.yl, new Ee(ae(ee(n.path)), s, 2 /* LimboResolution */ , Q.q));
    }
}

async function uc(t, e, n) {
    const s = b(t), i = [], r = [], o = [];
    s.vl.et() || (s.vl.forEach(((t, c) => {
        o.push(s.Ll(c, e, n).then((t => {
            if (t) {
                s.Ml && s.Vl.Ba(c.targetId, t.fromCache ? "not-current" : "current"), i.push(t);
                const e = er.oc(c.targetId, t);
                r.push(e);
            }
        })));
    })), await Promise.all(o), s.bl.ah(i), await async function(t, e) {
        const n = b(t);
        try {
            await n.persistence.runTransaction("notifyLocalViewChanges", "readwrite", (t => ws.forEach(e, (e => ws.forEach(e.ic, (s => n.persistence.Ti.nr(t, e.targetId, s))).next((() => ws.forEach(e.rc, (s => n.persistence.Ti.ir(t, e.targetId, s)))))))));
        } catch (t) {
            if (!As(t)) throw t;
            // If `notifyLocalViewChanges` fails, we did not advance the sequence
            // number for the documents that were included in this transaction.
            // This might trigger them to be deleted earlier than they otherwise
            // would have, but it should not invalidate the integrity of the data.
            R("LocalStore", "Failed to update sequence numbers: " + t);
        }
        for (const t of e) {
            const e = t.targetId;
            if (!t.fromCache) {
                const t = n.fc.get(e), s = t.Ot, i = t.Lt(s);
                // Advance the last limbo free snapshot version
                                n.fc = n.fc.zt(e, i);
            }
        }
    }(s.bh, r));
}

async function hc(t, e) {
    const n = b(t);
    if (!n.currentUser.isEqual(e)) {
        R("SyncEngine", "User change. New user:", e.A());
        const t = await rr(n.bh, e);
        n.currentUser = e, 
        // Fails tasks waiting for pending writes requested by previous user.
        function(t, e) {
            t.Fl.forEach((t => {
                t.forEach((t => {
                    t.reject(new F($.CANCELLED, e));
                }));
            })), t.Fl.clear();
        }(n, "'waitForPendingWrites' promise is rejected due to a user change."), 
        // TODO(b/114226417): Consider calling this only in the primary tab.
        n.Vl.Ua(e, t.mc, t.Ac), await uc(n, t.Ic);
    }
}

function lc(t, e) {
    const n = b(t), s = n.xl.get(e);
    if (s && s.Pl) return En().add(s.key);
    {
        let t = En();
        const s = n.Sl.get(e);
        if (!s) return t;
        for (const e of s) {
            const s = n.vl.get(e);
            t = t.Pe(s.view._l);
        }
        return t;
    }
}

/**
 * Reconcile the list of synced documents in an existing view with those
 * from persistence.
 */ async function _c(t, e) {
    const n = b(t), s = await fr(n.bh, e.query, 
    /* usePreviousResults= */ !0), i = e.view.Al(s);
    return n.Ml && oc(n, e.targetId, i.Il), i;
}

/**
 * Retrieves newly changed documents from remote document cache and raises
 * snapshots if needed.
 */
// PORTING NOTE: Multi-Tab only.
async function fc(t) {
    const e = b(t);
    return wr(e.bh).then((t => uc(e, t)));
}

/** Applies a mutation state to an existing batch.  */
// PORTING NOTE: Multi-Tab only.
async function dc(t, e, n, s) {
    const i = b(t), r = await function(t, e) {
        const n = b(t), s = b(n.zo);
        return n.persistence.runTransaction("Lookup mutation documents", "readonly", (t => s.yi(t, e).next((e => e ? n.Tc.Xo(t, e) : ws.resolve(null)))));
    }
    // PORTING NOTE: Multi-Tab only.
    (i.bh, e);
    null !== r ? ("pending" === n ? 
    // If we are the primary client, we need to send this write to the
    // backend. Secondary clients will ignore these writes since their remote
    // connection is disabled.
    await Eo(i.yl) : "acknowledged" === n || "rejected" === n ? (
    // NOTE: Both these methods are no-ops for batches that originated from
    // other clients.
    sc(i, e, s || null), nc(i, e), function(t, e) {
        b(b(t).zo).xi(e);
    }
    // PORTING NOTE: Multi-Tab only.
    (i.bh, e)) : V(), await uc(i, r)) : 
    // A throttled tab may not have seen the mutation before it was completed
    // and removed from the mutation queue, in which case we won't have cached
    // the affected documents. In this case we can safely ignore the update
    // since that means we didn't apply the mutation locally at all (if we
    // had, we would have cached the affected documents), and so we will just
    // see any resulting document changes via normal remote document updates
    // as applicable.
    R("SyncEngine", "Cannot apply mutation batch with id: " + e);
}

/** Applies a query target change from a different tab. */
// PORTING NOTE: Multi-Tab only.
async function wc(t, e) {
    const n = b(t);
    if (Rc(n), Pc(n), !0 === e && !0 !== n.kl) {
        // Secondary tabs only maintain Views for their local listeners and the
        // Views internal state may not be 100% populated (in particular
        // secondary tabs don't track syncedDocuments, the set of documents the
        // server considers to be in the target). So when a secondary becomes
        // primary, we need to need to make sure that all views for all targets
        // match the state on disk.
        const t = n.Vl.va(), e = await Ec(n, t.it());
        n.kl = !0, await Vo(n.yl, !0);
        for (const t of e) no(n.yl, t);
    } else if (!1 === e && !1 !== n.kl) {
        const t = [];
        let e = Promise.resolve();
        n.Sl.forEach(((s, i) => {
            n.Vl.Ma(i) ? t.push(i) : e = e.then((() => (ic(n, i), _r(n.bh, i, 
            /*keepPersistedTargetData=*/ !0)))), so(n.yl, i);
        })), await e, await Ec(n, t), 
        // PORTING NOTE: Multi-Tab only.
        function(t) {
            const e = b(t);
            e.xl.forEach(((t, n) => {
                so(e.yl, n);
            })), e.Nl.Oc(), e.xl = new Map, e.Cl = new nn(J.H);
        }
        /**
 * Reconcile the query views of the provided query targets with the state from
 * persistence. Raises snapshots for any changes that affect the local
 * client and returns the updated state of all target's query data.
 *
 * @param syncEngine - The sync engine implementation
 * @param targets - the list of targets with views that need to be recomputed
 * @param transitionToPrimary - `true` iff the tab transitions from a secondary
 * tab to a primary tab
 */
        // PORTING NOTE: Multi-Tab only.
        (n), n.kl = !1, await Vo(n.yl, !1);
    }
}

async function Ec(t, e, n) {
    const s = b(t), i = [], r = [];
    for (const t of e) {
        let e;
        const n = s.Sl.get(t);
        if (n && 0 !== n.length) {
            // For queries that have a local View, we fetch their current state
            // from LocalStore (as the resume token and the snapshot version
            // might have changed) and reconcile their views with the persisted
            // state (the list of syncedDocuments may have gotten out of sync).
            e = await lr(s.bh, ae(n[0]));
            for (const t of n) {
                const e = s.vl.get(t), n = await _c(s, e);
                n.snapshot && r.push(n.snapshot);
            }
        } else {
            // For queries that never executed on this client, we need to
            // allocate the target in LocalStore and initialize a new View.
            const n = await dr(s.bh, t);
            e = await lr(s.bh, n), await Go(s, Tc(n), t, 
            /*current=*/ !1);
        }
        i.push(e);
    }
    return s.bl.ah(r), i;
}

/**
 * Creates a `Query` object from the specified `Target`. There is no way to
 * obtain the original `Query`, so we synthesize a `Query` from the `Target`
 * object.
 *
 * The synthesized result might be different from the original `Query`, but
 * since the synthesized `Query` should return the same results as the
 * original one (only the presentation of results might differ), the potential
 * difference will not cause issues.
 */
// PORTING NOTE: Multi-Tab only.
function Tc(t) {
    return te(t.path, t.collectionGroup, t.orderBy, t.filters, t.limit, "F" /* First */ , t.startAt, t.endAt);
}

/** Returns the IDs of the clients that are currently active. */
// PORTING NOTE: Multi-Tab only.
function Ic(t) {
    const e = b(t);
    return b(b(e.bh).persistence).Uo();
}

/** Applies a query target change from a different tab. */
// PORTING NOTE: Multi-Tab only.
async function mc(t, e, n, s) {
    const i = b(t);
    if (i.kl) 
    // If we receive a target state notification via WebStorage, we are
    // either already secondary or another tab has taken the primary lease.
    R("SyncEngine", "Ignoring unexpected query state notification."); else if (i.Sl.has(e)) switch (n) {
      case "current":
      case "not-current":
        {
            const t = await wr(i.bh), s = Pn.Me(e, "current" === n);
            await uc(i, t, s);
            break;
        }

      case "rejected":
        await _r(i.bh, e, 
        /* keepPersistedTargetData */ !0), ic(i, e, s);
        break;

      default:
        V();
    }
}

/** Adds or removes Watch targets for queries from different tabs. */ async function Ac(t, e, n) {
    const s = Rc(t);
    if (s.kl) {
        for (const t of e) {
            if (s.Sl.has(t)) {
                // A target might have been added in a previous attempt
                R("SyncEngine", "Adding an already active target " + t);
                continue;
            }
            const e = await dr(s.bh, t), n = await lr(s.bh, e);
            await Go(s, Tc(e), n.targetId, 
            /*current=*/ !1), no(s.yl, n);
        }
        for (const t of n) 
        // Check that the target is still active since the target might have been
        // removed if it has been rejected by the backend.
        s.Sl.has(t) && 
        // Release queries that are still active.
        await _r(s.bh, t, 
        /* keepPersistedTargetData */ !1).then((() => {
            so(s.yl, t), ic(s, t);
        })).catch(Ai);
    }
}

function Rc(t) {
    const e = b(t);
    return e.yl.Dh.Lh = Jo.bind(null, e), e.yl.Dh.vn = lc.bind(null, e), e.yl.Dh.Mh = Xo.bind(null, e), 
    e.bl.ah = xo.bind(null, e.gl), e.bl.Bl = No.bind(null, e.gl), e;
}

function Pc(t) {
    const e = b(t);
    return e.yl.Dh.Bh = Zo.bind(null, e), e.yl.Dh.qh = tc.bind(null, e), e;
}

/**
 * Loads a Firestore bundle into the SDK. The returned promise resolves when
 * the bundle finished loading.
 *
 * @param syncEngine - SyncEngine to use.
 * @param bundleReader - Bundle to load into the SDK.
 * @param task - LoadBundleTask used to update the loading progress to public API.
 */ function yc(t, e, n) {
    const s = b(t);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async function(t, e, n) {
        try {
            const s = await e.getMetadata();
            if (await function(t, e) {
                const n = b(t), s = kn(e.createTime);
                return n.persistence.runTransaction("hasNewerBundle", "readonly", (t => n.wo.Os(t, e.id))).then((t => !!t && t.createTime._(s) >= 0));
            }
            /**
 * Saves the given `BundleMetadata` to local persistence.
 */ (t.bh, s)) return await e.close(), void n.ql(function(t) {
                return {
                    taskState: "Success",
                    documentsLoaded: t.totalDocuments,
                    bytesLoaded: t.totalBytes,
                    totalDocuments: t.totalDocuments,
                    totalBytes: t.totalBytes
                };
            }(s));
            n.Ul(Lo(s));
            const i = new Mo(s, t.bh, e.Qt);
            let r = await e.Ql();
            for (;r; ) {
                const t = await i.rl(r);
                t && n.Ul(t), r = await e.Ql();
            }
            const o = await i.complete();
            // TODO(b/160876443): This currently raises snapshots with
            // `fromCache=false` if users already listen to some queries and bundles
            // has newer version.
                        await uc(t, o.Go, 
            /* remoteEvent */ void 0), 
            // Save metadata, so loading the same bundle will skip.
            await function(t, e) {
                const n = b(t);
                return n.persistence.runTransaction("Save bundle", "readwrite", (t => n.wo.ks(t, e)));
            }
            /**
 * Returns a promise of a `NamedQuery` associated with given query name. Promise
 * resolves to undefined if no persisted data can be found.
 */ (t.bh, s), n.ql(o.progress);
        } catch (t) {
            y("SyncEngine", `Loading bundle failed with ${t}`), n.Kl(t);
        }
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /**
 * Provides all components needed for Firestore with in-memory persistence.
 * Uses EagerGC garbage collection.
 */)(s, e, n).then((() => {
        s.Vl.Wa();
    }));
}

class gc {
    constructor() {
        this.synchronizeTabs = !1;
    }
    async initialize(t) {
        this.Qt = jr(t.yu.t), this.Vl = this.Wl(t), this.persistence = this.jl(t), await this.persistence.start(), 
        this.Gl = this.zl(t), this.bh = this.Hl(t);
    }
    zl(t) {
        return null;
    }
    Hl(t) {
        return ir(this.persistence, new nr, t.Jl, this.Qt);
    }
    jl(t) {
        return new pr(vr.ia, this.Qt);
    }
    Wl(t) {
        return new Mr;
    }
    async terminate() {
        this.Gl && this.Gl.stop(), await this.Vl.ko(), await this.persistence.ko();
    }
}

/**
 * Provides all components needed for Firestore with IndexedDB persistence.
 */ class Vc extends gc {
    constructor(t, e, n) {
        super(), this.Yl = t, this.cacheSizeBytes = e, this.forceOwnership = n, this.synchronizeTabs = !1;
    }
    async initialize(t) {
        await super.initialize(t), await Er(this.bh), await this.Yl.initialize(this, t), 
        // Enqueue writes from a previous session
        await Pc(this.Yl._a), await Eo(this.Yl.yl);
    }
    Hl(t) {
        return ir(this.persistence, new nr, t.Jl, this.Qt);
    }
    zl(t) {
        const e = this.persistence.Ti.lr;
        return new ki(e, t._s);
    }
    jl(t) {
        const e = Xi(t.yu.t, t.yu.persistenceKey), n = void 0 !== this.cacheSizeBytes ? mi.Ys(this.cacheSizeBytes) : mi.ti;
        return new Hi(this.synchronizeTabs, e, t.clientId, n, t._s, Kr(), Wr(), this.Qt, this.Vl, !!this.forceOwnership);
    }
    Wl(t) {
        return new Mr;
    }
}

/**
 * Provides all components needed for Firestore with multi-tab IndexedDB
 * persistence.
 *
 * In the legacy client, this provider is used to provide both multi-tab and
 * non-multi-tab persistence since we cannot tell at build time whether
 * `synchronizeTabs` will be enabled.
 */ class pc extends Vc {
    constructor(t, e) {
        super(t, e, /* forceOwnership= */ !1), this.Yl = t, this.cacheSizeBytes = e, this.synchronizeTabs = !0;
    }
    async initialize(t) {
        await super.initialize(t);
        const e = this.Yl._a;
        this.Vl instanceof kr && (this.Vl._a = {
            eu: dc.bind(null, e),
            nu: mc.bind(null, e),
            su: Ac.bind(null, e),
            Uo: Ic.bind(null, e),
            tu: fc.bind(null, e)
        }, await this.Vl.start()), 
        // NOTE: This will immediately call the listener, so we make sure to
        // set it after localStore / remoteStore are started.
        await this.persistence.Ro((async t => {
            await wc(this.Yl._a, t), this.Gl && (t && !this.Gl.Er ? this.Gl.start(this.bh) : t || this.Gl.stop());
        }));
    }
    Wl(t) {
        const e = Kr();
        if (!kr.Kn(e)) throw new F($.UNIMPLEMENTED, "IndexedDB persistence is only available on platforms that support LocalStorage.");
        const n = Xi(t.yu.t, t.yu.persistenceKey);
        return new kr(e, t._s, n, t.clientId, t.Jl);
    }
}

/**
 * Initializes and wires the components that are needed to interface with the
 * network.
 */ class bc {
    async initialize(t, e) {
        this.bh || (this.bh = t.bh, this.Vl = t.Vl, this.Sh = this.Xl(e), this.yl = this.Zl(e), 
        this.gl = this.t_(e), this._a = this.e_(e, 
        /* startAsPrimary=*/ !t.synchronizeTabs), this.Vl.fa = t => Yo(this._a, t, 1 /* SharedClientState */), 
        this.yl.Dh.n_ = hc.bind(null, this._a), await Vo(this.yl, this._a.Ml));
    }
    t_(t) {
        return new So;
    }
    Xl(t) {
        const e = jr(t.yu.t), n = (s = t.yu, new Qr(s));
        var s;
        /** Return the Platform-specific connectivity monitor. */        return function(t, e, n) {
            return new Yr(t, e, n);
        }(t.credentials, n, e);
    }
    Zl(t) {
        return e = this.bh, n = this.Sh, s = t._s, i = t => Yo(this._a, t, 0 /* RemoteStore */), 
        r = Br.Kn() ? new Br : new Lr, new Zr(e, n, s, i, r);
        var e, n, s, i, r;
        /** Re-enables the network. Idempotent. */    }
    e_(t, e) {
        return function(t, e, n, 
        // PORTING NOTE: Manages state synchronization in multi-tab environments.
        s, i, r, o) {
            const c = new Wo(t, e, n, s, i, r);
            return o && (c.kl = !0), c;
        }(this.bh, this.yl, this.gl, this.Vl, t.Jl, t.pl, e);
    }
    terminate() {
        return async function(t) {
            const e = b(t);
            R("RemoteStore", "RemoteStore shutting down."), e.Nh.add(5 /* Shutdown */), await eo(e), 
            e.Fh.ko(), 
            // Set the OnlineState to Unknown (rather than Offline) to avoid potentially
            // triggering spurious listener events with cached data, etc.
            e.Oh.set("Unknown" /* Unknown */);
        }(this.yl);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * How many bytes to read each time when `ReadableStreamReader.read()` is
 * called. Only applicable for byte streams that we control (e.g. those backed
 * by an UInt8Array).
 */
/**
 * Builds a `ByteStreamReader` from a UInt8Array.
 * @param source - The data source to use.
 * @param bytesPerRead - How many bytes each `read()` from the returned reader
 *        will read.
 */
function vc(t, e = 10240) {
    let n = 0;
    return {
        async read() {
            if (n < t.byteLength) {
                const s = {
                    value: t.slice(n, n + e),
                    done: !1
                };
                return n += e, s;
            }
            // TypesScript's lib.dom.d.ts doesn't have proper typings of
            // ReadableStreamReadResult yet.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return {
                done: !0
            };
        },
        async cancel() {},
        releaseLock() {}
    };
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * On web, a `ReadableStream` is wrapped around by a `ByteStreamReader`.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/*
 * A wrapper implementation of Observer<T> that will dispatch events
 * asynchronously. To allow immediate silencing, a mute call is added which
 * causes events scheduled to no longer be raised.
 */
class Sc {
    constructor(t) {
        this.observer = t, 
        /**
         * When set to true, will not raise future events. Necessary to deal with
         * async detachment of listener.
         */
        this.muted = !1;
    }
    next(t) {
        this.observer.next && this.s_(this.observer.next, t);
    }
    error(t) {
        this.observer.error ? this.s_(this.observer.error, t) : console.error("Uncaught Error in snapshot listener:", t);
    }
    i_() {
        this.muted = !0;
    }
    s_(t, e) {
        this.muted || setTimeout((() => {
            this.muted || t(e);
        }), 0);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A class representing a bundle.
 *
 * Takes a bundle stream or buffer, and presents abstractions to read bundled
 * elements out of the underlying content.
 */ class Dc {
    constructor(
    /** The reader to read from underlying binary bundle data source. */
    t, e) {
        this.r_ = t, this.Qt = e, 
        /** Cached bundle metadata. */
        this.metadata = new ds, 
        /**
         * Internal buffer to hold bundle content, accumulating incomplete element
         * content.
         */
        this.buffer = new Uint8Array, this.o_ = new TextDecoder("utf-8"), 
        // Read the metadata (which is the first element).
        this.c_().then((t => {
            t && t.sl() ? this.metadata.resolve(t.payload.metadata) : this.metadata.reject(new Error(`The first element of the bundle is not a metadata, it is\n             ${JSON.stringify(null == t ? void 0 : t.payload)}`));
        }), (t => this.metadata.reject(t)));
    }
    close() {
        return this.r_.cancel();
    }
    async getMetadata() {
        return this.metadata.promise;
    }
    async Ql() {
        // Makes sure metadata is read before proceeding.
        return await this.getMetadata(), this.c_();
    }
    /**
     * Reads from the head of internal buffer, and pulling more data from
     * underlying stream if a complete element cannot be found, until an
     * element(including the prefixed length and the JSON string) is found.
     *
     * Once a complete element is read, it is dropped from internal buffer.
     *
     * Returns either the bundled element, or null if we have reached the end of
     * the stream.
     */    async c_() {
        const t = await this.a_();
        if (null === t) return null;
        const e = this.o_.decode(t), n = Number(e);
        isNaN(n) && this.u_(`length string (${e}) is not valid number`);
        const s = await this.h_(n);
        return new Oo(JSON.parse(s), t.length + n);
    }
    /** First index of '{' from the underlying buffer. */    l_() {
        return this.buffer.findIndex((t => t === "{".charCodeAt(0)));
    }
    /**
     * Reads from the beginning of the internal buffer, until the first '{', and
     * return the content.
     *
     * If reached end of the stream, returns a null.
     */    async a_() {
        for (;this.l_() < 0; ) {
            if (await this.__()) break;
        }
        // Broke out of the loop because underlying stream is closed, and there
        // happens to be no more data to process.
                if (0 === this.buffer.length) return null;
        const t = this.l_();
        // Broke out of the loop because underlying stream is closed, but still
        // cannot find an open bracket.
                t < 0 && this.u_("Reached the end of bundle when a length string is expected.");
        const e = this.buffer.slice(0, t);
        // Update the internal buffer to drop the read length.
                return this.buffer = this.buffer.slice(t), e;
    }
    /**
     * Reads from a specified position from the internal buffer, for a specified
     * number of bytes, pulling more data from the underlying stream if needed.
     *
     * Returns a string decoded from the read bytes.
     */    async h_(t) {
        for (;this.buffer.length < t; ) {
            await this.__() && this.u_("Reached the end of bundle when more is expected.");
        }
        const e = this.o_.decode(this.buffer.slice(0, t));
        // Update the internal buffer to drop the read json string.
                return this.buffer = this.buffer.slice(t), e;
    }
    u_(t) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        throw this.r_.cancel(), new Error(`Invalid bundle format: ${t}`);
    }
    /**
     * Pulls more data from underlying stream to internal buffer.
     * Returns a boolean indicating whether the stream is finished.
     */    async __() {
        const t = await this.r_.read();
        if (!t.done) {
            const e = new Uint8Array(this.buffer.length + t.value.length);
            e.set(this.buffer), e.set(t.value, this.buffer.length), this.buffer = e;
        }
        return t.done;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `FieldPath` refers to a field in a document. The path may consist of a
 * single field name (referring to a top-level field in the document), or a
 * list of field names (referring to a nested field in the document).
 *
 * Create a `FieldPath` by providing field names. If more than one field
 * name is provided, the path will point to a nested field in a document.
 */
class Cc {
    /**
     * Creates a FieldPath from the provided field names. If more than one field
     * name is provided, the path will point to a nested field in a document.
     *
     * @param fieldNames - A list of field names.
     */
    constructor(...t) {
        for (let e = 0; e < t.length; ++e) if (0 === t[e].length) throw new F($.INVALID_ARGUMENT, "Invalid field name at argument $(i + 1). Field names must not be empty.");
        this.f_ = new H(t);
    }
    /**
     * Returns true if this `FieldPath` is equal to the provided one.
     *
     * @param other - The `FieldPath` to compare against.
     * @returns true if this `FieldPath` is equal to the provided one.
     */    isEqual(t) {
        return this.f_.isEqual(t.f_);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Sentinel values that can be used when writing document fields with `set()`
 * or `update()`.
 */ class xc {
    /**
     * @param _methodName - The public API endpoint that returns this class.
     */
    constructor(t) {
        this._methodName = t;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A class implemented by all API types of the legacy Firestore API which
 * contains a reference to the API type in the firestore-exp API. All internal
 * code unwraps these references, which allows us to only use firestore-exp
 * types in the SDK.
 */ class Nc {
    constructor(t) {
        this.d_ = t;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function $c(t, e, n) {
    if (!n) throw new F($.INVALID_ARGUMENT, `Function ${t}() cannot be called with an empty ${e}.`);
}

function Fc(t, e) {
    if (void 0 === e) return {
        merge: !1
    };
    if (void 0 !== e.mergeFields && void 0 !== e.merge) throw new F($.INVALID_ARGUMENT, `Invalid options passed to function ${t}(): You cannot specify both "merge" and "mergeFields".`);
    return e;
}

/**
 * Validates that two boolean options are not set at the same time.
 */ function Oc(t, e, n, s) {
    if (!0 === e && !0 === s) throw new F($.INVALID_ARGUMENT, `${t} and ${n} cannot be used together.`);
}

/**
 * Validates that `path` refers to a document (indicated by the fact it contains
 * an even numbers of segments).
 */ function kc(t) {
    if (!J.wt(t)) throw new F($.INVALID_ARGUMENT, `Invalid document reference. Document references must have an even number of segments, but ${t} has ${t.length}.`);
}

/**
 * Validates that `path` refers to a collection (indicated by the fact it
 * contains an odd numbers of segments).
 */ function Mc(t) {
    if (J.wt(t)) throw new F($.INVALID_ARGUMENT, `Invalid collection reference. Collection references must have an odd number of segments, but ${t} has ${t.length}.`);
}

/**
 * Returns true if it's a non-null object without a custom prototype
 * (i.e. excludes Array, Date, etc.).
 */
/** Returns a string describing the type / value of the provided input. */
function Lc(t) {
    if (void 0 === t) return "undefined";
    if (null === t) return "null";
    if ("string" == typeof t) return t.length > 20 && (t = `${t.substring(0, 20)}...`), 
    JSON.stringify(t);
    if ("number" == typeof t || "boolean" == typeof t) return "" + t;
    if ("object" == typeof t) {
        if (t instanceof Array) return "an array";
        {
            const e = 
            /** Hacky method to try to get the constructor name for an object. */
            function(t) {
                if (t.constructor) {
                    const e = /function\s+([^\s(]+)\s*\(/.exec(t.constructor.toString());
                    if (e && e.length > 1) return e[1];
                }
                return null;
            }
            /**
 * Casts `obj` to `T`, optionally unwrapping Compat types to expose the
 * underlying instance. Throws if  `obj` is not an instance of `T`.
 *
 * This cast is used in the Lite and Full SDK to verify instance types for
 * arguments passed to the public API.
 */ (t);
            return e ? `a custom ${e} object` : "an object";
        }
    }
    return "function" == typeof t ? "a function" : V();
}

function Bc(t, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
e) {
    if ("_delegate" in t && (
    // Unwrap Compat types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t = t.d_), !(t instanceof e)) {
        if (e.name === t.constructor.name) throw new F($.INVALID_ARGUMENT, "Type does not match the expected instance. Did you pass a reference from a different Firestore SDK?");
        {
            const n = Lc(t);
            throw new F($.INVALID_ARGUMENT, `Expected type '${e.name}', but it was: ${n}`);
        }
    }
    return t;
}

function qc(t, e) {
    if (e <= 0) throw new F($.INVALID_ARGUMENT, `Function ${t}() requires a positive number, but it was: ${e}.`);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// settings() defaults:
/**
 * A concrete type describing all the values that can be applied via a
 * user-supplied firestore.Settings object. This is a separate type so that
 * defaults can be supplied and the value can be checked for equality.
 */
class Uc {
    constructor(t) {
        var e;
        if (void 0 === t.host) {
            if (void 0 !== t.ssl) throw new F($.INVALID_ARGUMENT, "Can't provide ssl option if host option is not set");
            this.host = "firestore.googleapis.com", this.ssl = true;
        } else this.host = t.host, this.ssl = null === (e = t.ssl) || void 0 === e || e;
        if (this.credentials = t.credentials, this.ignoreUndefinedProperties = !!t.ignoreUndefinedProperties, 
        void 0 === t.cacheSizeBytes) this.cacheSizeBytes = 41943040; else {
            if (-1 !== t.cacheSizeBytes && t.cacheSizeBytes < 1048576) throw new F($.INVALID_ARGUMENT, "cacheSizeBytes must be at least 1048576");
            this.cacheSizeBytes = t.cacheSizeBytes;
        }
        this.experimentalForceLongPolling = !!t.experimentalForceLongPolling, this.experimentalAutoDetectLongPolling = !!t.experimentalAutoDetectLongPolling, 
        Oc("experimentalForceLongPolling", t.experimentalForceLongPolling, "experimentalAutoDetectLongPolling", t.experimentalAutoDetectLongPolling);
    }
    isEqual(t) {
        return this.host === t.host && this.ssl === t.ssl && this.credentials === t.credentials && this.cacheSizeBytes === t.cacheSizeBytes && this.experimentalForceLongPolling === t.experimentalForceLongPolling && this.experimentalAutoDetectLongPolling === t.experimentalAutoDetectLongPolling && this.ignoreUndefinedProperties === t.ignoreUndefinedProperties;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Qc = new Map;

/**
 * An instance map that ensures only one Datastore exists per Firestore
 * instance.
 */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The Cloud Firestore service interface.
 *
 * Do not call this constructor directly. Instead, use {@link getFirestore}.
 */
class Kc {
    /** @hideconstructor */
    constructor(t, e) {
        this.w_ = "(lite)", this.E_ = new Uc({}), this.T_ = !1, t instanceof I ? (this.I_ = t, 
        this.m_ = new L) : (this.A_ = t, this.I_ = function(t) {
            if (!Object.prototype.hasOwnProperty.apply(t.options, [ "projectId" ])) throw new F($.INVALID_ARGUMENT, '"projectId" not provided in firebase.initializeApp.');
            return new I(t.options.projectId);
        }
        /**
 * Modify this instance to communicate with the Cloud Firestore emulator.
 *
 * Note: This must be called before this instance has been used to do any
 * operations.
 *
 * @param firestore - The Firestore instance to configure to connect to the
 * emulator.
 * @param host - the emulator host (ex: localhost).
 * @param port - the emulator port (ex: 9000).
 */ (t), this.m_ = new B(e));
    }
    /**
     * The {@link FirebaseApp} associated with this `Firestore` service
     * instance.
     */    get app() {
        if (!this.A_) throw new F($.FAILED_PRECONDITION, "Firestore was not initialized using the Firebase SDK. 'app' is not available");
        return this.A_;
    }
    get R_() {
        return this.T_;
    }
    get P_() {
        return void 0 !== this.y_;
    }
    g_(t) {
        if (this.T_) throw new F($.FAILED_PRECONDITION, "Firestore has already been started and its settings can no longer be changed. You can only modify settings before calling any other methods on a Firestore object.");
        this.E_ = new Uc(t), void 0 !== t.credentials && (this.m_ = function(t) {
            if (!t) return new L;
            switch (t.type) {
              case "gapi":
                const e = t.client;
                // Make sure this really is a Gapi client.
                                return p(!("object" != typeof e || null === e || !e.auth || !e.auth.getAuthHeaderValueForFirstParty)), 
                new U(e, t.sessionIndex || "0");

              case "provider":
                return t.client;

              default:
                throw new F($.INVALID_ARGUMENT, "makeCredentialsProvider failed due to invalid credential type");
            }
        }(t.credentials));
    }
    V_() {
        return this.E_;
    }
    p_() {
        return this.T_ = !0, this.E_;
    }
    _delete() {
        return this.y_ || (this.y_ = this.b_()), this.y_;
    }
    toJSON() {
        return {
            app: this.A_,
            t: this.I_,
            settings: this.E_
        };
    }
    /**
     * Terminates all components used by this client. Subclasses can override
     * this method to clean up their own dependencies, but must also call this
     * method.
     *
     * Only ever called once.
     */    b_() {
        /**
 * Removes all components associated with the provided instance. Must be called
 * when the `Firestore` instance is terminated.
 */
        return function(t) {
            const e = Qc.get(t);
            e && (R("ComponentProvider", "Removing Datastore"), Qc.delete(t), e.terminate());
        }(this), Promise.resolve();
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `DocumentReference` refers to a document location in a Firestore database
 * and can be used to write, read, or listen to the location. The document at
 * the referenced location may or may not exist.
 */
class Wc {
    /** @hideconstructor */
    constructor(t, e, n) {
        this.v_ = e, this.S_ = n, 
        /** The type of this Firestore reference. */
        this.type = "document", this.firestore = t;
    }
    get D_() {
        return this.S_.path;
    }
    /**
     * The document's identifier within its collection.
     */    get id() {
        return this.S_.path.tt();
    }
    /**
     * A string representing the path of the referenced document (relative
     * to the root of the database).
     */    get path() {
        return this.S_.path.rt();
    }
    /**
     * The collection this `DocumentReference` belongs to.
     */    get parent() {
        return new Gc(this.firestore, this.v_, this.S_.path.X());
    }
    /**
     * Applies a custom data converter to this `DocumentReference`, allowing you
     * to use your own custom model objects with Firestore. When you call {@link
     * setDoc}, {@link getDoc}, etc. with the returned `DocumentReference`
     * instance, the provided converter will convert between Firestore data and
     * your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `DocumentReference<U>` that uses the provided converter.
     */    withConverter(t) {
        return new Wc(this.firestore, t, this.S_);
    }
}

/**
 * A `Query` refers to a Query which you can read or listen to. You can also
 * construct refined `Query` objects by adding filters and ordering.
 */ class jc {
    // This is the lite version of the Query class in the main SDK.
    /** @hideconstructor protected */
    constructor(t, e, n) {
        this.v_ = e, this.C_ = n, 
        /** The type of this Firestore reference. */
        this.type = "query", this.firestore = t;
    }
    /**
     * Applies a custom data converter to this query, allowing you to use your own
     * custom model objects with Firestore. When you call {@link getDocs} with
     * the returned query, the provided converter will convert between Firestore
     * data and your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `Query<U>` that uses the provided converter.
     */    withConverter(t) {
        return new jc(this.firestore, t, this.C_);
    }
}

/**
 * A `CollectionReference` object can be used for adding documents, getting
 * document references, and querying for documents (using {@link query}).
 */ class Gc extends jc {
    /** @hideconstructor */
    constructor(t, e, n) {
        super(t, e, ee(n)), this.firestore = t, this.D_ = n, this.type = "collection";
    }
    /** The collection's identifier. */    get id() {
        return this.C_.path.tt();
    }
    /**
     * A string representing the path of the referenced collection (relative
     * to the root of the database).
     */    get path() {
        return this.C_.path.rt();
    }
    /**
     * A reference to the containing `DocumentReference` if this is a
     * subcollection. If this isn't a subcollection, the reference is null.
     */    get parent() {
        const t = this.D_.X();
        return t.et() ? null : new Wc(this.firestore, 
        /* converter= */ null, new J(t));
    }
    /**
     * Applies a custom data converter to this CollectionReference, allowing you
     * to use your own custom model objects with Firestore. When you call {@link
     * addDoc} with the returned `CollectionReference` instance, the provided
     * converter will convert between Firestore data and your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `CollectionReference<U>` that uses the provided converter.
     */    withConverter(t) {
        return new Gc(this.firestore, t, this.D_);
    }
}

function zc(t, e, ...n) {
    if (t instanceof Nc && (t = t.d_), $c("collection", "path", e), t instanceof Kc) {
        const s = G.ot(e, ...n);
        return Mc(s), new Gc(t, /* converter= */ null, s);
    }
    {
        if (!(t instanceof Wc || t instanceof Gc)) throw new F($.INVALID_ARGUMENT, "Expected first argument to collection() to be a CollectionReference, a DocumentReference or FirebaseFirestore");
        const s = G.ot(t.path, ...n).child(G.ot(e));
        return Mc(s), new Gc(t.firestore, 
        /* converter= */ null, s);
    }
}

// TODO(firestorelite): Consider using ErrorFactory -
// https://github.com/firebase/firebase-js-sdk/blob/0131e1f/packages/util/src/errors.ts#L106
/**
 * Creates and returns a new `Query` instance that includes all documents in the
 * database that are contained in a collection or subcollection with the
 * given `collectionId`.
 *
 * @param firestore - A reference to the root Firestore instance.
 * @param collectionId - Identifies the collections to query over. Every
 * collection or subcollection with this ID as the last segment of its path
 * will be included. Cannot contain a slash.
 * @returns The created `Query`.
 */ function Hc(t, e) {
    if (t = Bc(t, Kc), $c("collectionGroup", "collection id", e), e.indexOf("/") >= 0) throw new F($.INVALID_ARGUMENT, `Invalid collection ID '${e}' passed to function collectionGroup(). Collection IDs must not contain '/'.`);
    return new jc(t, 
    /* converter= */ null, 
    /**
 * Creates a new Query for a collection group query that matches all documents
 * within the provided collection group.
 */
    function(t) {
        return new Zt(G.ct(), t);
    }(e));
}

function Jc(t, e, ...n) {
    if (t instanceof Nc && (t = t.d_), 
    // We allow omission of 'pathString' but explicitly prohibit passing in both
    // 'undefined' and 'null'.
    1 === arguments.length && (e = S.u()), $c("doc", "path", e), t instanceof Kc) {
        const s = G.ot(e, ...n);
        return kc(s), new Wc(t, 
        /* converter= */ null, new J(s));
    }
    {
        if (!(t instanceof Wc || t instanceof Gc)) throw new F($.INVALID_ARGUMENT, "Expected first argument to collection() to be a CollectionReference, a DocumentReference or FirebaseFirestore");
        const s = t.D_.child(G.ot(e, ...n));
        return kc(s), new Wc(t.firestore, t instanceof Gc ? t.v_ : null, new J(s));
    }
}

/**
 * Returns true if the provided references are equal.
 *
 * @param left - A reference to compare.
 * @param right - A reference to compare.
 * @returns true if the references point to the same location in the same
 * Firestore database.
 */ function Yc(t, e) {
    return t instanceof Nc && (t = t.d_), e instanceof Nc && (e = e.d_), (t instanceof Wc || t instanceof Gc) && (e instanceof Wc || e instanceof Gc) && (t.firestore === e.firestore && t.path === e.path && t.v_ === e.v_);
}

/**
 * Returns true if the provided queries point to the same collection and apply
 * the same constraints.
 *
 * @param left - A `Query` to compare.
 * @param right - A `Query` to compare.
 * @returns true if the references point to the same location in the same
 * Firestore database.
 */ function Xc(t, e) {
    return t instanceof Nc && (t = t.d_), e instanceof Nc && (e = e.d_), t instanceof jc && e instanceof jc && (t.firestore === e.firestore && he(t.C_, e.C_) && t.v_ === e.v_);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable object representing a geographic location in Firestore. The
 * location is represented as latitude/longitude pair.
 *
 * Latitude values are in the range of [-90, 90].
 * Longitude values are in the range of [-180, 180].
 */ class Zc {
    /**
     * Creates a new immutable `GeoPoint` object with the provided latitude and
     * longitude values.
     * @param latitude - The latitude as number between -90 and 90.
     * @param longitude - The longitude as number between -180 and 180.
     */
    constructor(t, e) {
        if (!isFinite(t) || t < -90 || t > 90) throw new F($.INVALID_ARGUMENT, "Latitude must be a number between -90 and 90, but was: " + t);
        if (!isFinite(e) || e < -180 || e > 180) throw new F($.INVALID_ARGUMENT, "Longitude must be a number between -180 and 180, but was: " + e);
        this.x_ = t, this.N_ = e;
    }
    /**
     * The latitude of this `GeoPoint` instance.
     */    get latitude() {
        return this.x_;
    }
    /**
     * The longitude of this `GeoPoint` instance.
     */    get longitude() {
        return this.N_;
    }
    /**
     * Returns true if this `GeoPoint` is equal to the provided one.
     *
     * @param other - The `GeoPoint` to compare against.
     * @returns true if this `GeoPoint` is equal to the provided one.
     */    isEqual(t) {
        return this.x_ === t.x_ && this.N_ === t.N_;
    }
    toJSON() {
        return {
            latitude: this.x_,
            longitude: this.N_
        };
    }
    /**
     * Actually private to JS consumers of our API, so this function is prefixed
     * with an underscore.
     */    U(t) {
        return D(this.x_, t.x_) || D(this.N_, t.N_);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const ta = /^__.*__$/;

/** The result of parsing document data (e.g. for a setData call). */ class ea {
    constructor(t, e, n) {
        this.data = t, this.Gt = e, this.fieldTransforms = n;
    }
    F_(t, e) {
        return null !== this.Gt ? new Ke(t, this.data, this.Gt, e, this.fieldTransforms) : new Qe(t, this.data, e, this.fieldTransforms);
    }
}

/** The result of parsing "update" data (i.e. for an updateData call). */ class na {
    constructor(t, 
    // The fieldMask does not include document transforms.
    e, n) {
        this.data = t, this.Gt = e, this.fieldTransforms = n;
    }
    F_(t, e) {
        return new Ke(t, this.data, this.Gt, e, this.fieldTransforms);
    }
}

function sa(t) {
    switch (t) {
      case 0 /* Set */ :
 // fall through
              case 2 /* MergeSet */ :
 // fall through
              case 1 /* Update */ :
        return !0;

      case 3 /* Argument */ :
      case 4 /* ArrayArgument */ :
        return !1;

      default:
        throw V();
    }
}

/** A "context" object passed around while parsing user data. */ class ia {
    /**
     * Initializes a ParseContext with the given source and path.
     *
     * @param settings - The settings for the parser.
     * @param databaseId - The database ID of the Firestore instance.
     * @param serializer - The serializer to use to generate the Value proto.
     * @param ignoreUndefinedProperties - Whether to ignore undefined properties
     * rather than throw.
     * @param fieldTransforms - A mutable list of field transforms encountered
     * while parsing the data.
     * @param fieldMask - A mutable list of field paths encountered while parsing
     * the data.
     *
     * TODO(b/34871131): We don't support array paths right now, so path can be
     * null to indicate the context represents any location within an array (in
     * which case certain features will not work and errors will be somewhat
     * compromised).
     */
    constructor(t, e, n, s, i, r) {
        this.settings = t, this.t = e, this.Qt = n, this.ignoreUndefinedProperties = s, 
        // Minor hack: If fieldTransforms is undefined, we assume this is an
        // external call and we need to validate the entire path.
        void 0 === i && this.O_(), this.fieldTransforms = i || [], this.Gt = r || [];
    }
    get path() {
        return this.settings.path;
    }
    get k_() {
        return this.settings.k_;
    }
    /** Returns a new context with the specified settings overwritten. */    M_(t) {
        return new ia(Object.assign(Object.assign({}, this.settings), t), this.t, this.Qt, this.ignoreUndefinedProperties, this.fieldTransforms, this.Gt);
    }
    L_(t) {
        var e;
        const n = null === (e = this.path) || void 0 === e ? void 0 : e.child(t), s = this.M_({
            path: n,
            B_: !1
        });
        return s.q_(t), s;
    }
    U_(t) {
        var e;
        const n = null === (e = this.path) || void 0 === e ? void 0 : e.child(t), s = this.M_({
            path: n,
            B_: !1
        });
        return s.O_(), s;
    }
    Q_(t) {
        // TODO(b/34871131): We don't support array paths right now; so make path
        // undefined.
        return this.M_({
            path: void 0,
            B_: !0
        });
    }
    K_(t) {
        return ga(t, this.settings.methodName, this.settings.W_ || !1, this.path, this.settings.j_);
    }
    /** Returns 'true' if 'fieldPath' was traversed when creating this context. */    contains(t) {
        return void 0 !== this.Gt.find((e => t.nt(e))) || void 0 !== this.fieldTransforms.find((e => t.nt(e.field)));
    }
    O_() {
        // TODO(b/34871131): Remove null check once we have proper paths for fields
        // within arrays.
        if (this.path) for (let t = 0; t < this.path.length; t++) this.q_(this.path.get(t));
    }
    q_(t) {
        if (0 === t.length) throw this.K_("Document fields must not be empty");
        if (sa(this.k_) && ta.test(t)) throw this.K_('Document fields cannot begin and end with "__"');
    }
}

/**
 * Helper for parsing raw user input (provided via the API) into internal model
 * classes.
 */ class ra {
    constructor(t, e, n) {
        this.t = t, this.ignoreUndefinedProperties = e, this.Qt = n || jr(t);
    }
    /** Creates a new top-level parse context. */    G_(t, e, n, s = !1) {
        return new ia({
            k_: t,
            methodName: e,
            j_: n,
            path: H.ct(),
            B_: !1,
            W_: s
        }, this.t, this.Qt, this.ignoreUndefinedProperties);
    }
}

function oa(t) {
    const e = t.p_(), n = jr(t.I_);
    return new ra(t.I_, !!e.ignoreUndefinedProperties, n);
}

/** Parse document data from a set() call. */ function ca(t, e, n, s, i, r = {}) {
    const o = t.G_(r.merge || r.mergeFields ? 2 /* MergeSet */ : 0 /* Set */ , e, n, i);
    Aa("Data must be an object, but it was:", o, s);
    const c = Ia(s, o);
    let a, u;
    if (r.merge) a = new tt(o.Gt), u = o.fieldTransforms; else if (r.mergeFields) {
        const t = [];
        for (const s of r.mergeFields) {
            const i = Ra(e, s, n);
            if (!o.contains(i)) throw new F($.INVALID_ARGUMENT, `Field '${i}' is specified in your field mask but missing from your input data.`);
            Va(t, i) || t.push(i);
        }
        a = new tt(t), u = o.fieldTransforms.filter((t => a.Tt(t.field)));
    } else a = null, u = o.fieldTransforms;
    return new ea(new gt(c), a, u);
}

class aa extends xc {
    z_(t) {
        if (2 /* MergeSet */ !== t.k_) throw 1 /* Update */ === t.k_ ? t.K_(`${this._methodName}() can only appear at the top level of your update data`) : t.K_(`${this._methodName}() cannot be used with set() unless you pass {merge:true}`);
        // No transform to add for a delete, but we need to add it to our
        // fieldMask so it gets deleted.
        return t.Gt.push(t.path), null;
    }
    isEqual(t) {
        return t instanceof aa;
    }
}

/**
 * Creates a child context for parsing SerializableFieldValues.
 *
 * This is different than calling `ParseContext.contextWith` because it keeps
 * the fieldTransforms and fieldMask separate.
 *
 * The created context has its `dataSource` set to `UserDataSource.Argument`.
 * Although these values are used with writes, any elements in these FieldValues
 * are not considered writes since they cannot contain any FieldValue sentinels,
 * etc.
 *
 * @param fieldValue - The sentinel FieldValue for which to create a child
 *     context.
 * @param context - The parent context.
 * @param arrayElement - Whether or not the FieldValue has an array.
 */ function ua(t, e, n) {
    return new ia({
        k_: 3 /* Argument */ ,
        j_: e.settings.j_,
        methodName: t._methodName,
        B_: n
    }, e.t, e.Qt, e.ignoreUndefinedProperties);
}

class ha extends xc {
    z_(t) {
        return new xe(t.path, new ge);
    }
    isEqual(t) {
        return t instanceof ha;
    }
}

class la extends xc {
    constructor(t, e) {
        super(t), this.H_ = e;
    }
    z_(t) {
        const e = ua(this, t, 
        /*array=*/ !0), n = this.H_.map((t => Ta(t, e))), s = new Ve(n);
        return new xe(t.path, s);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

class _a extends xc {
    constructor(t, e) {
        super(t), this.H_ = e;
    }
    z_(t) {
        const e = ua(this, t, 
        /*array=*/ !0), n = this.H_.map((t => Ta(t, e))), s = new be(n);
        return new xe(t.path, s);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

class fa extends xc {
    constructor(t, e) {
        super(t), this.J_ = e;
    }
    z_(t) {
        const e = new Se(t.Qt, me(t.Qt, this.J_));
        return new xe(t.path, e);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

/** Parse update data from an update() call. */ function da(t, e, n, s) {
    const i = t.G_(1 /* Update */ , e, n);
    Aa("Data must be an object, but it was:", i, s);
    const r = [], o = new Vt;
    X(s, ((t, s) => {
        const c = ya(e, t, n);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                s instanceof Nc && (s = s.d_);
        const a = i.U_(c);
        if (s instanceof aa) 
        // Add it to the field mask, but don't add anything to updateData.
        r.push(c); else {
            const t = Ta(s, a);
            null != t && (r.push(c), o.set(c, t));
        }
    }));
    const c = new tt(r);
    return new na(o.Pt(), c, i.fieldTransforms);
}

/** Parse update data from a list of field/value arguments. */ function wa(t, e, n, s, i, r) {
    const o = t.G_(1 /* Update */ , e, n), c = [ Ra(e, s, n) ], a = [ i ];
    if (r.length % 2 != 0) throw new F($.INVALID_ARGUMENT, `Function ${e}() needs to be called with an even number of arguments that alternate between field names and values.`);
    for (let t = 0; t < r.length; t += 2) c.push(Ra(e, r[t])), a.push(r[t + 1]);
    const u = [], h = new Vt;
    // We iterate in reverse order to pick the last value for a field if the
    // user specified the field multiple times.
    for (let t = c.length - 1; t >= 0; --t) if (!Va(u, c[t])) {
        const e = c[t];
        let n = a[t];
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                n instanceof Nc && (n = n.d_);
        const s = o.U_(e);
        if (n instanceof aa) 
        // Add it to the field mask, but don't add anything to updateData.
        u.push(e); else {
            const t = Ta(n, s);
            null != t && (u.push(e), h.set(e, t));
        }
    }
    const l = new tt(u);
    return new na(h.Pt(), l, o.fieldTransforms);
}

/**
 * Parse a "query value" (e.g. value in a where filter or a value in a cursor
 * bound).
 *
 * @param allowArrays - Whether the query value is an array that may directly
 * contain additional arrays (e.g. the operand of an `in` query).
 */ function Ea(t, e, n, s = !1) {
    return Ta(n, t.G_(s ? 4 /* ArrayArgument */ : 3 /* Argument */ , e));
}

/**
 * Parses user data to Protobuf Values.
 *
 * @param input - Data to be parsed.
 * @param context - A context object representing the current path being parsed,
 * the source of the data being parsed, etc.
 * @returns The parsed value, or null if the value was a FieldValue sentinel
 * that should not be included in the resulting parsed data.
 */ function Ta(t, e) {
    if (
    // Unwrap the API type from the Compat SDK. This will return the API type
    // from firestore-exp.
    t instanceof Nc && (t = t.d_), ma(t)) return Aa("Unsupported field value:", e, t), 
    Ia(t, e);
    if (t instanceof xc) 
    // FieldValues usually parse into transforms (except FieldValue.delete())
    // in which case we do not want to include this field in our parsed data
    // (as doing so will overwrite the field directly prior to the transform
    // trying to transform it). So we don't add this location to
    // context.fieldMask and we return null as our parsing result.
    /**
 * "Parses" the provided FieldValueImpl, adding any necessary transforms to
 * context.fieldTransforms.
 */
    return function(t, e) {
        // Sentinels are only supported with writes, and not within arrays.
        if (!sa(e.k_)) throw e.K_(`${t._methodName}() can only be used with update() and set()`);
        if (!e.path) throw e.K_(`${t._methodName}() is not currently supported inside arrays`);
        const n = t.z_(e);
        n && e.fieldTransforms.push(n);
    }
    /**
 * Helper to parse a scalar value (i.e. not an Object, Array, or FieldValue)
 *
 * @returns The parsed value
 */ (t, e), null;
    if (void 0 === t && e.ignoreUndefinedProperties) 
    // If the input is undefined it can never participate in the fieldMask, so
    // don't handle this below. If `ignoreUndefinedProperties` is false,
    // `parseScalarValue` will reject an undefined value.
    return null;
    if (
    // If context.path is null we are inside an array and we don't support
    // field mask paths more granular than the top-level array.
    e.path && e.Gt.push(e.path), t instanceof Array) {
        // TODO(b/34871131): Include the path containing the array in the error
        // message.
        // In the case of IN queries, the parsed data is an array (representing
        // the set of values to be included for the IN query) that may directly
        // contain additional arrays (each representing an individual field
        // value), so we disable this validation.
        if (e.settings.B_ && 4 /* ArrayArgument */ !== e.k_) throw e.K_("Nested arrays are not supported");
        return function(t, e) {
            const n = [];
            let s = 0;
            for (const i of t) {
                let t = Ta(i, e.Q_(s));
                null == t && (
                // Just include nulls in the array for fields being replaced with a
                // sentinel.
                t = {
                    nullValue: "NULL_VALUE"
                }), n.push(t), s++;
            }
            return {
                arrayValue: {
                    values: n
                }
            };
        }(t, e);
    }
    return function(t, e) {
        t instanceof Nc && (t = t.d_);
        if (null === t) return {
            nullValue: "NULL_VALUE"
        };
        if ("number" == typeof t) return me(e.Qt, t);
        if ("boolean" == typeof t) return {
            booleanValue: t
        };
        if ("string" == typeof t) return {
            stringValue: t
        };
        if (t instanceof Date) {
            const n = K.fromDate(t);
            return {
                timestampValue: $n(e.Qt, n)
            };
        }
        if (t instanceof K) {
            // Firestore backend truncates precision down to microseconds. To ensure
            // offline mode works the same with regards to truncation, perform the
            // truncation immediately without waiting for the backend to do that.
            const n = new K(t.seconds, 1e3 * Math.floor(t.nanoseconds / 1e3));
            return {
                timestampValue: $n(e.Qt, n)
            };
        }
        if (t instanceof Zc) return {
            geoPointValue: {
                latitude: t.latitude,
                longitude: t.longitude
            }
        };
        if (t instanceof O) return {
            bytesValue: Fn(e.Qt, t.I)
        };
        if (t instanceof Wc) {
            const n = e.t, s = t.firestore.I_;
            if (!s.isEqual(n)) throw e.K_(`Document reference is for database ${s.projectId}/${s.database} but should be for database ${n.projectId}/${n.database}`);
            return {
                referenceValue: Mn(t.firestore.I_ || e.t, t.S_.path)
            };
        }
        throw e.K_(`Unsupported field value: ${Lc(t)}`);
    }
    /**
 * Checks whether an object looks like a JSON object that should be converted
 * into a struct. Normal class/prototype instances are considered to look like
 * JSON objects since they should be converted to a struct value. Arrays, Dates,
 * GeoPoints, etc. are not considered to look like JSON objects since they map
 * to specific FieldValue types other than ObjectValue.
 */ (t, e);
}

function Ia(t, e) {
    const n = {};
    return Z(t) ? 
    // If we encounter an empty object, we explicitly add it to the update
    // mask to ensure that the server creates a map entry.
    e.path && e.path.length > 0 && e.Gt.push(e.path) : X(t, ((t, s) => {
        const i = Ta(s, e.L_(t));
        null != i && (n[t] = i);
    })), {
        mapValue: {
            fields: n
        }
    };
}

function ma(t) {
    return !("object" != typeof t || null === t || t instanceof Array || t instanceof Date || t instanceof K || t instanceof Zc || t instanceof O || t instanceof Wc || t instanceof xc);
}

function Aa(t, e, n) {
    if (!ma(n) || !function(t) {
        return "object" == typeof t && null !== t && (Object.getPrototypeOf(t) === Object.prototype || null === Object.getPrototypeOf(t));
    }(n)) {
        const s = Lc(n);
        throw "an object" === s ? e.K_(t + " a custom object") : e.K_(t + " " + s);
    }
}

/**
 * Helper that calls fromDotSeparatedString() but wraps any error thrown.
 */ function Ra(t, e, n) {
    if (
    // If required, replace the FieldPath Compat class with with the firestore-exp
    // FieldPath.
    e instanceof Nc && (e = e.d_), e instanceof Cc) return e.f_;
    if ("string" == typeof e) return ya(t, e);
    throw ga("Field path arguments must be of type string or FieldPath.", t, 
    /* hasConverter= */ !1, 
    /* path= */ void 0, n);
}

/**
 * Matches any characters in a field path string that are reserved.
 */ const Pa = new RegExp("[~\\*/\\[\\]]");

/**
 * Wraps fromDotSeparatedString with an error message about the method that
 * was thrown.
 * @param methodName - The publicly visible method name
 * @param path - The dot-separated string form of a field path which will be
 * split on dots.
 * @param targetDoc - The document against which the field path will be
 * evaluated.
 */ function ya(t, e, n) {
    if (e.search(Pa) >= 0) throw ga(`Invalid field path (${e}). Paths must not contain '~', '*', '/', '[', or ']'`, t, 
    /* hasConverter= */ !1, 
    /* path= */ void 0, n);
    try {
        return new Cc(...e.split(".")).f_;
    } catch (s) {
        throw ga(`Invalid field path (${e}). Paths must not be empty, begin with '.', end with '.', or contain '..'`, t, 
        /* hasConverter= */ !1, 
        /* path= */ void 0, n);
    }
}

function ga(t, e, n, s, i) {
    const r = s && !s.et(), o = void 0 !== i;
    let c = `Function ${e}() called with invalid data`;
    n && (c += " (via `toFirestore()`)"), c += ". ";
    let a = "";
    return (r || o) && (a += " (found", r && (a += ` in field ${s}`), o && (a += ` in document ${i}`), 
    a += ")"), new F($.INVALID_ARGUMENT, c + t + a);
}

/** Checks `haystack` if FieldPath `needle` is present. Runs in O(n). */ function Va(t, e) {
    return t.some((t => t.isEqual(e)));
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Internal transaction object responsible for accumulating the mutations to
 * perform and the base versions for any documents read.
 */ class pa {
    constructor(t) {
        this.Sh = t, 
        // The version of each document that was read during this transaction.
        this.Y_ = new Map, this.mutations = [], this.X_ = !1, 
        /**
         * A deferred usage error that occurred previously in this transaction that
         * will cause the transaction to fail once it actually commits.
         */
        this.Z_ = null, 
        /**
         * Set of documents that have been written in the transaction.
         *
         * When there's more than one write to the same key in a transaction, any
         * writes after the first are handled differently.
         */
        this.tf = new Set;
    }
    async ef(t) {
        if (this.nf(), this.mutations.length > 0) throw new F($.INVALID_ARGUMENT, "Firestore transactions require all reads to be executed before all writes.");
        const e = await async function(t, e) {
            const n = b(t), s = Kn(n.Qt) + "/documents", i = {
                documents: e.map((t => Bn(n.Qt, t)))
            }, r = await n.Du("BatchGetDocuments", s, i), o = new Map;
            r.forEach((t => {
                const e = zn(n.Qt, t);
                o.set(e.key.toString(), e);
            }));
            const c = [];
            return e.forEach((t => {
                const e = o.get(t.toString());
                p(!!e), c.push(e);
            })), c;
        }(this.Sh, t);
        return e.forEach((t => {
            t instanceof St || t instanceof vt ? this.sf(t) : V();
        })), e;
    }
    set(t, e) {
        this.write(e.F_(t, this.jt(t))), this.tf.add(t.toString());
    }
    update(t, e) {
        try {
            this.write(e.F_(t, this.rf(t)));
        } catch (t) {
            this.Z_ = t;
        }
        this.tf.add(t.toString());
    }
    delete(t) {
        this.write(new He(t, this.jt(t))), this.tf.add(t.toString());
    }
    async commit() {
        if (this.nf(), this.Z_) throw this.Z_;
        const t = this.Y_;
        // For each mutation, note that the doc was written.
                this.mutations.forEach((e => {
            t.delete(e.key.toString());
        })), 
        // For each document that was read but not written to, we want to perform
        // a `verify` operation.
        t.forEach(((t, e) => {
            const n = J._t(e);
            this.mutations.push(new Je(n, this.jt(n)));
        })), await async function(t, e) {
            const n = b(t), s = Kn(n.Qt) + "/documents", i = {
                writes: e.map((t => Jn(n.Qt, t)))
            };
            await n.pu("Commit", s, i);
        }(this.Sh, this.mutations), this.X_ = !0;
    }
    sf(t) {
        let e;
        if (t instanceof vt) e = t.version; else {
            if (!(t instanceof St)) throw V();
            // For deleted docs, we must use baseVersion 0 when we overwrite them.
            e = W.min();
        }
        const n = this.Y_.get(t.key.toString());
        if (n) {
            if (!e.isEqual(n)) 
            // This transaction will fail no matter what.
            throw new F($.ABORTED, "Document version changed between two reads.");
        } else this.Y_.set(t.key.toString(), e);
    }
    /**
     * Returns the version of this document when it was read in this transaction,
     * as a precondition, or no precondition if it was not read.
     */    jt(t) {
        const e = this.Y_.get(t.toString());
        return !this.tf.has(t.toString()) && e ? Fe.updateTime(e) : Fe.Kt();
    }
    /**
     * Returns the precondition for a document if the operation is an update.
     */    rf(t) {
        const e = this.Y_.get(t.toString());
        // The first time a document is written, we want to take into account the
        // read time and existence
                if (!this.tf.has(t.toString()) && e) {
            if (e.isEqual(W.min())) 
            // The document doesn't exist, so fail the transaction.
            // This has to be validated locally because you can't send a
            // precondition that a document does not exist without changing the
            // semantics of the backend write to be an insert. This is the reverse
            // of what we want, since we want to assert that the document doesn't
            // exist but then send the update and have it fail. Since we can't
            // express that to the backend, we have to validate locally.
            // Note: this can change once we can send separate verify writes in the
            // transaction.
            throw new F($.INVALID_ARGUMENT, "Can't update a document that doesn't exist.");
            // Document exists, base precondition on document update time.
                        return Fe.updateTime(e);
        }
        // Document was not read, so we just use the preconditions for a blind
        // update.
        return Fe.exists(!0);
    }
    write(t) {
        this.nf(), this.mutations.push(t);
    }
    nf() {}
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * TransactionRunner encapsulates the logic needed to run and retry transactions
 * with backoff.
 */
class ba {
    constructor(t, e, n, s) {
        this._s = t, this.Sh = e, this.updateFunction = n, this.Es = s, this.cf = 5, this.Gu = new Gr(this._s, "transaction_retry" /* TransactionRetry */);
    }
    /** Runs the transaction and sets the result on deferred. */    run() {
        this.af();
    }
    af() {
        this.Gu.Lu((async () => {
            const t = new pa(this.Sh), e = this.uf(t);
            e && e.then((e => {
                this._s.Ps((() => t.commit().then((() => {
                    this.Es.resolve(e);
                })).catch((t => {
                    this.hf(t);
                }))));
            })).catch((t => {
                this.hf(t);
            }));
        }));
    }
    uf(t) {
        try {
            const e = this.updateFunction(t);
            return !at(e) && e.catch && e.then ? e : (this.Es.reject(Error("Transaction callback must return a Promise")), 
            null);
        } catch (t) {
            // Do not retry errors thrown by user provided updateFunction.
            return this.Es.reject(t), null;
        }
    }
    hf(t) {
        this.cf > 0 && this.lf(t) ? (this.cf -= 1, this._s.Ps((() => (this.af(), Promise.resolve())))) : this.Es.reject(t);
    }
    lf(t) {
        if ("FirebaseError" === t.name) {
            // In transactions, the backend will fail outdated reads with FAILED_PRECONDITION and
            // non-matching document versions with ABORTED. These errors should be retried.
            const e = t.code;
            return "aborted" === e || "failed-precondition" === e || !tn(e);
        }
        return !1;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * FirestoreClient is a top-level class that constructs and owns all of the
 * pieces of the client SDK architecture. It is responsible for creating the
 * async queue that is shared by all of the other components in the system.
 */
class va {
    constructor(t, 
    /**
     * Asynchronous queue responsible for all of our internal processing. When
     * we get incoming work from the user (via public API) or the network
     * (incoming GRPC messages), we should always schedule onto this queue.
     * This ensures all of our work is properly serialized (e.g. we don't
     * start processing a new operation while the previous one is waiting for
     * an async I/O to complete).
     */
    e, n) {
        this.credentials = t, this._s = e, this.yu = n, this.user = k.UNAUTHENTICATED, this.clientId = S.u(), 
        this._f = () => {}, this.C = new ds, this.credentials.v((t => {
            R("FirestoreClient", "Received user=", t.uid), this.user = t, this._f(t), this.C.resolve();
        }));
    }
    async getConfiguration() {
        return await this.C.promise, {
            _s: this._s,
            yu: this.yu,
            clientId: this.clientId,
            credentials: this.credentials,
            Jl: this.user,
            pl: 100
        };
    }
    ff(t) {
        this._f = t, 
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.C.promise.then((() => this._f(this.user)));
    }
    /**
     * Checks that the client has not been terminated. Ensures that other methods on
     * this class cannot be called after the client is terminated.
     */    df() {
        if (this._s.wf) throw new F($.FAILED_PRECONDITION, "The client has already been terminated.");
    }
    terminate() {
        this._s.Ef();
        const t = new ds;
        return this._s.Tf((async () => {
            try {
                this.If && await this.If.terminate(), this.mf && await this.mf.terminate(), 
                // `removeChangeListener` must be called after shutting down the
                // RemoteStore as it will prevent the RemoteStore from retrieving
                // auth tokens.
                this.credentials.S(), t.resolve();
            } catch (e) {
                const n = ps(e, "Failed to shutdown persistence");
                t.reject(n);
            }
        })), t.promise;
    }
}

async function Sa(t, e) {
    t._s.Af(), R("FirestoreClient", "Initializing OfflineComponentProvider");
    const n = await t.getConfiguration();
    await e.initialize(n);
    let s = n.Jl;
    t.ff((n => {
        s.isEqual(n) || (s = n, t._s.po((async () => {
            await rr(e.bh, n);
        })));
    })), 
    // When a user calls clearPersistence() in one client, all other clients
    // need to be terminated to allow the delete to succeed.
    e.persistence.Po((() => t.terminate())), t.mf = e;
}

async function Da(t, e) {
    t._s.Af();
    const n = await Ca(t);
    R("FirestoreClient", "Initializing OnlineComponentProvider");
    const s = await t.getConfiguration();
    await e.initialize(n, s), 
    // The CredentialChangeListener of the online component provider takes
    // precedence over the offline component provider.
    t.ff((n => t._s.po((() => async function(t, e) {
        const n = b(t);
        n._s.Af(), R("RemoteStore", "RemoteStore received new credentials");
        const s = ao(n);
        // Tear down and re-create our network streams. This will ensure we get a
        // fresh auth token for the new user and re-fill the write pipeline with
        // new mutations from the LocalStore (since mutations are per-user).
                n.Nh.add(3 /* CredentialChange */), await eo(n), s && 
        // Don't set the network status to Unknown if we are offline.
        n.Oh.set("Unknown" /* Unknown */), await n.Dh.n_(e), n.Nh.delete(3 /* CredentialChange */), 
        await to(n);
    }(e.yl, n))))), t.If = e;
}

async function Ca(t) {
    return t.mf || (R("FirestoreClient", "Using default OfflineComponentProvider"), 
    await Sa(t, new gc)), t.mf;
}

async function xa(t) {
    return t.If || (R("FirestoreClient", "Using default OnlineComponentProvider"), await Da(t, new bc)), 
    t.If;
}

function Na(t) {
    return Ca(t).then((t => t.persistence));
}

function $a(t) {
    return Ca(t).then((t => t.bh));
}

function Fa(t) {
    return xa(t).then((t => t.yl));
}

function Oa(t) {
    return xa(t).then((t => t._a));
}

async function ka(t) {
    const e = await xa(t), n = e.gl;
    return n.jh = jo.bind(null, e._a), n.Hh = zo.bind(null, e._a), n;
}

/** Enables the network connection and re-enqueues all pending operations. */ function Ma(t) {
    return t._s.enqueue((async () => {
        const e = await Na(t), n = await Fa(t);
        return e.yo(!0), function(t) {
            const e = b(t);
            return e.Nh.delete(0 /* UserDisabled */), to(e);
        }(n);
    }));
}

/** Disables the network connection. Pending operations will not complete. */ function La(t) {
    return t._s.enqueue((async () => {
        const e = await Na(t), n = await Fa(t);
        return e.yo(!1), async function(t) {
            const e = b(t);
            e.Nh.add(0 /* UserDisabled */), await eo(e), 
            // Set the OnlineState to Offline so get()s return from cache, etc.
            e.Oh.set("Offline" /* Offline */);
        }(n);
    }));
}

/**
 * Returns a Promise that resolves when all writes that were pending at the time
 * this method was called received server acknowledgement. An acknowledgement
 * can be either acceptance or rejection.
 */ function Ba(t, e) {
    const n = new ds;
    return t._s.Ps((async () => async function(t, e, n) {
        try {
            const s = await function(t, e) {
                const n = b(t);
                return n.persistence.runTransaction("read document", "readonly", (t => n.Tc.Ho(t, e)));
            }(t, e);
            s instanceof vt ? n.resolve(s) : s instanceof St ? n.resolve(null) : n.reject(new F($.UNAVAILABLE, "Failed to get document from cache. (However, this document may exist on the server. Run again without setting 'source' in the GetOptions to attempt to retrieve the document from the server.)"));
        } catch (t) {
            const s = ps(t, `Failed to get document '${e} from cache`);
            n.reject(s);
        }
    }
    /**
 * Retrieves a latency-compensated document from the backend via a
 * SnapshotListener.
 */ (await $a(t), e, n))), n.promise;
}

function qa(t, e, n = {}) {
    const s = new ds;
    return t._s.Ps((async () => function(t, e, n, s, i) {
        const r = new Sc({
            next: r => {
                // Remove query first before passing event to user to avoid
                // user actions affecting the now stale query.
                e.Ps((() => Co(t, o)));
                const c = r.docs.has(n);
                !c && r.fromCache ? 
                // TODO(dimond): If we're online and the document doesn't
                // exist then we resolve with a doc.exists set to false. If
                // we're offline however, we reject the Promise in this
                // case. Two options: 1) Cache the negative response from
                // the server so we can deliver that even when you're
                // offline 2) Actually reject the Promise in the online case
                // if the document doesn't exist.
                i.reject(new F($.UNAVAILABLE, "Failed to get document because the client is offline.")) : c && r.fromCache && s && "server" === s.source ? i.reject(new F($.UNAVAILABLE, 'Failed to get document from server. (However, this document does exist in the local cache. Run again without setting source to "server" to retrieve the cached document.)')) : i.resolve(r);
            },
            error: t => i.reject(t)
        }), o = new Fo(ee(n.path), r, {
            includeMetadataChanges: !0,
            nl: !0
        });
        return Do(t, o);
    }(await ka(t), t._s, e, n, s))), s.promise;
}

function Ua(t, e) {
    const n = new ds;
    return t._s.Ps((async () => async function(t, e, n) {
        try {
            const s = await fr(t, e, 
            /* usePreviousResults= */ !0), i = new Uo(e, s.Rc), r = i.fl(s.documents), o = i.wi(r, 
            /* updateLimboDocuments= */ !1);
            n.resolve(o.snapshot);
        } catch (t) {
            const s = ps(t, `Failed to execute query '${e} against cache`);
            n.reject(s);
        }
    }
    /**
 * Retrieves a latency-compensated query snapshot from the backend via a
 * SnapshotListener.
 */ (await $a(t), e, n))), n.promise;
}

function Qa(t, e, n = {}) {
    const s = new ds;
    return t._s.Ps((async () => function(t, e, n, s, i) {
        const r = new Sc({
            next: n => {
                // Remove query first before passing event to user to avoid
                // user actions affecting the now stale query.
                e.Ps((() => Co(t, o))), n.fromCache && "server" === s.source ? i.reject(new F($.UNAVAILABLE, 'Failed to get documents from server. (However, these documents may exist in the local cache. Run again without setting source to "server" to retrieve the cached documents.)')) : i.resolve(n);
            },
            error: t => i.reject(t)
        }), o = new Fo(n, r, {
            includeMetadataChanges: !0,
            nl: !0
        });
        return Do(t, o);
    }(await ka(t), t._s, e, n, s))), s.promise;
}

function Ka(t, e) {
    const n = new Sc(e);
    return t._s.Ps((async () => function(t, e) {
        b(t).Wh.add(e), 
        // Immediately fire an initial event, indicating all existing listeners
        // are in-sync.
        e.next();
    }(await ka(t), n))), () => {
        n.i_(), t._s.Ps((async () => function(t, e) {
            b(t).Wh.delete(e);
        }(await ka(t), n)));
    };
}

/**
 * Takes an updateFunction in which a set of reads and writes can be performed
 * atomically. In the updateFunction, the client can read and write values
 * using the supplied transaction object. After the updateFunction, all
 * changes will be committed. If a retryable error occurs (ex: some other
 * client has changed any of the data referenced), then the updateFunction
 * will be called again after a backoff. If the updateFunction still fails
 * after all retries, then the transaction will be rejected.
 *
 * The transaction object passed to the updateFunction contains methods for
 * accessing documents and collections. Unlike other datastore access, data
 * accessed with the transaction will not reflect local changes that have not
 * been committed. For this reason, it is required that all reads are
 * performed before any writes. Transactions must be performed while online.
 */ function Wa(t, e) {
    const n = new ds;
    return t._s.Ps((async () => {
        const s = await function(t) {
            return xa(t).then((t => t.Sh));
        }(t);
        new ba(t._s, s, e, n).run();
    })), n.promise;
}

function ja(t, e, n, s) {
    const i = function(t, e) {
        let n;
        n = "string" == typeof t ? (new TextEncoder).encode(t) : t;
        return function(t, e) {
            return new Dc(t, e);
        }(function(t, e) {
            if (t instanceof Uint8Array) return vc(t, e);
            if (t instanceof ArrayBuffer) return vc(new Uint8Array(t), e);
            if (t instanceof ReadableStream) return t.getReader();
            throw new Error("Source of `toByteStreamReader` has to be a ArrayBuffer or ReadableStream");
        }(n), e);
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (n, jr(e));
    t._s.Ps((async () => {
        yc(await Oa(t), i, s);
    }));
}

function Ga(t, e) {
    return t._s.enqueue((async () => function(t, e) {
        const n = b(t);
        return n.persistence.runTransaction("Get named query", "readonly", (t => n.wo.Ms(t, e)));
    }(await $a(t), e)));
}

class za {
    constructor() {
        // The last promise in the queue.
        this.Rf = Promise.resolve(), 
        // A list of retryable operations. Retryable operations are run in order and
        // retried with backoff.
        this.Pf = [], 
        // Is this AsyncQueue being shut down? Once it is set to true, it will not
        // be changed again.
        this.yf = !1, 
        // Operations scheduled to be queued in the future. Operations are
        // automatically removed after they are run or canceled.
        this.gf = [], 
        // visible for testing
        this.Vf = null, 
        // Flag set while there's an outstanding AsyncQueue operation, used for
        // assertion sanity-checks.
        this.pf = !1, 
        // List of TimerIds to fast-forward delays for.
        this.bf = [], 
        // Backoff timer used to schedule retries for retryable operations
        this.Gu = new Gr(this, "async_queue_retry" /* AsyncQueueRetry */), 
        // Visibility handler that triggers an immediate retry of all retryable
        // operations. Meant to speed up recovery when we regain file system access
        // after page comes into foreground.
        this.vf = () => {
            const t = Wr();
            t && R("AsyncQueue", "Visibility state changed to " + t.visibilityState), this.Gu.qu();
        };
        const t = Wr();
        t && "function" == typeof t.addEventListener && t.addEventListener("visibilitychange", this.vf);
    }
    get wf() {
        return this.yf;
    }
    /**
     * Adds a new operation to the queue without waiting for it to complete (i.e.
     * we ignore the Promise result).
     */    Ps(t) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.enqueue(t);
    }
    Tf(t) {
        this.Sf(), 
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.Df(t);
    }
    Ef() {
        if (!this.yf) {
            this.yf = !0;
            const t = Wr();
            t && "function" == typeof t.removeEventListener && t.removeEventListener("visibilitychange", this.vf);
        }
    }
    enqueue(t) {
        return this.Sf(), this.yf ? new Promise((t => {})) : this.Df(t);
    }
    po(t) {
        this.Ps((() => (this.Pf.push(t), this.Cf())));
    }
    /**
     * Runs the next operation from the retryable queue. If the operation fails,
     * reschedules with backoff.
     */    async Cf() {
        if (0 !== this.Pf.length) {
            try {
                await this.Pf[0](), this.Pf.shift(), this.Gu.reset();
            } catch (t) {
                if (!As(t)) throw t;
 // Failure will be handled by AsyncQueue
                                R("AsyncQueue", "Operation failed with retryable error: " + t);
            }
            this.Pf.length > 0 && 
            // If there are additional operations, we re-schedule `retryNextOp()`.
            // This is necessary to run retryable operations that failed during
            // their initial attempt since we don't know whether they are already
            // enqueued. If, for example, `op1`, `op2`, `op3` are enqueued and `op1`
            // needs to  be re-run, we will run `op1`, `op1`, `op2` using the
            // already enqueued calls to `retryNextOp()`. `op3()` will then run in the
            // call scheduled here.
            // Since `backoffAndRun()` cancels an existing backoff and schedules a
            // new backoff on every call, there is only ever a single additional
            // operation in the queue.
            this.Gu.Lu((() => this.Cf()));
        }
    }
    Df(t) {
        const e = this.Rf.then((() => (this.pf = !0, t().catch((t => {
            this.Vf = t, this.pf = !1;
            // Re-throw the error so that this.tail becomes a rejected Promise and
            // all further attempts to chain (via .then) will just short-circuit
            // and return the rejected Promise.
            throw P("INTERNAL UNHANDLED ERROR: ", 
            /**
 * Chrome includes Error.message in Error.stack. Other browsers do not.
 * This returns expected output of message + stack when available.
 * @param error - Error or FirestoreError
 */
            function(t) {
                let e = t.message || "";
                t.stack && (e = t.stack.includes(t.message) ? t.stack : t.message + "\n" + t.stack);
                return e;
            }
            /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t)), t;
        })).then((t => (this.pf = !1, t))))));
        return this.Rf = e, e;
    }
    Tr(t, e, n) {
        this.Sf(), 
        // Fast-forward delays for timerIds that have been overriden.
        this.bf.indexOf(t) > -1 && (e = 0);
        const s = Vs.Ts(this, t, e, n, (t => this.xf(t)));
        return this.gf.push(s), s;
    }
    Sf() {
        this.Vf && V();
    }
    Af() {}
    /**
     * Waits until all currently queued tasks are finished executing. Delayed
     * operations are not run.
     */    async Nf() {
        // Operations in the queue prior to draining may have enqueued additional
        // operations. Keep draining the queue until the tail is no longer advanced,
        // which indicates that no more new operations were enqueued and that all
        // operations were executed.
        let t;
        do {
            t = this.Rf, await t;
        } while (t !== this.Rf);
    }
    /**
     * For Tests: Determine if a delayed operation with a particular TimerId
     * exists.
     */    $f(t) {
        for (const e of this.gf) if (e.fs === t) return !0;
        return !1;
    }
    /**
     * For Tests: Runs some or all delayed operations early.
     *
     * @param lastTimerId - Delayed operations up to and including this TimerId
     * will be drained. Pass TimerId.All to run all delayed operations.
     * @returns a Promise that resolves once all operations have been run.
     */    Ff(t) {
        // Note that draining may generate more delayed ops, so we do that first.
        return this.Nf().then((() => {
            // Run ops in the same order they'd run if they ran naturally.
            this.gf.sort(((t, e) => t.ds - e.ds));
            for (const e of this.gf) if (e.Rs(), "all" /* All */ !== t && e.fs === t) break;
            return this.Nf();
        }));
    }
    /**
     * For Tests: Skip all subsequent delays for a timer id.
     */    Of(t) {
        this.bf.push(t);
    }
    /** Called once a DelayedOperation is run or canceled. */    xf(t) {
        // NOTE: indexOf / slice are O(n), but delayedOperations is expected to be small.
        const e = this.gf.indexOf(t);
        this.gf.splice(e, 1);
    }
}

function Ha(t) {
    /**
 * Returns true if obj is an object and contains at least one of the specified
 * methods.
 */
    return function(t, e) {
        if ("object" != typeof t || null === t) return !1;
        const n = t;
        for (const t of e) if (t in n && "function" == typeof n[t]) return !0;
        return !1;
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /**
 * Represents the task of loading a Firestore bundle. It provides progress of bundle
 * loading, as well as task completion and error events.
 *
 * The API is compatible with `Promise<LoadBundleTaskProgress>`.
 */ (t, [ "next", "error", "complete" ]);
}

class Ja {
    constructor() {
        this.kf = {}, this.Mf = new ds, this.Lf = {
            taskState: "Running",
            totalBytes: 0,
            totalDocuments: 0,
            bytesLoaded: 0,
            documentsLoaded: 0
        };
    }
    /**
     * Registers functions to listen to bundle loading progress events.
     * @param next - Called when there is a progress update from bundle loading. Typically `next` calls occur
     *   each time a Firestore document is loaded from the bundle.
     * @param error - Called when an error occurs during bundle loading. The task aborts after reporting the
     *   error, and there should be no more updates after this.
     * @param complete - Called when the loading task is complete.
     */    onProgress(t, e, n) {
        this.kf = {
            next: t,
            error: e,
            complete: n
        };
    }
    /**
     * Implements the `Promise<LoadBundleTaskProgress>.catch` interface.
     *
     * @param onRejected - Called when an error occurs during bundle loading.
     */    catch(t) {
        return this.Mf.promise.catch(t);
    }
    /**
     * Implements the `Promise<LoadBundleTaskProgress>.then` interface.
     *
     * @param onFulfilled - Called on the completion of the loading task with a final `LoadBundleTaskProgress` update.
     *   The update will always have its `taskState` set to `"Success"`.
     * @param onRejected - Called when an error occurs during bundle loading.
     */    then(t, e) {
        return this.Mf.promise.then(t, e);
    }
    /**
     * Notifies all observers that bundle loading has completed, with a provided
     * `LoadBundleTaskProgress` object.
     *
     * @private
     */    ql(t) {
        this.Ul(t), this.kf.complete && this.kf.complete(), this.Mf.resolve(t);
    }
    /**
     * Notifies all observers that bundle loading has failed, with a provided
     * `Error` as the reason.
     *
     * @private
     */    Kl(t) {
        this.Lf.taskState = "Error", this.kf.next && this.kf.next(this.Lf), this.kf.error && this.kf.error(t), 
        this.Mf.reject(t);
    }
    /**
     * Notifies a progress update of loading a bundle.
     * @param progress - The new progress.
     *
     * @private
     */    Ul(t) {
        this.Lf = t, this.kf.next && this.kf.next(t);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** DOMException error code constants. */ const Ya = -1;

/**
 * The Cloud Firestore service interface.
 *
 * Do not call this constructor directly. Instead, use {@link getFirestore}.
 */
class Xa extends Kc {
    /** @hideconstructor */
    constructor(t, e) {
        super(t, e), this.Bf = new za, this.w_ = "name" in t ? t.name : "[DEFAULT]";
    }
    b_() {
        return this.qf || 
        // The client must be initialized to ensure that all subsequent API
        // usage throws an exception.
        tu(this), this.qf.terminate();
    }
}

function Za(t) {
    return t.qf || tu(t), t.qf.df(), t.qf;
}

function tu(t) {
    const e = t.p_(), n = function(t, e, n) {
        return new T(t, e, n.host, n.ssl, n.experimentalForceLongPolling, n.experimentalAutoDetectLongPolling);
    }(t.I_, t.w_, e);
    t.qf = new va(t.m_, t.Bf, n);
}

/**
 * Attempts to enable persistent storage, if possible.
 *
 * Must be called before any other functions (other than
 * {@link initializeFirestore}, {@link getFirestore} or
 * {@link clearIndexedDbPersistence}.
 *
 * If this fails, `enableIndexedDbPersistence()` will reject the promise it
 * returns. Note that even after this failure, the `Firestore` instance will
 * remain usable, however offline persistence will be disabled.
 *
 * There are several reasons why this can fail, which can be identified by
 * the `code` on the error.
 *
 *   * failed-precondition: The app is already open in another browser tab.
 *   * unimplemented: The browser is incompatible with the offline
 *     persistence implementation.
 *
 * @param firestore - The `Firestore` instance to enable persistence for.
 * @param persistenceSettings - Optional settings object to configure
 * persistence.
 * @returns A promise that represents successfully enabling persistent storage.
 */
/**
 * Registers both the `OfflineComponentProvider` and `OnlineComponentProvider`.
 * If the operation fails with a recoverable error (see
 * `canRecoverFromIndexedDbError()` below), the returned Promise is rejected
 * but the client remains usable.
 */
function eu(t, e, n) {
    const s = new ds;
    return t._s.enqueue((async () => {
        try {
            await Sa(t, n), await Da(t, e), s.resolve();
        } catch (t) {
            if (!
            /**
 * Decides whether the provided error allows us to gracefully disable
 * persistence (as opposed to crashing the client).
 */
            function(t) {
                if ("FirebaseError" === t.name) return t.code === $.FAILED_PRECONDITION || t.code === $.UNIMPLEMENTED;
                if ("undefined" != typeof DOMException && t instanceof DOMException) 
                // There are a few known circumstances where we can open IndexedDb but
                // trying to read/write will fail (e.g. quota exceeded). For
                // well-understood cases, we attempt to detect these and then gracefully
                // fall back to memory persistence.
                // NOTE: Rather than continue to add to this list, we could decide to
                // always fall back, with the risk that we might accidentally hide errors
                // representing actual SDK bugs.
                // When the browser is out of quota we could get either quota exceeded
                // or an aborted error depending on whether the error happened during
                // schema migration.
                return 22 === t.code || 20 === t.code || 
                // Firefox Private Browsing mode disables IndexedDb and returns
                // INVALID_STATE for any usage.
                11 === t.code;
                return !0;
            }
            /**
 * Clears the persistent storage. This includes pending writes and cached
 * documents.
 *
 * Must be called while the `Firestore` instance is not started (after the app is
 * terminated or when the app is first initialized). On startup, this function
 * must be called before other functions (other than {@link
 * initializeFirestore} or {@link getFirestore})). If the `Firestore`
 * instance is still running, the promise will be rejected with the error code
 * of `failed-precondition`.
 *
 * Note: `clearIndexedDbPersistence()` is primarily intended to help write
 * reliable tests that use Cloud Firestore. It uses an efficient mechanism for
 * dropping existing data but does not attempt to securely overwrite or
 * otherwise make cached data unrecoverable. For applications that are sensitive
 * to the disclosure of cached data in between user sessions, we strongly
 * recommend not enabling persistence at all.
 *
 * @param firestore - The `Firestore` instance to clear persistence for.
 * @returns A promise that is resolved when the persistent storage is
 * cleared. Otherwise, the promise is rejected with an error.
 */ (t)) throw t;
            console.warn("Error enabling offline persistence. Falling back to persistence disabled: " + t), 
            s.reject(t);
        }
    })).then((() => s.promise));
}

function nu(t) {
    if (t.R_ && !t.P_) throw new F($.FAILED_PRECONDITION, "Persistence can only be cleared before a Firestore instance is initialized or after it is terminated.");
    const e = new ds;
    return t.Bf.Tf((async () => {
        try {
            await async function(t) {
                if (!Ts.Kn()) return Promise.resolve();
                const e = t + "main";
                await Ts.delete(e);
            }(Xi(t.I_, t.w_)), e.resolve();
        } catch (t) {
            e.reject(t);
        }
    })), e.promise;
}

/**
 * Waits until all currently pending writes for the active user have been
 * acknowledged by the backend.
 *
 * The returned Promise resolves immediately if there are no outstanding writes.
 * Otherwise, the Promise waits for all previously issued writes (including
 * those written in a previous app session), but it does not wait for writes
 * that were added after the function is called. If you want to wait for
 * additional writes, call `waitForPendingWrites()` again.
 *
 * Any outstanding `waitForPendingWrites()` Promises are rejected during user
 * changes.
 *
 * @returns A Promise which resolves when all currently pending writes have been
 * acknowledged by the backend.
 */ function su(t) {
    return function(t) {
        const e = new ds;
        return t._s.Ps((async () => ec(await Oa(t), e))), e.promise;
    }(Za(t = Bc(t, Xa)));
}

/**
 * Re-enables use of the network for this Firestore instance after a prior
 * call to {@link disableNetwork}.
 *
 * @returns A promise that is resolved once the network has been enabled.
 */ function iu(t) {
    if (t.R_ || t.P_) throw new F($.FAILED_PRECONDITION, "Firestore has already been started and persistence can no longer be enabled. You can only enable persistence before calling any other methods on a Firestore object.");
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Converts Firestore's internal types to the JavaScript types that we expose
 * to the user.
 */ class ru {
    Uf(t, e = "none") {
        switch (lt(t)) {
          case 0 /* NullValue */ :
            return null;

          case 1 /* BooleanValue */ :
            return t.booleanValue;

          case 2 /* NumberValue */ :
            return st(t.integerValue || t.doubleValue);

          case 3 /* TimestampValue */ :
            return this.Qf(t.timestampValue);

          case 4 /* ServerTimestampValue */ :
            return this.Kf(t, e);

          case 5 /* StringValue */ :
            return t.stringValue;

          case 6 /* BlobValue */ :
            return this.Wf(it(t.bytesValue));

          case 7 /* RefValue */ :
            return this.jf(t.referenceValue);

          case 8 /* GeoPointValue */ :
            return this.Gf(t.geoPointValue);

          case 9 /* ArrayValue */ :
            return this.zf(t.arrayValue, e);

          case 10 /* ObjectValue */ :
            return this.Hf(t.mapValue, e);

          default:
            throw V();
        }
    }
    Hf(t, e) {
        const n = {};
        return X(t.fields || {}, ((t, s) => {
            n[t] = this.Uf(s, e);
        })), n;
    }
    Gf(t) {
        return new Zc(st(t.latitude), st(t.longitude));
    }
    zf(t, e) {
        return (t.values || []).map((t => this.Uf(t, e)));
    }
    Kf(t, e) {
        switch (e) {
          case "previous":
            const n = ot(t);
            return null == n ? null : this.Uf(n, e);

          case "estimate":
            return this.Qf(ct(t));

          default:
            return null;
        }
    }
    Qf(t) {
        const e = nt(t);
        return new K(e.seconds, e.nanos);
    }
    Jf(t, e) {
        const n = G.ot(t);
        p(fs(n));
        const s = new I(n.get(1), n.get(3)), i = new J(n.Y(5));
        return s.isEqual(e) || 
        // TODO(b/64130202): Somehow support foreign references.
        P(`Document ${i} contains a document reference within a different database (${s.projectId}/${s.database}) which is not supported. It will be treated as a reference in the current database (${e.projectId}/${e.database}) instead.`), 
        i;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `DocumentSnapshot` contains data read from a document in your Firestore
 * database. The data can be extracted with `.data()` or `.get(<field>)` to
 * get a specific field.
 *
 * For a `DocumentSnapshot` that points to a non-existing document, any data
 * access will return 'undefined'. You can use the `exists()` method to
 * explicitly verify a document's existence.
 */ class ou {
    // Note: This class is stripped down version of the DocumentSnapshot in
    // the legacy SDK. The changes are:
    // - No support for SnapshotMetadata.
    // - No support for SnapshotOptions.
    /** @hideconstructor protected */
    constructor(t, e, n, s, i) {
        this.Yf = t, this.Xf = e, this.S_ = n, this.Zf = s, this.v_ = i;
    }
    /** Property of the `DocumentSnapshot` that provides the document's ID. */    get id() {
        return this.S_.path.tt();
    }
    /**
     * The `DocumentReference` for the document included in the `DocumentSnapshot`.
     */    get ref() {
        return new Wc(this.Yf, this.v_, this.S_);
    }
    /**
     * Signals whether or not the document at the snapshot's location exists.
     *
     * @returns true if the document exists.
     */    exists() {
        return null !== this.Zf;
    }
    /**
     * Retrieves all fields in the document as an `Object`. Returns `undefined` if
     * the document doesn't exist.
     *
     * @returns An `Object` containing all fields in the document or `undefined`
     * if the document doesn't exist.
     */    data() {
        if (this.Zf) {
            if (this.v_) {
                // We only want to use the converter and create a new DocumentSnapshot
                // if a converter has been provided.
                const t = new cu(this.Yf, this.Xf, this.S_, this.Zf, 
                /* converter= */ null);
                return this.v_.fromFirestore(t);
            }
            return this.Xf.Uf(this.Zf.bt());
        }
    }
    /**
     * Retrieves the field specified by `fieldPath`. Returns `undefined` if the
     * document or field doesn't exist.
     *
     * @param fieldPath - The path (for example 'foo' or 'foo.bar') to a specific
     * field.
     * @returns The data at the specified field location or undefined if no such
     * field exists in the document.
     */
    // We are using `any` here to avoid an explicit cast by our users.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(t) {
        if (this.Zf) {
            const e = this.Zf.data().field(au("DocumentSnapshot.get", t));
            if (null !== e) return this.Xf.Uf(e);
        }
    }
}

/**
 * A `QueryDocumentSnapshot` contains data read from a document in your
 * Firestore database as part of a query. The document is guaranteed to exist
 * and its data can be extracted with `.data()` or `.get(<field>)` to get a
 * specific field.
 *
 * A `QueryDocumentSnapshot` offers the same API surface as a
 * `DocumentSnapshot`. Since query results contain only existing documents, the
 * `exists` property will always be true and `data()` will never return
 * 'undefined'.
 */ class cu extends ou {
    /**
     * Retrieves all fields in the document as an `Object`.
     *
     * @override
     * @returns An `Object` containing all fields in the document.
     */
    data() {
        return super.data();
    }
}

/**
 * Helper that calls fromDotSeparatedString() but wraps any error thrown.
 */ function au(t, e) {
    return "string" == typeof e ? ya(t, e) : e instanceof Nc ? e.d_.f_ : e.f_;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function uu(t) {
    if (se(t) && 0 === t.xt.length) throw new F($.UNIMPLEMENTED, "limitToLast() queries require specifying at least one orderBy() clause");
}

/**
 * A `QueryConstraint` is used to narrow the set of documents returned by a
 * Firestore query. `QueryConstraint`s are created by invoking {@link where},
 * {@link orderBy}, {@link startAt}, {@link startAfter}, {@link
 * endBefore}, {@link endAt}, {@link limit} or {@link limitToLast} and
 * can then be passed to {@link query} to create a new query instance that
 * also contains this `QueryConstraint`.
 */ class hu {}

/**
 * Creates a new immutable instance of `query` that is extended to also include
 * additional query constraints.
 *
 * @param query - The query instance to use as a base for the new constraints.
 * @param queryConstraints - The list of `QueryConstraint`s to apply.
 * @throws if any of the provided query constraints cannot be combined with the
 * existing or new constraints.
 */ function lu(t, ...e) {
    for (const n of e) t = n.td(t);
    return t;
}

class _u extends hu {
    constructor(t, e, n) {
        super(), this.ed = t, this.nd = e, this.sd = n, this.type = "where";
    }
    td(t) {
        const e = oa(t.firestore), n = function(t, e, n, s, i, r, o) {
            let c;
            if (i.ut()) {
                if ("array-contains" /* ARRAY_CONTAINS */ === r || "array-contains-any" /* ARRAY_CONTAINS_ANY */ === r) throw new F($.INVALID_ARGUMENT, `Invalid Query. You can't perform '${r}' queries on FieldPath.documentId().`);
                if ("in" /* IN */ === r || "not-in" /* NOT_IN */ === r) {
                    mu(o, r);
                    const e = [];
                    for (const n of o) e.push(Iu(s, t, n));
                    c = {
                        arrayValue: {
                            values: e
                        }
                    };
                } else c = Iu(s, t, o);
            } else "in" /* IN */ !== r && "not-in" /* NOT_IN */ !== r && "array-contains-any" /* ARRAY_CONTAINS_ANY */ !== r || mu(o, r), 
            c = Ea(n, e, o, 
            /* allowArrays= */ "in" /* IN */ === r || "not-in" /* NOT_IN */ === r);
            const a = kt.create(i, r, c);
            return function(t, e) {
                if (e.Ct()) {
                    const n = re(t);
                    if (null !== n && !n.isEqual(e.field)) throw new F($.INVALID_ARGUMENT, `Invalid query. All where filters with an inequality (<, <=, !=, not-in, >, or >=) must be on the same field. But you have inequality filters on '${n.toString()}' and '${e.field.toString()}'`);
                    const s = ie(t);
                    null !== s && Au(t, e.field, s);
                }
                const n = function(t, e) {
                    for (const n of t.filters) if (e.indexOf(n.op) >= 0) return n.op;
                    return null;
                }(t, 
                /**
 * Given an operator, returns the set of operators that cannot be used with it.
 *
 * Operators in a query must adhere to the following set of rules:
 * 1. Only one array operator is allowed.
 * 2. Only one disjunctive operator is allowed.
 * 3. NOT_EQUAL cannot be used with another NOT_EQUAL operator.
 * 4. NOT_IN cannot be used with array, disjunctive, or NOT_EQUAL operators.
 *
 * Array operators: ARRAY_CONTAINS, ARRAY_CONTAINS_ANY
 * Disjunctive operators: IN, ARRAY_CONTAINS_ANY, NOT_IN
 */
                function(t) {
                    switch (t) {
                      case "!=" /* NOT_EQUAL */ :
                        return [ "!=" /* NOT_EQUAL */ , "not-in" /* NOT_IN */ ];

                      case "array-contains" /* ARRAY_CONTAINS */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "not-in" /* NOT_IN */ ];

                      case "in" /* IN */ :
                        return [ "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ ];

                      case "array-contains-any" /* ARRAY_CONTAINS_ANY */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ ];

                      case "not-in" /* NOT_IN */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ , "!=" /* NOT_EQUAL */ ];

                      default:
                        return [];
                    }
                }(e.op));
                if (null !== n) 
                // Special case when it's a duplicate op to give a slightly clearer error message.
                throw n === e.op ? new F($.INVALID_ARGUMENT, `Invalid query. You cannot use more than one '${e.op.toString()}' filter.`) : new F($.INVALID_ARGUMENT, `Invalid query. You cannot use '${e.op.toString()}' filters with '${n.toString()}' filters.`);
            }(t, a), a;
        }(t.C_, "where", e, t.firestore.I_, this.ed, this.nd, this.sd);
        return new jc(t.firestore, t.v_, function(t, e) {
            const n = t.filters.concat([ e ]);
            return new Zt(t.path, t.collectionGroup, t.xt.slice(), n, t.limit, t.limitType, t.startAt, t.endAt);
        }(t.C_, n));
    }
}

/**
 * Creates a `QueryConstraint` that enforces that documents must contain the
 * specified field and that the value should satisfy the relation constraint
 * provided.
 *
 * @param fieldPath - The path to compare
 * @param opStr - The operation string (e.g "&lt;", "&lt;=", "==", "&lt;",
 *   "&lt;=", "!=").
 * @param value - The value for comparison
 * @returns The created `Query`.
 */ class fu extends hu {
    constructor(t, e) {
        super(), this.ed = t, this.rd = e, this.type = "orderBy";
    }
    td(t) {
        const e = function(t, e, n) {
            if (null !== t.startAt) throw new F($.INVALID_ARGUMENT, "Invalid query. You must not call startAt() or startAfter() before calling orderBy().");
            if (null !== t.endAt) throw new F($.INVALID_ARGUMENT, "Invalid query. You must not call endAt() or endBefore() before calling orderBy().");
            const s = new Ht(e, n);
            return function(t, e) {
                if (null === ie(t)) {
                    // This is the first order by. It must match any inequality.
                    const n = re(t);
                    null !== n && Au(t, n, e.field);
                }
            }(t, s), s;
        }
        /**
 * Create a Bound from a query and a document.
 *
 * Note that the Bound will always include the key of the document
 * and so only the provided document will compare equal to the returned
 * position.
 *
 * Will throw if the document does not contain all fields of the order by
 * of the query or if any of the fields in the order by are an uncommitted
 * server timestamp.
 */ (t.C_, this.ed, this.rd);
        return new jc(t.firestore, t.v_, function(t, e) {
            // TODO(dimond): validate that orderBy does not list the same key twice.
            const n = t.xt.concat([ e ]);
            return new Zt(t.path, t.collectionGroup, n, t.filters.slice(), t.limit, t.limitType, t.startAt, t.endAt);
        }(t.C_, e));
    }
}

/**
 * Creates a `QueryConstraint` that sorts the query result by the
 * specified field, optionally in descending order instead of ascending.
 *
 * @param fieldPath - The field to sort by.
 * @param directionStr - Optional direction to sort by ('asc' or 'desc'). If
 * not specified, order will be ascending.
 * @returns The created `Query`.
 */ class du extends hu {
    constructor(t, e, n) {
        super(), this.type = t, this.od = e, this.ad = n;
    }
    td(t) {
        return new jc(t.firestore, t.v_, ue(t.C_, this.od, this.ad));
    }
}

/**
 * Creates a `QueryConstraint` that only returns the first matching documents.
 *
 * @param limit - The maximum number of items to return.
 * @returns The created `Query`.
 */ class wu extends hu {
    constructor(t, e, n) {
        super(), this.type = t, this.ud = e, this.hd = n;
    }
    td(t) {
        const e = Tu(t, this.type, this.ud, this.hd);
        return new jc(t.firestore, t.v_, function(t, e) {
            return new Zt(t.path, t.collectionGroup, t.xt.slice(), t.filters.slice(), t.limit, t.limitType, e, t.endAt);
        }(t.C_, e));
    }
}

class Eu extends hu {
    constructor(t, e, n) {
        super(), this.type = t, this.ud = e, this.hd = n;
    }
    td(t) {
        const e = Tu(t, this.type, this.ud, this.hd);
        return new jc(t.firestore, t.v_, function(t, e) {
            return new Zt(t.path, t.collectionGroup, t.xt.slice(), t.filters.slice(), t.limit, t.limitType, t.startAt, e);
        }(t.C_, e));
    }
}

/** Helper function to create a bound from a document or fields */
function Tu(t, e, n, s) {
    if (n[0] instanceof Nc && (n[0] = n[0].d_), n[0] instanceof ou) return function(t, e, n, s, i) {
        if (!s) throw new F($.NOT_FOUND, `Can't use a DocumentSnapshot that doesn't exist for ${n}().`);
        const r = [];
        // Because people expect to continue/end a query at the exact document
        // provided, we need to use the implicit sort order rather than the explicit
        // sort order, because it's guaranteed to contain the document key. That way
        // the position becomes unambiguous and the query continues/ends exactly at
        // the provided document. Without the key (by using the explicit sort
        // orders), multiple documents could match the position, yielding duplicate
        // results.
                for (const n of ce(t)) if (n.field.ut()) r.push(It(e, s.key)); else {
            const t = s.field(n.field);
            if (rt(t)) throw new F($.INVALID_ARGUMENT, 'Invalid query. You are trying to start or end a query using a document for which the field "' + n.field + '" is an uncommitted server timestamp. (Since the value of this field is unknown, you cannot start/end a query with it.)');
            if (null === t) {
                const t = n.field.rt();
                throw new F($.INVALID_ARGUMENT, `Invalid query. You are trying to start or end a query using a document for which the field '${t}' (used as the orderBy) does not exist.`);
            }
            r.push(t);
        }
        return new Gt(r, i);
    }
    /**
 * Converts a list of field values to a Bound for the given query.
 */ (t.C_, t.firestore.I_, e, n[0].Zf, s);
    {
        const i = oa(t.firestore);
        return function(t, e, n, s, i, r) {
            // Use explicit order by's because it has to match the query the user made
            const o = t.xt;
            if (i.length > o.length) throw new F($.INVALID_ARGUMENT, `Too many arguments provided to ${s}(). The number of arguments must be less than or equal to the number of orderBy() clauses`);
            const c = [];
            for (let r = 0; r < i.length; r++) {
                const a = i[r];
                if (o[r].field.ut()) {
                    if ("string" != typeof a) throw new F($.INVALID_ARGUMENT, `Invalid query. Expected a string for document ID in ${s}(), but got a ${typeof a}`);
                    if (!oe(t) && -1 !== a.indexOf("/")) throw new F($.INVALID_ARGUMENT, `Invalid query. When querying a collection and ordering by FieldPath.documentId(), the value passed to ${s}() must be a plain document ID, but '${a}' contains a slash.`);
                    const n = t.path.child(G.ot(a));
                    if (!J.wt(n)) throw new F($.INVALID_ARGUMENT, `Invalid query. When querying a collection group and ordering by FieldPath.documentId(), the value passed to ${s}() must result in a valid document path, but '${n}' is not because it contains an odd number of segments.`);
                    const i = new J(n);
                    c.push(It(e, i));
                } else {
                    const t = Ea(n, s, a);
                    c.push(t);
                }
            }
            return new Gt(c, r);
        }
        /**
 * Parses the given documentIdValue into a ReferenceValue, throwing
 * appropriate errors if the value is anything other than a DocumentReference
 * or String, or if the string is malformed.
 */ (t.C_, t.firestore.I_, i, e, n, s);
    }
}

function Iu(t, e, n) {
    if (n instanceof Nc && (n = n.d_), "string" == typeof n) {
        if ("" === n) throw new F($.INVALID_ARGUMENT, "Invalid query. When querying with FieldPath.documentId(), you must provide a valid document ID, but it was an empty string.");
        if (!oe(e) && -1 !== n.indexOf("/")) throw new F($.INVALID_ARGUMENT, `Invalid query. When querying a collection by FieldPath.documentId(), you must provide a plain document ID, but '${n}' contains a '/' character.`);
        const s = e.path.child(G.ot(n));
        if (!J.wt(s)) throw new F($.INVALID_ARGUMENT, `Invalid query. When querying a collection group by FieldPath.documentId(), the value provided must result in a valid document path, but '${s}' is not because it has an odd number of segments (${s.length}).`);
        return It(t, new J(s));
    }
    if (n instanceof Wc) return It(t, n.S_);
    throw new F($.INVALID_ARGUMENT, `Invalid query. When querying with FieldPath.documentId(), you must provide a valid string or a DocumentReference, but it was: ${Lc(n)}.`);
}

/**
 * Validates that the value passed into a disjunctive filter satisfies all
 * array requirements.
 */ function mu(t, e) {
    if (!Array.isArray(t) || 0 === t.length) throw new F($.INVALID_ARGUMENT, `Invalid Query. A non-empty array is required for '${e.toString()}' filters.`);
    if (t.length > 10) throw new F($.INVALID_ARGUMENT, `Invalid Query. '${e.toString()}' filters support a maximum of 10 elements in the value array.`);
}

function Au(t, e, n) {
    if (!n.isEqual(e)) throw new F($.INVALID_ARGUMENT, `Invalid query. You have a where filter with an inequality (<, <=, !=, not-in, >, or >=) on field '${e.toString()}' and so you must also use '${e.toString()}' as your first argument to orderBy(), but your first orderBy() is on field '${n.toString()}' instead.`);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Converts custom model object of type T into DocumentData by applying the
 * converter if it exists.
 *
 * This function is used when converting user objects to DocumentData
 * because we want to provide the user with a more specific error message if
 * their set() or fails due to invalid data originating from a toFirestore()
 * call.
 */ function Ru(t, e, n) {
    let s;
    // Cast to `any` in order to satisfy the union type constraint on
    // toFirestore().
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return s = t ? n && (n.merge || n.mergeFields) ? t.toFirestore(e, n) : t.toFirestore(e) : e, 
    s;
}

class Pu extends ru {
    constructor(t) {
        super(), this.firestore = t;
    }
    Wf(t) {
        return new O(t);
    }
    jf(t) {
        const e = this.Jf(t, this.firestore.I_);
        return new Wc(this.firestore, /* converter= */ null, e);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Metadata about a snapshot, describing the state of the snapshot.
 */ class yu {
    /** @hideconstructor */
    constructor(t, e) {
        this.hasPendingWrites = t, this.fromCache = e;
    }
    /**
     * Returns true if this `SnapshotMetadata` is equal to the provided one.
     *
     * @param other - The `SnapshotMetadata` to compare against.
     * @returns true if this `SnapshotMetadata` is equal to the provided one.
     */    isEqual(t) {
        return this.hasPendingWrites === t.hasPendingWrites && this.fromCache === t.fromCache;
    }
}

/**
 * A `DocumentSnapshot` contains data read from a document in your Firestore
 * database. The data can be extracted with `.data()` or `.get(<field>)` to
 * get a specific field.
 *
 * For a `DocumentSnapshot` that points to a non-existing document, any data
 * access will return 'undefined'. You can use the `exists()` method to
 * explicitly verify a document's existence.
 */ class gu extends ou {
    /** @hideconstructor protected */
    constructor(t, e, n, s, i, r) {
        super(t, e, n, s, r), this.Yf = t, this.ld = t, this.metadata = i;
    }
    /**
     * Property of the `DocumentSnapshot` that signals whether or not the data
     * exists. True if the document exists.
     */    exists() {
        return super.exists();
    }
    /**
     * Retrieves all fields in the document as an `Object`. Returns `undefined` if
     * the document doesn't exist.
     *
     * By default, `FieldValue.serverTimestamp()` values that have not yet been
     * set to their final value will be returned as `null`. You can override
     * this by passing an options object.
     *
     * @param options - An options object to configure how data is retrieved from
     * the snapshot (for example the desired behavior for server timestamps that
     * have not yet been set to their final value).
     * @returns An `Object` containing all fields in the document or `undefined` if
     * the document doesn't exist.
     */    data(t = {}) {
        if (this.Zf) {
            if (this.v_) {
                // We only want to use the converter and create a new DocumentSnapshot
                // if a converter has been provided.
                const e = new Vu(this.Yf, this.Xf, this.S_, this.Zf, this.metadata, 
                /* converter= */ null);
                return this.v_.fromFirestore(e, t);
            }
            return this.Xf.Uf(this.Zf.bt(), t.serverTimestamps);
        }
    }
    /**
     * Retrieves the field specified by `fieldPath`. Returns `undefined` if the
     * document or field doesn't exist.
     *
     * By default, a `FieldValue.serverTimestamp()` that has not yet been set to
     * its final value will be returned as `null`. You can override this by
     * passing an options object.
     *
     * @param fieldPath - The path (for example 'foo' or 'foo.bar') to a specific
     * field.
     * @param options - An options object to configure how the field is retrieved
     * from the snapshot (for example the desired behavior for server timestamps
     * that have not yet been set to their final value).
     * @returns The data at the specified field location or undefined if no such
     * field exists in the document.
     */
    // We are using `any` here to avoid an explicit cast by our users.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(t, e = {}) {
        if (this.Zf) {
            const n = this.Zf.data().field(au("DocumentSnapshot.get", t));
            if (null !== n) return this.Xf.Uf(n, e.serverTimestamps);
        }
    }
}

/**
 * A `QueryDocumentSnapshot` contains data read from a document in your
 * Firestore database as part of a query. The document is guaranteed to exist
 * and its data can be extracted with `.data()` or `.get(<field>)` to get a
 * specific field.
 *
 * A `QueryDocumentSnapshot` offers the same API surface as a
 * `DocumentSnapshot`. Since query results contain only existing documents, the
 * `exists` property will always be true and `data()` will never return
 * 'undefined'.
 */ class Vu extends gu {
    /**
     * Retrieves all fields in the document as an `Object`.
     *
     * By default, `FieldValue.serverTimestamp()` values that have not yet been
     * set to their final value will be returned as `null`. You can override
     * this by passing an options object.
     *
     * @override
     * @param options - An options object to configure how data is retrieved from
     * the snapshot (for example the desired behavior for server timestamps that
     * have not yet been set to their final value).
     * @returns An `Object` containing all fields in the document.
     */
    data(t = {}) {
        return super.data(t);
    }
}

/**
 * A `QuerySnapshot` contains zero or more `DocumentSnapshot` objects
 * representing the results of a query. The documents can be accessed as an
 * array via the `docs` property or enumerated using the `forEach` method. The
 * number of documents can be determined via the `empty` and `size`
 * properties.
 */ class pu {
    /** @hideconstructor */
    constructor(t, e, n, s) {
        this.Yf = t, this.Xf = e, this._d = s, this.metadata = new yu(s.hasPendingWrites, s.fromCache), 
        this.query = n;
    }
    /** An array of all the documents in the `QuerySnapshot`. */    get docs() {
        const t = [];
        return this.forEach((e => t.push(e))), t;
    }
    /** The number of documents in the `QuerySnapshot`. */    get size() {
        return this._d.docs.size;
    }
    /** True if there are no documents in the `QuerySnapshot`. */    get empty() {
        return 0 === this.size;
    }
    /**
     * Enumerates all of the documents in the `QuerySnapshot`.
     *
     * @param callback - A callback to be called with a `QueryDocumentSnapshot` for
     * each document in the snapshot.
     * @param thisArg - The `this` binding for the callback.
     */    forEach(t, e) {
        this._d.docs.forEach((n => {
            t.call(e, new Vu(this.Yf, this.Xf, n.key, n, new yu(this._d.De.has(n.key), this._d.fromCache), this.query.v_));
        }));
    }
    /**
     * Returns an array of the documents changes since the last snapshot. If this
     * is the first snapshot, all documents will be in the list as 'added'
     * changes.
     *
     * @param options - `SnapshotListenOptions` that control whether metadata-only
     * changes (i.e. only `DocumentSnapshot.metadata` changed) should trigger
     * snapshot events.
     */    docChanges(t = {}) {
        const e = !!t.includeMetadataChanges;
        if (e && this._d.xe) throw new F($.INVALID_ARGUMENT, "To include metadata changes with your document changes, you must also pass { includeMetadataChanges:true } to onSnapshot().");
        return this.fd && this.dd === e || (this.fd = 
        /** Calculates the array of DocumentChanges for a given ViewSnapshot. */
        function(t, e) {
            if (t._d.Se.et()) {
                // Special case the first snapshot because index calculation is easy and
                // fast
                let e, n = 0;
                return t._d.docChanges.map((s => {
                    const i = new Vu(t.Yf, t.Xf, s.doc.key, s.doc, new yu(t._d.De.has(s.doc.key), t._d.fromCache), t.query.v_);
                    return e = s.doc, {
                        type: "added",
                        doc: i,
                        oldIndex: -1,
                        newIndex: n++
                    };
                }));
            }
            {
                // A DocumentSet that is updated incrementally as changes are applied to use
                // to lookup the index of a document.
                let n = t._d.Se;
                return t._d.docChanges.filter((t => e || 3 /* Metadata */ !== t.type)).map((e => {
                    const s = new Vu(t.Yf, t.Xf, e.doc.key, e.doc, new yu(t._d.De.has(e.doc.key), t._d.fromCache), t.query.v_);
                    let i = -1, r = -1;
                    return 0 /* Added */ !== e.type && (i = n.indexOf(e.doc.key), n = n.delete(e.doc.key)), 
                    1 /* Removed */ !== e.type && (n = n.add(e.doc), r = n.indexOf(e.doc.key)), {
                        type: bu(e.type),
                        doc: s,
                        oldIndex: i,
                        newIndex: r
                    };
                }));
            }
        }(this, e), this.dd = e), this.fd;
    }
}

function bu(t) {
    switch (t) {
      case 0 /* Added */ :
        return "added";

      case 2 /* Modified */ :
      case 3 /* Metadata */ :
        return "modified";

      case 1 /* Removed */ :
        return "removed";

      default:
        return V();
    }
}

// TODO(firestoreexp): Add tests for snapshotEqual with different snapshot
// metadata
/**
 * Returns true if the provided snapshots are equal.
 *
 * @param left - A snapshot to compare.
 * @param right - A snapshot to compare.
 * @returns true if the snapshots are equal.
 */ function vu(t, e) {
    return t instanceof gu && e instanceof gu ? t.Yf === e.Yf && t.S_.isEqual(e.S_) && (null === t.Zf ? null === e.Zf : t.Zf.isEqual(e.Zf)) && t.v_ === e.v_ : t instanceof pu && e instanceof pu && (t.Yf === e.Yf && Xc(t.query, e.query) && t.metadata.isEqual(e.metadata) && t._d.isEqual(e._d));
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Reads the document referred to by this `DocumentReference`.
 *
 * Note: `getDoc()` attempts to provide up-to-date data when possible by waiting
 * for data from the server, but it may return cached data or fail if you are
 * offline and the server cannot be reached. To specify this behavior, invoke
 * {@link getDocFromCache} or {@link getDocFromServer}.
 *
 * @param reference - The reference of the document to fetch.
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ class Su extends ru {
    constructor(t) {
        super(), this.firestore = t;
    }
    Wf(t) {
        return new O(t);
    }
    jf(t) {
        const e = this.Jf(t, this.firestore.I_);
        return new Wc(this.firestore, /* converter= */ null, e);
    }
}

/**
 * Reads the document referred to by this `DocumentReference` from cache.
 * Returns an error if the document is not currently cached.
 *
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ function Du(t, e, n, ...s) {
    t = Bc(t, Wc);
    const i = Bc(t.firestore, Xa), r = oa(i);
    let o;
    // For Compat types, we have to "extract" the underlying types before
    // performing validation.
    e instanceof Nc && (e = e.d_), o = "string" == typeof e || e instanceof Cc ? wa(r, "updateDoc", t.S_, e, n, s) : da(r, "updateDoc", t.S_, e);
    return xu(i, [ o.F_(t.S_, Fe.exists(!0)) ]);
}

/**
 * Deletes the document referred to by the specified `DocumentReference`.
 *
 * @param reference - A reference to the document to delete.
 * @returns A Promise resolved once the document has been successfully
 * deleted from the backend (note that it won't resolve while you're offline).
 */ function Cu(t, ...e) {
    var n, s, i;
    t instanceof Nc && (t = t.d_);
    let r = {
        includeMetadataChanges: !1
    }, o = 0;
    "object" != typeof e[o] || Ha(e[o]) || (r = e[o], o++);
    const c = {
        includeMetadataChanges: r.includeMetadataChanges
    };
    if (Ha(e[o])) {
        const t = e[o];
        e[o] = null === (n = t.next) || void 0 === n ? void 0 : n.bind(t), e[o + 1] = null === (s = t.error) || void 0 === s ? void 0 : s.bind(t), 
        e[o + 2] = null === (i = t.complete) || void 0 === i ? void 0 : i.bind(t);
    }
    let a, u, h;
    if (t instanceof Wc) u = Bc(t.firestore, Xa), h = ee(t.S_.path), a = {
        next: n => {
            e[o] && e[o](Nu(u, t, n));
        },
        error: e[o + 1],
        complete: e[o + 2]
    }; else {
        const n = Bc(t, jc);
        u = Bc(n.firestore, Xa), h = n.C_;
        const s = new Su(u);
        a = {
            next: t => {
                e[o] && e[o](new pu(u, s, n, t));
            },
            error: e[o + 1],
            complete: e[o + 2]
        }, uu(t.C_);
    }
    return function(t, e, n, s) {
        const i = new Sc(s), r = new Fo(e, i, n);
        return t._s.Ps((async () => Do(await ka(t), r))), () => {
            i.i_(), t._s.Ps((async () => Co(await ka(t), r)));
        };
    }(Za(u), h, c, a);
}

/** Locally writes `mutations` on the async queue. */
function xu(t, e) {
    return function(t, e) {
        const n = new ds;
        return t._s.Ps((async () => Ho(await Oa(t), e, n))), n.promise;
    }(Za(t), e);
}

/**
 * Converts a ViewSnapshot that contains the single document specified by `ref`
 * to a DocumentSnapshot.
 */ function Nu(t, e, n) {
    const s = n.docs.get(e.S_), i = new Su(t);
    return new gu(t, i, e.S_, s, new yu(n.hasPendingWrites, n.fromCache), e.v_);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A write batch, used to perform multiple writes as a single atomic unit.
 *
 * A `WriteBatch` object can be acquired by calling {@link writeBatch}. It
 * provides methods for adding writes to the write batch. None of the writes
 * will be committed (or visible locally) until {@link WriteBatch#commit} is
 * called.
 */ class $u {
    /** @hideconstructor */
    constructor(t, e) {
        this.Yf = t, this.wd = e, this.Ed = [], this.Td = !1, this.Id = oa(t);
    }
    set(t, e, n) {
        this.md();
        const s = Fu(t, this.Yf), i = Ru(s.v_, e, n), r = ca(this.Id, "WriteBatch.set", s.S_, i, null !== s.v_, n);
        return this.Ed.push(r.F_(s.S_, Fe.Kt())), this;
    }
    update(t, e, n, ...s) {
        this.md();
        const i = Fu(t, this.Yf);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                let r;
        return e instanceof Nc && (e = e.d_), r = "string" == typeof e || e instanceof Cc ? wa(this.Id, "WriteBatch.update", i.S_, e, n, s) : da(this.Id, "WriteBatch.update", i.S_, e), 
        this.Ed.push(r.F_(i.S_, Fe.exists(!0))), this;
    }
    /**
     * Deletes the document referred to by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be deleted.
     * @returns This `WriteBatch` instance. Used for chaining method calls.
     */    delete(t) {
        this.md();
        const e = Fu(t, this.Yf);
        return this.Ed = this.Ed.concat(new He(e.S_, Fe.Kt())), this;
    }
    /**
     * Commits all of the writes in this write batch as a single atomic unit.
     *
     * The result of these writes will only be reflected in document reads that
     * occur after the returned Promise resolves. If the client is offline, the
     * write fails. If you would like to see local modifications or buffer writes
     * until the client is online, use the full Firestore SDK.
     *
     * @returns A Promise resolved once all of the writes in the batch have been
     * successfully written to the backend as an atomic unit (note that it won't
     * resolve while you're offline).
     */    commit() {
        return this.md(), this.Td = !0, this.Ed.length > 0 ? this.wd(this.Ed) : Promise.resolve();
    }
    md() {
        if (this.Td) throw new F($.FAILED_PRECONDITION, "A write batch can no longer be used after commit() has been called.");
    }
}

function Fu(t, e) {
    if (t instanceof Nc && (t = t.d_), t.firestore !== e) throw new F($.INVALID_ARGUMENT, "Provided document reference is from a different Firestore instance.");
    return t;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// TODO(mrschmidt) Consider using `BaseTransaction` as the base class in the
// legacy SDK.
/**
 * A reference to a transaction.
 *
 * The `Transaction` object passed to a transaction's `updateFunction` provides
 * the methods to read and write data within the transaction context. See
 * {@link runTransaction}.
 */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A reference to a transaction.
 *
 * The `Transaction` object passed to a transaction's `updateFunction` provides
 * the methods to read and write data within the transaction context. See
 * {@link runTransaction}.
 */
class Ou extends class {
    /** @hideconstructor */
    constructor(t, e) {
        this.Yf = t, this.Ad = e, this.Id = oa(t);
    }
    /**
     * Reads the document referenced by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be read.
     * @returns A `DocumentSnapshot` with the read data.
     */    get(t) {
        const e = Fu(t, this.Yf), n = new Pu(this.Yf);
        return this.Ad.ef([ e.S_ ]).then((t => {
            if (!t || 1 !== t.length) return V();
            const s = t[0];
            if (s instanceof St) return new ou(this.Yf, n, e.S_, null, e.v_);
            if (s instanceof vt) return new ou(this.Yf, n, s.key, s, e.v_);
            throw V();
        }));
    }
    set(t, e, n) {
        const s = Fu(t, this.Yf), i = Ru(s.v_, e, n), r = ca(this.Id, "Transaction.set", s.S_, i, null !== s.v_, n);
        return this.Ad.set(s.S_, r), this;
    }
    update(t, e, n, ...s) {
        const i = Fu(t, this.Yf);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                let r;
        return e instanceof Nc && (e = e.d_), r = "string" == typeof e || e instanceof Cc ? wa(this.Id, "Transaction.update", i.S_, e, n, s) : da(this.Id, "Transaction.update", i.S_, e), 
        this.Ad.update(i.S_, r), this;
    }
    /**
     * Deletes the document referred to by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be deleted.
     * @returns This `Transaction` instance. Used for chaining method calls.
     */    delete(t) {
        const e = Fu(t, this.Yf);
        return this.Ad.delete(e.S_), this;
    }
} {
    // This class implements the same logic as the Transaction API in the Lite SDK
    // but is subclassed in order to return its own DocumentSnapshot types.
    /** @hideconstructor */
    constructor(t, e) {
        super(t, e), this.Yf = t;
    }
    /**
     * Reads the document referenced by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be read.
     * @returns A `DocumentSnapshot` with the read data.
     */    get(t) {
        const e = Fu(t, this.Yf), n = new Su(this.Yf);
        return super.get(t).then((t => new gu(this.Yf, n, e.S_, t.Zf, new yu(
        /* hasPendingWrites= */ !1, 
        /* fromCache= */ !1), e.v_)));
    }
}

/**
 * Executes the given `updateFunction` and then attempts to commit the changes
 * applied within the transaction. If any document read within the transaction
 * has changed, Cloud Firestore retries the `updateFunction`. If it fails to
 * commit after 5 attempts, the transaction fails.
 *
 * The maximum number of writes allowed in a single transaction is 500.
 *
 * @param firestore - A reference to the Firestore database to run this
 * transaction against.
 * @param updateFunction - The function to execute within the transaction
 * context.
 * @returns If the transaction completed successfully or was explicitly aborted
 * (the `updateFunction` returned a failed promise), the promise returned by the
 * `updateFunction `is returned here. Otherwise, if the transaction failed, a
 * rejected promise with the corresponding failure error is returned.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Helper function to assert Uint8Array is available at runtime. */
function ku() {
    if ("undefined" == typeof Uint8Array) throw new F($.UNIMPLEMENTED, "Uint8Arrays are not available in this environment.");
}

/** Helper function to assert Base64 functions are available at runtime. */ function Mu() {
    if ("undefined" == typeof atob) throw new F($.UNIMPLEMENTED, "Blobs are unavailable in Firestore in this environment.");
}

/** Immutable class holding a blob (binary data) */ class Lu extends Nc {
    static fromBase64String(t) {
        return Mu(), new Lu(O.fromBase64String(t));
    }
    static fromUint8Array(t) {
        return ku(), new Lu(O.fromUint8Array(t));
    }
    toBase64() {
        return Mu(), this.d_.toBase64();
    }
    toUint8Array() {
        return ku(), this.d_.toUint8Array();
    }
    isEqual(t) {
        return this.d_.isEqual(t.d_);
    }
    toString() {
        return "Blob(base64: " + this.toBase64() + ")";
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Bu = "You are using the memory-only build of Firestore. Persistence support is only available via the @firebase/firestore bundle or the firebase-firestore.js build.";

/**
 * The persistence provider included with the memory-only SDK. This provider
 * errors for all attempts to access persistence.
 */ class qu {
    enableIndexedDbPersistence(t, e) {
        throw new F($.FAILED_PRECONDITION, Bu);
    }
    enableMultiTabIndexedDbPersistence(t) {
        throw new F($.FAILED_PRECONDITION, Bu);
    }
    clearIndexedDbPersistence(t) {
        throw new F($.FAILED_PRECONDITION, Bu);
    }
}

/**
 * The persistence provider included with the full Firestore SDK.
 */ class Uu {
    enableIndexedDbPersistence(t, e) {
        return function(t, e) {
            iu(t = Bc(t, Xa));
            const n = Za(t), s = t.p_(), i = new bc;
            return eu(n, i, new Vc(i, s.cacheSizeBytes, null == e ? void 0 : e.forceOwnership));
        }
        /**
 * Attempts to enable multi-tab persistent storage, if possible. If enabled
 * across all tabs, all operations share access to local persistence, including
 * shared execution of queries and latency-compensated local document updates
 * across all connected instances.
 *
 * If this fails, `enableMultiTabIndexedDbPersistence()` will reject the promise
 * it returns. Note that even after this failure, the `Firestore` instance will
 * remain usable, however offline persistence will be disabled.
 *
 * There are several reasons why this can fail, which can be identified by
 * the `code` on the error.
 *
 *   * failed-precondition: The app is already open in another browser tab and
 *     multi-tab is not enabled.
 *   * unimplemented: The browser is incompatible with the offline
 *     persistence implementation.
 *
 * @param firestore - The `Firestore` instance to enable persistence for.
 * @returns A promise that represents successfully enabling persistent
 * storage.
 */ (t.d_, {
            forceOwnership: e
        });
    }
    enableMultiTabIndexedDbPersistence(t) {
        return function(t) {
            iu(t = Bc(t, Xa));
            const e = Za(t), n = t.p_(), s = new bc;
            return eu(e, s, new pc(s, n.cacheSizeBytes));
        }(t.d_);
    }
    clearIndexedDbPersistence(t) {
        return nu(t.d_);
    }
}

/**
 * Compat class for Firestore. Exposes Firestore Legacy API, but delegates
 * to the functional API of firestore-exp.
 */ class Qu extends Nc {
    constructor(t, e, n) {
        super(e), this.Rd = n, this.INTERNAL = {
            delete: () => this.terminate()
        }, t instanceof I || (this.Pd = t);
    }
    get I_() {
        return this.d_.I_;
    }
    settings(t) {
        t.merge && 
        // Remove the property from the settings once the merge is completed
        delete (t = Object.assign(Object.assign({}, this.d_.V_()), t)).merge, this.d_.g_(t);
    }
    useEmulator(t, e) {
        !function(t, e, n) {
            const s = (t = Bc(t, Kc)).V_();
            "firestore.googleapis.com" !== s.host && s.host !== e && y("Host has been set in both settings() and useEmulator(), emulator host will be used"), 
            t.g_(Object.assign(Object.assign({}, s), {
                host: `${e}:${n}`,
                ssl: !1
            }));
        }(this.d_, t, e);
    }
    enableNetwork() {
        return Ma(Za(Bc(this.d_, Xa)));
    }
    disableNetwork() {
        return La(Za(Bc(this.d_, Xa)));
    }
    enablePersistence(t) {
        let e = !1, n = !1;
        return t && (e = !!t.synchronizeTabs, n = !!t.experimentalForceOwningTab, Oc("synchronizeTabs", e, "experimentalForceOwningTab", n)), 
        e ? this.Rd.enableMultiTabIndexedDbPersistence(this) : this.Rd.enableIndexedDbPersistence(this, n);
    }
    clearPersistence() {
        return this.Rd.clearIndexedDbPersistence(this);
    }
    terminate() {
        return this.Pd && (this.Pd._removeServiceInstance("firestore"), this.Pd._removeServiceInstance("firestore-exp")), 
        this.d_._delete();
    }
    waitForPendingWrites() {
        return su(this.d_);
    }
    onSnapshotsInSync(t) {
        return function(t, e) {
            return Ka(Za(t = Bc(t, Xa)), Ha(e) ? e : {
                next: e
            });
        }(this.d_, t);
    }
    get app() {
        if (!this.Pd) throw new F($.FAILED_PRECONDITION, "Firestore was not initialized using the Firebase SDK. 'app' is not available");
        return this.Pd;
    }
    collection(t) {
        try {
            return new ih(this, zc(this.d_, t));
        } catch (t) {
            throw Ju(t, "collection()", "Firestore.collection()");
        }
    }
    doc(t) {
        try {
            return new Hu(this, Jc(this.d_, t));
        } catch (t) {
            throw Ju(t, "doc()", "Firestore.doc()");
        }
    }
    collectionGroup(t) {
        try {
            return new eh(this, Hc(this.d_, t));
        } catch (t) {
            throw Ju(t, "collectionGroup()", "Firestore.collectionGroup()");
        }
    }
    runTransaction(t) {
        return function(t, e) {
            return Wa(Za(t), (n => e(new Ou(t, n))));
        }(this.d_, (e => t(new ju(this, e))));
    }
    batch() {
        return Za(this.d_), new Gu(new $u(this.d_, (t => xu(this.d_, t))));
    }
    loadBundle(t) {
        throw new F($.FAILED_PRECONDITION, '"loadBundle()" does not exist, have you imported "firebase/firestore/bundle"?');
    }
    namedQuery(t) {
        throw new F($.FAILED_PRECONDITION, '"namedQuery()" does not exist, have you imported "firebase/firestore/bundle"?');
    }
}

class Ku extends ru {
    constructor(t) {
        super(), this.firestore = t;
    }
    Wf(t) {
        return new Lu(new O(t));
    }
    jf(t) {
        const e = this.Jf(t, this.firestore.I_);
        return Hu.yd(e, this.firestore, /* converter= */ null);
    }
}

function Wu(t) {
    var e;
    e = t, m.setLogLevel(e);
}

/**
 * A reference to a transaction.
 */ class ju extends Nc {
    constructor(t, e) {
        super(e), this.Yf = t, this.Xf = new Ku(t);
    }
    get(t) {
        const e = rh(t);
        return this.d_.get(e).then((t => new Zu(this.Yf, new gu(this.Yf.d_, this.Xf, t.S_, t.Zf, t.metadata, e.v_))));
    }
    set(t, e, n) {
        const s = rh(t);
        return n ? (Fc("Transaction.set", n), this.d_.set(s, e, n)) : this.d_.set(s, e), 
        this;
    }
    update(t, e, n, ...s) {
        const i = rh(t);
        return 2 === arguments.length ? this.d_.update(i, e) : this.d_.update(i, e, n, ...s), 
        this;
    }
    delete(t) {
        const e = rh(t);
        return this.d_.delete(e), this;
    }
}

class Gu extends Nc {
    set(t, e, n) {
        const s = rh(t);
        return n ? (Fc("WriteBatch.set", n), this.d_.set(s, e, n)) : this.d_.set(s, e), 
        this;
    }
    update(t, e, n, ...s) {
        const i = rh(t);
        return 2 === arguments.length ? this.d_.update(i, e) : this.d_.update(i, e, n, ...s), 
        this;
    }
    delete(t) {
        const e = rh(t);
        return this.d_.delete(e), this;
    }
    commit() {
        return this.d_.commit();
    }
}

/**
 * Wraps a `PublicFirestoreDataConverter` translating the types from the
 * experimental SDK into corresponding types from the Classic SDK before passing
 * them to the wrapped converter.
 */ class zu extends Nc {
    constructor(t, e, n) {
        super(n), this.Yf = t, this.Xf = e;
    }
    fromFirestore(t, e) {
        const n = new Vu(this.Yf.d_, this.Xf, t.S_, t.Zf, t.metadata, 
        /* converter= */ null);
        return this.d_.fromFirestore(new th(this.Yf, n), null != e ? e : {});
    }
    toFirestore(t, e) {
        return e ? this.d_.toFirestore(t, e) : this.d_.toFirestore(t);
    }
    // Use the same instance of `FirestoreDataConverter` for the given instances
    // of `Firestore` and `PublicFirestoreDataConverter` so that isEqual() will
    // compare equal for two objects created with the same converter instance.
    static gd(t, e) {
        const n = zu.Vd;
        let s = n.get(t);
        s || (s = new WeakMap, n.set(t, s));
        let i = s.get(e);
        return i || (i = new zu(t, new Ku(t), e), s.set(e, i)), i;
    }
}

zu.Vd = new WeakMap;

/**
 * A reference to a particular document in a collection in the database.
 */
class Hu extends Nc {
    constructor(t, e) {
        super(e), this.firestore = t, this.Xf = new Ku(t);
    }
    static pd(t, e, n) {
        if (t.length % 2 != 0) throw new F($.INVALID_ARGUMENT, `Invalid document reference. Document references must have an even number of segments, but ${t.rt()} has ${t.length}`);
        return new Hu(e, new Wc(e.d_, n, new J(t)));
    }
    static yd(t, e, n) {
        return new Hu(e, new Wc(e.d_, n, t));
    }
    get id() {
        return this.d_.id;
    }
    get parent() {
        return new ih(this.firestore, this.d_.parent);
    }
    get path() {
        return this.d_.path;
    }
    collection(t) {
        try {
            return new ih(this.firestore, zc(this.d_, t));
        } catch (t) {
            throw Ju(t, "collection()", "DocumentReference.collection()");
        }
    }
    isEqual(t) {
        return t instanceof Nc && (t = t.d_), t instanceof Wc && Yc(this.d_, t);
    }
    set(t, e) {
        e = Fc("DocumentReference.set", e);
        try {
            return function(t, e, n) {
                t = Bc(t, Wc);
                const s = Bc(t.firestore, Xa), i = Ru(t.v_, e, n);
                return xu(s, [ ca(oa(s), "setDoc", t.S_, i, null !== t.v_, n).F_(t.S_, Fe.Kt()) ]);
            }(this.d_, t, e);
        } catch (t) {
            throw Ju(t, "setDoc()", "DocumentReference.set()");
        }
    }
    update(t, e, ...n) {
        try {
            return 1 === arguments.length ? Du(this.d_, t) : Du(this.d_, t, e, ...n);
        } catch (t) {
            throw Ju(t, "updateDoc()", "DocumentReference.update()");
        }
    }
    delete() {
        return xu(Bc((t = this.d_).firestore, Xa), [ new He(t.S_, Fe.Kt()) ]);
        var t;
        /**
 * Add a new document to specified `CollectionReference` with the given data,
 * assigning it a document ID automatically.
 *
 * @param reference - A reference to the collection to add this document to.
 * @param data - An Object containing the data for the new document.
 * @returns A Promise resolved with a `DocumentReference` pointing to the
 * newly created document after it has been written to the backend (Note that it
 * won't resolve while you're offline).
 */    }
    onSnapshot(...t) {
        const e = Yu(t), n = Xu(t, (t => new Zu(this.firestore, new gu(this.firestore.d_, this.Xf, t.S_, t.Zf, t.metadata, this.d_.v_))));
        return Cu(this.d_, e, n);
    }
    get(t) {
        let e;
        return e = "cache" === (null == t ? void 0 : t.source) ? function(t) {
            t = Bc(t, Wc);
            const e = Bc(t.firestore, Xa), n = Za(e), s = new Su(e);
            return Ba(n, t.S_).then((n => new gu(e, s, t.S_, n, new yu(n instanceof vt && n.Vt, 
            /* fromCache= */ !0), t.v_)));
        }
        /**
 * Reads the document referred to by this `DocumentReference` from the server.
 * Returns an error if the network is not available.
 *
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ (this.d_) : "server" === (null == t ? void 0 : t.source) ? function(t) {
            t = Bc(t, Wc);
            const e = Bc(t.firestore, Xa);
            return qa(Za(e), t.S_, {
                source: "server"
            }).then((n => Nu(e, t, n)));
        }
        /**
 * Executes the query and returns the results as a `QuerySnapshot`.
 *
 * Note: `getDocs()` attempts to provide up-to-date data when possible by
 * waiting for data from the server, but it may return cached data or fail if
 * you are offline and the server cannot be reached. To specify this behavior,
 * invoke {@link getDocsFromCache} or {@link getDocsFromServer}.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */ (this.d_) : function(t) {
            t = Bc(t, Wc);
            const e = Bc(t.firestore, Xa);
            return qa(Za(e), t.S_).then((n => Nu(e, t, n)));
        }(this.d_), e.then((t => new Zu(this.firestore, new gu(this.firestore.d_, this.Xf, t.S_, t.Zf, t.metadata, this.d_.v_))));
    }
    withConverter(t) {
        return new Hu(this.firestore, this.d_.withConverter(zu.gd(this.firestore, t)));
    }
}

/**
 * Replaces the function name in an error thrown by the firestore-exp API
 * with the function names used in the classic API.
 */ function Ju(t, e, n) {
    return t.message = t.message.replace(e, n), t;
}

/**
 * Iterates the list of arguments from an `onSnapshot` call and returns the
 * first argument that may be an `SnapshotListenOptions` object. Returns an
 * empty object if none is found.
 */ function Yu(t) {
    for (const e of t) if ("object" == typeof e && !Ha(e)) return e;
    return {};
}

/**
 * Creates an observer that can be passed to the firestore-exp SDK. The
 * observer converts all observed values into the format expected by the classic
 * SDK.
 *
 * @param args - The list of arguments from an `onSnapshot` call.
 * @param wrapper - The function that converts the firestore-exp type into the
 * type used by this shim.
 */ function Xu(t, e) {
    var n, s;
    let i;
    return i = Ha(t[0]) ? t[0] : Ha(t[1]) ? t[1] : "function" == typeof t[0] ? {
        next: t[0],
        error: t[1],
        complete: t[2]
    } : {
        next: t[1],
        error: t[2],
        complete: t[3]
    }, {
        next: t => {
            i.next && i.next(e(t));
        },
        error: null === (n = i.error) || void 0 === n ? void 0 : n.bind(i),
        complete: null === (s = i.complete) || void 0 === s ? void 0 : s.bind(i)
    };
}

class Zu extends Nc {
    constructor(t, e) {
        super(e), this.Yf = t;
    }
    get ref() {
        return new Hu(this.Yf, this.d_.ref);
    }
    get id() {
        return this.d_.id;
    }
    get metadata() {
        return this.d_.metadata;
    }
    get exists() {
        return this.d_.exists();
    }
    data(t) {
        return this.d_.data(t);
    }
    get(t, e) {
        return this.d_.get(t, e);
    }
    isEqual(t) {
        return vu(this.d_, t.d_);
    }
}

class th extends Zu {
    data(t) {
        return this.d_.data(t);
    }
}

class eh extends Nc {
    constructor(t, e) {
        super(e), this.firestore = t, this.Xf = new Ku(t);
    }
    where(t, e, n) {
        try {
            // The "as string" cast is a little bit of a hack. `where` accepts the
            // FieldPath Compat type as input, but is not typed as such in order to
            // not expose this via our public typings file.
            return new eh(this.firestore, lu(this.d_, function(t, e, n) {
                const s = e, i = au("where", t);
                return new _u(i, s, n);
            }(t, e, n)));
        } catch (t) {
            throw Ju(t, /(orderBy|where)\(\)/, "Query.$1()");
        }
    }
    orderBy(t, e) {
        try {
            // The "as string" cast is a little bit of a hack. `orderBy` accepts the
            // FieldPath Compat type as input, but is not typed as such in order to
            // not expose this via our public typings file.
            return new eh(this.firestore, lu(this.d_, function(t, e = "asc") {
                const n = e, s = au("orderBy", t);
                return new fu(s, n);
            }(t, e)));
        } catch (t) {
            throw Ju(t, /(orderBy|where)\(\)/, "Query.$1()");
        }
    }
    limit(t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(t) {
                return qc("limit", t), new du("limit", t, "F" /* First */);
            }
            /**
 * Creates a `QueryConstraint` that only returns the last matching documents.
 *
 * You must specify at least one `orderBy` clause for `limitToLast` queries,
 * otherwise an exception will be thrown during execution.
 *
 * @param limit - The maximum number of items to return.
 * @returns The created `Query`.
 */ (t)));
        } catch (t) {
            throw Ju(t, "limit()", "Query.limit()");
        }
    }
    limitToLast(t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(t) {
                return qc("limitToLast", t), new du("limitToLast", t, "L" /* Last */);
            }(t)));
        } catch (t) {
            throw Ju(t, "limitToLast()", "Query.limitToLast()");
        }
    }
    startAt(...t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(...t) {
                return new wu("startAt", t, /*before=*/ !0);
            }(...t)));
        } catch (t) {
            throw Ju(t, "startAt()", "Query.startAt()");
        }
    }
    startAfter(...t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(...t) {
                return new wu("startAfter", t, 
                /*before=*/ !1);
            }(...t)));
        } catch (t) {
            throw Ju(t, "startAfter()", "Query.startAfter()");
        }
    }
    endBefore(...t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(...t) {
                return new Eu("endBefore", t, /*before=*/ !0);
            }(...t)));
        } catch (t) {
            throw Ju(t, "endBefore()", "Query.endBefore()");
        }
    }
    endAt(...t) {
        try {
            return new eh(this.firestore, lu(this.d_, function(...t) {
                return new Eu("endAt", t, /*before=*/ !1);
            }(...t)));
        } catch (t) {
            throw Ju(t, "endAt()", "Query.endAt()");
        }
    }
    isEqual(t) {
        return Xc(this.d_, t.d_);
    }
    get(t) {
        let e;
        return e = "cache" === (null == t ? void 0 : t.source) ? 
        /**
 * Executes the query and returns the results as a `QuerySnapshot` from cache.
 * Returns an error if the document is not currently cached.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */
        function(t) {
            t = Bc(t, jc);
            const e = Bc(t.firestore, Xa), n = Za(e), s = new Su(e);
            return Ua(n, t.C_).then((n => new pu(e, s, t, n)));
        }
        /**
 * Executes the query and returns the results as a `QuerySnapshot` from the
 * server. Returns an error if the network is not available.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */ (this.d_) : "server" === (null == t ? void 0 : t.source) ? function(t) {
            t = Bc(t, jc);
            const e = Bc(t.firestore, Xa), n = Za(e), s = new Su(e);
            return Qa(n, t.C_, {
                source: "server"
            }).then((n => new pu(e, s, t, n)));
        }(this.d_) : function(t) {
            t = Bc(t, jc);
            const e = Bc(t.firestore, Xa), n = Za(e), s = new Su(e);
            return uu(t.C_), Qa(n, t.C_).then((n => new pu(e, s, t, n)));
        }(this.d_), e.then((t => new sh(this.firestore, new pu(this.firestore.d_, this.Xf, this.d_, t._d))));
    }
    onSnapshot(...t) {
        const e = Yu(t), n = Xu(t, (t => new sh(this.firestore, new pu(this.firestore.d_, this.Xf, this.d_, t._d))));
        return Cu(this.d_, e, n);
    }
    withConverter(t) {
        return new eh(this.firestore, this.d_.withConverter(zu.gd(this.firestore, t)));
    }
}

class nh extends Nc {
    constructor(t, e) {
        super(e), this.Yf = t;
    }
    get type() {
        return this.d_.type;
    }
    get doc() {
        return new th(this.Yf, this.d_.doc);
    }
    get oldIndex() {
        return this.d_.oldIndex;
    }
    get newIndex() {
        return this.d_.newIndex;
    }
}

class sh extends Nc {
    constructor(t, e) {
        super(e), this.Yf = t;
    }
    get query() {
        return new eh(this.Yf, this.d_.query);
    }
    get metadata() {
        return this.d_.metadata;
    }
    get size() {
        return this.d_.size;
    }
    get empty() {
        return this.d_.empty;
    }
    get docs() {
        return this.d_.docs.map((t => new th(this.Yf, t)));
    }
    docChanges(t) {
        return this.d_.docChanges(t).map((t => new nh(this.Yf, t)));
    }
    forEach(t, e) {
        this.d_.forEach((n => {
            t.call(e, new th(this.Yf, n));
        }));
    }
    isEqual(t) {
        return vu(this.d_, t.d_);
    }
}

class ih extends eh {
    constructor(t, e) {
        super(t, e), this.firestore = t, this.d_ = e;
    }
    get id() {
        return this.d_.id;
    }
    get path() {
        return this.d_.path;
    }
    get parent() {
        const t = this.d_.parent;
        return t ? new Hu(this.firestore, t) : null;
    }
    doc(t) {
        try {
            return new Hu(this.firestore, void 0 === t ? Jc(this.d_) : Jc(this.d_, t));
        } catch (t) {
            throw Ju(t, "doc()", "CollectionReference.doc()");
        }
    }
    add(t) {
        return function(t, e) {
            const n = Bc(t.firestore, Xa), s = Jc(t), i = Ru(t.v_, e);
            return xu(n, [ ca(oa(t.firestore), "addDoc", s.S_, i, null !== t.v_, {}).F_(s.S_, Fe.exists(!1)) ]).then((() => s));
        }(this.d_, t).then((t => new Hu(this.firestore, t)));
    }
    isEqual(t) {
        return Yc(this.d_, t.d_);
    }
    withConverter(t) {
        return new ih(this.firestore, this.d_.withConverter(zu.gd(this.firestore, t)));
    }
}

function rh(t) {
    return t instanceof Nc && (t = t.d_), Bc(t, Wc);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// The objects that are a part of this API are exposed to third-parties as
// compiled javascript so we want to flag our private members with a leading
// underscore to discourage their use.
/**
 * A `FieldPath` refers to a field in a document. The path may consist of a
 * single field name (referring to a top-level field in the document), or a list
 * of field names (referring to a nested field in the document).
 */ class oh extends Nc {
    /**
     * Creates a FieldPath from the provided field names. If more than one field
     * name is provided, the path will point to a nested field in a document.
     *
     * @param fieldNames - A list of field names.
     */
    constructor(...t) {
        super(new Cc(...t));
    }
    static documentId() {
        /**
         * Internal Note: The backend doesn't technically support querying by
         * document ID. Instead it queries by the entire document name (full path
         * included), but in the cases we currently support documentId(), the net
         * effect is the same.
         */
        return new oh(H.ht().rt());
    }
    isEqual(t) {
        return t instanceof Nc && (t = t.d_), t instanceof Cc && this.d_.f_.isEqual(t.f_);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Returns a sentinel for use with {@link updateDoc} or
 * {@link setDoc} with `{merge: true}` to mark a field for deletion.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ch extends Nc {
    static serverTimestamp() {
        const t = new ha("serverTimestamp");
        return t._methodName = "FieldValue.serverTimestamp", new ch(t);
    }
    static delete() {
        const t = new aa("deleteField");
        return t._methodName = "FieldValue.delete", new ch(t);
    }
    static arrayUnion(...t) {
        const e = 
        /**
 * Returns a special value that can be used with {@link setDoc} or {@link
 * updateDoc} that tells the server to union the given elements with any array
 * value that already exists on the server. Each specified element that doesn't
 * already exist in the array will be added to the end. If the field being
 * modified is not already an array it will be overwritten with an array
 * containing exactly the specified elements.
 *
 * @param elements - The elements to union into the array.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`.
 */
        function(...t) {
            // NOTE: We don't actually parse the data until it's used in set() or
            // update() since we'd need the Firestore instance to do this.
            return new la("arrayUnion", t);
        }
        /**
 * Returns a special value that can be used with {@link (setDoc:1)} or {@link
 * updateDoc} that tells the server to remove the given elements from any
 * array value that already exists on the server. All instances of each element
 * specified will be removed from the array. If the field being modified is not
 * already an array it will be overwritten with an empty array.
 *
 * @param elements - The elements to remove from the array.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`
 */ (...t);
        return e._methodName = "FieldValue.arrayUnion", new ch(e);
    }
    static arrayRemove(...t) {
        const e = function(...t) {
            // NOTE: We don't actually parse the data until it's used in set() or
            // update() since we'd need the Firestore instance to do this.
            return new _a("arrayRemove", t);
        }
        /**
 * Returns a special value that can be used with {@link setDoc} or {@link
 * updateDoc} that tells the server to increment the field's current value by
 * the given value.
 *
 * If either the operand or the current field value uses floating point
 * precision, all arithmetic follows IEEE 754 semantics. If both values are
 * integers, values outside of JavaScript's safe number range
 * (`Number.MIN_SAFE_INTEGER` to `Number.MAX_SAFE_INTEGER`) are also subject to
 * precision loss. Furthermore, once processed by the Firestore backend, all
 * integer operations are capped between -2^63 and 2^63-1.
 *
 * If the current field value is not of type `number`, or if the field does not
 * yet exist, the transformation sets the field to the given value.
 *
 * @param n - The value to increment by.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`
 */ (...t);
        return e._methodName = "FieldValue.arrayRemove", new ch(e);
    }
    static increment(t) {
        const e = function(t) {
            return new fa("increment", t);
        }(t);
        return e._methodName = "FieldValue.increment", new ch(e);
    }
    isEqual(t) {
        return this.d_.isEqual(t.d_);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function ah(t) {
    /**
 * Loads a Firestore bundle into the local cache.
 *
 * @param firestore - The `Firestore` instance to load bundles for for.
 * @param bundleData - An object representing the bundle to be loaded. Valid objects are
 *   `ArrayBuffer`, `ReadableStream<Uint8Array>` or `string`.
 *
 * @return
 *   A `LoadBundleTask` object, which notifies callers with progress updates, and completion
 *   or error events. It can be used as a `Promise<LoadBundleTaskProgress>`.
 */
    return function(t, e) {
        const n = Za(t = Bc(t, Xa)), s = new Ja;
        return ja(n, t.I_, e, s), s;
    }
    /**
 * Reads a Firestore `Query` from local cache, identified by the given name.
 *
 * The named queries are packaged  into bundles on the server side (along
 * with resulting documents), and loaded to local cache using `loadBundle`. Once in local
 * cache, use this method to extract a `Query` by name.
 */ (this.d_, t);
}

function uh(t) {
    return (e = this.d_, n = t, Ga(Za(e = Bc(e, Xa)), n).then((t => t ? new jc(e, null, t.query) : null))).then((t => t ? new eh(this, t) : null));
    var e, n;
}

export { Lu as Blob, Ya as CACHE_SIZE_UNLIMITED, ih as CollectionReference, Hu as DocumentReference, Zu as DocumentSnapshot, Xa as ExpFirebaseFirestore, oh as FieldPath, ch as FieldValue, Qu as Firestore, Zc as GeoPoint, Uu as IndexedDbPersistenceProvider, qu as MemoryPersistenceProvider, eh as Query, th as QueryDocumentSnapshot, sh as QuerySnapshot, K as Timestamp, ju as Transaction, Gu as WriteBatch, ah as loadBundle, uh as namedQuery, Wu as setLogLevel };
//# sourceMappingURL=prebuilt.js.map
